var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

// js/common/actions/confirm-button.js
var ConfirmButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      if (!window.confirm(this.getAttribute("data-message") ?? "Once confirmed, this action cannot be undone.")) {
        event.preventDefault();
      }
    });
  }
};
if (!window.customElements.get("confirm-button")) {
  window.customElements.define("confirm-button", ConfirmButton);
}

// js/common/actions/copy-button.js
var _CopyButton_instances, copyToClipboard_fn;
var CopyButton = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _CopyButton_instances);
    this.addEventListener("click", __privateMethod(this, _CopyButton_instances, copyToClipboard_fn));
  }
  get buttonElement() {
    return this.querySelector("button");
  }
};
_CopyButton_instances = new WeakSet();
copyToClipboard_fn = async function() {
  if (!navigator.clipboard) {
    return;
  }
  await navigator.clipboard.writeText(this.getAttribute("data-text") ?? "");
  if (this.hasAttribute("data-success-message")) {
    const originalMessage = this.buttonElement.textContent;
    this.buttonElement.textContent = this.getAttribute("data-success-message");
    setTimeout(() => {
      this.buttonElement.textContent = originalMessage;
    }, 1500);
  }
};
if (!window.customElements.get("copy-button")) {
  window.customElements.define("copy-button", CopyButton);
}

// js/common/actions/share-button.js
var _ShareButton_instances, showSystemShare_fn;
var ShareButton = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _ShareButton_instances);
    if (navigator.share) {
      this.querySelector(".share-buttons--native").hidden = false;
      this.addEventListener("click", __privateMethod(this, _ShareButton_instances, showSystemShare_fn));
    }
  }
};
_ShareButton_instances = new WeakSet();
showSystemShare_fn = async function() {
  try {
    await navigator.share({
      title: this.hasAttribute("share-title") ? this.getAttribute("share-title") : document.title,
      url: this.hasAttribute("share-url") ? this.getAttribute("share-url") : window.location.href
    });
  } catch (error) {
  }
};
if (!window.customElements.get("share-button")) {
  window.customElements.define("share-button", ShareButton);
}

// js/common/animation/marquee-text.js
import { inView, animate } from "vendor";
var _currentAnimation, _MarqueeText_instances, direction_get, scroller_get, initialize_fn;
var MarqueeText = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _MarqueeText_instances);
    __privateAdd(this, _currentAnimation);
    inView(this, __privateMethod(this, _MarqueeText_instances, initialize_fn).bind(this), { margin: "400px" });
    if (this.hasAttribute("pause-on-hover")) {
      this.addEventListener("pointerenter", () => __privateGet(this, _currentAnimation)?.pause());
      this.addEventListener("pointerleave", () => __privateGet(this, _currentAnimation)?.play());
    }
  }
};
_currentAnimation = new WeakMap();
_MarqueeText_instances = new WeakSet();
direction_get = function() {
  return this.getAttribute("direction") === "right" ? 1 : -1;
};
scroller_get = function() {
  return this.shadowRoot.querySelector('[part="scroller"]');
};
initialize_fn = function() {
  if (this.children[0]?.childElementCount === 0) {
    return;
  }
  this.attachShadow({ mode: "open" }).appendChild(
    document.createRange().createContextualFragment(`
      <slot part="scroller"></slot>
    `)
  );
  const fragment = document.createDocumentFragment();
  const duplicateCount = Math.ceil(this.clientWidth / this.firstElementChild.clientWidth);
  for (let i = 1; i <= duplicateCount; ++i) {
    for (let y = 0; y < 2; ++y) {
      const node = this.firstElementChild.cloneNode(true);
      const value = 100 * i * (y % 2 === 0 ? -1 : 1);
      node.setAttribute("aria-hidden", "true");
      node.style.cssText = `position: absolute; inset-inline-start: calc(${value}%);`;
      fragment.appendChild(node);
    }
  }
  this.append(fragment);
  // Copies are often created already held, or held because they start outside the screen. Put every logo back
  // before the strip moves, so the row stays complete the whole way across the page.
  this.querySelectorAll("img").forEach((img) => {
    if (img.getAttribute("data-gl-parked") === "1") {
      const srcset = img.getAttribute("data-gl-srcset");
      const src = img.getAttribute("data-gl-src");
      if (srcset) img.setAttribute("srcset", srcset);
      if (src) img.setAttribute("src", src);
      img.removeAttribute("data-gl-srcset");
      img.removeAttribute("data-gl-src");
      img.removeAttribute("data-gl-parked");
    }
    img.loading = "eager";
  });
  __privateSet(this, _currentAnimation, animate(
    __privateGet(this, _MarqueeText_instances, scroller_get),
    { transform: ["translateX(0)", `translateX(calc(var(--transform-logical-flip) * ${__privateGet(this, _MarqueeText_instances, direction_get) * 100}%))`] },
    {
      duration: 1 / parseFloat(this.getAttribute("speed")) * (__privateGet(this, _MarqueeText_instances, scroller_get).clientWidth / 300),
      easing: "linear",
      repeat: Infinity
    }
  ));
};
if (!window.customElements.get("marquee-text")) {
  window.customElements.define("marquee-text", MarqueeText);
}

// js/common/behavior/gesture-area.js
var _domElement, _thresholdDistance, _thresholdTime, _signal, _firstClientX, _tracking, _start, _GestureArea_instances, touchStart_fn, preventTouch_fn, gestureStart_fn, gestureMove_fn, gestureEnd_fn;
var GestureArea = class {
  constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
    __privateAdd(this, _GestureArea_instances);
    __privateAdd(this, _domElement);
    __privateAdd(this, _thresholdDistance);
    __privateAdd(this, _thresholdTime);
    __privateAdd(this, _signal);
    __privateAdd(this, _firstClientX);
    __privateAdd(this, _tracking, false);
    __privateAdd(this, _start, {});
    __privateSet(this, _domElement, domElement);
    __privateSet(this, _thresholdDistance, thresholdDistance);
    __privateSet(this, _thresholdTime, thresholdTime);
    __privateSet(this, _signal, signal ?? new AbortController().signal);
    __privateGet(this, _domElement).addEventListener("touchstart", __privateMethod(this, _GestureArea_instances, touchStart_fn).bind(this), { passive: true, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("touchmove", __privateMethod(this, _GestureArea_instances, preventTouch_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerdown", __privateMethod(this, _GestureArea_instances, gestureStart_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointermove", __privateMethod(this, _GestureArea_instances, gestureMove_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerup", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerleave", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointercancel", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
  }
};
_domElement = new WeakMap();
_thresholdDistance = new WeakMap();
_thresholdTime = new WeakMap();
_signal = new WeakMap();
_firstClientX = new WeakMap();
_tracking = new WeakMap();
_start = new WeakMap();
_GestureArea_instances = new WeakSet();
touchStart_fn = function(event) {
  __privateSet(this, _firstClientX, event.touches[0].clientX);
};
preventTouch_fn = function(event) {
  if (Math.abs(event.touches[0].clientX - __privateGet(this, _firstClientX)) > 10) {
    event.preventDefault();
  }
};
gestureStart_fn = function(event) {
  __privateSet(this, _tracking, true);
  __privateSet(this, _start, {
    time: (/* @__PURE__ */ new Date()).getTime(),
    x: event.clientX,
    y: event.clientY
  });
};
gestureMove_fn = function(event) {
  if (__privateGet(this, _tracking)) {
    event.preventDefault();
  }
};
gestureEnd_fn = function(event) {
  if (!__privateGet(this, _tracking)) {
    return;
  }
  __privateSet(this, _tracking, false);
  const now = (/* @__PURE__ */ new Date()).getTime(), deltaTime = now - __privateGet(this, _start).time, deltaX = event.clientX - __privateGet(this, _start).x, deltaY = event.clientY - __privateGet(this, _start).y;
  if (deltaTime > __privateGet(this, _thresholdTime)) {
    return;
  }
  let matchedEvent;
  if (deltaX === 0 && deltaY === 0) {
    matchedEvent = "tap";
  } else if (deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swiperight";
  } else if (-deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeleft";
  } else if (deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipedown";
  } else if (-deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeup";
  }
  if (matchedEvent) {
    __privateGet(this, _domElement).dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
  }
};

// js/common/utilities/country-selector.js
var _onCountryChangedListener, _CountrySelector_instances, onCountryChanged_fn;
var CountrySelector = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CountrySelector_instances);
    __privateAdd(this, _onCountryChangedListener, __privateMethod(this, _CountrySelector_instances, onCountryChanged_fn).bind(this));
  }
  connectedCallback() {
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement.addEventListener("change", __privateGet(this, _onCountryChangedListener));
    if (this.hasAttribute("country") && this.getAttribute("country") !== "") {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute("country"))
      );
    }
    this.countryElement.dispatchEvent(new Event("change"));
  }
  disconnectedCallback() {
    this.countryElement.removeEventListener("change", __privateGet(this, _onCountryChangedListener));
  }
};
_onCountryChangedListener = new WeakMap();
_CountrySelector_instances = new WeakSet();
onCountryChanged_fn = function() {
  const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute("data-provinces"));
  this.provinceElement.parentElement.hidden = provinces.length === 0;
  if (provinces.length === 0) {
    return;
  }
  this.provinceElement.innerHTML = "";
  provinces.forEach((data) => {
    const selected = data[1] === this.getAttribute("province") || data[0] === this.getAttribute("province");
    this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
  });
};
if (!window.customElements.get("country-selector")) {
  window.customElements.define("country-selector", CountrySelector);
}

// js/common/utilities/cached-fetch.js
var cachedMap = /* @__PURE__ */ new Map();
var cachedStatus = /* @__PURE__ */ new Map();
var cachedInflight = /* @__PURE__ */ new Map();
function cachedFetch(url, options) {
  if (cachedMap.has(url)) {
    return Promise.resolve(new Response(cachedMap.get(url), { status: cachedStatus.get(url) || 200 }));
  }
  if (cachedInflight.has(url)) {
    return cachedInflight.get(url).then((result) => new Response(result.content, { status: result.status }));
  }
  const pending = fetch(url, options).then((response) => {
    const contentType = response.headers.get("Content-Type") || "";
    const cacheable = response.status === 200 && (contentType.match(/application\/json/i) || contentType.match(/text\//i));
    return response.text().then((content) => {
      if (cacheable) {
        cachedMap.set(url, content);
        cachedStatus.set(url, response.status);
      }
      cachedInflight.delete(url);
      return { content, status: response.status };
    });
  }).catch((error) => {
    cachedInflight.delete(url);
    throw error;
  });
  cachedInflight.set(url, pending);
  return pending.then((result) => new Response(result.content, { status: result.status }));
}
cachedFetch.peek = (url) => cachedMap.has(url) ? cachedMap.get(url) : null;
window.cachedFetch = cachedFetch;

// js/common/utilities/extract-section-id.js
function extractSectionId(element) {
  if (!element) {
    return "";
  }
  const section = element.classList?.contains("shopify-section") ? element : element.closest?.(".shopify-section");
  if (section?.id) {
    return section.id.replace("shopify-section-", "");
  }
  return element.closest?.("[section-id]")?.getAttribute("section-id") || "";
}

// js/common/utilities/dom.js
function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }
  for (const template of root.querySelectorAll("template")) {
    element = deepQuerySelector(template.content, selector);
    if (element) {
      return element;
    }
  }
  return null;
}
function throttle(callback) {
  let requestId = null, lastArgs;
  const later = (context) => () => {
    requestId = null;
    callback.apply(context, lastArgs);
  };
  const throttled = (...args) => {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(later(this));
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}
function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
function waitForEvent(element, eventName) {
  return new Promise((resolve) => {
    const done = (event) => {
      if (event.target === element) {
        element.removeEventListener(eventName, done);
        resolve(event);
      }
    };
    element.addEventListener(eventName, done);
  });
}

// js/common/utilities/media.js
function restoreDeferredImages(element, options) {
  element.querySelectorAll("img[data-srcset], img[data-src]").forEach((img) => {
    if (img.hasAttribute("data-srcset")) {
      img.srcset = img.getAttribute("data-srcset");
      img.removeAttribute("data-srcset");
    }
    if (img.hasAttribute("data-src")) {
      img.src = img.getAttribute("data-src");
      img.removeAttribute("data-src");
    }
  });
  // A color change reveals every slide of that color. Images can load then, but a video file
  // waits until that slide is the one on screen.
  if (options && options.video === false) return;
  // Gallery videos keep source and poster URLs in data-src/data-poster until the slide is shown (snippets/media.liquid).
  const restoredVideos = /* @__PURE__ */ new Set();
  element.querySelectorAll("video[data-poster]").forEach((video) => {
    video.poster = video.getAttribute("data-poster");
    video.removeAttribute("data-poster");
  });
  element.querySelectorAll("video[data-src], video source[data-src]").forEach((node) => {
    if (!node.hasAttribute("data-src")) return;
    node.src = node.getAttribute("data-src");
    node.removeAttribute("data-src");
    restoredVideos.add(node.tagName === "VIDEO" ? node : node.closest("video"));
  });
  // Only videos whose sources were just restored are reloaded, so a video already playing is never reset.
  restoredVideos.forEach((video) => {
    if (video && typeof video.load === "function") video.load();
  });
}
// Autoplay waits for the page load event so a video never shares bandwidth with the first images.
function afterPageLoad(callback) {
  if (document.readyState === "complete") {
    callback();
  } else {
    window.addEventListener("load", () => callback(), { once: true });
  }
}
function videoLoaded(videoOrArray) {
  if (!videoOrArray) {
    return Promise.resolve();
  }
  videoOrArray = videoOrArray instanceof Element ? [videoOrArray] : Array.from(videoOrArray);
  return Promise.all(
    videoOrArray.map((video) => {
      return new Promise((resolve) => {
        if (video.tagName === "VIDEO" && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA || !video.offsetParent || video.parentNode.hasAttribute("suspended")) {
          resolve();
        } else {
          video.oncanplay = () => resolve();
        }
      });
    })
  );
}
function imageLoaded(imageOrArray) {
  if (!imageOrArray) {
    return Promise.resolve();
  }
  imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
  return Promise.all(
    imageOrArray.map((image) => {
      return new Promise((resolve) => {
        if (image.tagName !== "IMG" || image.complete) {
          resolve();
          return;
        }
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    })
  );
}
function generateSrcset(imageObjectOrString, widths = []) {
  let imageUrl, maxWidth;
  if (typeof imageObjectOrString === "string") {
    imageUrl = new URL(imageObjectOrString.startsWith("//") ? `https:${imageObjectOrString}` : imageObjectOrString);
    maxWidth = parseInt(imageUrl.searchParams.get("width"));
  } else {
    imageUrl = new URL(imageObjectOrString["src"].startsWith("//") ? `https:${imageObjectOrString["src"]}` : imageObjectOrString["src"]);
    maxWidth = imageObjectOrString["width"];
  }
  return widths.filter((width) => width <= maxWidth).map((width) => {
    imageUrl.searchParams.set("width", width.toString());
    return `${imageUrl.href} ${width}w`;
  }).join(", ");
}
function createMediaImg(media, widths = [], attributes = {}) {
  const image = new Image(media["preview_image"]["width"], media["preview_image"]["height"]), src = media["preview_image"]["src"], featuredMediaUrl = new URL(src.startsWith("//") ? `https:${src}` : src);
  const maxWidth = media["preview_image"]["width"] || 0;
  const allowed = widths.filter((width) => !maxWidth || width <= maxWidth);
  // The src is what loads before the image is on the page. A card-sized file appears immediately; srcset still offers the larger ones.
  const chosen = allowed.find((width) => width >= 600) || allowed[allowed.length - 1];
  if (chosen) featuredMediaUrl.searchParams.set("width", String(chosen));
  for (const attributeKey in attributes) {
    image.setAttribute(attributeKey, attributes[attributeKey]);
  }
  image.alt = media["alt"] || "";
  image.src = featuredMediaUrl.href;
  image.srcset = generateSrcset(media["preview_image"], widths);
  return image;
}

// js/common/utilities/media-query.js
function matchesMediaQuery(breakpointName) {
  if (!window.themeVariables.mediaQueries.hasOwnProperty(breakpointName)) {
    throw `Media query ${breakpointName} does not exist`;
  }
  return window.matchMedia(window.themeVariables.mediaQueries[breakpointName]).matches;
}
function mediaQueryListener(breakpointName, func) {
  if (!window.themeVariables.mediaQueries.hasOwnProperty(breakpointName)) {
    throw `Media query ${breakpointName} does not exist`;
  }
  return window.matchMedia(window.themeVariables.mediaQueries[breakpointName]).addEventListener("change", func);
}

// js/common/utilities/player.js
var _callback, _duration, _remainingTime, _startTime, _timer, _state, _onVisibilityChangeListener, _mustResumeOnVisibility, _Player_instances, onVisibilityChange_fn;
var Player = class extends EventTarget {
  constructor(durationInSec, stopOnVisibility = true) {
    super();
    __privateAdd(this, _Player_instances);
    __privateAdd(this, _callback);
    __privateAdd(this, _duration);
    __privateAdd(this, _remainingTime);
    __privateAdd(this, _startTime);
    __privateAdd(this, _timer);
    __privateAdd(this, _state, "paused");
    __privateAdd(this, _onVisibilityChangeListener, __privateMethod(this, _Player_instances, onVisibilityChange_fn).bind(this));
    __privateAdd(this, _mustResumeOnVisibility, true);
    __privateSet(this, _callback, () => this.dispatchEvent(new CustomEvent("player:end")));
    __privateSet(this, _duration, __privateSet(this, _remainingTime, durationInSec * 1e3));
    if (stopOnVisibility) {
      document.addEventListener("visibilitychange", __privateGet(this, _onVisibilityChangeListener));
    }
  }
  pause() {
    if (__privateGet(this, _state) !== "started") {
      return;
    }
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "paused");
    __privateSet(this, _remainingTime, __privateGet(this, _remainingTime) - ((/* @__PURE__ */ new Date()).getTime() - __privateGet(this, _startTime)));
    this.dispatchEvent(new CustomEvent("player:pause"));
  }
  resume(restartTimer = false) {
    if (__privateGet(this, _state) !== "stopped") {
      if (restartTimer) {
        this.start();
      } else {
        clearTimeout(__privateGet(this, _timer));
        __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
        __privateSet(this, _state, "started");
        __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
        this.dispatchEvent(new CustomEvent("player:resume"));
      }
    }
  }
  start() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
    __privateSet(this, _state, "started");
    __privateSet(this, _remainingTime, __privateGet(this, _duration));
    __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
    this.dispatchEvent(new CustomEvent("player:start"));
  }
  stop() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "stopped");
    this.dispatchEvent(new CustomEvent("player:stop"));
  }
};
_callback = new WeakMap();
_duration = new WeakMap();
_remainingTime = new WeakMap();
_startTime = new WeakMap();
_timer = new WeakMap();
_state = new WeakMap();
_onVisibilityChangeListener = new WeakMap();
_mustResumeOnVisibility = new WeakMap();
_Player_instances = new WeakSet();
onVisibilityChange_fn = function() {
  if (document.visibilityState === "hidden") {
    __privateSet(this, _mustResumeOnVisibility, __privateGet(this, _state) === "started");
    this.pause();
    this.dispatchEvent(new CustomEvent("player:visibility-pause"));
  } else if (document.visibilityState === "visible" && __privateGet(this, _mustResumeOnVisibility)) {
    this.resume();
    this.dispatchEvent(new CustomEvent("player:visibility-resume"));
  }
};

// js/common/utilities/qr-code.js
var QrCode = class extends HTMLElement {
  connectedCallback() {
    new window.QRCode(this, {
      text: this.getAttribute("identifier"),
      width: this.hasAttribute("width") ? parseInt(this.getAttribute("width")) : 200,
      height: this.hasAttribute("height") ? parseInt(this.getAttribute("height")) : 200
    });
  }
};
if (!window.customElements.get("qr-code")) {
  window.customElements.define("qr-code", QrCode);
}

// js/common/utilities/accessibility.js
function announceText(holderId, message) {
  const holder = document.getElementById(holderId);
  holder.textContent = "";
  setTimeout(() => {
    holder.textContent = message;
  }, 100);
}
function announceStatus(message) {
  announceText("status-announcement", message);
}
function announceError(message) {
  announceText("error-announcement", message);
}

// js/common/behavior/height-observer.js
var _resizeObserver, _HeightObserver_instances, updateCustomProperties_fn;
var HeightObserver = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _HeightObserver_instances);
    __privateAdd(this, _resizeObserver, new ResizeObserver(throttle(__privateMethod(this, _HeightObserver_instances, updateCustomProperties_fn).bind(this))));
  }
  connectedCallback() {
    __privateGet(this, _resizeObserver).observe(this);
    if (!window.ResizeObserver) {
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(2)}px`);
    }
  }
  disconnectedCallback() {
    __privateGet(this, _resizeObserver).unobserve(this);
  }
};
_resizeObserver = new WeakMap();
_HeightObserver_instances = new WeakSet();
updateCustomProperties_fn = function(entries) {
  entries.forEach((entry) => {
    if (entry.target === this) {
      const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${height.toFixed(2)}px`);
    }
  });
};
if (!window.customElements.get("height-observer")) {
  window.customElements.define("height-observer", HeightObserver);
}

// js/common/behavior/loading-bar.js
import { animate as animate2 } from "vendor";
var _LoadingBar_instances, onLoadingStart_fn, onLoadingEnd_fn;
var LoadingBar = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _LoadingBar_instances);
    document.addEventListener("theme:loading:start", __privateMethod(this, _LoadingBar_instances, onLoadingStart_fn).bind(this));
    document.addEventListener("theme:loading:end", __privateMethod(this, _LoadingBar_instances, onLoadingEnd_fn).bind(this));
  }
};
_LoadingBar_instances = new WeakSet();
onLoadingStart_fn = function() {
  animate2(this, { opacity: [0, 1], transform: ["scaleX(0)", "scaleX(0.4)"] }, { duration: 0.25 });
};
onLoadingEnd_fn = async function() {
  await animate2(this, { transform: [null, "scaleX(1)"] }, { duration: 0.25 }).finished;
  animate2(this, { opacity: 0 }, { duration: 0.25 });
};
if (!window.customElements.get("loading-bar")) {
  window.customElements.define("loading-bar", LoadingBar);
}

// js/common/behavior/safe-sticky.js
import { inView as inView2 } from "vendor";
var _resizeObserver2, _checkPositionListener, _initialTop, _lastKnownY, _currentTop, _position, _SafeSticky_instances, recalculateStyles_fn, checkPosition_fn;
var SafeSticky = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _SafeSticky_instances);
    __privateAdd(this, _resizeObserver2, new ResizeObserver(__privateMethod(this, _SafeSticky_instances, recalculateStyles_fn).bind(this)));
    __privateAdd(this, _checkPositionListener, throttle(__privateMethod(this, _SafeSticky_instances, checkPosition_fn).bind(this)));
    __privateAdd(this, _initialTop, 0);
    __privateAdd(this, _lastKnownY, 0);
    /* we could initialize it to window.scrollY but this avoids a costly reflow */
    __privateAdd(this, _currentTop, 0);
    __privateAdd(this, _position, "relative");
    if (window.Shopify.designMode) {
      document.addEventListener("shopify:section:load", __privateMethod(this, _SafeSticky_instances, recalculateStyles_fn).bind(this));
    }
  }
  connectedCallback() {
    inView2(
      this,
      () => {
        window.addEventListener("scroll", __privateGet(this, _checkPositionListener));
        __privateGet(this, _resizeObserver2).observe(this);
        return () => {
          window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
          __privateGet(this, _resizeObserver2).unobserve(this);
        };
      },
      { margin: "500px" }
    );
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
    __privateGet(this, _resizeObserver2).unobserve(this);
  }
};
_resizeObserver2 = new WeakMap();
_checkPositionListener = new WeakMap();
_initialTop = new WeakMap();
_lastKnownY = new WeakMap();
_currentTop = new WeakMap();
_position = new WeakMap();
_SafeSticky_instances = new WeakSet();
recalculateStyles_fn = function() {
  this.style.removeProperty("top");
  const computedStyles = getComputedStyle(this);
  __privateSet(this, _initialTop, parseInt(computedStyles.top));
  __privateSet(this, _position, computedStyles.position);
  __privateMethod(this, _SafeSticky_instances, checkPosition_fn).call(this);
};
checkPosition_fn = function() {
  if (__privateGet(this, _position) !== "sticky") {
    return this.style.removeProperty("top");
  }
  let bounds = this.getBoundingClientRect(), maxTop = bounds.top + window.scrollY - this.offsetTop + __privateGet(this, _initialTop), minTop = this.clientHeight - window.innerHeight + 20;
  if (window.scrollY < __privateGet(this, _lastKnownY)) {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) - (window.scrollY - __privateGet(this, _lastKnownY)));
  } else {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) + (__privateGet(this, _lastKnownY) - window.scrollY));
  }
  __privateSet(this, _currentTop, Math.min(Math.max(__privateGet(this, _currentTop), -minTop), maxTop, __privateGet(this, _initialTop)));
  __privateSet(this, _lastKnownY, window.scrollY);
  this.style.top = `${Math.round(__privateGet(this, _currentTop))}px`;
};
if (!window.customElements.get("safe-sticky")) {
  window.customElements.define("safe-sticky", SafeSticky);
}

// js/common/carousel/carousel-navigation.js
// theme.js can define these controls while the page is still parsing, before the carousel they point at exists.
function whenControlledCarousel(host, ready) {
  const tryBind = () => {
    if (!host.isConnected || !host.carousel) return false;
    return ready() !== false;
  };
  if (tryBind()) return () => {};
  if (!host.getAttribute("aria-controls")) return () => {};
  const observer = new MutationObserver(() => {
    if (!tryBind()) return;
    observer.disconnect();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  return () => observer.disconnect();
}
var _abortController, _allItems, _CarouselNavigation_instances, onCarouselFilter_fn;
var CarouselNavigation = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CarouselNavigation_instances);
    __privateAdd(this, _abortController);
    __privateAdd(this, _allItems, []);
  }
  connectedCallback() {
    this._cancelCarouselWait?.();
    this._cancelCarouselWait = whenControlledCarousel(this, () => {
      if (this._navReady) return true;
      const buttons = Array.from(this.querySelectorAll("button"));
      if (!buttons.length) return false;
      this._navReady = true;
      __privateSet(this, _abortController, new AbortController());
      __privateSet(this, _allItems, buttons);
      __privateGet(this, _allItems).forEach((button) => button.addEventListener("click", () => this.onButtonClicked(this.items.indexOf(button)), { signal: __privateGet(this, _abortController).signal }));
      this.carousel.addEventListener("carousel:change", (event) => this.onNavigationChange(event.detail.index), { signal: __privateGet(this, _abortController).signal });
      this.carousel.addEventListener("carousel:filter", __privateMethod(this, _CarouselNavigation_instances, onCarouselFilter_fn).bind(this), { signal: __privateGet(this, _abortController).signal });
      return true;
    });
  }
  disconnectedCallback() {
    this._cancelCarouselWait?.();
    this._cancelCarouselWait = null;
    this._navReady = false;
    __privateGet(this, _abortController)?.abort();
  }
  get items() {
    return __privateGet(this, _allItems).filter((item) => !item.hasAttribute("hidden"));
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
  get selectedIndex() {
    return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
  }
  onButtonClicked(newIndex) {
    this.carousel.select(newIndex);
    this.onNavigationChange(newIndex);
  }
  onNavigationChange(newIndex) {
    this.items.forEach((button, index) => button.setAttribute("aria-current", newIndex === index ? "true" : "false"));
    if (this.hasAttribute("align-selected") && (this.scrollWidth !== this.clientWidth || this.scrollHeight !== this.clientHeight)) {
      this.scrollTo({
        left: this.items[newIndex].offsetLeft - this.clientWidth / 2 + this.items[newIndex].clientWidth / 2,
        top: this.items[newIndex].offsetTop - this.clientHeight / 2 + this.items[newIndex].clientHeight / 2,
        behavior: matchesMediaQuery("motion-safe") ? "smooth" : "auto"
      });
    }
  }
};
_abortController = new WeakMap();
_allItems = new WeakMap();
_CarouselNavigation_instances = new WeakSet();
onCarouselFilter_fn = function(event) {
  __privateGet(this, _allItems).forEach((item, index) => {
    item.toggleAttribute("hidden", (event.detail.filteredIndexes || []).includes(index));
  });
};
function syncCarouselEdgeButton(host, edge) {
  const button = host.firstElementChild;
  const carousel = host.carousel;
  if (!button || !carousel || typeof carousel.selectedIndex !== "number") return;
  const count = carousel.cells?.length || 0;
  const index = carousel.selectedIndex || 0;
  button.disabled = count < 2 || (edge === "start" ? index <= 0 : index >= count - 1);
}
var CarouselPrevButton = class extends HTMLElement {
  #abortController;
  #cancelWait;
  #sync() {
    syncCarouselEdgeButton(this, "start");
  }
  connectedCallback() {
    this.#cancelWait?.();
    this.#cancelWait = whenControlledCarousel(this, () => {
      if (this.#abortController) return true;
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;
      this.addEventListener("click", () => this.carousel.previous(), { signal });
      this.#sync();
      this.carousel.addEventListener("carousel:change", () => this.#sync(), { signal });
      this.carousel.addEventListener("carousel:filter", () => this.#sync(), { signal });
      this.carousel.addEventListener("carousel:settle", () => this.#sync(), { signal });
      return true;
    });
  }
  disconnectedCallback() {
    this.#cancelWait?.();
    this.#cancelWait = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
var CarouselNextButton = class extends HTMLElement {
  #abortController;
  #cancelWait;
  #sync() {
    syncCarouselEdgeButton(this, "end");
  }
  connectedCallback() {
    this.#cancelWait?.();
    this.#cancelWait = whenControlledCarousel(this, () => {
      if (this.#abortController) return true;
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;
      this.addEventListener("click", () => this.carousel.next(), { signal });
      this.#sync();
      this.carousel.addEventListener("carousel:change", () => this.#sync(), { signal });
      this.carousel.addEventListener("carousel:filter", () => this.#sync(), { signal });
      this.carousel.addEventListener("carousel:settle", () => this.#sync(), { signal });
      return true;
    });
  }
  disconnectedCallback() {
    this.#cancelWait?.();
    this.#cancelWait = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }
  get carousel() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
if (!window.customElements.get("carousel-prev-button")) {
  window.customElements.define("carousel-prev-button", CarouselPrevButton);
}
if (!window.customElements.get("carousel-next-button")) {
  window.customElements.define("carousel-next-button", CarouselNextButton);
}
if (!window.customElements.get("carousel-navigation")) {
  window.customElements.define("carousel-navigation", CarouselNavigation);
}

// js/common/carousel/effect-carousel.js
import { animate as animate3, timeline, inView as inView3 } from "vendor";
var _listenersAbortController, _gestureArea, _player, _targetIndex, _preventInitialTransition, _EffectCarousel_instances, setupListeners_fn, onKeyboardNavigation_fn, preloadImages_fn;
var EffectCarousel = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _EffectCarousel_instances);
    __privateAdd(this, _listenersAbortController);
    __privateAdd(this, _gestureArea);
    __privateAdd(this, _player);
    __privateAdd(this, _targetIndex, 0);
    __privateAdd(this, _preventInitialTransition, false);
    __privateMethod(this, _EffectCarousel_instances, setupListeners_fn).call(this);
    inView3(this, () => this.onBecameVisible());
    this.addEventListener("carousel:settle", (event) => {
      this.allCells.forEach((cell) => cell.classList.toggle("is-selected", cell === event.detail.cell));
    });
  }
  connectedCallback() {
    __privateSet(this, _targetIndex, Math.max(
      0,
      this.cells.findIndex((item) => item.classList.contains("is-selected"))
    ));
    inView3(this, () => __privateMethod(this, _EffectCarousel_instances, preloadImages_fn).call(this));
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (PROPERTIES)
   * -------------------------------------------------------------------------------------------------------------------
   */
  get allowSwipe() {
    return this.hasAttribute("allow-swipe");
  }
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return __privateGet(this, _targetIndex);
  }
  get player() {
    return __privateGet(this, _player);
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (METHODS)
   * -------------------------------------------------------------------------------------------------------------------
   */
  previous({ instant = false } = {}) {
    return this.select((this.selectedIndex - 1 + this.cells.length) % this.cells.length, { instant, direction: "previous" });
  }
  next({ instant = false } = {}) {
    return this.select((this.selectedIndex + 1 + this.cells.length) % this.cells.length, { instant, direction: "next" });
  }
  async select(index, { instant = false, direction = null } = {}) {
    if (!(index in this.cells)) {
      return Promise.resolve();
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if (index === this.selectedIndex) {
      return Promise.resolve();
    }
    __privateGet(this, _player)?.pause();
    const [fromSlide, toSlide] = [this.selectedCell, this.cells[index]];
    direction ??= index > this.selectedIndex ? "next" : "previous";
    __privateSet(this, _targetIndex, index);
    this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index, cell: this.cells[index] } }));
    const animationControls = this.createOnChangeAnimationControls(fromSlide, toSlide, { direction });
    if ("leaveControls" in animationControls && "enterControls" in animationControls) {
      const leaveAnimationControls = animationControls.leaveControls();
      if (instant) {
        leaveAnimationControls.finish();
      }
      await leaveAnimationControls.finished;
      __privateGet(this, _player)?.resume(true);
      fromSlide.classList.remove("is-selected");
      toSlide.classList.add("is-selected");
      const enterAnimationControls = animationControls.enterControls();
      if (instant) {
        enterAnimationControls.finish();
      }
      await enterAnimationControls.finished;
    } else {
      if (instant) {
        animationControls.finish();
      }
      __privateGet(this, _player)?.resume(true);
      toSlide.classList.add("is-selected");
      await animationControls.finished;
      fromSlide.classList.remove("is-selected");
    }
    this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index, cell: this.cells[index] } }));
  }
  /**
   * Filter cells by indexes. This will automatically add the "hidden" attribute to cells whose index belong to this
   * list. It will also take care of properly adjusting the controls. As a reaction, a "carousel:filter" with the
   * filtered indexes will be emitted.
   */
  filter(indexes = []) {
    const selected = this.selectedCell;
    this.allCells.forEach((cell, index) => {
      const hidden = indexes.includes(index);
      cell.toggleAttribute("hidden", hidden);
      if (!hidden) {
        restoreDeferredImages(cell, { video: cell === selected });
      }
    });
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
  async onBecameVisible() {
    const animationControls = await this.createOnBecameVisibleAnimationControls(this.selectedCell);
    [this.selectedCell, ...this.selectedCell.querySelectorAll("[reveal-on-scroll]")].forEach((element) => {
      element.removeAttribute("reveal-on-scroll");
    });
    if (__privateGet(this, _preventInitialTransition) && typeof animationControls.finish === "function") {
      animationControls.finish();
    }
    return animationControls.finished.then(() => {
      __privateGet(this, _player)?.resume(true);
      this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
    });
  }
  /**
   * The animation controls when the carousel enter into the view for the first time (by default, none)
   */
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate3(toSlide, {}, { duration: 0 });
  }
  /**
   * Define the transition when the slide changes
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    return timeline([
      [fromSlide, { opacity: [1, 0] }, { duration: 0.3 }],
      [toSlide, { opacity: [0, 1] }, { duration: 0.3, at: "<" }]
    ]);
  }
  /**
   * When the breakpoint changes (for instance from mobile to desktop), we may have to clean up the existing
   * attributes leave by Motion
   */
  cleanUpAnimations() {
    this.allCells.forEach((cell) => {
      cell.style.removeProperty("opacity");
      cell.style.removeProperty("visibility");
    });
  }
};
_listenersAbortController = new WeakMap();
_gestureArea = new WeakMap();
_player = new WeakMap();
_targetIndex = new WeakMap();
_preventInitialTransition = new WeakMap();
_EffectCarousel_instances = new WeakSet();
/**
 * -------------------------------------------------------------------------------------------------------------------
 * PRIVATE
 * -------------------------------------------------------------------------------------------------------------------
 */
setupListeners_fn = function() {
  if (this.hasAttribute("disabled-on")) {
    mediaQueryListener(this.getAttribute("disabled-on"), (event) => {
      if (event.matches) {
        __privateGet(this, _listenersAbortController)?.abort();
        this.cleanUpAnimations();
      } else {
        __privateMethod(this, _EffectCarousel_instances, setupListeners_fn).call(this);
      }
    });
    if (matchesMediaQuery(this.getAttribute("disabled-on"))) {
      return;
    }
  }
  __privateSet(this, _listenersAbortController, new AbortController());
  const listenerOptions = { signal: __privateGet(this, _listenersAbortController).signal };
  if (Shopify.designMode) {
    this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => __privateSet(this, _preventInitialTransition, event.detail.load), listenerOptions);
  }
  if (this.allCells.length > 1) {
    this.addEventListener("carousel:change", __privateMethod(this, _EffectCarousel_instances, preloadImages_fn));
    if (this.allowSwipe) {
      __privateSet(this, _gestureArea, new GestureArea(this, { signal: __privateGet(this, _listenersAbortController).signal }));
      this.addEventListener("swipeleft", this.next, listenerOptions);
      this.addEventListener("swiperight", this.previous, listenerOptions);
    }
    if (!this.hasAttribute("disable-keyboard-navigation")) {
      this.tabIndex = 0;
      this.addEventListener("keydown", __privateMethod(this, _EffectCarousel_instances, onKeyboardNavigation_fn), listenerOptions);
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }), listenerOptions);
    }
    if (this.hasAttribute("autoplay")) {
      __privateGet(this, _player) ?? __privateSet(this, _player, new Player(this.getAttribute("autoplay") ?? 5));
      __privateGet(this, _player).addEventListener("player:end", this.next.bind(this), listenerOptions);
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => __privateGet(this, _player).stop(), listenerOptions);
        this.addEventListener("shopify:block:deselect", () => __privateGet(this, _player).start(), listenerOptions);
      }
    }
  }
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * OTHER
 * -------------------------------------------------------------------------------------------------------------------
 */
onKeyboardNavigation_fn = function(event) {
  if (event.target !== this) {
    return;
  }
  if (event.code === "ArrowLeft") {
    this.previous();
  } else if (event.code === "ArrowRight") {
    this.next();
  }
};
preloadImages_fn = function() {
  const previousSlide = this.cells[(this.selectedIndex - 1 + this.cells.length) % this.cells.length], nextSlide = this.cells[(this.selectedIndex + 1 + this.cells.length) % this.cells.length];
  const selected = this.selectedCell;
  [previousSlide, selected, nextSlide].forEach((item) => {
    restoreDeferredImages(item);
    Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
  });
  if (selected) {
    Array.from(selected.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
  }
};
if (!window.customElements.get("effect-carousel")) {
  window.customElements.define("effect-carousel", EffectCarousel);
}

// js/common/carousel/scroll-carousel.js
import { inView as inView4 } from "vendor";
var _hasPendingProgrammaticScroll, _onMouseDownListener, _onMouseMoveListener, _onMouseClickListener, _onMouseUpListener, _targetIndex2, _forceChangeEvent, _dragPosition, _isDragging, _dispatchableScrollEvents, _scrollTimeout, _ScrollCarousel_instances, setupListeners_fn2, updateTargetIndex_fn, onScroll_fn, onScrollEnd_fn, calculateLeftScroll_fn, calculateClosestIndexToAlignment_fn, onMouseDown_fn, onMouseMove_fn, onMouseClick_fn, onMouseUp_fn, onResize_fn, onMutate_fn, adaptHeight_fn, preloadImages_fn2;
var ScrollCarousel = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _ScrollCarousel_instances);
    __privateAdd(this, _hasPendingProgrammaticScroll, false);
    __privateAdd(this, _onMouseDownListener, __privateMethod(this, _ScrollCarousel_instances, onMouseDown_fn).bind(this));
    __privateAdd(this, _onMouseMoveListener, __privateMethod(this, _ScrollCarousel_instances, onMouseMove_fn).bind(this));
    __privateAdd(this, _onMouseClickListener, __privateMethod(this, _ScrollCarousel_instances, onMouseClick_fn).bind(this));
    __privateAdd(this, _onMouseUpListener, __privateMethod(this, _ScrollCarousel_instances, onMouseUp_fn).bind(this));
    __privateAdd(this, _targetIndex2, 0);
    // The cell index to which we are currently going to
    __privateAdd(this, _forceChangeEvent, false);
    __privateAdd(this, _dragPosition, {});
    __privateAdd(this, _isDragging, false);
    __privateAdd(this, _dispatchableScrollEvents, { nearingStart: true, nearingEnd: true, leavingStart: true, leavingEnd: true });
    __privateAdd(this, _scrollTimeout);
    __privateMethod(this, _ScrollCarousel_instances, setupListeners_fn2).call(this);
    new ResizeObserver(__privateMethod(this, _ScrollCarousel_instances, onResize_fn).bind(this)).observe(this);
    new MutationObserver(__privateMethod(this, _ScrollCarousel_instances, onMutate_fn).bind(this)).observe(this, { subtree: true, attributes: true, attributeFilter: ["hidden"] });
  }
  connectedCallback() {
    const boot = () => {
      if (!this.isConnected || !this.allCells.length) return;
      __privateMethod(this, _ScrollCarousel_instances, setupListeners_fn2).call(this);
      if (this._carouselBooted) return;
      this._carouselBooted = true;
      this.dataset.carouselBoot = "true";
      __privateSet(this, _targetIndex2, Math.max(
        0,
        this.cells.findIndex((item) => item.classList.contains("is-initial"))
      ));
      if (__privateGet(this, _targetIndex2) > 0) {
        this.select(__privateGet(this, _targetIndex2), { instant: true });
      }
      if (this.adaptiveHeight) {
        __privateMethod(this, _ScrollCarousel_instances, adaptHeight_fn).call(this);
      }
      inView4(this, () => __privateMethod(this, _ScrollCarousel_instances, preloadImages_fn2).call(this));
      this.dataset.carouselBoot = "false";
    };
    boot();
    if (this._scrollListenersReady) return;
    this._carouselBootObserver?.disconnect();
    this._carouselBootObserver = new MutationObserver(() => {
      boot();
      if (!this._scrollListenersReady) return;
      this._carouselBootObserver?.disconnect();
      this._carouselBootObserver = null;
    });
    this._carouselBootObserver.observe(this, { childList: true });
  }
  disconnectedCallback() {
    this._carouselBooted = false;
    this._carouselBootObserver?.disconnect();
    this._carouselBootObserver = null;
    this.removeEventListener("mousemove", __privateGet(this, _onMouseMoveListener));
    document.removeEventListener("mouseup", __privateGet(this, _onMouseUpListener));
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (PROPERTIES)
   * -------------------------------------------------------------------------------------------------------------------
   */
  get cellSelector() {
    return this.hasAttribute("cell-selector") ? this.getAttribute("cell-selector") : null;
  }
  get allCells() {
    return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children);
  }
  get cells() {
    return this.allCells.filter((cell) => !cell.hasAttribute("hidden"));
  }
  get selectedCell() {
    return this.cells[this.selectedIndex];
  }
  get selectedIndex() {
    return __privateGet(this, _targetIndex2);
  }
  get cellAlign() {
    if (!this.cells[0]) return "center";
    const scrollSnapAlign = getComputedStyle(this.cells[0]).scrollSnapAlign;
    return scrollSnapAlign === "none" ? "center" : scrollSnapAlign;
  }
  get groupCells() {
    if (this.hasAttribute("group-cells")) {
      const number = parseInt(this.getAttribute("group-cells"));
      if (!isNaN(number)) return number;
      if (!this.cells[0]) return 1;
      return Math.floor(this.clientWidth / this.cells[0].clientWidth);
    } else {
      return 1;
    }
  }
  get adaptiveHeight() {
    return this.hasAttribute("adaptive-height");
  }
  get isScrollable() {
    const differenceWidth = this.scrollWidth - this.clientWidth;
    const differenceHeight = this.scrollHeight - this.clientHeight;
    return differenceWidth > 1 || differenceHeight > 1;
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PUBLIC API (METHODS)
   * -------------------------------------------------------------------------------------------------------------------
   */
  previous({ instant = false } = {}) {
    this.select(Math.max(__privateGet(this, _targetIndex2) - this.groupCells, 0), { instant });
  }
  next({ instant = false } = {}) {
    this.select(Math.min(__privateGet(this, _targetIndex2) + this.groupCells, this.cells.length - 1), { instant });
  }
  select(index, { instant = false } = {}) {
    if (!(index in this.cells)) {
      return;
    }
    this.dispatchEvent(new CustomEvent("carousel:select", { detail: { index, cell: this.cells[index] } }));
    if ("checkVisibility" in this && this.checkVisibility() || this.offsetWidth > 0 && this.offsetHeight > 0) {
      const targetScrollLeft = __privateMethod(this, _ScrollCarousel_instances, calculateLeftScroll_fn).call(this, this.cells[index]);
      if (this.scrollLeft !== targetScrollLeft) {
        __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, index);
        __privateSet(this, _hasPendingProgrammaticScroll, true);
        this.scrollTo({ left: targetScrollLeft, behavior: instant ? "auto" : "smooth" });
      } else {
        __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this));
      }
    } else {
      __privateSet(this, _targetIndex2, index);
      __privateSet(this, _forceChangeEvent, true);
    }
  }
  /**
   * Filter cells by indexes. This will automatically add the "hidden" attribute to cells whose index belong to this
   * list. It will also take care of properly adjusting the controls. As a reaction, a "carousel:filter" with the
   * filtered indexes will be emitted.
   */
  filter(indexes = []) {
    const selected = this.selectedCell;
    this.allCells.forEach((cell, index) => {
      const hidden = indexes.includes(index);
      cell.toggleAttribute("hidden", hidden);
      if (!hidden) {
        restoreDeferredImages(cell, { video: cell === selected });
        if (this.hasAttribute("defer-adjacent-media")) {
          cell.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute("loading"));
        }
      }
    });
    __privateSet(this, _forceChangeEvent, true);
    this.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes: indexes } }));
  }
};
_hasPendingProgrammaticScroll = new WeakMap();
_onMouseDownListener = new WeakMap();
_onMouseMoveListener = new WeakMap();
_onMouseClickListener = new WeakMap();
_onMouseUpListener = new WeakMap();
_targetIndex2 = new WeakMap();
_forceChangeEvent = new WeakMap();
_dragPosition = new WeakMap();
_isDragging = new WeakMap();
_dispatchableScrollEvents = new WeakMap();
_scrollTimeout = new WeakMap();
_ScrollCarousel_instances = new WeakSet();
/**
 * -------------------------------------------------------------------------------------------------------------------
 * PRIVATE METHODS
 * -------------------------------------------------------------------------------------------------------------------
 */
/**
 * Setup all the listeners needed for the carousel to work properly
 */
setupListeners_fn2 = function() {
  if (this._scrollListenersReady || this.allCells.length < 2) return;
  this._scrollListenersReady = true;
  if (this.allCells.length > 1) {
    this.addEventListener("carousel:change", __privateMethod(this, _ScrollCarousel_instances, preloadImages_fn2));
    this.addEventListener("scroll", throttle(__privateMethod(this, _ScrollCarousel_instances, onScroll_fn).bind(this)));
    this.addEventListener("scrollend", __privateMethod(this, _ScrollCarousel_instances, onScrollEnd_fn));
    if (this.hasAttribute("allow-drag")) {
      const mediaQuery = window.matchMedia("screen and (pointer: fine)");
      mediaQuery.addEventListener("change", (event) => {
        if (event.matches) {
          this.addEventListener("mousedown", __privateGet(this, _onMouseDownListener));
        } else {
          this.removeEventListener("mousedown", __privateGet(this, _onMouseDownListener));
        }
      });
      if (mediaQuery.matches) {
        this.addEventListener("mousedown", __privateGet(this, _onMouseDownListener));
      }
    }
    if (this.adaptiveHeight) {
      this.addEventListener("carousel:settle", __privateMethod(this, _ScrollCarousel_instances, adaptHeight_fn));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.select(this.cells.indexOf(event.target), { instant: event.detail.load }));
    }
  }
};
updateTargetIndex_fn = function(newValue) {
  if (newValue === __privateGet(this, _targetIndex2) && !__privateGet(this, _forceChangeEvent)) {
    return;
  }
  __privateSet(this, _targetIndex2, newValue);
  __privateSet(this, _forceChangeEvent, false);
  this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index: newValue, cell: this.cells[newValue] } }));
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * SCROLL MANAGEMENT
 * -------------------------------------------------------------------------------------------------------------------
 */
onScroll_fn = function() {
  const scrollEdgeThreshold = 100, normalizedScrollLeft = Math.round(Math.abs(this.scrollLeft));
  if (normalizedScrollLeft < scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["nearingStart"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "start" } }));
    __privateGet(this, _dispatchableScrollEvents)["nearingStart"] = false;
    __privateGet(this, _dispatchableScrollEvents)["leavingStart"] = true;
  }
  if (normalizedScrollLeft >= scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["leavingStart"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "start" } }));
    __privateGet(this, _dispatchableScrollEvents)["leavingStart"] = false;
    __privateGet(this, _dispatchableScrollEvents)["nearingStart"] = true;
  }
  if (this.scrollWidth - this.clientWidth < normalizedScrollLeft + scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["nearingEnd"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-nearing", { detail: { position: "end" } }));
    __privateGet(this, _dispatchableScrollEvents)["nearingEnd"] = false;
    __privateGet(this, _dispatchableScrollEvents)["leavingEnd"] = true;
  }
  if (this.scrollWidth - this.clientWidth >= normalizedScrollLeft + scrollEdgeThreshold && __privateGet(this, _dispatchableScrollEvents)["leavingEnd"]) {
    this.dispatchEvent(new CustomEvent("scroll:edge-leaving", { detail: { position: "end" } }));
    __privateGet(this, _dispatchableScrollEvents)["leavingEnd"] = false;
    __privateGet(this, _dispatchableScrollEvents)["nearingEnd"] = true;
  }
  if (!("onscrollend" in window)) {
    clearTimeout(__privateGet(this, _scrollTimeout));
    __privateSet(this, _scrollTimeout, setTimeout(() => {
      this.dispatchEvent(new CustomEvent("scrollend", { bubbles: true }));
    }, 75));
  }
  if (__privateGet(this, _hasPendingProgrammaticScroll)) {
    return;
  }
  __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this));
};
onScrollEnd_fn = function() {
  __privateSet(this, _hasPendingProgrammaticScroll, false);
  if (!__privateGet(this, _isDragging)) {
    this.style.removeProperty("scroll-snap-type");
  }
  __privateMethod(this, _ScrollCarousel_instances, updateTargetIndex_fn).call(this, __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this));
  this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
};
/**
 * Calculate the amount to scroll to align the cell with the "cell-align" rule
 */
calculateLeftScroll_fn = function(cell) {
  let scrollLeft;
  switch (this.cellAlign) {
    case "start":
      scrollLeft = document.dir === "ltr" ? cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0) : cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineStart) || 0);
      break;
    case "center":
      scrollLeft = Math.round(cell.offsetLeft - this.clientWidth / 2 + cell.clientWidth / 2);
      break;
    case "end":
      scrollLeft = document.dir === "ltr" ? cell.offsetLeft + cell.offsetWidth - this.clientWidth + (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0) : cell.offsetLeft - (parseInt(getComputedStyle(this).scrollPaddingInlineEnd) || 0);
      break;
  }
  return document.dir === "ltr" ? Math.min(Math.max(scrollLeft, 0), this.scrollWidth - this.clientWidth) : Math.min(Math.max(scrollLeft, this.clientWidth - this.scrollWidth), 0);
};
calculateClosestIndexToAlignment_fn = function() {
  let cellAlign = this.cellAlign, offsetAccumulators, targetPoint;
  if (cellAlign === "center") {
    offsetAccumulators = this.cells.map((cell) => Math.round(cell.offsetLeft + cell.clientWidth / 2));
    targetPoint = Math.round(this.scrollLeft + this.clientWidth / 2);
  } else if (cellAlign === "start" && document.dir === "ltr" || cellAlign === "end" && document.dir === "rtl") {
    offsetAccumulators = this.cells.map((cell) => cell.offsetLeft);
    targetPoint = this.scrollLeft;
  } else {
    offsetAccumulators = this.cells.map((cell) => cell.offsetLeft + cell.clientWidth);
    targetPoint = this.scrollLeft + this.clientWidth;
  }
  return offsetAccumulators.indexOf(offsetAccumulators.reduce((prev, curr) => Math.abs(curr - targetPoint) < Math.abs(prev - targetPoint) ? curr : prev));
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * DRAG FEATURE
 * -------------------------------------------------------------------------------------------------------------------
 */
onMouseDown_fn = function(event) {
  __privateSet(this, _dragPosition, {
    // The current scroll
    left: this.scrollLeft,
    top: this.scrollTop,
    // Get the current mouse position
    x: event.clientX,
    y: event.clientY
  });
  __privateSet(this, _isDragging, true);
  this.style.setProperty("scroll-snap-type", "none");
  this.addEventListener("mousemove", __privateGet(this, _onMouseMoveListener));
  this.addEventListener("click", __privateGet(this, _onMouseClickListener), { once: true });
  document.addEventListener("mouseup", __privateGet(this, _onMouseUpListener));
};
onMouseMove_fn = function(event) {
  event.preventDefault();
  const [dx, dy] = [event.clientX - __privateGet(this, _dragPosition).x, event.clientY - __privateGet(this, _dragPosition).y];
  this.scrollTop = __privateGet(this, _dragPosition).top - dy;
  this.scrollLeft = __privateGet(this, _dragPosition).left - dx;
};
onMouseClick_fn = function(event) {
  if (event.clientX - __privateGet(this, _dragPosition).x !== 0) {
    event.preventDefault();
  }
};
onMouseUp_fn = function(event) {
  __privateSet(this, _isDragging, false);
  if (event.clientX - __privateGet(this, _dragPosition).x === 0) {
    this.style.removeProperty("scroll-snap-type");
  } else if (!__privateGet(this, _hasPendingProgrammaticScroll)) {
    this.scrollTo({ left: __privateMethod(this, _ScrollCarousel_instances, calculateLeftScroll_fn).call(this, this.selectedCell), behavior: "smooth" });
  }
  this.removeEventListener("mousemove", __privateGet(this, _onMouseMoveListener));
  document.removeEventListener("mouseup", __privateGet(this, _onMouseUpListener));
};
/**
 * -------------------------------------------------------------------------------------------------------------------
 * OTHER
 * -------------------------------------------------------------------------------------------------------------------
 */
onResize_fn = function() {
  if (!this.cells.length) return;
  if (this.selectedIndex !== __privateMethod(this, _ScrollCarousel_instances, calculateClosestIndexToAlignment_fn).call(this)) {
    this.select(this.selectedIndex, { instant: true });
  }
  if (this.adaptiveHeight) {
    __privateMethod(this, _ScrollCarousel_instances, adaptHeight_fn).call(this);
  }
  this.classList.toggle("is-scrollable", this.scrollWidth > this.clientWidth);
};
onMutate_fn = function() {
  __privateSet(this, _forceChangeEvent, true);
};
adaptHeight_fn = function() {
  const cell = this.selectedCell;
  if (!cell || this.clientHeight === cell.clientHeight) {
    return;
  }
  this.style.maxHeight = null;
  if (this.isScrollable) {
    this.style.maxHeight = `${cell.clientHeight}px`;
  }
};
preloadImages_fn2 = function(event) {
  const reveal = (includeAdjacentVideos = false) => {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const previousSlide = this.cells[Math.max(this.selectedIndex - 1, 0)], nextSlide = this.cells[Math.min(this.selectedIndex + 1, this.cells.length - 1)];
      const selected = this.selectedCell;
      // A product gallery keeps every other color in data-src. The slides of the color on screen
      // must all receive their files, including when the page was opened from search, related
      // products, or complete the look. Loading only the next slide left the rest blank.
      const targets = this.hasAttribute("defer-adjacent-media") ? this.cells : [previousSlide, selected, nextSlide];
      const seen = /* @__PURE__ */ new Set();
      targets.filter((item) => item).forEach((item) => {
        if (seen.has(item)) return;
        seen.add(item);
        const isNeighbor = item === previousSlide || item === selected || item === nextSlide;
        restoreDeferredImages(item, { video: item === selected || includeAdjacentVideos && isNeighbor });
        Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
      });
      // Only the visible slide upgrades video preload. Adjacent slides keep preload=none so product
      // pages with several videos do not download metadata for files the shopper has not opened.
      if (selected) {
        Array.from(selected.querySelectorAll('video[preload="none"]')).forEach((video) => {
          video.setAttribute("preload", "metadata");
          // A second load() after play() has started aborts that playback, which is why the
          // same slide sometimes starts and sometimes stays on the poster.
          const host = video.closest("video-media");
          const playbackRequested = host && (host._wantsPlayback || host._autoplayQueued || host.hasAttribute("playing"));
          if (playbackRequested || video.dataset.galleryLoadStarted === "true") return;
          if (video.networkState === HTMLMediaElement.NETWORK_LOADING) return;
          if (video.paused && video.readyState === 0 && typeof video.load === "function") video.load();
        });
      }
      if (includeAdjacentVideos) {
        [previousSlide, nextSlide].filter((item) => item && item !== selected).forEach((item) => {
          Array.from(item.querySelectorAll('video[preload="none"]')).forEach((video) => video.setAttribute("preload", "metadata"));
        });
      }
    });
  };
  const userMoved = event && event.type === "carousel:change" && this.dataset.carouselBoot !== "true";
  if (this.hasAttribute("defer-adjacent-media") && document.readyState !== "complete" && !userMoved) {
    if (this.selectedCell) restoreDeferredImages(this.selectedCell);
    if (this.dataset.adjacentScheduled !== "true") {
      this.dataset.adjacentScheduled = "true";
      // afterPageLoad runs immediately when the document is already complete. A load listener
      // attached in that gap never fired, so a prefetched product kept only its first image.
      afterPageLoad(() => {
        const run = () => {
          if (this.isConnected) reveal(false);
        };
        if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 1800 });
        else setTimeout(run, 200);
      });
    }
    return;
  }
  reveal(userMoved);
};
if (!window.customElements.get("scroll-carousel")) {
  window.customElements.define("scroll-carousel", ScrollCarousel);
}

// js/common/cart/fetch-cart.js
var createCartPromise = () => {
  return new Promise(async (resolve) => {
    resolve(await (await fetch(`${Shopify.routes.root}cart.js`)).json());
  });
};
var cartRequest = null;
function loadCart() {
  if (!cartRequest) cartRequest = createCartPromise();
  return cartRequest;
}
var fetchCart = {
  then(onFulfilled, onRejected) {
    return loadCart().then(onFulfilled, onRejected);
  }
};
var cartSyncId = 0;
document.addEventListener("cart:change", (event) => {
  cartSyncId++;
  cartRequest = Promise.resolve(event.detail["cart"]);
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    cartRequest = createCartPromise();
  }
});
document.addEventListener("cart:refresh", () => {
  cartSyncId++;
  cartRequest = createCartPromise();
});
var cartViewToken = 0;
function cartCountValue() {
  const bubble = document.querySelector("cart-count");
  if (bubble && bubble.textContent.trim() !== "") return parseInt(bubble.textContent, 10) || 0;
  const drawerCount = document.querySelector(".cart-drawer__count");
  if (drawerCount) return parseInt(drawerCount.textContent.replace(/\D/g, ""), 10) || 0;
  return 0;
}
function payloadFromFormData(formData) {
  const body = {};
  for (const [key, value] of formData.entries()) {
    if (key === "sections" || key === "sections_url" || key === "on_success") continue;
    const property = key.match(/^properties\[(.+)\]$/);
    if (property) {
      body.properties ??= {};
      body.properties[property[1]] = value;
      continue;
    }
    body[key] = value;
  }
  if (typeof body.id === "string" && /^\d+$/.test(body.id)) body.id = Number(body.id);
  body.quantity = Math.max(1, parseInt(body.quantity, 10) || 1);
  return body;
}
function cartForTheme(cart) {
  const source = cart && Array.isArray(cart.items) ? cart : { items: [], item_count: 0 };
  const discounts = source.cart_level_discount_applications || [];
  return {
    ...source,
    discount_codes: source.discount_codes || [],
    cart_level_discount_applications: discounts.map((discount) => discount?.discount_application ? discount : { discount_application: discount })
  };
}
function cartDotLabel(count) {
  const value = Number(count) || 0;
  return value > 9 ? "9+" : String(value);
}
function publishCartCount(count) {
  document.querySelectorAll("cart-count").forEach((element) => {
    element.itemCount = count;
  });
  document.querySelectorAll("cart-dot").forEach((element) => {
    element.textContent = cartDotLabel(count);
    element.classList.toggle("is-visible", count > 0);
  });
}
function bundledSectionIds() {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
  return sections;
}
function itemCountFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (typeof html !== "string") continue;
    const match = html.match(/cart-drawer__count[^>]*>\s*\((\d+)\)/);
    if (match) return parseInt(match[1], 10);
  }
  return null;
}
function hydrateCartAfterAdd(sections, generation) {
  const syncAtStart = cartSyncId;
  fetch(`${Shopify.routes.root}cart.js`, { cache: "no-store" }).then((response) => response.json()).then((cart) => {
    if (generation !== addGeneration || syncAtStart !== cartSyncId || !Array.isArray(cart?.items)) return;
    cartRequest = Promise.resolve({ ...cartForTheme(cart), sections });
    publishCartCount(cart.item_count);
  }).catch(() => {});
}
async function refreshCartSections(detail, generation) {
  const token = ++cartViewToken;
  const syncAtStart = cartSyncId;
  const sectionsToBundle = bundledSectionIds();
  const sections = {};
  let fullCart = null;
  const cartPromise = fetch(`${Shopify.routes.root}cart.js`, { priority: "high", cache: "no-store" }).then((response) => response.json()).then((cart) => {
    fullCart = cart;
  }).catch(() => {});
  const sectionPromise = Promise.all(sectionsToBundle.map(async (id) => {
    try {
      const response = await fetch(`${Shopify.routes.root}?section_id=${encodeURIComponent(id)}`, { priority: "high", cache: "no-store" });
      if (response.ok) sections[id] = await response.text();
    } catch (error) {
    }
  }));
  await Promise.all([cartPromise, sectionPromise]);
  if (token !== cartViewToken || syncAtStart !== cartSyncId) return;
  if (generation != null && generation !== addGeneration) return;
  const cart = cartForTheme(fullCart || detail.cart);
  cart.sections = sections;
  document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
    bubbles: true,
    detail: {
      baseEvent: "variant:add",
      onSuccessDo: detail.onSuccessDo,
      cart
    }
  }));
}
function cleanText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}
function productTitleName(titleElement) {
  if (!titleElement) return "";
  const clone = titleElement.cloneNode(true);
  clone.querySelectorAll(".product-title__color, .featured-product__color").forEach((node) => node.remove());
  return cleanText(clone.textContent);
}
function parseVariantJson(script) {
  try {
    return script ? JSON.parse(script.textContent) : null;
  } catch (error) {
    return null;
  }
}
function formatPreviewMoney(cents) {
  if (cents == null || cents === "") return "";
  const settings = window.themeVariables?.settings || {};
  const format = (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat) || "{{amount}}";
  const group = (decimals, thousands, decimal) => {
    const [whole, fraction] = (Number(cents) / 100).toFixed(decimals).split(".");
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : "");
  };
  const text = format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case "amount_no_decimals": return group(0, ",", ".");
      case "amount_with_comma_separator": return group(2, ".", ",");
      case "amount_no_decimals_with_comma_separator": return group(0, ".", ",");
      case "amount_with_apostrophe_separator": return group(2, "'", ".");
      case "amount_no_decimals_with_space_separator": return group(0, " ", ",");
      case "amount_with_space_separator": return group(2, " ", ",");
      default: return group(2, ",", ".");
    }
  });
  return cleanText(new DOMParser().parseFromString(text, "text/html").body.textContent);
}
// The variant script keeps the variant from page load, while the picker holds the one chosen since. The preview uses
// whichever matches the id being added, so it never shows the first variant's options or photo for another variant.
function addedVariantJson(section, target) {
  const form = target?.matches?.("form") ? target : target?.querySelector?.('form[action*="/cart/add"]') || target?.closest?.("form");
  const idField = form?.elements?.namedItem?.("id");
  const addedId = String(idField?.value || "");
  // form.id would be the <input name="id">, so the form's own id comes from the attribute
  const formId = form?.getAttribute("id");
  const candidates = [];
  (section.querySelectorAll?.("variant-picker") || []).forEach((picker) => {
    const pickerForm = picker.getAttribute("form-id");
    if (formId && pickerForm && pickerForm !== formId) return;
    candidates.push(picker.selectedVariant, parseVariantJson(picker.querySelector("script[data-variant]")));
  });
  if (!addedId) return candidates.find((variant) => variant?.id) || null;
  return candidates.find((variant) => variant?.id != null && String(variant.id) === addedId) || null;
}
// GL UNISEX (added 2026-11-03): remember which gender page a unisex product was added from (hidden line property
// _gl_gender), so the cart drawer / cart page show that gender's photo. Only set for unisex galleries and cards.
function glAddGender(formData, element) {
  if (!formData || !element || formData.has("properties[_gl_gender]")) return;
  let gender = "";
  const card = element.closest?.("product-card");
  if (card) {
    gender = card.querySelector('[data-gl-card-active="true"]')?.getAttribute("data-gl-card-gender") || "";
  } else if (glQuickBuyGender(element)) {
    gender = glQuickBuyGender(element);
  } else {
    const form = element.matches?.("form") ? element : element.closest?.("form");
    const formId = form?.getAttribute("id");
    const gallery = formId ? document.querySelector(`product-gallery[data-gl-gender][form="${CSS.escape(formId)}"]`) : null;
    gender = gallery?.getAttribute("data-gl-gender") || "";
  }
  if (["women", "men", "girls", "boys"].includes(gender)) formData.set("properties[_gl_gender]", gender);
}
function addedVariantImage(section, variant) {
  const media = variant?.featured_media;
  if (!media?.id) return null;
  const glGallery = section.querySelector?.("product-gallery[data-gl-order]");
  const glCells = glGallery ? Array.from(glGallery.querySelectorAll(".product-gallery__media")) : [];
  const glCell = glGallery ? glCells[glVariantIndex(glGallery, media)] : null;
  const image = glCell?.querySelector("img") || section.querySelector?.(`.product-gallery__media[data-media-id="${CSS.escape(String(media.id))}"] img`);
  const shown = image && (image.currentSrc || image.getAttribute("src") || image.getAttribute("data-src"));
  if (shown) return { url: shown, alt: image.alt || "" };
  const source = media.preview_image?.src || variant.featured_image?.src;
  if (!source) return null;
  return { url: /[?&]width=/.test(source) ? source : `${source}${source.includes("?") ? "&" : "?"}width=300`, alt: media.alt || "" };
}
function previewFromAddContext(target, control) {
  const card = target?.matches?.("product-card") ? target : target?.closest?.("product-card") || control?.closest?.("product-card");
  const source = control || target;
  if (card) {
    const image = card.querySelector(".product-card__image--primary");
    const checked = card.querySelector('.product-card__info [type="radio"]:checked');
    const color = cleanText(checked?.getAttribute("data-option-value-name") || checked?.value);
    const size = cleanText(source?.getAttribute?.("data-size"));
    const quickAdd = card.querySelector("[data-product-card-quick-add]");
    const sizeIndex = Number(quickAdd?.getAttribute("data-size-index") || 0);
    const colorIndexAttr = quickAdd?.getAttribute("data-color-index");
    const colorIndex = colorIndexAttr == null || colorIndexAttr === "" ? null : Number(colorIndexAttr);
    const parts = [];
    if (size) parts[sizeIndex] = size;
    if (color && colorIndex !== null) parts[colorIndex] = color;
    const priceNode = card.querySelector("sale-price .money") || card.querySelector("sale-price") || card.querySelector(".price-list");
    return {
      title: productTitleName(card.querySelector(".product-title")),
      variant: parts.filter(Boolean).join(" / "),
      price: cleanText(priceNode?.textContent),
      imageUrl: image?.currentSrc || image?.getAttribute("src") || "",
      imageAlt: image?.alt || "",
      url: card.querySelector('a[href*="/products/"]')?.href || ""
    };
  }
  const section = target?.closest?.(".shopify-section, quick-buy-modal") || document;
  // Picker inputs hold option ids, and quick buy has no title/price blocks, so the added variant's JSON fills those gaps
  const variantJson = addedVariantJson(section, target);
  const variantImage = addedVariantImage(section, variantJson);
  const image = variantImage ? null : section.querySelector?.(".product-gallery__media.is-initial img, .product-gallery__media img, .quick-buy-modal__media img");
  const options = variantJson?.options?.length ? variantJson.options.map(cleanText).filter(Boolean) : Array.from(section.querySelectorAll?.(".variant-picker input:checked") || [], (input) => cleanText(input.getAttribute("data-option-value-name") || input.value)).filter(Boolean);
  const priceNode = section.querySelector?.(".product-info sale-price .money, .product-info sale-price, sale-price .money");
  const variantTitle = cleanText(variantJson?.public_title);
  const variantName = cleanText(variantJson?.name);
  const titleFromVariant = variantTitle && variantName.endsWith(` - ${variantTitle}`) ? variantName.slice(0, -(variantTitle.length + 3)) : variantName;
  return {
    title: productTitleName(section.querySelector?.(".product-title")) || titleFromVariant,
    variant: options.join(" / "),
    price: cleanText(priceNode?.textContent) || formatPreviewMoney(variantJson?.price),
    priceCents: variantJson?.price,
    imageUrl: variantImage?.url || image?.currentSrc || image?.getAttribute("src") || "",
    imageAlt: variantImage ? variantImage.alt : image?.alt || "",
    url: section.querySelector?.('a[href*="/products/"]')?.href || ""
  };
}
function paintDrawerCount(drawer, count) {
  const title = drawer?.querySelector(".cart-drawer__title");
  if (!title) return;
  let node = title.querySelector(".cart-drawer__count");
  if (!count) {
    node?.remove();
    return;
  }
  if (!node) {
    node = document.createElement("span");
    node.className = "cart-drawer__count";
    title.append(node);
  }
  node.textContent = `(${count})`;
}
function buildOptimisticLine(preview) {
  const row = document.createElement("line-item");
  row.className = "line-item line-item--drawer";
  row.setAttribute("data-optimistic-line", "new");
  row.setAttribute("data-variant-id", String(preview.variantId || ""));
  if (preview.imageUrl) {
    const image = document.createElement("img");
    image.className = "line-item__media";
    image.alt = preview.imageAlt || preview.title || "";
    image.decoding = "sync";
    image.loading = "eager";
    image.src = preview.imageUrl;
    row.append(image);
  } else {
    const media = document.createElement("span");
    media.className = "line-item__media cart-pending-line__media";
    row.append(media);
  }
  const info = document.createElement("div");
  info.className = "line-item-info";
  const details = document.createElement("div");
  details.className = "line-item__details v-stack justify-items-start gap-1";
  const title = document.createElement(preview.url ? "a" : "p");
  title.className = "line-item__title";
  if (preview.url) title.href = preview.url;
  title.textContent = preview.title || "";
  details.append(title);
  if (preview.variant) {
    const variant = document.createElement("p");
    variant.className = "line-item__variant text-subdued";
    // Each option value gets its own direction like the Liquid line item, so 5–6Y does not turn into 6Y–5 on RTL pages
    preview.variant.split(" / ").forEach((value, index) => {
      if (index > 0) variant.append(" / ");
      const valueText = document.createElement("bdi");
      valueText.textContent = value;
      variant.append(valueText);
    });
    details.append(variant);
  }
  const prices = document.createElement("div");
  prices.className = "line-item__prices";
  if (preview.price) {
    const priceList = document.createElement("price-list");
    priceList.className = "price-list";
    const sale = document.createElement("sale-price");
    sale.className = "text-subdued";
    const money = document.createElement("span");
    money.className = "money";
    money.textContent = preview.price;
    sale.append(money);
    priceList.append(sale);
    prices.append(priceList);
  }
  const qty = document.createElement("div");
  qty.className = "cart-qty-control";
  const qtyBox = document.createElement("div");
  qtyBox.className = "cart-qty";
  const summary = document.createElement("div");
  summary.className = "cart-qty__summary";
  const label = document.createElement("span");
  label.className = "cart-qty__label";
  label.textContent = `${preview.qtyLabel || "Qty"}:`;
  const value = document.createElement("span");
  value.className = "cart-qty__value";
  value.textContent = String(preview.quantity || 1);
  summary.append(label, value);
  qtyBox.append(summary);
  qty.append(qtyBox);
  prices.append(qty);
  info.append(details, prices);
  row.append(info);
  return row;
}
// The empty drawer carries the real footer and shipping messages in a template, so the first add shows the same
// drawer as every later add instead of a bare subtotal that fills in once cart/add.js answers
function fillPreviewFromTemplate(drawer, template, preview) {
  const fragment = template.content.cloneNode(true);
  const quantity = preview.quantity || 1;
  const label = fragment.querySelector(".cart-drawer__subtotal > span:first-child");
  if (label) label.textContent = quantity === 1 && template.dataset.subtotalOne ? template.dataset.subtotalOne : preview.subtotalLabel || label.textContent;
  const amount = fragment.querySelector(".cart-drawer__subtotal > .money");
  if (amount) amount.textContent = preview.price || "";
  fragment.querySelectorAll("[name='checkout']").forEach((button) => {
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
  });
  // The bar is rendered for an empty cart: it is only kept when the added total is known and still below the threshold
  const threshold = Number(template.dataset.shipThreshold) || 0;
  const unitCents = preview.priceCents == null || preview.priceCents === "" ? NaN : Number(preview.priceCents);
  const totalCents = Number.isFinite(unitCents) ? unitCents * quantity : null;
  fragment.querySelectorAll(".cart-shipping-bar").forEach((bar) => {
    if (totalCents == null || threshold <= 0 || totalCents >= threshold) {
      bar.remove();
      return;
    }
    const progress = Math.min(100, Math.max(0, Math.round(totalCents * 100 / threshold)));
    bar.style.setProperty("--cart-shipping-progress", `${progress}%`);
    bar.querySelector("[role='progressbar']")?.setAttribute("aria-valuenow", String(progress));
    // The amount left is for an empty cart, so it stays hidden until the server render replaces the bar
    bar.querySelector(".cart-shipping-bar__amount")?.style.setProperty("visibility", "hidden");
  });
  const footer = fragment.querySelector("[slot='footer']");
  footer?.setAttribute("data-optimistic-footer", "");
  footer?.remove();
  const list = drawer.querySelector(".cart-drawer__items");
  Array.from(fragment.children).forEach((node) => node.setAttribute("data-optimistic-preview", ""));
  if (list) list.before(fragment);
  else drawer.append(fragment);
  if (footer) drawer.append(footer);
  return footer;
}
function ensureOptimisticFooter(drawer, preview) {
  if (drawer.querySelector("[slot='footer']")) return null;
  const template = drawer.querySelector("template[data-cart-preview]");
  if (template) return fillPreviewFromTemplate(drawer, template, preview);
  const slot = document.createElement("div");
  slot.slot = "footer";
  slot.setAttribute("data-optimistic-footer", "");
  const form = document.createElement("form");
  form.className = "cart-drawer__footer";
  form.method = "post";
  form.action = `${window.Shopify?.routes?.root || "/"}cart`;
  const summary = document.createElement("div");
  summary.className = "cart-drawer__summary";
  const subtotal = document.createElement("p");
  subtotal.className = "cart-drawer__subtotal";
  const label = document.createElement("span");
  label.textContent = preview.subtotalLabel || "";
  const amount = document.createElement("span");
  amount.className = "money";
  amount.textContent = preview.price || "";
  subtotal.append(label, amount);
  summary.append(subtotal);
  const actions = document.createElement("div");
  actions.className = "cart-drawer__actions";
  const button = document.createElement("button");
  button.type = "submit";
  button.name = "checkout";
  button.className = "button w-full";
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  button.textContent = window.themeVariables?.strings?.checkout || "Checkout";
  actions.append(button);
  form.append(summary, actions);
  slot.append(form);
  drawer.append(slot);
  return slot;
}
let addGeneration = 0;
let addSerial = Promise.resolve();
let optimisticAdds = 0;
let drawerWasOpenBeforeAdds = false;
function endAddingState(drawer, { hideIfEmpty = false } = {}) {
  if (!drawer) return;
  optimisticAdds = 0;
  drawer.classList.remove("cart-drawer--adding");
  drawer.querySelectorAll('[data-optimistic-line="new"], [data-optimistic-footer], [data-optimistic-preview]').forEach((node) => node.remove());
  drawer.querySelectorAll('[data-optimistic-line="bump"]').forEach((node) => node.removeAttribute("data-optimistic-line"));
  const hasItems = Boolean(drawer.querySelector(".line-item"));
  if (!hasItems) {
    drawer.querySelectorAll(":scope > p.h5").forEach((node) => {
      node.hidden = false;
    });
    drawer.classList.add("drawer--center-body");
    if (hideIfEmpty && !drawerWasOpenBeforeAdds && drawer.open) drawer.hide();
  }
}
// The drawer opens on the same tap, already showing the product from the page. cart/add.js then replaces that row
// with the server cart. Adds run one at a time so a second tap cannot overwrite the first.
function cartDrawerBody(drawer) {
  return drawer?.shadowRoot?.querySelector('[part="body"]');
}
function scrollCartDrawerToAddedLine(drawer) {
  const body = cartDrawerBody(drawer);
  if (!body || !drawer?.dataset.addedVariant) return;
  const line = drawer.querySelector(`line-item[data-variant-id="${CSS.escape(drawer.dataset.addedVariant)}"]`) || drawer.querySelector(".cart-drawer__items .line-item");
  body._addedLineLock = (body._addedLineLock || 0) + 1;
  if (!line) {
    body.scrollTop = 0;
  } else {
    const bodyRect = body.getBoundingClientRect();
    const lineRect = line.getBoundingClientRect();
    const padding = 12;
    let next = body.scrollTop;
    if (lineRect.top < bodyRect.top + padding) next = body.scrollTop + (lineRect.top - bodyRect.top) - padding;
    else if (lineRect.bottom > bodyRect.bottom - padding) next = body.scrollTop + (lineRect.bottom - bodyRect.bottom) + padding;
    body.scrollTop = Math.max(0, next);
  }
  requestAnimationFrame(() => {
    body._addedLineLock = Math.max(0, (body._addedLineLock || 1) - 1);
  });
}
function pinAddedCartLine(drawer, variantId) {
  if (!drawer) return;
  if (variantId) drawer.dataset.addedVariant = String(variantId);
  const body = cartDrawerBody(drawer);
  if (body && !body._addedLinePin) {
    body._addedLinePin = true;
    const release = () => {
      delete drawer.dataset.addedVariant;
    };
    body.addEventListener("pointerdown", release, { passive: true });
    body.addEventListener("wheel", release, { passive: true });
  }
  clearTimeout(drawer._addedLineTimer);
  drawer._addedLineTimer = setTimeout(() => {
    delete drawer.dataset.addedVariant;
  }, 3000);
  scrollCartDrawerToAddedLine(drawer);
  requestAnimationFrame(() => requestAnimationFrame(() => scrollCartDrawerToAddedLine(drawer)));
}
function showPendingAdd(onSuccessDo, preview = {}) {
  const drawer = document.querySelector("cart-drawer");
  const opensDrawer = window.themeVariables.settings.cartType === "drawer" || onSuccessDo === "force_open_drawer";
  const holdLock = Boolean(preview.modal?.open) || Boolean(drawer && opensDrawer);
  if (holdLock) pushScrollLock();
  try {
    if (preview.modal?.open) {
      try {
        preview.modal.hide(false);
      } catch (error) {
        preview.modal.removeAttribute("open");
      }
    }
    if (!drawer || !opensDrawer) return null;
    if (optimisticAdds === 0) drawerWasOpenBeforeAdds = Boolean(drawer.open);
    optimisticAdds += 1;
    drawer.classList.add("cart-drawer--adding");
    drawer.classList.remove("drawer--center-body");
    drawer.querySelectorAll(":scope > p.h5").forEach((node) => {
      node.hidden = true;
    });
    const variantId = String(preview.variantId || "");
    const quantity = preview.quantity || 1;
    const existing = variantId ? Array.from(drawer.querySelectorAll("line-item")).find((line) => line.getAttribute("data-variant-id") === variantId) : null;
    const existingQty = existing?.querySelector(".cart-qty__value");
    let row = null;
    let previousQty = null;
    if (existing && existingQty) {
      previousQty = parseInt(existingQty.textContent, 10) || 1;
      existingQty.textContent = String(previousQty + quantity);
      existing.setAttribute("data-optimistic-line", "bump");
    } else {
      let list = drawer.querySelector(".cart-drawer__items");
      if (!list) {
        list = document.createElement("div");
        list.className = "cart-drawer__items";
        list.setAttribute("data-optimistic-list", "");
        drawer.append(list);
      }
      row = buildOptimisticLine({
        ...preview,
        qtyLabel: drawer.getAttribute("data-qty-label") || "Qty",
        variantId,
        quantity
      });
      list.prepend(row);
    }
    ensureOptimisticFooter(drawer, {
      ...preview,
      subtotalLabel: drawer.getAttribute("data-subtotal-label") || ""
    });
    if (!drawer.open) drawer.show();
    pinAddedCartLine(drawer, variantId);
    return {
      cancel() {
        if (existingQty && previousQty != null && existing?.isConnected) {
          const current = parseInt(existingQty.textContent, 10) || previousQty;
          existingQty.textContent = String(Math.max(1, current - quantity));
          if (existing.getAttribute("data-optimistic-line") === "bump") existing.removeAttribute("data-optimistic-line");
        } else {
          row?.remove();
        }
        optimisticAdds = Math.max(0, optimisticAdds - 1);
        if (!drawer.querySelector("[data-optimistic-line]")) drawer.classList.remove("cart-drawer--adding");
        if (!drawer.querySelector(".line-item")) {
          drawer.querySelector("[data-optimistic-list]")?.remove();
          drawer.querySelectorAll("[data-optimistic-footer], [data-optimistic-preview]").forEach((node) => node.remove());
          drawer.querySelectorAll(":scope > p.h5").forEach((node) => {
            node.hidden = false;
          });
          drawer.classList.add("drawer--center-body");
        }
      }
    };
  } finally {
    if (holdLock) popScrollLock();
  }
}
function publishAddResult(target, payload, added, onSuccessDo, sectionsToBundle, generation) {
  const items = added.hasOwnProperty("items") ? added["items"] : [added];
  const sections = added["sections"] || {};
  const parsedCount = itemCountFromSections(sections);
  const cartContent = cartForTheme({
    item_count: parsedCount == null ? cartCountValue() : parsedCount,
    items,
    sections
  });
  target.dispatchEvent(new CustomEvent("variant:add", {
    bubbles: true,
    detail: { items, cart: cartContent, onSuccessDo }
  }));
  const hasRenderedCart = sectionsToBundle.some((id) => typeof sections[id] === "string" && sections[id].length > 0);
  if (!hasRenderedCart || generation !== addGeneration) return { cartContent, rendered: hasRenderedCart };
  publishCartCount(cartContent.item_count);
  document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
    bubbles: true,
    detail: {
      baseEvent: "variant:add",
      onSuccessDo,
      cart: cartContent
    }
  }));
  endAddingState(document.querySelector("cart-drawer"));
  hydrateCartAfterAdd(sections, generation);
  return { cartContent, rendered: true };
}
async function addVariantFast(formData, { dispatchTarget, preview } = {}) {
  const generation = ++addGeneration;
  const target = dispatchTarget || document;
  const onSuccessDo = formData.get("on_success") || void 0;
  const payload = payloadFromFormData(formData);
  const productPreview = {
    ...(preview || previewFromAddContext(target)),
    variantId: payload.id,
    quantity: payload.quantity
  };
  publishCartCount(cartCountValue() + payload.quantity);
  const pending = showPendingAdd(onSuccessDo, productPreview);
  paintDrawerCount(document.querySelector("cart-drawer"), cartCountValue());
  const sectionsToBundle = bundledSectionIds();
  if (sectionsToBundle.length) {
    payload.sections = sectionsToBundle.join(",");
    payload.sections_url = window.themeVariables?.settings?.pageType === "cart" ? `${window.location.pathname}${window.location.search}` : Shopify.routes.root;
  }
  const run = async () => {
    const drawer = document.querySelector("cart-drawer");
    let response;
    let added = {};
    try {
      response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        priority: "high",
        body: JSON.stringify(payload)
      });
      added = await response.json();
    } catch (error) {
      pending?.cancel();
      publishCartCount(Math.max(0, cartCountValue() - payload.quantity));
      paintDrawerCount(drawer, cartCountValue());
      if (generation === addGeneration) {
        await refreshCartSections({ onSuccessDo, cart: cartForTheme({ item_count: cartCountValue(), items: [] }) }, generation);
        if (generation === addGeneration) endAddingState(drawer, { hideIfEmpty: true });
      }
      return false;
    }
    if (!response.ok) {
      pending?.cancel();
      publishCartCount(Math.max(0, cartCountValue() - payload.quantity));
      paintDrawerCount(drawer, cartCountValue());
      if (added["description"]) announceError(added["description"]);
      target.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: { error: added["description"] }
      }));
      if (generation === addGeneration) {
        await refreshCartSections({ onSuccessDo, cart: cartForTheme({ item_count: cartCountValue(), items: [] }) }, generation);
        if (generation === addGeneration) endAddingState(drawer, { hideIfEmpty: true });
      }
      return false;
    }
    if (window.themeVariables.settings.cartType === "page" && window.themeVariables.settings.pageType !== "cart") {
      window.location.href = `${Shopify.routes.root}cart`;
      return true;
    }
    const result = publishAddResult(target, payload, added, onSuccessDo, sectionsToBundle, generation);
    if (generation !== addGeneration) return true;
    if (!result.rendered) {
      await refreshCartSections({ onSuccessDo, cart: result.cartContent }, generation);
      if (generation === addGeneration) endAddingState(drawer);
    }
    return true;
  };
  const task = addSerial.then(run, run);
  addSerial = task.then(() => {
  }, () => {
  });
  return task;
}

// js/common/cart/cart-count.js
var _abortController2, _CartCount_instances, updateFromServer_fn;
var CartCount = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartCount_instances);
    __privateAdd(this, _abortController2);
  }
  connectedCallback() {
    __privateSet(this, _abortController2, new AbortController());
    document.addEventListener("cart:change", (event) => this.itemCount = event.detail["cart"]["item_count"], { signal: __privateGet(this, _abortController2).signal });
    document.addEventListener("cart:refresh", __privateMethod(this, _CartCount_instances, updateFromServer_fn).bind(this), { signal: __privateGet(this, _abortController2).signal });
    window.addEventListener("pageshow", __privateMethod(this, _CartCount_instances, updateFromServer_fn).bind(this), { signal: __privateGet(this, _abortController2).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController2).abort();
  }
  set itemCount(count) {
    this.innerText = count;
  }
};
_abortController2 = new WeakMap();
_CartCount_instances = new WeakSet();
updateFromServer_fn = async function() {
  this.itemCount = (await fetchCart)["item_count"];
};
if (!window.customElements.get("cart-count")) {
  window.customElements.define("cart-count", CartCount);
}

// js/common/cart/cart-discount.js
// Shopify allows 5 product or order codes plus 1 shipping code on the same cart.
var MAX_CART_DISCOUNT_CODES = 6;
function sameDiscountCode(left, right) {
  return String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase();
}
function uniqueDiscountCodes(codes) {
  const unique = [];
  for (const code of codes) {
    const trimmed = String(code || "").trim();
    if (!trimmed || unique.some((existing) => sameDiscountCode(existing, trimmed))) continue;
    unique.push(trimmed);
  }
  return unique;
}
function discountInputCodes(value) {
  return uniqueDiscountCodes(String(value || "").split(","));
}
function chipDiscountCodes(scope) {
  const root = scope?.closest?.("cart-drawer, .shopify-section") || document;
  return uniqueDiscountCodes(Array.from(root.querySelectorAll("cart-discount-remove-button[discount-code]"), (button) => button.getAttribute("discount-code")));
}
async function cartBeforeDiscountChange() {
  try {
    return await loadCart();
  } catch (error) {
    return null;
  }
}
function codesOnCart(cart, scope) {
  const stored = uniqueDiscountCodes((cart?.discount_codes || []).map((discount) => discount.code));
  return uniqueDiscountCodes([...stored, ...chipDiscountCodes(scope)]);
}
function appliedDiscountCodes(cart) {
  return uniqueDiscountCodes((cart?.discount_codes || []).filter((discount) => discount.applicable === true).map((discount) => discount.code));
}
function codeAdjustsMerchandise(cart, code) {
  const titles = [];
  for (const application of cart?.cart_level_discount_applications || []) {
    titles.push(application?.title || application?.discount_application?.title);
  }
  for (const item of cart?.items || []) {
    for (const allocation of item.line_level_discount_allocations || []) titles.push(allocation?.discount_application?.title);
    for (const discount of item.discounts || []) titles.push(discount?.title);
  }
  return titles.some((title) => sameDiscountCode(title, code));
}
async function sectionMarkup(id) {
  const response = await fetch(`${Shopify.routes.root}?section_id=${encodeURIComponent(id)}`, { cache: "no-store", headers: { Accept: "text/html" } });
  return response.ok ? response.text() : "";
}
async function saveCartDiscountCodes(codes, withSections = true) {
  const sections = withSections ? bundledSectionIds().filter(Boolean) : [];
  const payload = { discount: uniqueDiscountCodes(codes).join(",") };
  if (sections.length) {
    payload.sections = sections.join(",");
    payload.sections_url = window.themeVariables?.settings?.pageType === "cart" ? `${window.location.pathname}${window.location.search}` : Shopify.routes.root;
  }
  const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) return null;
  const cart = await response.json();
  cart.sections = cart.sections || {};
  await Promise.all(sections.map(async (id) => {
    if (typeof cart.sections[id] === "string" && cart.sections[id].length > 0) return;
    const html = await sectionMarkup(id);
    if (html) cart.sections[id] = html;
  }));
  const themed = cartForTheme(cart);
  themed.sections = cart.sections;
  return themed;
}
function discountCodeStatus(cart, code) {
  return (cart?.discount_codes || []).find((discount) => sameDiscountCode(discount.code, code));
}
function publishUpdatedCart(cart) {
  cartRequest = Promise.resolve(cart);
  document.dispatchEvent(new CustomEvent("cart:change", {
    bubbles: true,
    detail: { cart, baseEvent: "discount:update" }
  }));
  const codes = uniqueDiscountCodes((cart?.discount_codes || []).map((discount) => discount.code));
  document.querySelectorAll('cart-drawer [name="discount"]').forEach((input) => {
    input.value = codes.join(",");
  });
}
// The drawer re-renders while a code is checked, so its loader state lives here instead of on the field being replaced.
// The loader stays until the re-rendered drawer is in place, however long Shopify takes, and never flashes too briefly.
var MIN_DISCOUNT_LOADER_MS = 600;
var DRAWER_REFRESH_TIMEOUT_MS = 15e3;
var drawerDiscountPending = false;
function syncDrawerDiscountState() {
  document.querySelectorAll("cart-drawer cart-discount-field").forEach((field) => {
    if (drawerDiscountPending) field.setAttribute("aria-busy", "true");
    else field.removeAttribute("aria-busy");
  });
  document.querySelectorAll("cart-drawer [data-cart-discount-apply]").forEach((button) => {
    button.disabled = drawerDiscountPending;
  });
}
function nextDrawerRefresh() {
  return new Promise((resolve) => {
    const timer = setTimeout(done, DRAWER_REFRESH_TIMEOUT_MS);
    function done() {
      clearTimeout(timer);
      document.removeEventListener("cart-drawer:refreshed", done);
      resolve();
    }
    document.addEventListener("cart-drawer:refreshed", done);
  });
}
// Like checkout, a rejected code stays in the field with its message under it
function showDrawerDiscountNotice(notice) {
  const field = document.querySelector("cart-drawer cart-discount-field");
  if (!field) return;
  const input = field.querySelector('input:not([type="hidden"])');
  if (input && notice.value) input.value = notice.value;
  field.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true, detail: { code: notice.kind, discountCode: notice.code } }));
}
var AbstractCartDiscount = class extends HTMLElement {
  discountError(code) {
    this.dispatchEvent(new CustomEvent("cart:discount:error", { bubbles: true, detail: { code } }));
  }
  setDiscountBusy(target, busy) {
    const field = target.matches?.("cart-discount-field") ? target : target.closest("cart-drawer, .cart-discount")?.querySelector("cart-discount-field");
    if (busy) field?.setAttribute("aria-busy", "true");
    else field?.removeAttribute("aria-busy");
    const applyButton = (field || target).closest?.(".cart-discount")?.querySelector("[data-cart-discount-apply]");
    if (!applyButton) return;
    applyButton.disabled = busy;
  }
  async toggleDiscount(event) {
    const target = event.currentTarget;
    const inDrawer = Boolean(target.closest?.("cart-drawer"));
    if (inDrawer ? drawerDiscountPending : target.dataset.discountLock === "true") return;
    const removingCode = target.getAttribute("discount-code");
    const typedValue = removingCode ? "" : event.target?.value || target.querySelector?.('input:not([type="hidden"])')?.value || "";
    const startedAt = performance.now();
    let drawerRefreshed = null;
    let notice = null;
    if (inDrawer) {
      drawerDiscountPending = true;
      syncDrawerDiscountState();
    } else {
      target.dataset.discountLock = "true";
      this.setDiscountBusy(target, true);
    }
    try {
      const previousCart = await cartBeforeDiscountChange();
      const existingCodes = codesOnCart(previousCart, target);
      let nextCodes = existingCodes;
      let addedCodes = [];
      if (removingCode) {
        nextCodes = existingCodes.filter((code) => !sameDiscountCode(code, removingCode));
      } else {
        addedCodes = discountInputCodes(typedValue).filter((code) => !existingCodes.some((existing) => sameDiscountCode(existing, code)));
        if (addedCodes.length === 0) return;
        if (existingCodes.length + addedCodes.length > MAX_CART_DISCOUNT_CODES) {
          notice = { kind: "limit", value: typedValue };
          return;
        }
        nextCodes = uniqueDiscountCodes([...existingCodes, ...addedCodes]);
      }
      let updatedCart = await saveCartDiscountCodes(nextCodes);
      if (!updatedCart) {
        notice = { kind: "default", value: typedValue };
        return;
      }
      // Like checkout, a new code never pushes out a code that is already applied: it is the new code that is refused
      const appliedBefore = inDrawer && addedCodes.length > 0 ? appliedDiscountCodes(previousCart) : [];
      const displacesApplied = appliedBefore.some((code) => discountCodeStatus(updatedCart, code)?.applicable !== true);
      const rejectedCodes = displacesApplied ? addedCodes : addedCodes.filter((code) => discountCodeStatus(updatedCart, code)?.applicable !== true);
      const acceptedCodes = addedCodes.filter((code) => !rejectedCodes.some((rejected) => sameDiscountCode(rejected, code)));
      if (rejectedCodes.length > 0) {
        // A code refused next to applied codes is tried on its own, to tell a combination conflict from an invalid code
        let conflict = displacesApplied;
        let probed = false;
        if (!conflict && appliedBefore.length > 0) {
          probed = true;
          const probeCart = await saveCartDiscountCodes([rejectedCodes[0]], false);
          conflict = discountCodeStatus(probeCart, rejectedCodes[0])?.applicable === true;
        }
        const keptCodes = uniqueDiscountCodes([...existingCodes, ...acceptedCodes]);
        const restoredCart = await saveCartDiscountCodes(keptCodes) || await saveCartDiscountCodes(keptCodes);
        if (restoredCart) updatedCart = restoredCart;
        else if (probed) updatedCart = null;
        notice = { kind: conflict ? "conflict" : "default", code: rejectedCodes[0], value: typedValue };
        window.pendingCartDiscountNotice = inDrawer ? null : "invalid";
      } else {
        window.pendingCartDiscountNotice = null;
      }
      if (inDrawer) drawerRefreshed = nextDrawerRefresh();
      if (updatedCart) {
        publishUpdatedCart(updatedCart);
      } else {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
      if (!inDrawer) notice = null;
    } catch (error) {
      notice = { kind: "default", value: typedValue };
    } finally {
      if (inDrawer) {
        if (drawerRefreshed) await drawerRefreshed;
        const remaining = MIN_DISCOUNT_LOADER_MS - (performance.now() - startedAt);
        if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
        drawerDiscountPending = false;
        syncDrawerDiscountState();
        if (notice) showDrawerDiscountNotice(notice);
      } else {
        target.dataset.discountLock = "false";
        this.setDiscountBusy(target, false);
        if (notice) this.discountError(notice.kind);
      }
    }
  }
};
var _abortController3, _CartDiscountBanner_instances, onCartDiscountError_fn;
var CartDiscountBanner = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDiscountBanner_instances);
    __privateAdd(this, _abortController3);
  }
  connectedCallback() {
    __privateSet(this, _abortController3, new AbortController());
    document.addEventListener("cart:discount:error", __privateMethod(this, _CartDiscountBanner_instances, onCartDiscountError_fn).bind(this), { signal: __privateGet(this, _abortController3).signal });
    if (window.pendingCartDiscountNotice) {
      __privateMethod(this, _CartDiscountBanner_instances, onCartDiscountError_fn).call(this, { detail: { code: window.pendingCartDiscountNotice } });
      queueMicrotask(() => {
        window.pendingCartDiscountNotice = null;
      });
    }
  }
  disconnectedCallback() {
    __privateGet(this, _abortController3).abort();
  }
};
_abortController3 = new WeakMap();
_CartDiscountBanner_instances = new WeakSet();
onCartDiscountError_fn = function(event) {
  const conflictMessage = this.querySelector('[data-discount-message="conflict"]');
  let kind = event.detail?.code === "limit" ? "limit" : "default";
  if (event.detail?.code === "conflict" && conflictMessage && event.detail.discountCode) {
    kind = "conflict";
    conflictMessage.textContent = (conflictMessage.dataset.template || "").replace("[code]", event.detail.discountCode);
  }
  this.dataset.status = "error";
  this.querySelectorAll("[data-discount-message]").forEach((paragraph) => {
    paragraph.hidden = paragraph.dataset.discountMessage !== kind;
  });
  this.hidden = false;
};
function discountPillMarkup(code, shipping) {
  const item = document.createElement("li");
  item.className = `cart-drawer__applied-discount${shipping ? " cart-drawer__applied-discount--shipping" : ""}`;
  item.innerHTML = `<span class="cart-drawer__applied-discount-icon" aria-hidden="true"><svg fill="none" width="14" viewBox="0 0 24 24"><path d="M22.707 12.293l-11-11A1.002 1.002 0 0 0 11 1H2a1 1 0 0 0-1 1v9c0 .265.105.52.293.707l11 11a.997.997 0 0 0 1.414 0l9-9a.999.999 0 0 0 0-1.414zM7 9a2 2 0 1 1-.001-3.999A2 2 0 0 1 7 9zm6 8.414L8.586 13 10 11.586 14.414 16 13 17.414zm3-3L11.586 10 13 8.586 17.414 13 16 14.414z" fill="currentColor"></path></svg></span><span class="cart-drawer__applied-discount-name"></span><cart-discount-remove-button><button type="button" class="cart-drawer__applied-discount-remove" aria-label=""><svg fill="none" width="10" viewBox="0 0 16 16"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.5"></path></svg></button></cart-discount-remove-button>`;
  item.querySelector(".cart-drawer__applied-discount-name").textContent = code;
  const removeButton = item.querySelector("cart-discount-remove-button");
  removeButton.setAttribute("discount-code", code);
  item.querySelector("button").setAttribute("aria-label", code);
  return item;
}
function ensureDiscountPills(cart) {
  const footer = document.querySelector("cart-drawer [slot='footer'], cart-drawer .cart-drawer__footer");
  if (!footer) return;
  const codes = uniqueDiscountCodes((cart?.discount_codes || []).filter((discount) => discount.applicable !== false).map((discount) => discount.code));
  if (!codes.length) return;
  let list = footer.querySelector(".cart-drawer__applied-discounts");
  if (!list) {
    list = document.createElement("ul");
    list.className = "cart-drawer__applied-discounts unstyled-list";
    list.setAttribute("role", "list");
    footer.querySelector(".cart-discount")?.insertAdjacentElement("afterend", list);
  }
  // The list is rendered hidden when Liquid sees no code discounts, which is always the case for a shipping code alone
  list.hidden = false;
  const present = Array.from(list.querySelectorAll("[discount-code]"), (button) => button.getAttribute("discount-code"));
  codes.forEach((code) => {
    if (present.some((existing) => sameDiscountCode(existing, code))) return;
    list.append(discountPillMarkup(code, !codeAdjustsMerchandise(cart, code)));
  });
}
document.addEventListener("cart-drawer:refreshed", () => {
  syncDrawerDiscountState();
  if (!cartRequest) return;
  Promise.resolve(cartRequest).then((cart) => ensureDiscountPills(cart)).catch(() => {});
});
// Shipping codes only reach the page through /cart.js. Wait until after load so /cart.js does not
// compete with LCP images on home, collection, and product pages.
function initDrawerDiscountPills() {
  if (!document.querySelector("cart-drawer cart-discount-field")) return;
  const run = () => loadCart().then((cart) => ensureDiscountPills(cart)).catch(() => {});
  const schedule = () => {
    if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 500);
  };
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}
initDrawerDiscountPills();
var _hiddenDiscountInputOriginalValue, _CartDiscountField_instances, hiddenDiscountInput_get, updateHiddenInput_fn;
var CartDiscountField = class extends AbstractCartDiscount {
  constructor() {
    super();
    __privateAdd(this, _CartDiscountField_instances);
    __privateAdd(this, _hiddenDiscountInputOriginalValue);
    const hiddenDiscountInput = __privateGet(this, _CartDiscountField_instances, hiddenDiscountInput_get);
    __privateSet(this, _hiddenDiscountInputOriginalValue, hiddenDiscountInput ? hiddenDiscountInput.value : "");
    this.applyDiscount = (value) => {
      if (this.dataset.discountLock === "true" || this.closest("cart-drawer") && drawerDiscountPending) return;
      const input = this.querySelector('input:not([type="hidden"])');
      if (input && value != null) input.value = value;
      return this.toggleDiscount({ currentTarget: this, target: input || this });
    };
    this.addEventListener("input", __privateMethod(this, _CartDiscountField_instances, updateHiddenInput_fn));
  }
};
_hiddenDiscountInputOriginalValue = new WeakMap();
_CartDiscountField_instances = new WeakSet();
hiddenDiscountInput_get = function() {
  return this.querySelector('[name="discount"]');
};
updateHiddenInput_fn = function() {
  window.pendingCartDiscountNotice = null;
  const banner = this.closest(".cart-discount")?.querySelector("cart-discount-banner");
  if (!banner) return;
  banner.hidden = true;
  banner.dataset.status = "";
};
var CartDiscountRemoveButton = class extends AbstractCartDiscount {
  constructor() {
    super();
    this.addEventListener("click", this.toggleDiscount.bind(this));
  }
};
if (!window.customElements.get("cart-discount-field")) {
  window.customElements.define("cart-discount-field", CartDiscountField);
}
if (!window.customElements.get("cart-discount-remove-button")) {
  window.customElements.define("cart-discount-remove-button", CartDiscountRemoveButton);
}
if (!window.customElements.get("cart-discount-banner")) {
  window.customElements.define("cart-discount-banner", CartDiscountBanner);
}

// js/common/cart/cart-dot.js
var _abortController4, _CartDot_instances, updateFromServer_fn2;
var CartDot = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDot_instances);
    __privateAdd(this, _abortController4);
  }
  connectedCallback() {
    __privateSet(this, _abortController4, new AbortController());
    document.addEventListener("cart:change", (event) => {
      const count = event.detail["cart"]["item_count"];
      this.textContent = cartDotLabel(count);
      this.classList.toggle("is-visible", count > 0);
    }, { signal: __privateGet(this, _abortController4).signal });
    document.addEventListener("cart:refresh", __privateMethod(this, _CartDot_instances, updateFromServer_fn2).bind(this), { signal: __privateGet(this, _abortController4).signal });
    window.addEventListener("pageshow", __privateMethod(this, _CartDot_instances, updateFromServer_fn2).bind(this), { signal: __privateGet(this, _abortController4).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController4).abort();
  }
};
_abortController4 = new WeakMap();
_CartDot_instances = new WeakSet();
updateFromServer_fn2 = async function() {
  const count = (await fetchCart)["item_count"];
  this.textContent = cartDotLabel(count);
  this.classList.toggle("is-visible", count > 0);
};
if (!window.customElements.get("cart-dot")) {
  window.customElements.define("cart-dot", CartDot);
}

// js/common/cart/cart-note.js
var _CartNote_instances, onNoteChanged_fn;
var CartNote = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _CartNote_instances);
    this.addEventListener("change", __privateMethod(this, _CartNote_instances, onNoteChanged_fn));
  }
};
_CartNote_instances = new WeakSet();
onNoteChanged_fn = function(event) {
  if (event.target.getAttribute("name") !== "note") {
    return;
  }
  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note: event.target.value }),
    keepalive: true
    // Allows to make sure the request is fired even when submitting the form
  });
};
if (!window.customElements.get("cart-note")) {
  window.customElements.define("cart-note", CartNote);
}

// js/common/cart/free-shipping-bar.js
var _onCartChangedListener, _currencyFormatter, _threshold, _FreeShippingBar_instances, updateMessage_fn, onCartChanged_fn;
var FreeShippingBar = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _FreeShippingBar_instances);
    __privateAdd(this, _onCartChangedListener, __privateMethod(this, _FreeShippingBar_instances, onCartChanged_fn).bind(this));
    __privateAdd(this, _currencyFormatter, new Intl.NumberFormat(Shopify.locale, { style: "currency", currency: Shopify.currency.active }));
    __privateAdd(this, _threshold);
    __privateSet(this, _threshold, parseFloat(this.getAttribute("threshold").replace(/[^0-9.]/g, "")) * 100);
    this.setAttribute("threshold", __privateGet(this, _threshold));
  }
  static get observedAttributes() {
    return ["threshold", "total-price"];
  }
  connectedCallback() {
    document.addEventListener("cart:change", __privateGet(this, _onCartChangedListener));
  }
  disconnectedCallback() {
    document.removeEventListener("cart:change", __privateGet(this, _onCartChangedListener));
  }
  get totalPrice() {
    return parseFloat(this.getAttribute("total-price"));
  }
  set totalPrice(value) {
    this.setAttribute("total-price", value);
  }
  attributeChangedCallback() {
    __privateMethod(this, _FreeShippingBar_instances, updateMessage_fn).call(this);
  }
};
_onCartChangedListener = new WeakMap();
_currencyFormatter = new WeakMap();
_threshold = new WeakMap();
_FreeShippingBar_instances = new WeakSet();
updateMessage_fn = function() {
  const messageElement = this.querySelector("span");
  if (this.totalPrice >= __privateGet(this, _threshold)) {
    messageElement.innerHTML = this.getAttribute("reached-message");
  } else {
    const replacement = `${__privateGet(this, _currencyFormatter).format((__privateGet(this, _threshold) - this.totalPrice) / 100).replace(/\$/g, "$$$$")}`;
    messageElement.innerHTML = this.getAttribute("unreached-message").replace(new RegExp("({{.*}})", "g"), replacement);
  }
};
onCartChanged_fn = function(event) {
  const priceForItems = event.detail["cart"]["items"].filter((item) => item["requires_shipping"]).reduce((sum, item) => sum + item["final_line_price"], 0), cartDiscount = event.detail["cart"]["cart_level_discount_applications"].reduce(
    (sum, discountAllocation) => sum + discountAllocation["discount_application"]["total_allocated_amount"],
    0
  );
  this.totalPrice = priceForItems - cartDiscount;
};
if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// js/common/cart/line-item-quantity.js
import { Delegate } from "vendor";
var _delegate, _LineItemQuantity_instances, onQuantityChanged_fn, onChangeLinkClicked_fn, changeLineItemQuantity_fn;
var LineItemQuantity = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _LineItemQuantity_instances);
    __privateAdd(this, _delegate, new Delegate(this));
    __privateGet(this, _delegate).on("change", "[data-line-key]", __privateMethod(this, _LineItemQuantity_instances, onQuantityChanged_fn).bind(this));
    __privateGet(this, _delegate).on("click", '[href*="/cart/change"]', __privateMethod(this, _LineItemQuantity_instances, onChangeLinkClicked_fn).bind(this));
  }
};
_delegate = new WeakMap();
_LineItemQuantity_instances = new WeakSet();
onQuantityChanged_fn = function(event, target) {
  __privateMethod(this, _LineItemQuantity_instances, changeLineItemQuantity_fn).call(this, target.getAttribute("data-line-key"), parseInt(target.value));
};
onChangeLinkClicked_fn = function(event, target) {
  event.preventDefault();
  const url = new URL(target.href);
  __privateMethod(this, _LineItemQuantity_instances, changeLineItemQuantity_fn).call(this, url.searchParams.get("id"), parseInt(url.searchParams.get("quantity")));
};
changeLineItemQuantity_fn = async function(lineKey, targetQuantity) {
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  const lineItem = this.closest("line-item");
  lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity } }));
  let sectionsToBundle = [];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
  const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      id: lineKey,
      quantity: targetQuantity,
      sections: sectionsToBundle.join(",")
    })
  });
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  if (!response.ok) {
    const responseContent = await response.json();
    this.parentElement.querySelector('[role="alert"]')?.remove();
    const errorSvg = `<svg width="13" height="13" fill="none" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.5" r="6.5" fill="#BF1515"/>
        <path fill="#fff" d="M6.75 7.97a.387.387 0 0 1-.3-.12.606.606 0 0 1-.12-.34l-.3-3.82c-.02-.247.033-.443.16-.59.127-.153.313-.23.56-.23.24 0 .42.077.54.23.127.147.18.343.16.59l-.3 3.82a.522.522 0 0 1-.12.34.344.344 0 0 1-.28.12Zm0 2.08a.744.744 0 0 1-.55-.21.751.751 0 0 1-.2-.54c0-.213.067-.387.2-.52.14-.14.323-.21.55-.21.233 0 .413.07.54.21.133.133.2.307.2.52 0 .22-.067.4-.2.54-.127.14-.307.21-.54.21Z"/>
      </svg>`;
    this.insertAdjacentHTML("afterend", `<p class="h-stack gap-2 justify-center text-xs" role="alert">${errorSvg} ${responseContent["description"]}</p>`);
    this.querySelector("quantity-selector")?.restoreDefaultValue();
    const quantitySelect = this.querySelector("select[data-line-key]");
    if (quantitySelect?.dataset.defaultValue) {
      quantitySelect.value = quantitySelect.dataset.defaultValue;
    }
  } else {
    const cartContent = await response.json();
    const lineItemAfterChange = cartContent["items"].filter((lineItem2) => lineItem2["key"] === lineKey);
    lineItem?.dispatchEvent(
      new CustomEvent("line-item:change", {
        bubbles: true,
        detail: {
          quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]["quantity"],
          cart: cartContent
        }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "line-item:change",
          cart: cartContent
        }
      })
    );
  }
};
if (!window.customElements.get("line-item-quantity")) {
  window.customElements.define("line-item-quantity", LineItemQuantity);
}
document.addEventListener("toggle", (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.classList.contains("cart-qty") || !details.open) {
    return;
  }
  document.querySelectorAll("details.cart-qty[open]").forEach((other) => {
    if (other !== details) {
      other.open = false;
    }
  });
}, true);

// js/common/cart/shipping-estimator.js
var _estimateShippingListener, _ShippingEstimator_instances, estimateShipping_fn, getAsyncShippingRates_fn, formatShippingRates_fn, formatError_fn;
var ShippingEstimator = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ShippingEstimator_instances);
    __privateAdd(this, _estimateShippingListener, __privateMethod(this, _ShippingEstimator_instances, estimateShipping_fn).bind(this));
  }
  connectedCallback() {
    this.submitButton = this.querySelector('[type="button"]');
    this.resultsElement = this.querySelector('[aria-live="polite"]');
    this.submitButton.addEventListener("click", __privateGet(this, _estimateShippingListener));
  }
  disconnectedCallback() {
    this.submitButton.removeEventListener("click", __privateGet(this, _estimateShippingListener));
  }
};
_estimateShippingListener = new WeakMap();
_ShippingEstimator_instances = new WeakSet();
estimateShipping_fn = async function(event) {
  event.preventDefault();
  const zip = this.querySelector('[name="address[zip]"]').value, country = this.querySelector('[name="address[country]"]').value, province = this.querySelector('[name="address[province]"]').value;
  this.submitButton.setAttribute("aria-busy", "true");
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  const prepareResponse = await fetch(
    `${Shopify.routes.root}cart/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`,
    { method: "POST" }
  );
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  if (prepareResponse.ok) {
    const shippingRates = await __privateMethod(this, _ShippingEstimator_instances, getAsyncShippingRates_fn).call(this, zip, country, province);
    __privateMethod(this, _ShippingEstimator_instances, formatShippingRates_fn).call(this, shippingRates);
  } else {
    const jsonError = await prepareResponse.json();
    __privateMethod(this, _ShippingEstimator_instances, formatError_fn).call(this, jsonError);
  }
  this.resultsElement.hidden = false;
  this.submitButton.removeAttribute("aria-busy");
};
getAsyncShippingRates_fn = async function(zip, country, province) {
  const response = await fetch(
    `${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`
  );
  const responseAsText = await response.text();
  if (responseAsText === "null") {
    return __privateMethod(this, _ShippingEstimator_instances, getAsyncShippingRates_fn).call(this, zip, country, province);
  } else {
    return JSON.parse(responseAsText)["shipping_rates"];
  }
};
formatShippingRates_fn = function(shippingRates) {
  let formattedShippingRates = shippingRates.map((shippingRate) => {
    return `<li>${shippingRate["presentment_name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
  });
  this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : `<ul class="list-disc" role="list">${formattedShippingRates}</ul>`}
      </div>
    `;
};
formatError_fn = function(errors) {
  let formattedShippingRates = Object.keys(errors).map((errorKey) => {
    return `<li>${errors[errorKey]}</li>`;
  }).join("");
  this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="list-disc" role="list">${formattedShippingRates}</ul>
      </div>
    `;
};
if (!window.customElements.get("shipping-estimator")) {
  window.customElements.define("shipping-estimator", ShippingEstimator);
}

// js/common/facets/facets-form.js
var _isDirty, _FacetsForm_instances, form_get, buildUrl_fn, onFormChanged_fn, onFormSubmitted_fn;
var FacetsForm = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _FacetsForm_instances);
    __privateAdd(this, _isDirty, false);
    this.addEventListener("change", __privateMethod(this, _FacetsForm_instances, onFormChanged_fn));
    this.addEventListener("submit", __privateMethod(this, _FacetsForm_instances, onFormSubmitted_fn));
  }
};
_isDirty = new WeakMap();
_FacetsForm_instances = new WeakSet();
form_get = function() {
  return this.querySelector("form");
};
buildUrl_fn = function() {
  const searchParams = new URLSearchParams(new FormData(__privateGet(this, _FacetsForm_instances, form_get))), url = new URL(__privateGet(this, _FacetsForm_instances, form_get).action);
  url.search = "";
  searchParams.forEach((value, key) => url.searchParams.append(key, value));
  // One colour swatch stands for every spelling of that colour ("Jet Black" / "JET BLACK").
  __privateGet(this, _FacetsForm_instances, form_get).querySelectorAll("input[data-filter-aliases]:checked").forEach((input) => {
    const existing = url.searchParams.getAll(input.name);
    input.getAttribute("data-filter-aliases").split("|||").forEach((alias) => {
      if (alias && !existing.includes(alias)) url.searchParams.append(input.name, alias);
    });
  });
  ["page", "filter.v.price.gte", "filter.v.price.lte"].forEach((optionToClear) => {
    if (url.searchParams.get(optionToClear) === "") {
      url.searchParams.delete(optionToClear);
    }
  });
  url.searchParams.delete("price_preset");
  keepUrlSubcategories(url);
  url.searchParams.set("section_id", this.getAttribute("section-id"));
  return url;
};
onFormChanged_fn = function() {
  __privateSet(this, _isDirty, true);
  if (this.hasAttribute("update-on-change")) {
    if (HTMLFormElement.prototype.requestSubmit) {
      __privateGet(this, _FacetsForm_instances, form_get).requestSubmit();
    } else {
      __privateGet(this, _FacetsForm_instances, form_get).dispatchEvent(new Event("submit", { cancelable: true }));
    }
  } else {
    cachedFetch(__privateMethod(this, _FacetsForm_instances, buildUrl_fn).call(this).toString());
  }
};
onFormSubmitted_fn = function(event) {
  event.preventDefault();
  if (!__privateGet(this, _isDirty)) {
    return;
  }
  this.dispatchEvent(
    new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url: __privateMethod(this, _FacetsForm_instances, buildUrl_fn).call(this)
      }
    })
  );
  __privateSet(this, _isDirty, false);
};
if (!window.customElements.get("facets-form")) {
  window.customElements.define("facets-form", FacetsForm);
}

// js/common/overlay/dialog-element.js
import { animate as animate4, FocusTrap, Delegate as Delegate2 } from "vendor";
var lockLayerCount = 0;
var lockScrollY = 0;
function pushScrollLock() {
  lockLayerCount += 1;
  syncDocumentScrollLock();
}
function popScrollLock() {
  lockLayerCount = Math.max(0, lockLayerCount - 1);
  syncDocumentScrollLock();
}
function syncDocumentScrollLock() {
  const root = document.documentElement;
  if (lockLayerCount > 0) {
    if (!root.classList.contains("lock")) {
      lockScrollY = window.scrollY || window.pageYOffset || 0;
      const scrollbarSize = Math.max(0, window.innerWidth - root.clientWidth);
      root.style.setProperty("--lock-scrollbar-size", `${scrollbarSize}px`);
      root.style.setProperty("--lock-scroll-top", `${-lockScrollY}px`);
      root.classList.toggle("lock--scrollbar-left", root.getBoundingClientRect().left > 0);
    }
    root.classList.add("lock");
    return;
  }
  if (!root.classList.contains("lock")) {
    return;
  }
  const scrollY = lockScrollY;
  root.classList.remove("lock", "lock--scrollbar-left");
  root.style.removeProperty("--lock-scrollbar-size");
  root.style.removeProperty("--lock-scroll-top");
  window.scrollTo(0, scrollY);
}
var _isLocked, _delegate2, _abortController5, _focusTrap, _originalParentBeforeAppend, _DialogElement_instances, allowOutsideClick_fn, allowOutsideClickTouch_fn, allowOutsideClickMouse_fn, onToggleClicked_fn, updateSlotVisibility_fn;
var DialogElement = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _DialogElement_instances);
    __privateAdd(this, _isLocked, false);
    __privateAdd(this, _delegate2, new Delegate2(document.body));
    __privateAdd(this, _abortController5);
    __privateAdd(this, _focusTrap);
    __privateAdd(this, _originalParentBeforeAppend);
    // A cached async theme.js defines <header-sidebar> before the parser sets its attributes.
    // The constructor would then stamp the default drawer, which has no main-panel slot, so the
    // menu opens empty on every page after the first. Wait until connectedCallback, when the
    // template attribute is present.
    if (this.localName === "header-sidebar" && !this.hasAttribute("template")) {
      this._deferHeaderSidebarShadow = true;
    } else if (this.shadowDomTemplate) {
      this.attachShadow({ mode: "open" }).appendChild(document.getElementById(this.shadowDomTemplate).content.cloneNode(true));
      this.shadowRoot.addEventListener("slotchange", (event) => __privateMethod(this, _DialogElement_instances, updateSlotVisibility_fn).call(this, event.target));
    }
    this.addEventListener("dialog:force-close", (event) => {
      this.hide();
      event.stopPropagation();
    });
  }
  static get observedAttributes() {
    return ["id", "open"];
  }
  connectedCallback() {
    if (this._deferHeaderSidebarShadow && !this.shadowRoot) {
      const templateId = this.getAttribute("template");
      const template = templateId && document.getElementById(templateId);
      if (template) {
        this._deferHeaderSidebarShadow = false;
        this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
        this.shadowRoot.addEventListener("slotchange", (event) => __privateMethod(this, _DialogElement_instances, updateSlotVisibility_fn).call(this, event.target));
      }
    }
    if (this.id) {
      __privateGet(this, _delegate2).off().on("click", `[aria-controls="${this.id}"]`, __privateMethod(this, _DialogElement_instances, onToggleClicked_fn).bind(this));
    }
    __privateSet(this, _abortController5, new AbortController());
    this.setAttribute("role", "dialog");
    if (this.shadowDomTemplate) {
      this.getShadowPartByName("overlay")?.addEventListener("click", this.hide.bind(this), { signal: this.abortController.signal });
      Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => __privateMethod(this, _DialogElement_instances, updateSlotVisibility_fn).call(this, slot));
    }
    if (window.Shopify?.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.show(!event.detail.load), { signal: this.abortController.signal });
      this.addEventListener("shopify:block:deselect", this.hide, { signal: this.abortController.signal });
      this._shopifySection = this._shopifySection || this.closest(".shopify-section");
      if (this._shopifySection) {
        if (this.hasAttribute("handle-editor-events")) {
          this._shopifySection.addEventListener("shopify:section:select", (event) => this.show(!event.detail.load), { signal: this.abortController.signal });
          this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this.abortController.signal });
        }
        this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this.abortController.signal });
      }
    }
  }
  disconnectedCallback() {
    __privateGet(this, _delegate2).off();
    this.abortController.abort();
    this.focusTrap?.deactivate({ onDeactivate: () => {
    } });
    if (__privateGet(this, _isLocked)) {
      __privateSet(this, _isLocked, false);
      lockLayerCount -= 1;
      syncDocumentScrollLock();
    }
  }
  /**
   * Open the dialog element (the animation can be disabled by passing false as an argument). This function should
   * normally not be directly overriden on children classes
   */
  show(animate27 = true) {
    if (this.open) {
      return Promise.resolve();
    }
    this.setAttribute("open", animate27 ? "" : "immediate");
    return waitForEvent(this, "dialog:after-show");
  }
  /**
   * Hide the dialog element. This function should normally not be directly overriden on children classes
   */
  hide(animate27 = true) {
    if (!this.open) {
      return Promise.resolve();
    }
    if (animate27 === false) this._instantHide = true;
    this.removeAttribute("open");
    return waitForEvent(this, "dialog:after-hide");
  }
  /**
   * Get the abort controller used to clean listeners. You can retrieve it in children classes to add your own listeners
   * that will be cleaned when the element is removed or re-rendered
   */
  get abortController() {
    return __privateGet(this, _abortController5);
  }
  /**
   * Get all the elements controlling this dialog (typically, button). An element controls this dialog if it has an
   * aria-controls attribute matching the ID of this dialog element
   */
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  /**
   * Returns if the dialog is open or closed
   */
  get open() {
    return this.hasAttribute("open");
  }
  /**
   * If true is returned, then FocusTrap will activate and manage all the focus management. This is required for good
   * accessibility (such as keyboard management) and should normally not be set to false in children classes unless
   * there is a very good reason to do so
   */
  get shouldTrapFocus() {
    return true;
  }
  /**
   * When the dialog focus is trapped, define if the page is lock (not scrollable). This is usually desirable on
   * full screen modals
   */
  get shouldLock() {
    return false;
  }
  /**
   * By default, when the focus is trapped on an element, a click outside the trapped element close it. Sometimes, it
   * may be desirable to turn off all interactions so that all clicks outside don't do anything
   */
  get clickOutsideDeactivates() {
    return true;
  }
  /**
   * Sometimes (especially for drawer) we need to ensure that an element is on top of everything else. To do that,
   * we need to move the element to the body. We are doing that on open, and then restore the initial position on
   * close
   */
  get shouldAppendToBody() {
    return false;
  }
  /**
   * Decide which element to focus first when the dialog focus is trapped. By default, the first focusable element
   * will be focused, but this can be overridden by passing a selector in the "initial-focus" attribute
   */
  get initialFocus() {
    return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") === "false" ? false : this.querySelector(this.getAttribute("initial-focus")) : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
  }
  /**
   * If set to true, then focus trap will not automatically scroll to the first focused element, which can cause
   * annoying experience.
   */
  get preventScrollWhenTrapped() {
    return true;
  }
  /**
   * Get the focus trap element configured with all the other attributes
   */
  get focusTrap() {
    return __privateSet(this, _focusTrap, __privateGet(this, _focusTrap) || new FocusTrap.createFocusTrap(this, {
      onDeactivate: this.hide.bind(this),
      allowOutsideClick: this.clickOutsideDeactivates ? __privateMethod(this, _DialogElement_instances, allowOutsideClick_fn).bind(this) : false,
      initialFocus: matchesMediaQuery("supports-hover") ? this.initialFocus : false,
      fallbackFocus: this,
      tabbableOptions: {
        getShadowRoot: true
      },
      preventScroll: this.preventScrollWhenTrapped
    }));
  }
  /**
   * Get the ShadowDOM template (if any). If there is one defined, the dialog automatically constructs it with the
   * shadow DOM
   */
  get shadowDomTemplate() {
    return this.getAttribute("template");
  }
  /**
   * For dialog that use Shadow DOM, this allows a quick retrieval of parts by name
   */
  getShadowPartByName(name) {
    return this.shadowRoot?.querySelector(`[part="${name}"]`);
  }
  /**
   * Callback called when attributes changes. This is the part that glues everything
   */
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "open":
        this.controls.forEach((toggle) => toggle.setAttribute("aria-expanded", newValue === null ? "false" : "true"));
        const epoch = this._dialogEpoch = (this._dialogEpoch || 0) + 1;
        // Only fast-forward an animation that is still playing. Once Motion finishes an animation it writes the end
        // value inline and cancels it, and finish() on a cancelled animation revives it with fill "both": the old
        // leave keyframes (overlay at opacity 0, content pushed off screen) then outrank every later animation and
        // hide the dialog right after it reopens.
        const previousState = this._dialogAnimation?.playState;
        if (previousState === "running" || previousState === "paused") {
          try {
            this._dialogAnimation.finish();
          } catch (error) {
          }
        }
        if (oldValue === null && (newValue === "" || newValue === "immediate")) {
          __privateSet(this, _originalParentBeforeAppend, null);
          this.style.setProperty("display", "block");
          this.dispatchEvent(new CustomEvent("dialog:before-show"));
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            __privateSet(this, _originalParentBeforeAppend, this.parentElement);
            document.body.append(this);
          }
          const animationControls = this.createEnterAnimationControls();
          this._dialogAnimation = animationControls;
          if (newValue === "immediate") {
            animationControls.finish();
          }
          animationControls.finished.then(() => {
            if (epoch !== this._dialogEpoch) return;
            this.dispatchEvent(new CustomEvent("dialog:after-show"));
          });
          if (this.shouldTrapFocus) {
            this.focusTrap.activate({
              checkCanFocusTrap: () => animationControls.finished
            });
          }
          if (this.shouldLock) {
            lockLayerCount += 1;
            __privateSet(this, _isLocked, true);
            syncDocumentScrollLock();
          }
        } else if (oldValue !== null && newValue === null) {
          const instant = this._instantHide === true;
          this._instantHide = false;
          this.dispatchEvent(new CustomEvent("dialog:before-hide"));
          const animationControls = this.createLeaveAnimationControls();
          this._dialogAnimation = animationControls;
          if (instant) animationControls.finish();
          const hideTransitionPromise = animationControls.finished;
          hideTransitionPromise.then(() => {
            if (epoch !== this._dialogEpoch || this.open) return;
            this.style.setProperty("display", "none");
            if (this.parentElement === document.body && __privateGet(this, _originalParentBeforeAppend)) {
              __privateGet(this, _originalParentBeforeAppend).appendChild(this);
              __privateSet(this, _originalParentBeforeAppend, null);
            }
            this.dispatchEvent(new CustomEvent("dialog:after-hide"));
          });
          this.focusTrap?.deactivate({
            returnFocus: !instant,
            checkCanReturnFocus: () => hideTransitionPromise
          });
          if (this.shouldLock) {
            __privateSet(this, _isLocked, false);
            lockLayerCount -= 1;
            syncDocumentScrollLock();
          }
        }
        this.dispatchEvent(new CustomEvent("toggle", { bubbles: true }));
        break;
    }
  }
  /**
   * Create the animation controls for the enter animation
   */
  createEnterAnimationControls() {
    return animate4(this, {}, { duration: 0 });
  }
  /**
   * Create the animation controls for the leave animation
   */
  createLeaveAnimationControls() {
    return animate4(this, {}, { duration: 0 });
  }
  /**
   * When "clickOutsideDeactivates" is true, this method is called on the final click destination. If this method
   * returns true, then the dialog closes (if false, the dialog remains in its current state). By default, this
   * will close the dialog if a click is done outside the dialog. However, this may be overridden in children classes
   * to provide custom behavior (for instance, to only allow some elements to close the dialog)
   */
  hideForOutsideClickTarget(target) {
    return !this.contains(target);
  }
  /**
   * When "clickOutsideDeactivates" is set to true, this method allows to control which element, when clicked, allows
   * to pass-through and have its behavior being executed
   */
  allowOutsideClickForTarget(target) {
    return false;
  }
};
_isLocked = new WeakMap();
_delegate2 = new WeakMap();
_abortController5 = new WeakMap();
_focusTrap = new WeakMap();
_originalParentBeforeAppend = new WeakMap();
_DialogElement_instances = new WeakSet();
/**
 * If "clickOutsideDeactivates" is true, then this listener will be called on every click outside the element. This
 * allows function separates touch and non-touch events
 */
allowOutsideClick_fn = function(event) {
  if ("TouchEvent" in window && event instanceof TouchEvent) {
    return __privateMethod(this, _DialogElement_instances, allowOutsideClickTouch_fn).call(this, event);
  } else {
    return __privateMethod(this, _DialogElement_instances, allowOutsideClickMouse_fn).call(this, event);
  }
};
/**
 * If "clickOutsideDeactivates" is true, this listener will be called on every touch click outside the trapped
 * element. By default, this will allow any click outside to cause the dialog to close
 */
allowOutsideClickTouch_fn = function(event) {
  event.target.addEventListener(
    "touchend",
    (subEvent) => {
      const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
      if (this.hideForOutsideClickTarget(endTarget)) {
        this.hide();
      }
    },
    { once: true, signal: this.abortController.signal }
  );
  return this.allowOutsideClickForTarget(event.target);
};
/**
 * If "clickOutsideDeactivates" is true, this listener will be called on every mouse click outside the trapped
 * element. By default, this will allow any click outside to cause the dialog to close.
 */
allowOutsideClickMouse_fn = function(event) {
  if (event.type !== "click") {
    return false;
  }
  if (this.hideForOutsideClickTarget(event.target)) {
    this.hide();
  }
  if (this.allowOutsideClickForTarget(event.target)) {
    return true;
  }
  let target = event.target, closestControl = event.target.closest("[aria-controls]");
  if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
    target = closestControl;
  }
  return this.id !== target.getAttribute("aria-controls");
};
/**
 * This function is called whenever a toggle (an element controlling this dialog) is called. This simply open
 * the dialog if closed, or close it if open
 */
onToggleClicked_fn = function(event) {
  event?.preventDefault();
  this.open ? this.hide() : this.show();
};
/**
 * Hide the slots that do not have any children
 */
updateSlotVisibility_fn = function(slot) {
  if (!["header", "footer"].includes(slot.name)) {
    return;
  }
  slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
};
var DialogCloseButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
  }
};
if (!window.customElements.get("dialog-element")) {
  window.customElements.define("dialog-element", DialogElement);
}
if (!window.customElements.get("dialog-close-button")) {
  window.customElements.define("dialog-close-button", DialogCloseButton);
}

// js/common/overlay/drawer.js
import { timeline as timeline3 } from "vendor";

// js/common/overlay/modal.js
import { animate as animate5, timeline as timeline2 } from "vendor";
var Modal = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
  }
  get shadowDomTemplate() {
    return this.getAttribute("template") || "modal-default-template";
  }
  get shouldLock() {
    return true;
  }
  get shouldAppendToBody() {
    return true;
  }
  createEnterAnimationControls() {
    if (matchesMediaQuery("sm")) {
      return animate5(this, { opacity: [0, 1] }, { duration: 0.2 });
    } else {
      return timeline2([
        [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
  createLeaveAnimationControls() {
    if (matchesMediaQuery("sm")) {
      return animate5(this, { opacity: [1, 0] }, { duration: 0.2 });
    } else {
      return timeline2([
        [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(0)", "translateY(100%)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
};
if (!window.customElements.get("x-modal")) {
  window.customElements.define("x-modal", Modal);
}

// js/common/overlay/drawer.js
var Drawer = class extends Modal {
  get shadowDomTemplate() {
    return this.getAttribute("template") || "drawer-default-template";
  }
  get openFrom() {
    return this.getAttribute("open-from") || "right";
  }
  createEnterAnimationControls() {
    this.getShadowPartByName("content").style.marginInlineStart = this.openFrom === "right" ? "auto" : 0;
    return timeline3([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { transform: [`translateX(calc(var(--transform-logical-flip) * ${this.openFrom === "right" ? "100%" : "-100%"}))`, "translateX(0)"] },
        { duration: 0.3, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
  createLeaveAnimationControls() {
    return timeline3([
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { transform: ["translateX(0)", `translateX(calc(var(--transform-logical-flip) * ${this.openFrom === "right" ? "100%" : "-100%"}))`] },
        { duration: 0.3, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
};
if (!window.customElements.get("x-drawer")) {
  window.customElements.define("x-drawer", Drawer);
}

// js/common/overlay/popin.js
import { animate as animate6 } from "vendor";
var PopIn = class extends DialogElement {
  get shouldTrapFocus() {
    return false;
  }
  createEnterAnimationControls() {
    return animate6(this, { opacity: [0, 1], transform: ["translateY(25px)", "translateY(0)"] }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] });
  }
  createLeaveAnimationControls() {
    return animate6(this, { opacity: [1, 0], transform: ["translateY(0)", "translateY(25px)"] }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] });
  }
};
if (!window.customElements.get("pop-in")) {
  window.customElements.define("pop-in", PopIn);
}

// js/common/overlay/popover.js
import { animate as animate7, timeline as timeline4 } from "vendor";
var Popover = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));
    if (this.hasAttribute("close-on-listbox-select")) {
      this.addEventListener("listbox:select", this.hide, { signal: this.abortController.signal });
    }
    if (this.hasAttribute("close-on-listbox-change")) {
      this.addEventListener("change", this.hide, { signal: this.abortController.signal });
    }
  }
  get shadowDomTemplate() {
    return this.getAttribute("template") || "popover-default-template";
  }
  get shouldLock() {
    return matchesMediaQuery("md-max");
  }
  get shouldAppendToBody() {
    return matchesMediaQuery("md-max");
  }
  get preventScrollWhenTrapped() {
    return true;
  }
  createEnterAnimationControls() {
    if (matchesMediaQuery("md")) {
      return animate7(this, { opacity: [0, 1] }, { duration: 0.2 });
    } else {
      return timeline4([
        [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
  createLeaveAnimationControls() {
    if (matchesMediaQuery("md")) {
      return animate7(this, { opacity: [1, 0] }, { duration: 0.2 });
    } else {
      return timeline4([
        [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] }],
        [this.getShadowPartByName("content"), { transform: ["translateY(0)", "translateY(100%)"] }, { duration: 0.3, easing: [0.645, 0.045, 0.355, 1], at: "<" }]
      ]);
    }
  }
};
if (!window.customElements.get("x-popover")) {
  window.customElements.define("x-popover", Popover);
}

// js/common/facets/facets-drawer.js
var _FacetsDrawer_instances, updateFacets_fn;
var FacetsDrawer = class extends Drawer {
  constructor() {
    super();
    __privateAdd(this, _FacetsDrawer_instances);
    this.addEventListener("dialog:after-hide", __privateMethod(this, _FacetsDrawer_instances, updateFacets_fn));
  }
};
_FacetsDrawer_instances = new WeakSet();
updateFacets_fn = function() {
  const form = this.querySelector("facets-form form");
  if (HTMLFormElement.prototype.requestSubmit) {
    form?.requestSubmit();
  } else {
    form?.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  }
};
if (!window.customElements.get("facets-drawer")) {
  window.customElements.define("facets-drawer", FacetsDrawer);
}

// js/common/facets/facet-link.js
var _FacetLink_instances, onFacetUpdate_fn;
var FacetLink = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _FacetLink_instances);
    this.addEventListener("click", __privateMethod(this, _FacetLink_instances, onFacetUpdate_fn).bind(this));
  }
};
_FacetLink_instances = new WeakSet();
function buildFacetLinkUrl(anchor) {
  const url = new URL(anchor.href, window.location.origin);
  const colorFilterNav = anchor.closest("[data-color-filter-params]");
  if (colorFilterNav) {
    const currentUrl = new URL(window.location.href);
    colorFilterNav.getAttribute("data-color-filter-params").split("|||").forEach((name) => {
      if (!name) return;
      url.searchParams.delete(name);
      currentUrl.searchParams.getAll(name).forEach((value) => {
        url.searchParams.append(name, value);
      });
    });
  }
  // A removed colour chip also removes the other spellings of that colour.
  const removeParam = anchor.getAttribute("data-remove-param");
  if (removeParam) {
    const removeValues = (anchor.getAttribute("data-remove-values") || "").split("|||").filter(Boolean);
    const kept = url.searchParams.getAll(removeParam).filter((value) => !removeValues.includes(value));
    url.searchParams.delete(removeParam);
    kept.forEach((value) => url.searchParams.append(removeParam, value));
  }
  url.searchParams.delete("page");
  return url;
}
function facetSectionRequestUrl(anchor) {
  const sectionId = extractSectionId(anchor) || document.querySelector("facets-form[section-id]")?.getAttribute("section-id") || "";
  if (!sectionId || !anchor?.href) return "";
  const url = buildFacetLinkUrl(anchor);
  url.searchParams.set("section_id", sectionId);
  return url.toString();
}
function prefetchFacetLink(anchor) {
  const requestUrl = facetSectionRequestUrl(anchor);
  if (!requestUrl) return;
  cachedFetch(requestUrl, { priority: "high" }).then((response) => response.text()).then((html) => warmHtmlImages(html, 4)).catch(() => {
  });
}
function showCollectionCardSkeletons(section) {
  const list = section?.querySelector("product-list");
  if (!list) return;
  const cards = list.querySelectorAll("product-card");
  const count = Math.max(cards.length, 4);
  const height = cards[0]?.getAttribute("data-card-height") || "standard";
  const fragment = document.createDocumentFragment();
  for (let index = 0; index < count; index += 1) {
    const card = document.createElement("div");
    card.className = "collection-card-skeleton";
    card.setAttribute("aria-hidden", "true");
    card.innerHTML = `<span class="collection-card-skeleton__media" data-card-height="${height}"></span><span class="collection-card-skeleton__line"></span><span class="collection-card-skeleton__line collection-card-skeleton__line--short"></span>`;
    fragment.appendChild(card);
  }
  list.replaceChildren(fragment);
}
function shouldShowSubcategorySkeleton(requestUrl) {
  return !(requestUrl && typeof cachedFetch.peek === "function" && cachedFetch.peek(requestUrl));
}
function prefetchCollectionSubcategories(root) {
  const connection = navigator.connection;
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || "")) return;
  const links = Array.from((root || document).querySelectorAll("collection-category-nav a[data-subcat-tag], collection-category-nav a[data-category-clear]"))
    .filter((anchor) => !anchor.classList.contains("is-active")).slice(0, 2);
  let index = 0;
  let active = 0;
  const pump = () => {
    while (active < 1 && index < links.length && document.visibilityState !== "hidden") {
      const anchor = links[index++];
      if (!anchor || anchor.classList.contains("is-active")) continue;
      active += 1;
      const sectionId = extractSectionId(anchor) || document.querySelector("facets-form[section-id]")?.getAttribute("section-id") || "";
      if (!sectionId) {
        active -= 1;
        continue;
      }
      const url = buildFacetLinkUrl(anchor);
      url.searchParams.set("section_id", sectionId);
      cachedFetch(url.toString(), { priority: "low" }).catch(() => {}).finally(() => {
        active -= 1;
        pump();
      });
    }
  };
  pump();
}
function isSubcategoryTag(value) {
  return String(value || "").replace(/[‎‏‪-‮⁦-⁩]/g, "").trim().toLowerCase().startsWith("subcat:");
}
// The Arabic storefront has no tag filter in the form or in Shopify's filter links, so a
// colour/size/price change would lose the chosen subcategory. Carry it over from the page URL.
function keepUrlSubcategories(url) {
  if (!document.querySelector("collection-category-nav[data-subcat-from-url]")) return;
  if (url.searchParams.getAll("filter.p.tag").some(isSubcategoryTag)) return;
  new URL(window.location.href).searchParams.getAll("filter.p.tag").filter(isSubcategoryTag).forEach((value) => url.searchParams.append("filter.p.tag", value));
}
function dropSubcategoryTags(url) {
  const tags = url.searchParams.getAll("filter.p.tag");
  const kept = tags.filter((value) => !isSubcategoryTag(value));
  if (kept.length === tags.length) return false;
  url.searchParams.delete("filter.p.tag");
  kept.forEach((value) => url.searchParams.append("filter.p.tag", value));
  url.searchParams.delete("page");
  return true;
}
function hasNonSubcategoryFilters(url) {
  let found = false;
  url.searchParams.forEach((value, key) => {
    if (!key.startsWith("filter.") || found) return;
    if (key === "filter.p.tag" && isSubcategoryTag(value)) return;
    found = true;
  });
  return found;
}
function isEmptyCollectionResult(doc) {
  if (doc.querySelector("[data-gl-variant-filter-miss]")) return true;
  const main = doc.querySelector(".collection__main");
  return Boolean(main) && !main.querySelector("product-list product-card");
}
async function subcategoryHasProducts(url) {
  const load = async (target) => new DOMParser().parseFromString(await (await cachedFetch(target.toString())).text(), "text/html");
  if (!isEmptyCollectionResult(await load(url))) return true;
  // Same colour fallbacks as facet:update (Arabic/English names), but never drop the colour.
  const retryUrl = new URL(url.toString());
  const cleaned = typeof window.glCleanTagFilters === "function" && window.glCleanTagFilters(retryUrl);
  const expanded = typeof window.glExpandColorFilters === "function" && window.glExpandColorFilters(retryUrl);
  if (!cleaned && !expanded) return false;
  return !isEmptyCollectionResult(await load(retryUrl));
}
var subcategoryCheckSerial = 0;
window.glCheckSubcategoryAvailability = function(nav) {
  if (!nav?.isConnected) return;
  const links = Array.from(nav.querySelectorAll("a[data-subcat-tag]"));
  const showAll = () => links.forEach((link) => {
    const item = link.closest("li");
    if (item) item.hidden = false;
  });
  // A selected subcategory hides the real counts, so each other chip is checked
  // against the current colour, size and price filters with its own request.
  if (!hasNonSubcategoryFilters(new URL(window.location.href))) {
    showAll();
    return;
  }
  const sectionId = extractSectionId(nav) || document.querySelector("facets-form[section-id]")?.getAttribute("section-id") || "";
  if (!sectionId) return;
  const serial = String(++subcategoryCheckSerial);
  nav.dataset.subcatCheck = serial;
  links.forEach((link) => {
    const item = link.closest("li");
    if (!item || link.classList.contains("is-active")) return;
    const url = buildFacetLinkUrl(link);
    url.searchParams.set("section_id", sectionId);
    subcategoryHasProducts(url).then((available) => {
      if (nav.dataset.subcatCheck !== serial) return;
      item.hidden = !available;
    }).catch(() => {
    });
  });
};
onFacetUpdate_fn = function(event) {
  event.preventDefault();
  const anchor = this.querySelector("a");
  if (!anchor) {
    return;
  }
  const sectionId = extractSectionId(this) || document.querySelector("facets-form[section-id]")?.getAttribute("section-id") || "";
  const removableFacet = this.closest(".removable-facet");
  const activeFacets = this.closest(".active-facets");
  if (removableFacet) {
    removableFacet.remove();
    if (activeFacets && !activeFacets.querySelector(".removable-facet")) {
      activeFacets.remove();
    }
  } else if (activeFacets && anchor.classList.contains("facets-clear-all")) {
    activeFacets.remove();
  }
  const isCategoryLink = anchor.hasAttribute("data-subcat-tag") || anchor.hasAttribute("data-category-clear");
  if (isCategoryLink) {
    const list = anchor.closest(".collection-category-filter__list");
    list?.querySelectorAll(".collection-category-filter__link").forEach((link) => {
      const active = link === anchor;
      link.classList.toggle("is-active", active);
      if (active) link.setAttribute("aria-current", "true");
      else link.removeAttribute("aria-current");
    });
    if (shouldShowSubcategorySkeleton(facetSectionRequestUrl(anchor))) showCollectionCardSkeletons(anchor.closest(".shopify-section"));
  }
  const url = buildFacetLinkUrl(anchor);
  if (!isCategoryLink && !anchor.classList.contains("facets-clear-all")) keepUrlSubcategories(url);
  const colorFilterNav = anchor.closest("[data-color-filter-params]");
  if (!sectionId) {
    window.location.href = url.toString();
    return;
  }
  url.searchParams.set("section_id", sectionId);
  document.dispatchEvent(
    new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url,
        scrollToProducts: !this.hasAttribute("data-facet-no-scroll"),
        keepVariantColor: Boolean(colorFilterNav),
        preserveCategoryNav: isCategoryLink
      }
    })
  );
};
if (!window.customElements.get("facet-link")) {
  window.customElements.define("facet-link", FacetLink);
}

// js/common/facets/facets-sort-popover.js
var _FacetsSortPopover_instances, onSortChange_fn;
var FacetsSortPopover = class extends Popover {
  constructor() {
    super();
    __privateAdd(this, _FacetsSortPopover_instances);
    this.addEventListener("listbox:change", __privateMethod(this, _FacetsSortPopover_instances, onSortChange_fn));
  }
};
_FacetsSortPopover_instances = new WeakSet();
onSortChange_fn = function(event) {
  const url = new URL(window.location.href);
  url.searchParams.set("sort_by", event.detail.value);
  url.searchParams.delete("page");
  url.searchParams.set("section_id", this.getAttribute("section-id"));
  this.dispatchEvent(
    new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url
      }
    })
  );
};
if (!window.customElements.get("facets-sort-popover")) {
  window.customElements.define("facets-sort-popover", FacetsSortPopover);
}

// js/common/facets/facets-listeners.js
import { Delegate as Delegate3 } from "vendor";
var delegate = new Delegate3(document.body);
var accordionState = new Map(
  Array.from(document.querySelectorAll("facets-form details[data-accordion-id]"), (details) => [details.getAttribute("data-accordion-id"), details.open])
);
delegate.on(
  "toggle",
  "facets-form details[data-accordion-id]",
  (event, detailsElement) => {
    const accordionId = detailsElement.getAttribute("data-accordion-id");
    if (!accordionId) {
      return;
    }
    accordionState.set(accordionId, detailsElement.open);
  },
  true
);
delegate.on(
  "click",
  "[data-price-preset]",
  (event, input) => {
    if (input.dataset.priceActive !== "true") {
      return;
    }
    const form = input.closest("form");
    const minInput = form?.querySelector("[data-price-gte]");
    const maxInput = form?.querySelector("[data-price-lte]");
    if (!minInput || !maxInput) {
      return;
    }
    event.preventDefault();
    input.checked = false;
    input.dataset.priceActive = "false";
    minInput.value = "";
    maxInput.value = "";
    input.dispatchEvent(new Event("change", { bubbles: true }));
  },
  true
);
delegate.on(
  "change",
  "[data-price-preset]",
  (event, input) => {
    const form = input.closest("form");
    const minInput = form?.querySelector("[data-price-gte]");
    const maxInput = form?.querySelector("[data-price-lte]");
    if (!minInput || !maxInput || !input.checked) {
      return;
    }
    minInput.value = input.dataset.priceMin || "";
    maxInput.value = input.dataset.priceMax || "";
    form.querySelectorAll("[data-price-preset]").forEach((preset) => {
      preset.dataset.priceActive = preset === input ? "true" : "false";
    });
    input.blur();
  },
  true
);
// Product card sizes sit on one line. When any card in a list (collection grid, search results, predictive search)
// cannot fit its sizes, the whole list gets [data-size-trigger-mode] and theme.css shows the round size button on
// every card there instead, so cards in one list always match. The size line stays laid out (only invisible), so it
// is measured again on resize and the list switches back when there is room.
function productCardSizesGroup(sizes) {
  return sizes.closest("product-list, .predictive-search__products, [data-product-card-group]") || sizes.closest(".shopify-section") || document.body;
}
function productCardSizesOverflow(sizes) {
  return sizes.clientWidth > 0 && sizes.scrollWidth > sizes.clientWidth + 1;
}
function updateSizeTriggerGroups(sizesList) {
  // Every group is measured before any attribute changes, so the page lays out once instead of once per group.
  const groups = Array.from(new Set(Array.from(sizesList, productCardSizesGroup)));
  const needsTrigger = groups.map((group) => Array.from(group.querySelectorAll("[data-product-card-sizes]")).some(productCardSizesOverflow));
  groups.forEach((group, index) => group.toggleAttribute("data-size-trigger-mode", needsTrigger[index]));
}
var productCardSizesObserver = typeof ResizeObserver === "function" ? new ResizeObserver((entries) => updateSizeTriggerGroups(entries.map((entry) => entry.target))) : null;
function observeProductCardSizes(root = document) {
  const added = Array.from(root.querySelectorAll?.("[data-product-card-sizes]:not([data-size-observed])") || []);
  if (root.matches?.("[data-product-card-sizes]:not([data-size-observed])")) added.push(root);
  added.forEach((sizes) => {
    sizes.setAttribute("data-size-observed", "");
    productCardSizesObserver?.observe(sizes);
  });
  // The ResizeObserver reports every newly observed line before the next paint, in one batch after layout,
  // so measuring here as well only forced an extra synchronous layout on each DOM insertion.
  if (added.length && !productCardSizesObserver) updateSizeTriggerGroups(added);
}
observeProductCardSizes();
// Cards added later (infinite scroll, filters, predictive search) join their list's decision as soon as they appear.
new MutationObserver((mutations) => {
  mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
    if (node.nodeType === 1) observeProductCardSizes(node);
  }));
}).observe(document.documentElement, { childList: true, subtree: true });
// Web fonts change label widths without resizing the line, so everything is measured again once they load.
function updateAllSizeTriggerGroups() {
  updateSizeTriggerGroups(document.querySelectorAll("[data-size-observed]"));
}
document.fonts?.ready.then(updateAllSizeTriggerGroups);
document.fonts?.addEventListener?.("loadingdone", updateAllSizeTriggerGroups);
function searchResourceTabPairs(tabsRoot) {
  if (!tabsRoot) return [];
  const buttons = Array.from(tabsRoot.children).filter((node) => node.matches?.('button[role="tab"]'));
  const panels = Array.from(tabsRoot.children).filter((node) => node.matches?.('[role="tabpanel"]'));
  return buttons.map((button, index) => ({ button, panel: panels[index] })).filter((item) => item.panel);
}
function searchResourceType(button, panel) {
  return button?.dataset?.searchResource || panel?.dataset?.searchResource || "";
}
function isAllowedSearchResourceTab(tabsRoot, button, panel) {
  if (!tabsRoot) return true;
  const type = searchResourceType(button, panel);
  if (type === "article" && tabsRoot.getAttribute("data-show-posts-tab") === "false") return false;
  if (type === "page" && tabsRoot.getAttribute("data-show-pages-tab") === "false") return false;
  return true;
}
function nonProductSearchTabs(tabsRoot) {
  return searchResourceTabPairs(tabsRoot).filter(({ button, panel }) => {
    if (panel.querySelector(".collection, product-list")) return false;
    return isAllowedSearchResourceTab(tabsRoot, button, panel);
  });
}
function searchResourceTabsKey() {
  const params = new URLSearchParams(window.location.search);
  return `gl-search-tabs:${document.documentElement.lang || ""}:${window.location.pathname}:${params.get("q") || ""}`;
}
function rememberSearchResourceTabs(tabsRoot) {
  const extras = nonProductSearchTabs(tabsRoot);
  if (!extras.length) return;
  try {
    sessionStorage.setItem(searchResourceTabsKey(), JSON.stringify(extras.map(({ button, panel }) => ({
      button: button.outerHTML,
      panel: panel.outerHTML
    }))));
  } catch (error) {
  }
}
function readRememberedSearchTabs() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(searchResourceTabsKey()) || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    return [];
  }
}
function ensureSearchTabList(tabsRoot) {
  const template = tabsRoot?.querySelector("template");
  if (!template || template.content.querySelector('[part="tab-list"]')) return;
  const scrollable = template.content.ownerDocument.createElement("div");
  scrollable.setAttribute("part", "tab-list-scrollable");
  scrollable.className = "scrollable";
  const slot = template.content.ownerDocument.createElement("slot");
  slot.setAttribute("role", "tablist");
  slot.setAttribute("part", "tab-list");
  slot.setAttribute("name", "title");
  scrollable.appendChild(slot);
  const contentSlot = template.content.querySelector('slot[name="content"]');
  if (contentSlot) template.content.insertBefore(scrollable, contentSlot);
  else template.content.appendChild(scrollable);
}
function ensureLiveSearchTabList(tabsRoot) {
  ensureSearchTabList(tabsRoot);
  const shadow = tabsRoot.shadowRoot;
  if (!shadow || shadow.querySelector('[part="tab-list"]')) return;
  const scrollable = document.createElement("div");
  scrollable.setAttribute("part", "tab-list-scrollable");
  scrollable.className = "scrollable";
  const slot = document.createElement("slot");
  slot.setAttribute("role", "tablist");
  slot.setAttribute("part", "tab-list");
  slot.setAttribute("name", "title");
  scrollable.appendChild(slot);
  const contentSlot = shadow.querySelector('slot[name="content"]');
  if (contentSlot) shadow.insertBefore(scrollable, contentSlot);
  else shadow.appendChild(scrollable);
}
function appendSearchResourceTab(tabsRoot, button, panel) {
  const label = (button.textContent || "").trim();
  if (!label || !panel) return false;
  if (!isAllowedSearchResourceTab(tabsRoot, button, panel)) return false;
  const existing = Array.from(tabsRoot.querySelectorAll(':scope > button[role="tab"]')).some((item) => (item.textContent || "").trim() === label);
  if (existing) return false;
  const panelClone = panel.cloneNode ? panel.cloneNode(true) : panel;
  if (panelClone === panel && panel.ownerDocument !== tabsRoot.ownerDocument) return false;
  const buttonClone = button.cloneNode ? button.cloneNode(true) : button;
  panelClone.hidden = true;
  tabsRoot.append(buttonClone, panelClone);
  return true;
}
function graftSearchResourceTabs(sourceTabs, targetTabs) {
  if (!sourceTabs || !targetTabs) return false;
  let added = false;
  nonProductSearchTabs(sourceTabs).forEach(({ button, panel }) => {
    if (appendSearchResourceTab(targetTabs, button, panel)) added = true;
  });
  if (added || targetTabs.querySelectorAll(':scope > button[role="tab"]').length > 1) finishSearchTabList(targetTabs);
  return added;
}
function finishSearchTabList(tabsRoot) {
  if (!tabsRoot || tabsRoot.querySelectorAll(':scope > button[role="tab"]').length < 2) return;
  ensureLiveSearchTabList(tabsRoot);
  if (tabsRoot.isConnected) tabsRoot.shadowRoot?.dispatchEvent(new Event("slotchange"));
}
function applyRememberedSearchTabs(tabsRoot, stored) {
  const holder = document.createElement("div");
  let added = false;
  stored.forEach((item) => {
    if (!item?.button || !item?.panel) return;
    holder.innerHTML = item.button + item.panel;
    const button = holder.querySelector('button[role="tab"]');
    const panel = holder.querySelector('[role="tabpanel"]');
    if (button && panel && appendSearchResourceTab(tabsRoot, button, panel)) added = true;
    holder.replaceChildren();
  });
  if (added) finishSearchTabList(tabsRoot);
  return added;
}
async function restoreFilteredSearchTabs() {
  const section = document.querySelector(".shopify-section--main-search");
  const tabs = section?.querySelector("x-tabs.main-search__tabs");
  if (!tabs) return;
  if (nonProductSearchTabs(tabs).length) {
    rememberSearchResourceTabs(tabs);
    return;
  }
  const current = new URL(window.location.href);
  const hasFilter = Array.from(current.searchParams.keys()).some((key) => key.startsWith("filter."));
  if (!hasFilter) return;
  const remembered = readRememberedSearchTabs();
  if (remembered.length && applyRememberedSearchTabs(tabs, remembered)) return;
  const sectionId = section.id.replace("shopify-section-", "");
  if (!sectionId) return;
  const plain = new URL(current.href);
  Array.from(plain.searchParams.keys()).forEach((key) => {
    if (key.startsWith("filter.") || key === "page" || key === "sort_by" || key === "section_id") plain.searchParams.delete(key);
  });
  plain.searchParams.set("section_id", sectionId);
  try {
    const html = await (await cachedFetch(plain.toString())).text();
    const source = new DOMParser().parseFromString(html, "text/html").querySelector("x-tabs.main-search__tabs");
    if (!graftSearchResourceTabs(source, tabs)) return;
    finishSearchTabList(tabs);
    rememberSearchResourceTabs(tabs);
  } catch (error) {
  }
}
function readCollectionCardLayout(root) {
  const list = root?.querySelector?.("product-list[collection-desktop-layout], product-list[collection-mobile-layout]");
  if (!list) return null;
  return {
    desktop: list.getAttribute("collection-desktop-layout") || "",
    mobile: list.getAttribute("collection-mobile-layout") || ""
  };
}
function applyCollectionCardLayout(root, layout) {
  if (!root || !layout) return;
  root.querySelectorAll("product-list").forEach((list) => {
    if (!list.hasAttribute("collection-desktop-layout") && !list.hasAttribute("collection-mobile-layout")) return;
    if (layout.desktop) list.setAttribute("collection-desktop-layout", layout.desktop);
    if (layout.mobile) list.setAttribute("collection-mobile-layout", layout.mobile);
    updateProductListImageSizes(list);
  });
  root.querySelectorAll("collection-layout-switch").forEach((control) => {
    const value = control.getAttribute("device") === "mobile" ? layout.mobile : layout.desktop;
    if (!value) return;
    control.querySelectorAll("button").forEach((button) => {
      button.classList.toggle("is-active", button.value === value);
    });
  });
}
var facetRequestSerial = 0;
document.addEventListener("facet:update", async (event) => {
  const requestSerial = ++facetRequestSerial;
  const url = event.detail.url, shopifySection = document.getElementById(`shopify-section-${url.searchParams.get("section_id")}`);
  const preservedNavs = event.detail.preserveCategoryNav ? Array.from(shopifySection?.querySelectorAll("collection-category-nav") || []) : [];
  const peeked = cachedFetch.peek(url.toString());
  try {
    if (peeked == null) document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    const html = peeked != null ? peeked : await (await cachedFetch(url.toString())).text();
    if (requestSerial !== facetRequestSerial) return;
    const tempContent = new DOMParser().parseFromString(html, "text/html");
    const emptyResults = tempContent.querySelector("[data-gl-variant-filter-miss]");
    if (emptyResults && (event.detail.filterFallback || 0) < 2) {
      const nextUrl = new URL(url.toString());
      const cleaned = typeof window.glCleanTagFilters === "function" && window.glCleanTagFilters(nextUrl);
      const expanded = typeof window.glExpandColorFilters === "function" && window.glExpandColorFilters(nextUrl);
      const hasSubcategory = nextUrl.searchParams.getAll("filter.p.tag").some((value) => String(value || "").replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, "").trim().toLowerCase().startsWith("subcat:"));
      // A chip click keeps the chosen subcategory; a colour/size change keeps the chosen filter
      // and leaves the subcategory instead, since that subcategory has no such product.
      const dropped = !cleaned && !expanded && hasSubcategory && (event.detail.preserveCategoryNav ? typeof window.glDropVariantFilters === "function" && window.glDropVariantFilters(nextUrl) : dropSubcategoryTags(nextUrl));
      if (cleaned || expanded || dropped) {
        document.dispatchEvent(new CustomEvent("facet:update", {
          bubbles: true,
          detail: {
            url: nextUrl,
            scrollToProducts: event.detail.scrollToProducts,
            keepVariantColor: false,
            preserveCategoryNav: dropped ? false : event.detail.preserveCategoryNav,
            filterFallback: (event.detail.filterFallback || 0) + 1
          }
        }));
        return;
      }
    }
    if (peeked == null) document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    const newShopifySection = tempContent.querySelector(".shopify-section");
    const previousSearchTabs = shopifySection?.querySelector("x-tabs.main-search__tabs");
    const nextSearchTabs = newShopifySection?.querySelector("x-tabs.main-search__tabs");
    if (previousSearchTabs && nextSearchTabs) {
      graftSearchResourceTabs(previousSearchTabs, nextSearchTabs);
      if (!nonProductSearchTabs(nextSearchTabs).length) {
        applyRememberedSearchTabs(nextSearchTabs, readRememberedSearchTabs());
      }
      finishSearchTabList(nextSearchTabs);
      rememberSearchResourceTabs(nextSearchTabs);
    }
    newShopifySection.querySelectorAll("facets-form details[data-accordion-id]").forEach((detailsElement) => {
      const accordionId = detailsElement.getAttribute("data-accordion-id");
      const isLocked = detailsElement.closest("accordion-disclosure")?.hasAttribute("data-locked");
      if (isLocked) {
        detailsElement.open = true;
        detailsElement.setAttribute("aria-expanded", "true");
        return;
      }
      if (accordionState.has(accordionId)) {
        detailsElement.open = accordionState.get(accordionId);
        detailsElement.setAttribute("aria-expanded", detailsElement.open ? "true" : "false");
      }
    });
    const focusedElement = document.activeElement;
    const openFacetsDrawer = document.querySelector("facets-drawer[open]");
    // Subcategory and other facet swaps replace this section with HTML that was often prefetched
    // before the customer changed card size. Keep the size already on the page.
    applyCollectionCardLayout(newShopifySection, readCollectionCardLayout(shopifySection));
    shopifySection.replaceChildren(...document.importNode(newShopifySection, true).childNodes);
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete("section_id");
    history.replaceState({ ...(history.state || {}) }, "", clonedUrl.toString());
    if (preservedNavs.length) {
      shopifySection.querySelectorAll("collection-category-nav").forEach((nav, index) => {
        if (preservedNavs[index]) nav.replaceWith(preservedNavs[index]);
      });
    } else if (typeof window.glRestoreCollectionSubcategories === "function") {
      shopifySection.querySelectorAll("collection-category-nav").forEach((nav) => {
        nav.dataset.subcatReady = "false";
        window.glRestoreCollectionSubcategories(nav);
      });
    }
    if (openFacetsDrawer?.isConnected) {
      const replacementDrawer = shopifySection.querySelector("facets-drawer");
      if (replacementDrawer) {
        const returnParent = replacementDrawer.parentElement;
        openFacetsDrawer.replaceChildren(...Array.from(replacementDrawer.childNodes));
        replacementDrawer.remove();
        __privateSet(openFacetsDrawer, _originalParentBeforeAppend, returnParent);
        if (openFacetsDrawer.parentElement !== document.body) {
          document.body.append(openFacetsDrawer);
        }
        openFacetsDrawer.setAttribute("open", "");
      }
    }
    if (window.DoublyGlobalCurrency && typeof window.DoublyGlobalCurrency.convertAll === "function") {
      const savedCurrency = document.cookie.match(/(?:^|; )gl_currency=([^;]*)/);
      const activeCurrency = savedCurrency ? decodeURIComponent(savedCurrency[1]) : "";
      if (activeCurrency) window.DoublyGlobalCurrency.convertAll(activeCurrency);
    }
    if (focusedElement?.id && document.getElementById(focusedElement.id)) {
      document.getElementById(focusedElement.id).focus();
    }
    if (event.detail.keepVariantColor) restoreCollectionCardColors(shopifySection);
    const scrollToProductList = () => {
      if (event.detail.scrollToProducts === false) {
        return;
      }
      shopifySection.querySelector(".collection").scrollIntoView({ block: "start", behavior: "smooth" });
    };
    if ("requestIdleCallback" in window) {
      requestIdleCallback(scrollToProductList, { timeout: 500 });
    } else {
      requestAnimationFrame(scrollToProductList);
    }
  } catch (e) {
  }
});
document.addEventListener("pointerenter", (event) => {
  const anchor = event.target.closest?.("a[data-subcat-tag], a[data-category-clear]");
  if (anchor) prefetchFacetLink(anchor);
}, true);
document.addEventListener("pointerdown", (event) => {
  const anchor = event.target.closest?.("a[data-subcat-tag], a[data-category-clear]");
  if (anchor) prefetchFacetLink(anchor);
}, true);
document.addEventListener("focusin", (event) => {
  const anchor = event.target.closest?.("a[data-subcat-tag], a[data-category-clear]");
  if (anchor) prefetchFacetLink(anchor);
}, true);
function initCollectionSubcategories() {
  // The nav's inline script can run before this file loads, so check the chips here too.
  document.querySelectorAll("collection-category-nav").forEach((nav) => window.glCheckSubcategoryAvailability(nav));
  // Warmed after the page load event so these section renders never share bandwidth with the first products.
  afterPageLoad(() => prefetchCollectionSubcategories(document));
  restoreFilteredSearchTabs();
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCollectionSubcategories, { once: true });
} else {
  initCollectionSubcategories();
}



// js/common/feedback/progress-bar.js
import { inView as inView5 } from "vendor";
var _allowUpdatingProgress, _ProgressBar_instances, calculateProgressBar_fn;
var ProgressBar = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProgressBar_instances);
    __privateAdd(this, _allowUpdatingProgress, !this.hasAttribute("animate-on-scroll"));
  }
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  connectedCallback() {
    if (this.hasAttribute("animate-on-scroll")) {
      inView5(this, () => {
        __privateSet(this, _allowUpdatingProgress, true);
        __privateMethod(this, _ProgressBar_instances, calculateProgressBar_fn).call(this);
      });
    }
  }
  get progress() {
    return Math.min(1, this.getAttribute("aria-valuenow") / this.getAttribute("aria-valuemax"));
  }
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  attributeChangedCallback() {
    if (__privateGet(this, _allowUpdatingProgress)) {
      __privateMethod(this, _ProgressBar_instances, calculateProgressBar_fn).call(this);
    }
  }
};
_allowUpdatingProgress = new WeakMap();
_ProgressBar_instances = new WeakSet();
calculateProgressBar_fn = function() {
  this.style.setProperty("--progress", `${this.progress}`);
};
if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}

// js/common/form/price-range.js
var PriceRange = class extends HTMLElement {
  #abortController;
  connectedCallback() {
    this.#abortController = new AbortController();
    const rangeLowerBound = this.querySelector('input[type="range"]:first-child'), rangeHigherBound = this.querySelector('input[type="range"]:last-child'), textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]'), textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    textInputLowerBound.addEventListener("focus", () => textInputLowerBound.select(), { signal: this.#abortController.signal });
    textInputHigherBound.addEventListener("focus", () => textInputHigherBound.select(), { signal: this.#abortController.signal });
    textInputLowerBound.addEventListener(
      "change",
      (event) => {
        event.preventDefault();
        event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1), event.target.min);
        rangeLowerBound.value = event.target.value;
        rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(rangeLowerBound.value) / parseInt(rangeLowerBound.max) * 100}%`);
      },
      { signal: this.#abortController.signal }
    );
    textInputHigherBound.addEventListener(
      "change",
      (event) => {
        event.preventDefault();
        event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1), event.target.max);
        rangeHigherBound.value = event.target.value;
        rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(rangeHigherBound.value) / parseInt(rangeHigherBound.max) * 100}%`);
      },
      { signal: this.#abortController.signal }
    );
    rangeLowerBound.addEventListener(
      "change",
      (event) => {
        event.stopPropagation();
        textInputLowerBound.value = event.target.value;
        textInputLowerBound.dispatchEvent(new Event("change", { bubbles: true }));
      },
      { signal: this.#abortController.signal }
    );
    rangeHigherBound.addEventListener(
      "change",
      (event) => {
        event.stopPropagation();
        textInputHigherBound.value = event.target.value;
        textInputHigherBound.dispatchEvent(new Event("change", { bubbles: true }));
      },
      { signal: this.#abortController.signal }
    );
    rangeLowerBound.addEventListener(
      "input",
      (event) => {
        event.target.value = Math.min(parseInt(event.target.value), parseInt(textInputHigherBound.value || event.target.max) - 1);
        event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        textInputLowerBound.value = event.target.value;
      },
      { signal: this.#abortController.signal }
    );
    rangeHigherBound.addEventListener(
      "input",
      (event) => {
        event.target.value = Math.max(parseInt(event.target.value), parseInt(textInputLowerBound.value || event.target.min) + 1);
        event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
        textInputHigherBound.value = event.target.value;
      },
      { signal: this.#abortController.signal }
    );
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
};
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}

// js/common/form/quantity-selector.js
var _abortController6, _decreaseButton, _increaseButton, _inputElement, _QuantitySelector_instances, onDecreaseQuantity_fn, onIncreaseQuantity_fn, updateUI_fn;
var QuantitySelector = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _QuantitySelector_instances);
    __privateAdd(this, _abortController6);
    __privateAdd(this, _decreaseButton);
    __privateAdd(this, _increaseButton);
    __privateAdd(this, _inputElement);
  }
  connectedCallback() {
    __privateSet(this, _abortController6, new AbortController());
    __privateSet(this, _decreaseButton, this.querySelector("button:first-of-type"));
    __privateSet(this, _increaseButton, this.querySelector("button:last-of-type"));
    __privateSet(this, _inputElement, this.querySelector("input"));
    __privateGet(this, _decreaseButton)?.addEventListener("click", __privateMethod(this, _QuantitySelector_instances, onDecreaseQuantity_fn).bind(this), { signal: __privateGet(this, _abortController6).signal });
    __privateGet(this, _increaseButton)?.addEventListener("click", __privateMethod(this, _QuantitySelector_instances, onIncreaseQuantity_fn).bind(this), { signal: __privateGet(this, _abortController6).signal });
    __privateGet(this, _inputElement)?.addEventListener("input", () => __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this), { signal: __privateGet(this, _abortController6).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController6).abort();
  }
  get quantity() {
    return __privateGet(this, _inputElement).value;
  }
  set quantity(quantity) {
    __privateGet(this, _inputElement).value = quantity;
    __privateGet(this, _inputElement).dispatchEvent(new Event("change", { bubbles: true }));
    __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
  }
  restoreDefaultValue() {
    __privateGet(this, _inputElement).value = __privateGet(this, _inputElement).defaultValue;
    __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
  }
};
_abortController6 = new WeakMap();
_decreaseButton = new WeakMap();
_increaseButton = new WeakMap();
_inputElement = new WeakMap();
_QuantitySelector_instances = new WeakSet();
onDecreaseQuantity_fn = function() {
  if (this.hasAttribute("allow-reset-to-zero") && __privateGet(this, _inputElement).value === __privateGet(this, _inputElement).min) {
    __privateGet(this, _inputElement).value = 0;
  } else {
    __privateGet(this, _inputElement).stepDown();
  }
  announceStatus(__privateGet(this, _inputElement).value);
  __privateGet(this, _inputElement).dispatchEvent(new Event("change", { bubbles: true }));
  __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
};
onIncreaseQuantity_fn = function() {
  __privateGet(this, _inputElement).stepUp();
  announceStatus(__privateGet(this, _inputElement).value);
  __privateGet(this, _inputElement).dispatchEvent(new Event("change", { bubbles: true }));
  __privateMethod(this, _QuantitySelector_instances, updateUI_fn).call(this);
};
updateUI_fn = function() {
  if (__privateGet(this, _decreaseButton)) {
    if (this.hasAttribute("allow-reset-to-zero") && __privateGet(this, _inputElement).value === __privateGet(this, _inputElement).min) {
      __privateGet(this, _decreaseButton).disabled = false;
    } else {
      __privateGet(this, _decreaseButton).disabled = parseInt(__privateGet(this, _inputElement).value) <= parseInt(__privateGet(this, _inputElement).min);
    }
  }
  if (__privateGet(this, _increaseButton)) {
    __privateGet(this, _increaseButton).disabled = __privateGet(this, _inputElement).hasAttribute("max") ? parseInt(__privateGet(this, _inputElement).value) >= parseInt(__privateGet(this, _inputElement).max) : false;
  }
};
var _QuantityInput_instances, inputElement_get, onValueInput_fn, onValueChange_fn;
var QuantityInput = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuantityInput_instances);
    __privateGet(this, _QuantityInput_instances, inputElement_get).addEventListener("input", __privateMethod(this, _QuantityInput_instances, onValueInput_fn).bind(this));
    __privateGet(this, _QuantityInput_instances, inputElement_get).addEventListener("change", __privateMethod(this, _QuantityInput_instances, onValueChange_fn).bind(this));
    __privateGet(this, _QuantityInput_instances, inputElement_get).addEventListener("focus", () => __privateGet(this, _QuantityInput_instances, inputElement_get).select());
  }
  connectedCallback() {
    this.style.setProperty("--quantity-selector-character-count", `${__privateGet(this, _QuantityInput_instances, inputElement_get).value.length}ch`);
  }
  get quantity() {
    return parseInt(__privateGet(this, _QuantityInput_instances, inputElement_get).value);
  }
};
_QuantityInput_instances = new WeakSet();
inputElement_get = function() {
  return this.firstElementChild;
};
onValueInput_fn = function() {
  if (__privateGet(this, _QuantityInput_instances, inputElement_get).value === "") {
    __privateGet(this, _QuantityInput_instances, inputElement_get).value = __privateGet(this, _QuantityInput_instances, inputElement_get).min || 1;
  }
  this.style.setProperty("--quantity-selector-character-count", `${__privateGet(this, _QuantityInput_instances, inputElement_get).value.length}ch`);
};
onValueChange_fn = function() {
  if (!__privateGet(this, _QuantityInput_instances, inputElement_get).checkValidity()) {
    __privateGet(this, _QuantityInput_instances, inputElement_get).stepDown();
  }
};
if (!window.customElements.get("quantity-selector")) {
  window.customElements.define("quantity-selector", QuantitySelector);
}
if (!window.customElements.get("quantity-input")) {
  window.customElements.define("quantity-input", QuantityInput);
}

// js/common/list/listbox.js
var _accessibilityInitialized, _hiddenInput, _Listbox_instances, setupAccessibility_fn, onOptionClicked_fn, onInputChanged_fn, onKeyDown_fn;
var Listbox = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _Listbox_instances);
    __privateAdd(this, _accessibilityInitialized, false);
    __privateAdd(this, _hiddenInput);
    this.addEventListener("keydown", __privateMethod(this, _Listbox_instances, onKeyDown_fn));
  }
  static get observedAttributes() {
    return ["aria-activedescendant"];
  }
  connectedCallback() {
    __privateSet(this, _hiddenInput, this.querySelector('input[type="hidden"]'));
    __privateGet(this, _hiddenInput)?.addEventListener("change", __privateMethod(this, _Listbox_instances, onInputChanged_fn).bind(this));
    if (!__privateGet(this, _accessibilityInitialized)) {
      requestAnimationFrame(__privateMethod(this, _Listbox_instances, setupAccessibility_fn).bind(this));
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "aria-activedescendant" && oldValue !== null && newValue !== oldValue) {
      Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
        if (option.id === newValue) {
          option.setAttribute("aria-selected", "true");
          if (__privateGet(this, _hiddenInput) && __privateGet(this, _hiddenInput).value !== option.value) {
            __privateGet(this, _hiddenInput).value = option.value;
            __privateGet(this, _hiddenInput).dispatchEvent(new Event("change", { bubbles: true }));
          }
          if (this.hasAttribute("aria-owns")) {
            this.getAttribute("aria-owns").split(" ").forEach((boundId) => {
              document.getElementById(boundId).textContent = option.getAttribute("title") || option.innerText || option.value;
            });
          }
          option.dispatchEvent(
            new CustomEvent("listbox:change", {
              bubbles: true,
              detail: {
                value: option.value
              }
            })
          );
        } else {
          option.setAttribute("aria-selected", "false");
        }
      });
    }
  }
};
_accessibilityInitialized = new WeakMap();
_hiddenInput = new WeakMap();
_Listbox_instances = new WeakSet();
setupAccessibility_fn = function() {
  this.setAttribute("role", "listbox");
  Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
    option.addEventListener("click", __privateMethod(this, _Listbox_instances, onOptionClicked_fn).bind(this));
    option.id = "option-" + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));
    if (option.getAttribute("aria-selected") === "true") {
      this.setAttribute("aria-activedescendant", option.id);
    }
  });
  __privateSet(this, _accessibilityInitialized, true);
};
onOptionClicked_fn = function(event) {
  if (event.currentTarget.getAttribute("type") === "submit") {
    return;
  }
  this.setAttribute("aria-activedescendant", event.currentTarget.id);
  event.currentTarget.dispatchEvent(
    new CustomEvent("listbox:select", {
      bubbles: true,
      detail: {
        value: event.currentTarget.value
      }
    })
  );
};
onInputChanged_fn = function(event) {
  this.setAttribute("aria-activedescendant", this.querySelector(`[role="option"][value="${CSS.escape(event.target.value)}"]`).id);
};
onKeyDown_fn = function(event) {
  if (event.key === "ArrowUp") {
    event.target.previousElementSibling?.focus();
    event.preventDefault();
  } else if (event.key === "ArrowDown") {
    event.target.nextElementSibling?.focus();
    event.preventDefault();
  }
};
if (!window.customElements.get("x-listbox")) {
  window.customElements.define("x-listbox", Listbox);
}

// js/common/media/image-parallax.js
import { scroll, animate as animate8 } from "vendor";
var _ImageParallax_instances, setupParallax_fn;
var ImageParallax = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ImageParallax_instances);
  }
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      __privateMethod(this, _ImageParallax_instances, setupParallax_fn).call(this);
    }
  }
};
_ImageParallax_instances = new WeakSet();
setupParallax_fn = function() {
  const [scale, translate] = [1.3, 0.15 * 100 / 1.3], isFirstSection = this.closest(".shopify-section").matches(":first-child");
  scroll(animate8(this.querySelector("img"), { transform: [`scale(${scale}) translateY(-${translate}%)`, `scale(${scale}) translateY(${translate}%)`] }, { easing: "linear" }), {
    target: this.querySelector("img"),
    offset: [isFirstSection ? "start start" : "start end", "end start"]
  });
};
if (!window.customElements.get("image-parallax")) {
  window.customElements.define("image-parallax", ImageParallax);
}

// js/common/product/gift-card-recipient.js
var _recipientCheckbox, _recipientOtherProperties, _recipientSendOnProperty, _offsetProperty, _recipientFieldsContainer, _GiftCardRecipient_instances, synchronizeProperties_fn, formatDate_fn;
var GiftCardRecipient = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _GiftCardRecipient_instances);
    __privateAdd(this, _recipientCheckbox);
    __privateAdd(this, _recipientOtherProperties, []);
    __privateAdd(this, _recipientSendOnProperty);
    __privateAdd(this, _offsetProperty);
    __privateAdd(this, _recipientFieldsContainer);
  }
  connectedCallback() {
    const properties = Array.from(this.querySelectorAll('[name*="properties"]')), checkboxPropertyName = "properties[__shopify_send_gift_card_to_recipient]";
    __privateSet(this, _recipientCheckbox, properties.find((input) => input.name === checkboxPropertyName));
    __privateSet(this, _recipientOtherProperties, properties.filter((input) => input.name !== checkboxPropertyName));
    __privateSet(this, _recipientFieldsContainer, this.querySelector(".gift-card-recipient__fields"));
    __privateSet(this, _offsetProperty, this.querySelector('[name="properties[__shopify_offset]"]'));
    if (__privateGet(this, _offsetProperty)) {
      __privateGet(this, _offsetProperty).value = (/* @__PURE__ */ new Date()).getTimezoneOffset().toString();
    }
    __privateSet(this, _recipientSendOnProperty, this.querySelector('[name="properties[Send on]"]'));
    const minDate = /* @__PURE__ */ new Date();
    const maxDate = /* @__PURE__ */ new Date();
    maxDate.setDate(minDate.getDate() + 90);
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("min", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, minDate));
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("max", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, maxDate));
    __privateGet(this, _recipientCheckbox)?.addEventListener("change", __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).bind(this));
    __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).call(this);
  }
};
_recipientCheckbox = new WeakMap();
_recipientOtherProperties = new WeakMap();
_recipientSendOnProperty = new WeakMap();
_offsetProperty = new WeakMap();
_recipientFieldsContainer = new WeakMap();
_GiftCardRecipient_instances = new WeakSet();
synchronizeProperties_fn = function() {
  __privateGet(this, _recipientOtherProperties).forEach((property) => property.disabled = !__privateGet(this, _recipientCheckbox).checked);
  __privateGet(this, _recipientFieldsContainer).toggleAttribute("hidden", !__privateGet(this, _recipientCheckbox).checked);
};
formatDate_fn = function(date) {
  const offset = date.getTimezoneOffset();
  const offsetDate = new Date(date.getTime() - offset * 60 * 1e3);
  return offsetDate.toISOString().split("T")[0];
};
if (!window.customElements.get("gift-card-recipient")) {
  window.customElements.define("gift-card-recipient", GiftCardRecipient);
}

// js/common/product/product-card.js
import { Delegate as Delegate4 } from "vendor";
var collectionCardColorChoices = /* @__PURE__ */ new Map();
function restoreCollectionCardColors(root) {
  if (!root?.querySelector("[data-keep-variant-color]") || collectionCardColorChoices.size === 0) return;
  root.querySelectorAll("product-card[data-product-id]").forEach((card) => {
    const choice = collectionCardColorChoices.get(card.getAttribute("data-product-id"));
    if (!choice) return;
    const match = Array.from(card.querySelectorAll('.product-card__info [type="radio"]')).find((input) => (input.getAttribute("data-option-value-name") || "").trim().toLowerCase() === choice);
    if (!match || match.checked) return;
    match.checked = true;
    match.dispatchEvent(new Event("change", { bubbles: true }));
  });
}
function rememberCollectionCardColor(card, input) {
  if (!card.closest("[data-keep-variant-color]")) return;
  const productId = card.getAttribute("data-product-id");
  const colorName = (input.getAttribute("data-option-value-name") || "").trim().toLowerCase();
  if (productId && colorName) collectionCardColorChoices.set(productId, colorName);
}
var _delegate3, _ProductCard_instances, onSwatchHovered_fn, onSwatchChanged_fn, createMediaImg_fn;
var hoverImageObserver = null;
var cardImageObserver = null;
var colorImageObserver = null;
var swatchScrollerObserver = null;
/**
 * Same result as measuring each row on its own, but every row is written, then measured, in shared passes,
 * so a grid of cards costs a few layouts instead of three per card.
 */
function swatchesThatFit(widths, gap, available) {
  let used = 0;
  let count = 0;
  for (let index = 0; index < widths.length; index++) {
    const next = count === 0 ? widths[index] : used + gap + widths[index];
    if (next > available + 0.5) break;
    used = next;
    count += 1;
  }
  return count;
}
function updateSwatchScrollers(scrollers) {
  const rows = [];
  const setHidden = (row, item, hidden) => {
    item.hidden = hidden;
    if (!item.htmlFor) return;
    const input = row.fieldset.querySelector(`[id="${CSS.escape(item.htmlFor)}"]`);
    if (input) input.hidden = hidden;
  };
  for (const scroller of scrollers) {
    const fieldset = scroller.querySelector(".product-card__swatches");
    const more = scroller.querySelector("[data-swatch-more]");
    if (!fieldset || !more || scroller.clientWidth === 0) continue;
    const items = [...fieldset.querySelectorAll(":scope > .color-swatch")];
    items.forEach((item) => setHidden({ fieldset }, item, false));
    more.hidden = true;
    rows.push({ fieldset, more, items });
  }
  rows.forEach((row) => {
    row.gap = parseFloat(getComputedStyle(row.fieldset).columnGap) || 0;
    row.shown = row.items.filter((item) => item.getBoundingClientRect().width > 0);
    row.widths = row.shown.map((item) => item.getBoundingClientRect().width);
  });
  const applyLimit = (row, visible) => {
    const remaining = row.widths.length - visible;
    row.more.hidden = false;
    row.more.textContent = `+${remaining}`;
    row.more.setAttribute("aria-label", `+${remaining}`);
    row.shown.forEach((item, index) => setHidden(row, item, index >= visible));
  };
  rows.forEach((row) => {
    if (row.fieldset.clientWidth === 0 || row.widths.length === 0) return;
    const overflows = row.fieldset.scrollWidth > row.fieldset.clientWidth + 1;
    const fitted = swatchesThatFit(row.widths, row.gap, row.fieldset.clientWidth);
    if (!overflows && fitted >= row.widths.length) {
      row.more.hidden = true;
      row.more.textContent = "+";
      return;
    }
    // Show the count first so the row keeps room for it, then fit the swatches into what is left.
    row.more.hidden = false;
    row.more.textContent = `+${Math.max(row.widths.length - Math.min(fitted, row.widths.length - 1), 1)}`;
    let visible = swatchesThatFit(row.widths, row.gap, row.fieldset.clientWidth);
    if (visible >= row.widths.length) visible = row.widths.length - 1;
    applyLimit(row, visible);
    const refit = swatchesThatFit(row.widths, row.gap, row.fieldset.clientWidth);
    if (refit >= row.widths.length) {
      row.more.hidden = true;
      row.more.textContent = "+";
      row.shown.forEach((item) => setHidden(row, item, false));
      return;
    }
    if (refit !== visible) {
      visible = refit;
      applyLimit(row, visible);
    }
    const last = row.shown[visible - 1];
    if (last && visible > 0) {
      const itemBox = last.getBoundingClientRect();
      const box = row.fieldset.getBoundingClientRect();
      const clipped = document.documentElement.dir === "rtl" ? itemBox.left < box.left - 1 : itemBox.right > box.right + 1;
      if (clipped) applyLimit(row, visible - 1);
    }
    const selected = row.shown.find((item) => item.previousElementSibling?.checked || item.classList.contains("is-selected"));
    if (selected?.hidden) {
      const displaced = [...row.shown].reverse().find((item) => item !== selected && !item.hidden);
      if (displaced) {
        setHidden(row, displaced, true);
        setHidden(row, selected, false);
      }
    }
  });
}
function updateSwatchScroller(scroller) {
  updateSwatchScrollers([scroller]);
}
function updateAllSwatchScrollers() {
  updateSwatchScrollers(document.querySelectorAll("[data-swatch-scroller]"));
}
document.fonts?.ready.then(updateAllSwatchScrollers);
function whenPageLoaded(callback) {
  const run = () => "requestIdleCallback" in window ? requestIdleCallback(callback, { timeout: 2e3 }) : setTimeout(callback, 200);
  document.readyState === "complete" ? run() : window.addEventListener("load", run, { once: true });
}
var ProductCard = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductCard_instances);
    __privateAdd(this, _delegate3, new Delegate4(this));
  }
  /**
   * Hover images are rendered with data-src/data-srcset (see snippets/product-card.liquid). With a mouse, they are loaded on the
   * first hover, or once the page has fully loaded for cards close to the viewport, so hovering is instant without slowing the page.
   */
  loadHoverImageWhenUseful() {
    const image = this.querySelector(".product-card__image--secondary[data-srcset], .product-card__image--secondary[data-src]");
    if (!image || !window.matchMedia("(pointer: fine)").matches) {
      return;
    }
    const reveal = (root) => {
      const hoverImage = root.querySelector?.(".product-card__image--secondary");
      if (!hoverImage || hoverImage.dataset.hoverRequested === "true") return;
      hoverImage.dataset.hoverRequested = "true";
      hoverImage.loading = "eager";
      restoreDeferredImages(root);
      const mark = () => {
        if (hoverImage.naturalWidth > 0) hoverImage.classList.add("is-loaded");
      };
      if (hoverImage.complete && hoverImage.naturalWidth > 0) mark();
      else hoverImage.addEventListener("load", mark, { once: true });
    };
    this.querySelector(".product-card__figure")?.addEventListener("pointerenter", () => reveal(this), { once: true });
    whenPageLoaded(() => {
      hoverImageObserver ??= new IntersectionObserver((entries) => {
        entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
          hoverImageObserver.unobserve(entry.target);
          reveal(entry.target);
        });
      }, { rootMargin: "200px 0px" });
      if (this.isConnected) {
        hoverImageObserver.observe(this);
      }
    });
  }
  /**
   * Mobile swatch rows scroll on one line (theme.css): flag the ones that do not fit, and when they are scrolled to the end
   */
  observeSwatchScroller() {
    const scroller = this.querySelector("[data-swatch-scroller]");
    if (!scroller) {
      return;
    }
    swatchScrollerObserver ??= new ResizeObserver((entries) => updateSwatchScrollers(entries.map((entry) => entry.target)));
    swatchScrollerObserver.observe(scroller);
    if (!scroller.hasAttribute("data-scroll-bound")) {
      scroller.setAttribute("data-scroll-bound", "");
      let scrollFrame = 0;
      scroller.querySelector(".product-card__swatches")?.addEventListener("scroll", () => {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = 0;
          updateSwatchScroller(scroller);
        });
      }, { passive: true });
    }
  }
  preloadColorImages() {
    const primary = this.querySelector(".product-card__image--primary");
    if (!primary || this._colorsPreloaded) return;
    this._colorsPreloaded = true;
    this.querySelectorAll('.product-card__info [type="radio"][data-variant-media]').forEach((control) => {
      if (control.checked || control._preloadedMedia) return;
      try {
        control._preloadedMedia = __privateMethod(this, _ProductCard_instances, createMediaImg_fn).call(this, JSON.parse(control.getAttribute("data-variant-media")), primary.className, primary.sizes);
      } catch (error) {
      }
    });
  }
  connectedCallback() {
    if (this._cardReady) return;
    // theme.js can define this element while the card HTML is still parsing, before the photo and swatches exist.
    if (!this.querySelector(".product-card__image--primary") && !this.querySelector(".product-card__figure")) {
      this._cardContentObserver ??= new MutationObserver(() => {
        if (!this.querySelector(".product-card__image--primary") && !this.querySelector(".product-card__figure")) return;
        this._cardContentObserver.disconnect();
        this._cardContentObserver = null;
        this.connectedCallback();
      });
      this._cardContentObserver.observe(this, { childList: true, subtree: true });
      return;
    }
    this._cardReady = true;
    this.loadHoverImageWhenUseful();
    this.observeSwatchScroller();
    const primaryImage = this.querySelector(".product-card__image--primary");
    if (primaryImage?.getAttribute("loading") === "lazy") {
      // Below-fold card photos wait until the page has loaded so they do not hold the collection or product load event.
      afterPageLoad(() => {
        if (!this.isConnected) return;
        cardImageObserver ??= new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            cardImageObserver.unobserve(entry.target);
            const image = entry.target.querySelector(".product-card__image--primary");
            if (!image) return;
            image.loading = "eager";
            image.fetchPriority = "auto";
          });
        }, { rootMargin: "700px 0px" });
        cardImageObserver.observe(this);
      });
    }
    if (this.querySelector("[data-variant-media]")) {
      whenPageLoaded(() => {
        colorImageObserver ??= new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            colorImageObserver.unobserve(entry.target);
            entry.target.preloadColorImages();
          });
        });
        if (this.isConnected) colorImageObserver.observe(this);
      });
    }
    __privateGet(this, _delegate3).on("change", '.product-card__info [type="radio"]', __privateMethod(this, _ProductCard_instances, onSwatchChanged_fn).bind(this));
    let pointerSelectingSwatch = false;
    this._releaseVariantScrollLock = null;
    const holdWindowForSwatch = () => {
      this._releaseVariantScrollLock?.();
      const x = window.scrollX;
      const y = window.scrollY;
      const hold = () => {
        if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
      };
      window.addEventListener("scroll", hold, { passive: true });
      const frame = requestAnimationFrame(hold);
      let finish;
      const timer = window.setTimeout(() => finish(), 400);
      finish = () => {
        window.clearTimeout(timer);
        cancelAnimationFrame(frame);
        window.removeEventListener("scroll", hold);
        if (this._releaseVariantScrollLock === finish) this._releaseVariantScrollLock = null;
      };
      this._releaseVariantScrollLock = finish;
    };
    this.addEventListener("pointerdown", (event) => {
      pointerSelectingSwatch = Boolean(event.target.closest?.(".product-card__swatches"));
      if (pointerSelectingSwatch && window.matchMedia("(min-width: 700px)").matches) holdWindowForSwatch();
    });
    this.addEventListener("focusin", (event) => {
      if (!pointerSelectingSwatch || event.target?.type !== "radio") return;
      pointerSelectingSwatch = false;
      const scroller = this.closest(".product-list");
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth + 1) return;
      const left = scroller.scrollLeft;
      const lock = () => {
        if (scroller.scrollLeft !== left) scroller.scrollLeft = left;
      };
      scroller.addEventListener("scroll", lock, { passive: true });
      window.setTimeout(() => {
        lock();
        scroller.removeEventListener("scroll", lock);
      }, 400);
    });
    __privateGet(this, _delegate3).on("pointerover", '.product-card__info [type="radio"] + label', __privateMethod(this, _ProductCard_instances, onSwatchHovered_fn).bind(this), true);
    __privateGet(this, _delegate3).on("click", "[data-product-card-add]", this.onQuickAddClick.bind(this));
    const sizeModal = this.querySelector("[data-product-card-size-modal]");
    if (sizeModal && !sizeModal.hasAttribute("data-quick-add-bound")) {
      sizeModal.setAttribute("data-quick-add-bound", "");
      sizeModal.addEventListener("click", (event) => {
        const button = event.target.closest("[data-product-card-add]");
        if (!button || !sizeModal.contains(button)) {
          return;
        }
        this.onQuickAddClick(event, button);
      });
    }
    this.updateQuickAddAvailability();
  }
  disconnectedCallback() {
    this._cardReady = false;
    this._cardContentObserver?.disconnect();
    this._cardContentObserver = null;
    this._releaseVariantScrollLock?.();
    __privateGet(this, _delegate3).off();
    hoverImageObserver?.unobserve(this);
    cardImageObserver?.unobserve(this);
    colorImageObserver?.unobserve(this);
    const scroller = this.querySelector("[data-swatch-scroller]");
    if (scroller) {
      swatchScrollerObserver?.unobserve(scroller);
    }
  }
  async onQuickAddClick(event, target) {
    event.preventDefault();
    event.stopPropagation();
    if (target.disabled || target.getAttribute("aria-busy") === "true") {
      return;
    }
    const variantId = this.resolveQuickAddVariantId(target);
    if (!variantId) {
      return;
    }
    if (target.hasAttribute("data-adding")) return;
    target.setAttribute("data-adding", "");
    target.setAttribute("aria-busy", "true");
    const instant = window.themeVariables?.settings?.cartType === "drawer";
    if (!instant) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    }
    const formData = new FormData();
    formData.set("id", variantId);
    formData.set("quantity", "1");
    formData.set("on_success", "force_open_drawer");
    glAddGender(formData, this);
    try {
      await addVariantFast(formData, {
        dispatchTarget: this,
        preview: {
          ...previewFromAddContext(this, target),
          modal: target.closest("[data-product-card-size-modal]")
        }
      });
    } finally {
      target.removeAttribute("data-adding");
      target.removeAttribute("aria-busy");
      if (!instant) {
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }
  }
  updateQuickAddAvailability() {
    const quickAdd = this.querySelector("[data-product-card-quick-add]");
    const variantsScript = this.querySelector("[data-product-card-variants]");
    if (!quickAdd || !variantsScript) {
      return;
    }
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }
    const sizeIndex = Number(quickAdd.getAttribute("data-size-index") || 0);
    const colorIndexAttr = quickAdd.getAttribute("data-color-index");
    const colorIndex = colorIndexAttr == null || colorIndexAttr === "" ? null : Number(colorIndexAttr);
    let colorValue = null;
    if (colorIndex !== null) {
      const selectedColorControl = this.querySelector('.product-card__info [type="radio"]:checked');
      if (selectedColorControl?.hasAttribute("data-variant-id")) {
        const colorVariant = variants.find((variant) => String(variant.id) === selectedColorControl.getAttribute("data-variant-id"));
        colorValue = colorVariant?.options?.[colorIndex] ?? null;
      }
      if (!colorValue) {
        colorValue = (variants.find((variant) => variant.available) || variants[0])?.options?.[colorIndex] ?? null;
      }
    }
    const sizeButtons = Array.from(quickAdd.querySelectorAll("[data-product-card-add][data-size]"));
    const sizeTrigger = this.querySelector("[data-product-card-size-trigger]");
    const sizeModal = sizeTrigger ? document.getElementById(sizeTrigger.getAttribute("aria-controls")) : null;
    sizeModal?.querySelectorAll("[data-product-card-add][data-size]").forEach((button) => {
      if (!sizeButtons.includes(button)) {
        sizeButtons.push(button);
      }
    });
    sizeButtons.forEach((button) => {
      const size = button.getAttribute("data-size");
      button.disabled = !variants.some((variant) => variant.available && variant.options?.[sizeIndex] === size && (colorValue == null || variant.options?.[colorIndex] === colorValue));
    });
  }
  resolveQuickAddVariantId(target) {
    const variantsScript = this.querySelector("[data-product-card-variants]");
    if (target.hasAttribute("data-variant-id") && !target.hasAttribute("data-size")) {
      return target.getAttribute("data-variant-id");
    }
    if (!variantsScript) {
      return target.getAttribute("data-variant-id");
    }
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return target.getAttribute("data-variant-id");
    }
    const quickAdd = this.querySelector("[data-product-card-quick-add]");
    const size = target.getAttribute("data-size");
    const sizeIndex = Number(quickAdd?.getAttribute("data-size-index") || 0);
    const colorIndexAttr = quickAdd?.getAttribute("data-color-index");
    const colorIndex = colorIndexAttr == null || colorIndexAttr === "" ? null : Number(colorIndexAttr);
    let colorValue = null;
    if (colorIndex !== null) {
      const selectedColorControl = this.querySelector('.product-card__info [type="radio"]:checked');
      if (selectedColorControl?.hasAttribute("data-variant-id")) {
        const colorVariant = variants.find((variant) => String(variant.id) === selectedColorControl.getAttribute("data-variant-id"));
        colorValue = colorVariant?.options?.[colorIndex] ?? null;
      }
      if (!colorValue) {
        colorValue = (variants.find((variant) => variant.available) || variants[0])?.options?.[colorIndex] ?? null;
      }
    }
    const match = variants.find((variant) => {
      if (size && variant.options?.[sizeIndex] !== size) {
        return false;
      }
      if (colorValue != null && variant.options?.[colorIndex] !== colorValue) {
        return false;
      }
      return variant.available !== false;
    }) || variants.find((variant) => {
      if (size && variant.options?.[sizeIndex] !== size) {
        return false;
      }
      if (colorValue != null && variant.options?.[colorIndex] !== colorValue) {
        return false;
      }
      return true;
    });
    if (match?.available === false) {
      return null;
    }
    return match ? String(match.id) : target.getAttribute("data-variant-id");
  }
};
_delegate3 = new WeakMap();
_ProductCard_instances = new WeakSet();
onSwatchHovered_fn = async function(event, target) {
  const control = target.control;
  const primaryMediaElement = this.querySelector(".product-card__image--primary");
  if (!primaryMediaElement || !control.hasAttribute("data-variant-media") || control._preloadedMedia) {
    return;
  }
  control._preloadedMedia = __privateMethod(this, _ProductCard_instances, createMediaImg_fn).call(this, JSON.parse(control.getAttribute("data-variant-media")), primaryMediaElement.className, primaryMediaElement.sizes);
};
onSwatchChanged_fn = async function(event, target) {
  rememberCollectionCardColor(this, target);
  this.updateQuickAddAvailability();
  if (target.hasAttribute("data-product-url")) {
    this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"], a[href*="/products/"]`).forEach((link) => {
      link.href = target.getAttribute("data-product-url");
    });
  } else if (target.hasAttribute("data-variant-id")) {
    this.querySelectorAll(`a[href^="${Shopify.routes.root}products/"], a[href*="/products/"]`).forEach((link) => {
      const url = new URL(link.href);
      url.searchParams.set("variant", target.getAttribute("data-variant-id"));
      link.href = `${url.pathname}${url.search}${url.hash}`;
    });
  }
  if (!target.hasAttribute("data-variant-media")) {
    return;
  }
  let newMedia = JSON.parse(target.getAttribute("data-variant-media")), primaryMediaElement = this.querySelector(".product-card__image--primary"), secondaryMediaElement = this.querySelector(".product-card__image--secondary"), newPrimaryMediaElement = target._preloadedMedia || __privateMethod(this, _ProductCard_instances, createMediaImg_fn).call(this, newMedia, primaryMediaElement.className, primaryMediaElement.sizes), newSecondaryMediaElement = null;
  target._preloadedMedia = null;
  if (secondaryMediaElement && target.hasAttribute("data-variant-secondary-media")) {
    let newSecondaryMedia = JSON.parse(target.getAttribute("data-variant-secondary-media"));
    newSecondaryMediaElement = __privateMethod(this, _ProductCard_instances, createMediaImg_fn).call(this, newSecondaryMedia, secondaryMediaElement.className, secondaryMediaElement.sizes);
    newSecondaryMediaElement.ariaHidden = "true";
  }
  if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
    this._mediaSwapToken = (this._mediaSwapToken || 0) + 1;
    const swapToken = this._mediaSwapToken;
    if (secondaryMediaElement && newSecondaryMediaElement) {
      secondaryMediaElement.replaceWith(newSecondaryMediaElement);
    }
    const swap = () => {
      if (swapToken !== this._mediaSwapToken || !primaryMediaElement.isConnected) return;
      primaryMediaElement.replaceWith(newPrimaryMediaElement);
    };
    if (newPrimaryMediaElement.complete && newPrimaryMediaElement.naturalWidth > 0) swap();
    else newPrimaryMediaElement.addEventListener("load", swap, { once: true });
  }
};
createMediaImg_fn = function(media, className, sizes) {
  const image = createMediaImg(media, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800, 2000], { class: className.replace(/\bis-loaded\b/, "").trim(), sizes });
  // Same as the Liquid card images, so a swatch swap never leaves a photo the browser can drag out of the card
  image.draggable = false;
  image.addEventListener("load", () => image.classList.add("is-loaded"), { once: true });
  if (image.complete && image.naturalWidth > 0) image.classList.add("is-loaded");
  return image;
};
if (!window.customElements.get("product-card")) {
  window.customElements.define("product-card", ProductCard);
}

// theme.js is async and can define these elements while the product HTML is still parsing.
// The cart form is printed after the variant picker, so a one-time lookup in connectedCallback misses it.
function resolveForm(id) {
  if (!id) return null;
  const byId = document.getElementById(id);
  if (byId?.tagName === "FORM") return byId;
  return document.forms.namedItem(id) || null;
}
function whenFormReady(id, callback, signal) {
  if (signal?.aborted) return;
  let done = false;
  const finish = (form) => {
    if (done || signal?.aborted || !form) return;
    done = true;
    callback(form);
  };
  const existing = resolveForm(id);
  if (existing) {
    finish(existing);
    return;
  }
  const observer = new MutationObserver(() => {
    const form = resolveForm(id);
    if (!form) return;
    observer.disconnect();
    finish(form);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  const stop = () => observer.disconnect();
  signal?.addEventListener("abort", stop, { once: true });
}
function enableVariantInput(form) {
  const idInput = form?.querySelector?.('[name="id"]');
  if (idInput) idInput.disabled = false;
  return idInput;
}

// js/common/product/product-form.js
var _abortController7, _ProductForm_instances, form_get2, onSubmit_fn;
var ProductForm = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductForm_instances);
    __privateAdd(this, _abortController7);
  }
  connectedCallback() {
    __privateSet(this, _abortController7, new AbortController());
    const signal = __privateGet(this, _abortController7).signal;
    const bind = () => {
      const form = __privateGet(this, _ProductForm_instances, form_get2);
      if (!form) return false;
      form.addEventListener("submit", __privateMethod(this, _ProductForm_instances, onSubmit_fn).bind(this), { signal });
      enableVariantInput(form);
      return true;
    };
    if (bind()) return;
    const observer = new MutationObserver(() => {
      if (bind()) observer.disconnect();
    });
    observer.observe(this, { childList: true, subtree: true });
    signal.addEventListener("abort", () => observer.disconnect(), { once: true });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController7).abort();
  }
};
_abortController7 = new WeakMap();
_ProductForm_instances = new WeakSet();
form_get2 = function() {
  return this.querySelector('form[action*="/cart/add"]');
};
onSubmit_fn = async function(event) {
  event.preventDefault();
  const form = __privateGet(this, _ProductForm_instances, form_get2);
  if (form.hasAttribute("data-adding") || event.submitter?.getAttribute("aria-busy") === "true") {
    return;
  }
  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }
  form.setAttribute("data-adding", "");
  const instant = window.themeVariables?.settings?.cartType === "drawer";
  const submitButtons = Array.from(form.elements).filter((button) => button.type === "submit");
  submitButtons.forEach((submitButton) => {
    submitButton.setAttribute("aria-busy", "true");
  });
  if (!instant) {
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  }
  const formData = new FormData(form);
  glAddGender(formData, form);
  try {
    await addVariantFast(formData, { dispatchTarget: form, preview: previewFromAddContext(form) });
  } finally {
    form.removeAttribute("data-adding");
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute("aria-busy");
    });
    if (!instant) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }
};
if (!window.customElements.get("product-form")) {
  window.customElements.define("product-form", ProductForm);
}

// js/common/product/product-form-listeners.js
var _abortController8, _BuyButtons_instances, onVariantAdded_fn, onCartError_fn;
var BuyButtons = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _BuyButtons_instances);
    __privateAdd(this, _abortController8);
  }
  connectedCallback() {
    __privateSet(this, _abortController8, new AbortController());
    const signal = __privateGet(this, _abortController8).signal;
    whenFormReady(this.getAttribute("form"), (form) => {
      form.addEventListener("cart:error", __privateMethod(this, _BuyButtons_instances, onCartError_fn).bind(this), { signal });
      form.addEventListener("variant:add", __privateMethod(this, _BuyButtons_instances, onVariantAdded_fn).bind(this), { signal });
    }, signal);
  }
  disconnectedCallback() {
    __privateGet(this, _abortController8).abort();
  }
};
_abortController8 = new WeakMap();
_BuyButtons_instances = new WeakSet();
onVariantAdded_fn = function(event) {
  if (window.themeVariables.settings.cartType === "message") {
    const bannerElement = document.createRange().createContextualFragment(`
        <div class="banner banner--success" role="status" aria-live="polite">
          ${window.themeVariables.strings.addedToCart}
        </div>
      `).firstElementChild;
    this.prepend(bannerElement);
    setTimeout(() => {
      bannerElement.remove();
    }, 2500);
  } else if (window.themeVariables.settings.cartType === "drawer") {
    const items = event.detail.items;
    let content = [];
    items.forEach((item) => {
      content.push(window.themeVariables.strings.addedToCartWithTitle.replace("{{ product_title }}", item?.title || ""));
    });
    announceStatus(content.join(", "));
  }
};
onCartError_fn = function(event) {
  const bannerElement = document.createRange().createContextualFragment(`
      <div class="banner banner--error" role="alert" aria-live="polite">
        ${event.detail.error}
      </div>
    `).firstElementChild;
  this.prepend(bannerElement);
  setTimeout(() => {
    bannerElement.remove();
  }, 2500);
};
if (!window.customElements.get("buy-buttons")) {
  window.customElements.define("buy-buttons", BuyButtons);
}

// js/common/product/product-gallery.js
import { PhotoSwipeLightbox } from "vendor";
// How much of a slide is actually on screen, after the carousel and other overflow ancestors clip it.
// A slide parked beside the viewport can still sit inside the window box; overflow is what hides it.
function galleryMediaVisibility(element) {
  const rect = element.getBoundingClientRect();
  const area = rect.width * rect.height;
  if (area < 1) return { ratio: 0, viewportShare: 0, inView: false, outOfView: true };
  let left = Math.max(rect.left, 0);
  let top = Math.max(rect.top, 0);
  let right = Math.min(rect.right, window.innerWidth);
  let bottom = Math.min(rect.bottom, window.innerHeight);
  const clips = element._galleryClipNodes || (element._galleryClipNodes = galleryClipNodes(element));
  clips.forEach((clipNode) => {
    const clip = clipNode.getBoundingClientRect();
    if (clipNode._galleryClipsX) {
      left = Math.max(left, clip.left);
      right = Math.min(right, clip.right);
    }
    if (clipNode._galleryClipsY) {
      top = Math.max(top, clip.top);
      bottom = Math.min(bottom, clip.bottom);
    }
  });
  const width = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return { ratio: 0, viewportShare: 0, inView: false, outOfView: true };
  const ratio = width * height / area;
  const viewportShare = width * height / Math.max(window.innerWidth * window.innerHeight, 1);
  // A slide that fills the screen can still be taller than the viewport, so either measure counts.
  const inView = ratio >= 0.55 || viewportShare >= 0.4 && ratio >= 0.2;
  const outOfView = ratio < 0.2 && viewportShare < 0.2;
  return { ratio, viewportShare, inView, outOfView };
}
function galleryClipNodes(element) {
  const clips = [];
  let node = element.parentElement;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    const clipsX = style.overflowX !== "visible" && style.overflowX !== "";
    const clipsY = style.overflowY !== "visible" && style.overflowY !== "";
    if (clipsX || clipsY) {
      node._galleryClipsX = clipsX;
      node._galleryClipsY = clipsY;
      clips.push(node);
    }
    node = node.parentElement;
  }
  return clips;
}
function revealCurrentVariantMedia(gallery) {
  const carousel = gallery?.carousel;
  if (!carousel || !gallery.isConnected) return;
  const selected = carousel.selectedCell;
  carousel.cells.forEach((cell) => {
    restoreDeferredImages(cell, { video: cell === selected });
    cell.querySelectorAll("img").forEach((img) => {
      if (img.getAttribute("loading") === "lazy") img.removeAttribute("loading");
      if (!img.getAttribute("src") || !img.complete || img.naturalWidth > 0) return;
      const src = img.currentSrc || img.src;
      if (!src) return;
      img.removeAttribute("src");
      img.src = src;
    });
  });
}
var videoAutoplayWaiting = new WeakSet();
var _abortController9, _photoSwipeInstance, _onGestureChangedListener, _settledMedia, _videoAutoplayCleanup, _ProductGallery_instances, registerLightboxUi_fn, onSectionRerender_fn, onVariantChange_fn, onMediaChange_fn, onMediaSettle_fn, onCarouselClick_fn, onGestureStart_fn, onGestureChanged_fn, setupVideoAutoplay_fn, syncVideoAutoplay_fn;
var ProductGallery = class extends HTMLElement {
  /* Keep track of the currently settled media */
  constructor() {
    super();
    __privateAdd(this, _ProductGallery_instances);
    __privateAdd(this, _abortController9);
    __privateAdd(this, _photoSwipeInstance);
    __privateAdd(this, _onGestureChangedListener, __privateMethod(this, _ProductGallery_instances, onGestureChanged_fn).bind(this));
    __privateAdd(this, _settledMedia);
    __privateAdd(this, _videoAutoplayCleanup);
    this.addEventListener("lightbox:open", (event) => this.openLightBox(event?.detail?.index));
  }
  connectedCallback() {
    __privateGet(this, _videoAutoplayCleanup)?.();
    __privateGet(this, _abortController9)?.abort();
    __privateSet(this, _abortController9, new AbortController());
    const signal = __privateGet(this, _abortController9).signal;
    this._galleryBooted = false;
    const start = () => {
      if (signal.aborted || this._galleryBooted) return this._galleryBooted;
      if (!this.carousel) return false;
      this._galleryBooted = true;
      whenFormReady(this.getAttribute("form"), (form) => {
        form.addEventListener("product:rerender", __privateMethod(this, _ProductGallery_instances, onSectionRerender_fn).bind(this), { signal });
        form.addEventListener("variant:change", __privateMethod(this, _ProductGallery_instances, onVariantChange_fn).bind(this), { signal });
      }, signal);
      this.carousel.addEventListener("carousel:change", __privateMethod(this, _ProductGallery_instances, onMediaChange_fn).bind(this), { signal });
      this.carousel.addEventListener("carousel:settle", __privateMethod(this, _ProductGallery_instances, onMediaSettle_fn).bind(this), { signal });
      this.carousel.addEventListener("click", __privateMethod(this, _ProductGallery_instances, onCarouselClick_fn).bind(this), { signal });
      if (this.hasAttribute("allow-zoom")) {
        this.carousel.addEventListener("gesturestart", __privateMethod(this, _ProductGallery_instances, onGestureStart_fn).bind(this), { capture: false, signal });
      }
      __privateMethod(this, _ProductGallery_instances, onMediaChange_fn).call(this);
      __privateMethod(this, _ProductGallery_instances, setupVideoAutoplay_fn).call(this);
      const revealVisible = () => revealCurrentVariantMedia(this);
      afterPageLoad(() => {
        if ("requestIdleCallback" in window) requestIdleCallback(revealVisible, { timeout: 1800 });
        else setTimeout(revealVisible, 200);
      });
      window.addEventListener("pageshow", revealVisible, { signal });
      if (document.prerendering) {
        document.addEventListener("prerenderingchange", revealVisible, { signal });
      }
      return true;
    };
    if (start()) return;
    const observer = new MutationObserver(() => {
      if (start()) observer.disconnect();
    });
    observer.observe(this, { childList: true, subtree: true });
    signal.addEventListener("abort", () => observer.disconnect(), { once: true });
  }
  disconnectedCallback() {
    this._galleryBooted = false;
    __privateGet(this, _videoAutoplayCleanup)?.();
    __privateSet(this, _videoAutoplayCleanup, void 0);
    __privateGet(this, _abortController9).abort();
  }
  get viewInSpaceButton() {
    return this.querySelector("[data-shopify-xr]");
  }
  get carousel() {
    return this.querySelector(".product-gallery__carousel");
  }
  /**
   * Create the PhotoSwipe instance if it does not already exist. This is done on demand, so until the lightbox is
   * open, nothing is created to not impact performance
   */
  get lightBox() {
    if (__privateGet(this, _photoSwipeInstance)) {
      return __privateGet(this, _photoSwipeInstance);
    }
    __privateSet(this, _photoSwipeInstance, new PhotoSwipeLightbox({
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      maxZoomLevel: parseInt(this.getAttribute("allow-zoom")) || 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      // UX
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      close: false,
      zoom: false
    }));
    __privateGet(this, _photoSwipeInstance).on("uiRegister", __privateMethod(this, _ProductGallery_instances, registerLightboxUi_fn).bind(this));
    __privateGet(this, _photoSwipeInstance).addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement);
    __privateGet(this, _photoSwipeInstance).init();
    return __privateGet(this, _photoSwipeInstance);
  }
  get filteredIndexes() {
    return glMapIndexes(this, readFilteredIndexes(this)) || [];
  }
  /**
   * Open the lightbox at the given index (by default, it opens the selected image)
   */
  openLightBox(index) {
    restoreDeferredImages(this.carousel);
    const images = this.carousel.cells.flatMap((cell) => Array.from(cell.querySelectorAll(":scope > img")));
    const dataSource = images.map((image) => {
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute("width")),
        height: parseInt(image.getAttribute("height")),
        alt: image.alt,
        thumbCropped: true
      };
    });
    const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
    this.lightBox.loadAndOpen(index ?? imageCells.indexOf(this.carousel.selectedCell), dataSource);
  }
};
_abortController9 = new WeakMap();
_photoSwipeInstance = new WeakMap();
_onGestureChangedListener = new WeakMap();
_settledMedia = new WeakMap();
_videoAutoplayCleanup = new WeakMap();
_ProductGallery_instances = new WeakSet();
/**
 * Add custom elements to PhotoSwipe gallery
 */
registerLightboxUi_fn = function() {
  __privateGet(this, _photoSwipeInstance).pswp.ui.registerElement({
    name: "close-button",
    className: "circle-button circle-button--xl hover:animate-icon-block",
    ariaLabel: window.themeVariables.strings.closeGallery,
    order: 2,
    isButton: true,
    html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
    onClick: () => {
      __privateGet(this, _photoSwipeInstance).pswp.close();
    }
  });
  if (__privateGet(this, _photoSwipeInstance).pswp.options.dataSource.length > 1) {
    __privateGet(this, _photoSwipeInstance).pswp.ui.registerElement({
      name: "previous-button",
      className: "circle-button hover:animate-icon-inline",
      ariaLabel: window.themeVariables.strings.previous,
      order: 1,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
          <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `,
      onClick: () => {
        __privateGet(this, _photoSwipeInstance).pswp.prev();
      }
    });
    __privateGet(this, _photoSwipeInstance).pswp.ui.registerElement({
      name: "next-button",
      className: "circle-button hover:animate-icon-inline",
      ariaLabel: window.themeVariables.strings.next,
      order: 3,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
          <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `,
      onClick: () => {
        __privateGet(this, _photoSwipeInstance).pswp.next();
      }
    });
  }
};
/**
 * When the section is re-rendered upon variant changes, the media might have been filtered.
 * The markup can sit in a template, so the element is not upgraded and has no filteredIndexes
 * getter. Reading that missing getter used to pass undefined into filter(), and filter() then
 * treated it as an empty list and revealed every color.
 */
onSectionRerender_fn = function(event) {
  const galleryMarkup = deepQuerySelector(event.detail.htmlFragment, `${this.tagName}[form="${this.getAttribute("form")}"]`);
  if (!galleryMarkup || !this.carousel) {
    return;
  }
  const next = galleryMarkup.getAttribute("filtered-indexes");
  const indexes = glMapIndexes(this, readFilteredIndexes(galleryMarkup));
  if (next == null || !indexes) {
    return;
  }
  this.setAttribute("filtered-indexes", next);
  this.carousel.filter(indexes);
  this.querySelectorAll('.product-gallery__media[hidden] video-media').forEach((host) => {
    host._userPausedWhileVisible = false;
    host.pause();
  });
  __privateMethod(this, _ProductGallery_instances, syncVideoAutoplay_fn).call(this);
};
// GL UNISEX GALLERY (added 2026-11-03): a unisex gallery opened from a gendered collection is rendered in a different
// order (that gender's photos first). data-gl-order lists the original media positions in page order, so the theme's
// position-based logic (colour filtering, variant selection) is translated to the page order. Without it nothing changes.
function glOrder(gallery) {
  const raw = gallery?.getAttribute?.("data-gl-order");
  if (!raw) return null;
  const order = raw.split(",").map((value) => parseInt(value, 10)).filter((value) => Number.isInteger(value));
  return order.length ? order : null;
}
function glMapIndexes(gallery, indexes) {
  const order = glOrder(gallery);
  if (!order || !indexes) return indexes;
  return indexes.map((index) => order.indexOf(index + 1)).filter((index) => index >= 0);
}
function glVariantIndex(gallery, featuredMedia) {
  const fallback = featuredMedia["position"] - 1;
  if (!glOrder(gallery)) return fallback;
  const cells = Array.from(gallery.querySelectorAll(".product-gallery__media"));
  const own = cells.findIndex((cell) => cell.getAttribute("data-media-id") === String(featuredMedia["id"]));
  if (own < 0) return fallback;
  const gender = gallery.getAttribute("data-gl-gender");
  const alt = cells[own].getAttribute("data-media-alt");
  const preferred = cells.findIndex((cell) => cell.getAttribute("data-media-alt") === alt && cell.getAttribute("data-gl-media-gender") === gender && cell.getAttribute("data-media-type") === "image");
  return preferred >= 0 ? preferred : own;
}
function readFilteredIndexes(gallery) {
  const raw = gallery?.getAttribute?.("filtered-indexes");
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((index) => parseInt(index, 10) - 1).filter((index) => Number.isInteger(index) && index >= 0);
  } catch (error) {
    return null;
  }
}
/**
 * When the variant changes, we check the alt tags for each media and filter them
 */
onVariantChange_fn = function(event) {
  if (!event.detail.variant) {
    return;
  }
  if (event.detail.variant["featured_media"] && event.detail.previousVariant?.["featured_media"]?.["id"] !== event.detail.variant["featured_media"]["id"]) {
    const position = glVariantIndex(this, event.detail.variant["featured_media"]), filteredIndexBelowPosition = this.filteredIndexes.filter((filteredIndex) => filteredIndex < position);
    if (this.carousel.isScrollable) {
      this.carousel.select(position - filteredIndexBelowPosition.length, { instant: true });
    } else {
      this.querySelector(`[data-media-id="${event.detail.variant["featured_media"]["id"]}"]`)?.scrollIntoView({ block: "start", behavior: "auto" });
    }
  }
};
/**
 * When the media is about to change, we perform some logic
 */
onMediaChange_fn = function() {
  if (!__privateGet(this, _settledMedia)) {
    return;
  }
  switch (__privateGet(this, _settledMedia).getAttribute("data-media-type")) {
    case "external_video":
    case "video":
    case "model":
      __privateGet(this, _settledMedia).firstElementChild.pause();
  }
};
/**
 * When the media settles, we have to update various elements such as the AR button, the autoplay strategy...
 */
onMediaSettle_fn = function(event) {
  const media = event ? event.detail.cell : this.carousel.selectedCell, zoomButton = this.querySelector(".product-gallery__zoom-button");
  switch (media.getAttribute("data-media-type")) {
    case "image":
      this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
      zoomButton?.classList.remove("product-gallery__zoom-button--hidden");
      break;
    case "external_video":
    case "video":
      this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", this.viewInSpaceButton?.getAttribute("data-shopify-model3d-default-id"));
      zoomButton?.classList.add("product-gallery__zoom-button--hidden");
      __privateMethod(this, _ProductGallery_instances, syncVideoAutoplay_fn).call(this);
      break;
    case "model":
      if (matchesMediaQuery("md")) {
        media.firstElementChild.play();
      }
      this.viewInSpaceButton?.setAttribute("data-shopify-model3d-id", event.detail.cell.getAttribute("data-media-id"));
      zoomButton?.classList.add("product-gallery__zoom-button--hidden");
      break;
  }
  __privateSet(this, _settledMedia, media);
};
/**
 * Detect a click on an image on desktop, and open the lightbox for the corresponding image
 */
onCarouselClick_fn = function(event) {
  if (!this.hasAttribute("allow-zoom") || !matchesMediaQuery("md") || event.target.tagName !== "IMG") {
    return;
  }
  const media = event.target.closest(".product-gallery__media");
  if (media.getAttribute("data-media-type") !== "image") {
    return;
  }
  const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
  this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: imageCells.indexOf(media) } }));
};
/**
 * For iOS devices only, we use the gesturechange event to easily detect a "pinch to zoom"
 */
onGestureStart_fn = function(event) {
  event.preventDefault();
  this.carousel.addEventListener("gesturechange", __privateGet(this, _onGestureChangedListener), { capture: false, signal: __privateGet(this, _abortController9).signal });
};
onGestureChanged_fn = function(event) {
  event.preventDefault();
  const media = event.target.closest(".product-gallery__media");
  if (media.getAttribute("data-media-type") !== "image") {
    return;
  }
  const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute("data-media-type") === "image");
  if (event.scale > 1.5) {
    this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: imageCells.indexOf(media) } }));
    this.removeEventListener("gesturechange", __privateGet(this, _onGestureChangedListener));
  }
};
function prepareGalleryVideo(cell) {
  const host = cell.querySelector("video-media");
  if (!host) return null;
  const videoBeforeRestore = host.querySelector("video");
  if (videoBeforeRestore && videoBeforeRestore.getAttribute("preload") === "none") videoBeforeRestore.preload = "metadata";
  restoreDeferredImages(cell);
  if (host.hasAttribute("host")) host.setAttribute("autoplay", "");
  const video = host.querySelector("video");
  if (video) {
    if (!host._autoplayMuted) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
      host._autoplayMuted = true;
    }
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    if (video.getAttribute("preload") === "none") video.preload = "metadata";
    // restoreDeferredImages already calls load() when it attaches the file. Calling load()
    // again here cancels the play() that follows whenever the two overlap.
    video.dataset.galleryLoadStarted = "true";
  }
  return host;
}
setupVideoAutoplay_fn = function() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const gallery = this;
  const signal = __privateGet(this, _abortController9).signal;
  let scheduled = false;
  const requestSync = () => {
    if (scheduled || signal.aborted) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      if (!gallery.isConnected || signal.aborted) return;
      __privateMethod(gallery, _ProductGallery_instances, syncVideoAutoplay_fn).call(gallery);
    });
  };
  const intersection = new IntersectionObserver(requestSync, { threshold: [0, 0.2, 0.4, 0.55, 0.75, 1] });
  const watchCell = (cell) => {
    if (!cell || cell._galleryAutoplayObserved) return;
    const type = cell.getAttribute("data-media-type");
    if (type !== "video" && type !== "external_video") return;
    cell._galleryAutoplayObserved = true;
    intersection.observe(cell);
  };
  const mutations = new MutationObserver(() => {
    gallery.querySelectorAll(".product-gallery__media").forEach(watchCell);
    requestSync();
  });
  const resize = new ResizeObserver(requestSync);
  gallery.querySelectorAll(".product-gallery__media").forEach(watchCell);
  if (gallery.carousel) {
    mutations.observe(gallery.carousel, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden"] });
    resize.observe(gallery.carousel);
    gallery.carousel.addEventListener("scroll", requestSync, { passive: true, signal });
  }
  window.addEventListener("scroll", requestSync, { passive: true, signal });
  window.addEventListener("resize", () => {
    gallery.querySelectorAll(".product-gallery__media").forEach((cell) => {
      delete cell._galleryClipNodes;
    });
    requestSync();
  }, { signal });
  // A pause the theme did not request came from the shopper, so leave the video alone while it stays
  // visible. Clicks cannot tell us this: the browser's video controls often send the page no events.
  gallery.addEventListener("pause", (event) => {
    const video = event.target;
    if (!video || video.tagName !== "VIDEO") return;
    const host = video.closest("video-media");
    if (!host || !gallery.contains(host) || host._programmaticPause) return;
    if (!video.paused || video.seeking || video.ended || document.hidden) return;
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
    host._userPausedWhileVisible = true;
    host._wantsPlayback = false;
  }, { capture: true, signal });
  __privateSet(this, _videoAutoplayCleanup, () => {
    intersection.disconnect();
    mutations.disconnect();
    resize.disconnect();
    gallery.querySelectorAll(".product-gallery__media").forEach((cell) => {
      delete cell._galleryAutoplayObserved;
    });
  });
  requestSync();
};
syncVideoAutoplay_fn = function() {
  if (!this.isConnected) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (document.readyState !== "complete") {
    if (!videoAutoplayWaiting.has(this)) {
      videoAutoplayWaiting.add(this);
      afterPageLoad(() => {
        videoAutoplayWaiting.delete(this);
        if (this.isConnected) __privateMethod(this, _ProductGallery_instances, syncVideoAutoplay_fn).call(this);
      });
    }
    return;
  }
  const cells = Array.from(this.querySelectorAll('.product-gallery__media[data-media-type="video"], .product-gallery__media[data-media-type="external_video"]'));
  let winner = null;
  let winnerScore = 0;
  cells.forEach((cell) => {
    const visibility = cell.hasAttribute("hidden") ? { ratio: 0, viewportShare: 0, inView: false, outOfView: true } : galleryMediaVisibility(cell);
    cell._galleryVisibility = visibility;
    const score = Math.max(visibility.ratio, visibility.viewportShare);
    if (visibility.inView && score > winnerScore) {
      winner = cell;
      winnerScore = score;
    }
  });
  cells.forEach((cell) => {
    const host = cell.querySelector("video-media");
    if (!host) return;
    const visibility = cell._galleryVisibility;
    const video = host.querySelector("video");
    if (document.fullscreenElement && host.contains(document.fullscreenElement)) return;
    if (video && document.pictureInPictureElement === video) return;
    if (cell === winner) {
      if (host._userPausedWhileVisible || host.hasAttribute("suspended")) return;
      if (!host.playing && !host._autoplayQueued) {
        host._autoplayQueued = true;
        const clearQueued = () => {
          host._autoplayQueued = false;
        };
        prepareGalleryVideo(cell)?.play();
        host.addEventListener("media:play", clearQueued, { once: true });
        host.addEventListener("media:pause", clearQueued, { once: true });
        setTimeout(clearQueued, 1500);
      }
      return;
    }
    if (visibility.outOfView || cell.hasAttribute("hidden")) {
      host._userPausedWhileVisible = false;
      if (host.hasAttribute("suspended")) host.removeAttribute("suspended");
    }
    if (host.playing || host._wantsPlayback || host._autoplayQueued) host.pause();
  });
};
var _intersectionObserver, _hasProgrammaticScroll, _scrollDirection, _lastScrollPosition, _ProductGalleryNavigation_instances, onMediaObserve_fn;
var ProductGalleryNavigation = class extends CarouselNavigation {
  constructor() {
    super();
    __privateAdd(this, _ProductGalleryNavigation_instances);
    __privateAdd(this, _intersectionObserver, new IntersectionObserver(__privateMethod(this, _ProductGalleryNavigation_instances, onMediaObserve_fn).bind(this), { threshold: [0, 0.5, 1] }));
    __privateAdd(this, _hasProgrammaticScroll, false);
    __privateAdd(this, _scrollDirection, "bottom");
    __privateAdd(this, _lastScrollPosition);
    window.addEventListener("scroll", () => {
      if (window.scrollY > __privateGet(this, _lastScrollPosition)) {
        __privateSet(this, _scrollDirection, "bottom");
      } else {
        __privateSet(this, _scrollDirection, "top");
      }
      __privateSet(this, _lastScrollPosition, window.scrollY);
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.carousel.allCells.forEach((cell) => __privateGet(this, _intersectionObserver).observe(cell));
  }
  onButtonClicked(newIndex) {
    if (this.carousel.isScrollable) {
      super.onButtonClicked(newIndex);
    } else {
      this.carousel.cells[newIndex]?.scrollIntoView({ block: "start", behavior: "smooth" });
      this.onNavigationChange(newIndex);
      __privateSet(this, _hasProgrammaticScroll, true);
      if (!("onscrollend" in window)) {
        setTimeout(() => {
          __privateSet(this, _hasProgrammaticScroll, false);
        }, 1e3);
      } else {
        window.addEventListener(
          "scrollend",
          () => {
            __privateSet(this, _hasProgrammaticScroll, false);
          },
          { once: true }
        );
      }
    }
  }
};
_intersectionObserver = new WeakMap();
_hasProgrammaticScroll = new WeakMap();
_scrollDirection = new WeakMap();
_lastScrollPosition = new WeakMap();
_ProductGalleryNavigation_instances = new WeakSet();
/**
 * Use the intersection observer to change the selected icon
 */
onMediaObserve_fn = function(entries) {
  if (this.carousel.isScrollable) {
    return;
  }
  const firstEntry = entries.find((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
  if (!firstEntry || __privateGet(this, _hasProgrammaticScroll)) {
    return;
  }
  const selectedItem = this.items.find((item) => item.getAttribute("aria-current") === "true"), candidateItem = this.items.find((item) => item.getAttribute("data-media-id") === firstEntry.target.getAttribute("data-media-id"));
  if (!selectedItem) {
    return;
  }
  if (__privateGet(this, _scrollDirection) === "bottom" && parseInt(candidateItem.getAttribute("data-media-position")) > parseInt(selectedItem.getAttribute("data-media-position"))) {
    selectedItem.setAttribute("aria-current", "false");
    candidateItem.setAttribute("aria-current", "true");
  } else if (__privateGet(this, _scrollDirection) === "top" && parseInt(candidateItem.getAttribute("data-media-position")) < parseInt(selectedItem.getAttribute("data-media-position"))) {
    selectedItem.setAttribute("aria-current", "false");
    candidateItem.setAttribute("aria-current", "true");
  }
};
var OpenLightBoxButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true })));
  }
};
if (!window.customElements.get("product-gallery")) {
  window.customElements.define("product-gallery", ProductGallery);
}
if (!window.customElements.get("product-gallery-navigation")) {
  window.customElements.define("product-gallery-navigation", ProductGalleryNavigation);
}
if (!window.customElements.get("open-lightbox-button")) {
  window.customElements.define("open-lightbox-button", OpenLightBoxButton);
}

// js/common/product/product-list.js
import { inView as inView6, animate as animate9, stagger } from "vendor";
var ProductList = class extends HTMLElement {
  connectedCallback() {
    if (matchesMediaQuery("motion-safe")) {
      const revealElements = this.querySelectorAll('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]');
      if (revealElements.length > 0) {
        inView6(this, this.reveal.bind(this));
      }
    }
  }
  reveal() {
    const revealElements = this.querySelectorAll('product-card[reveal-on-scroll="true"], .product-list__promo[reveal-on-scroll="true"]');
    if (revealElements.length === 0) return;
    animate9(
      revealElements,
      {
        opacity: [0, 1],
        transform: ["translateY(20px)", "translateY(0)"]
      },
      {
        duration: 0.2,
        easing: "ease-in-out",
        delay: stagger(0.05, { start: 0.4, easing: "ease-out" })
      }
    );
  }
};
if (!window.customElements.get("product-list")) {
  window.customElements.define("product-list", ProductList);
}

// The currency app (Beast) formats prices once, when the page loads: products added later must be formatted the same way
function convertAddedPrices() {
  const savedCurrency = document.cookie.match(/(?:^|; )gl_currency=([^;]*)/);
  const currency = savedCurrency ? decodeURIComponent(savedCurrency[1]) : window.DoublyGlobalCurrency?.currentCurrency;
  if (currency && typeof window.DoublyGlobalCurrency?.convertAll === "function") {
    return window.DoublyGlobalCurrency.convertAll(currency);
  }
}
function revealShippingAmounts() {
  document.querySelectorAll(".cart-shipping-bar__amount.is-pending").forEach((node) => {
    node.classList.remove("is-pending");
  });
}
function convertCartDrawerPrices() {
  const revealTimer = setTimeout(revealShippingAmounts, 1000);
  const finish = () => {
    clearTimeout(revealTimer);
    revealShippingAmounts();
  };
  try {
    const pending = convertAddedPrices();
    if (pending && typeof pending.then === "function") {
      pending.then(finish, finish);
      return;
    }
  } catch (error) {
  }
  finish();
}
function bootShippingAmounts() {
  const beastReady = window.DoublyGlobalCurrency && typeof window.DoublyGlobalCurrency.convertAll === "function";
  if (beastReady) {
    convertCartDrawerPrices();
    return;
  }
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    const ready = window.DoublyGlobalCurrency && typeof window.DoublyGlobalCurrency.convertAll === "function";
    if (ready || attempts >= 40) {
      clearInterval(timer);
      if (ready) convertCartDrawerPrices();
      else revealShippingAmounts();
    }
  }, 50);
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bootShippingAmounts, { once: true });
else bootShippingAmounts();
var CollectionInfiniteScroll = class extends HTMLElement {
  connectedCallback() {
    this.loading = false;
    this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
      rootMargin: "1200px 0px"
    });
    // The next page is only fetched once the current one has fully loaded, so it never competes with the first products.
    // The rest of this page's cards go in right after load, so their photos (lazy) are ready before the shopper scrolls to them.
    whenPageLoaded(() => {
      if (!this.isConnected) return;
      const list = document.querySelector(this.getAttribute("list-selector"));
      if (list) while (this.revealRestBatch(list)) ;
      this.observer.observe(this);
      if (!this.hasRestCards()) this.prefetchNext();
    });
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
  }
  /**
   * Downloads the next page's HTML while the shopper is still browsing the current one, so reaching the end of the
   * list shows the next products at once instead of waiting for a request.
   */
  restTemplate() {
    const selector = this.getAttribute("list-selector");
    return Array.from(document.querySelectorAll("template[data-gl-rest-cards]")).find((node) => node.getAttribute("data-list-selector") === selector && node.content.children.length);
  }
  hasRestCards() {
    return Boolean(this.restTemplate());
  }
  /**
   * Cards past the first screen are already in the page, inside a template, so reaching them does not wait on the network.
   * One batch per frame: a tall screen fills, and the cards further down stay unloaded until the shopper gets there.
   */
  revealRestBatch(list) {
    const rest = this.restTemplate();
    if (!rest) return false;
    const batch = [];
    while (batch.length < 6 && rest.content.firstElementChild) batch.push(rest.content.firstElementChild);
    list.append(...batch);
    updateProductListImageSizes(list);
    convertAddedPrices();
    restoreCollectionCardColors(list);
    if (!rest.content.children.length) {
      rest.remove();
      this.prefetchNext();
    }
    return true;
  }
  prefetchNext() {
    const nextUrl = this.getAttribute("next-url");
    const sectionId = this.getAttribute("section-id");
    if (!nextUrl || !sectionId || this.prefetched?.nextUrl === nextUrl || this.hasRestCards()) return;
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set("section_id", sectionId);
    const controller = this.abortController = new AbortController();
    const start = () => fetch(url.toString(), { signal: controller.signal, priority: "low" }).then((response) => response.ok ? response.text() : null);
    const promise = new Promise((resolve) => {
      const run = () => start().then(resolve, () => resolve(null));
      if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 200);
    });
    this.prefetched = { nextUrl, promise };
  }
  async onIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting) || this.loading) {
      return;
    }
    const list = document.querySelector(this.getAttribute("list-selector"));
    if (!list) {
      this.remove();
      return;
    }
    if (this.revealRestBatch(list)) {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.observer.unobserve(this);
        this.observer.observe(this);
      });
      return;
    }
    const nextUrl = this.getAttribute("next-url");
    const sectionId = this.getAttribute("section-id");
    if (!nextUrl || !sectionId) {
      this.observer.disconnect();
      this.remove();
      return;
    }
    this.loading = true;
    this.classList.add("is-loading");
    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set("section_id", sectionId);
    try {
      let html = this.prefetched?.nextUrl === nextUrl ? await this.prefetched.promise : null;
      this.prefetched = null;
      if (!html) {
        this.abortController = new AbortController();
        const response = await fetch(url.toString(), { signal: this.abortController.signal });
        if (!response.ok) {
          this.loading = false;
          this.classList.remove("is-loading");
          return;
        }
        html = await response.text();
      }
      const doc = new DOMParser().parseFromString(html, "text/html");
      const incomingList = doc.querySelector(this.getAttribute("list-selector"));
      if (incomingList) {
        const nodes = Array.from(incomingList.children).map((node) => document.importNode(node, true));
        list.append(...nodes);
        updateProductListImageSizes(list);
        convertAddedPrices();
      }
      const nextLoader = doc.querySelector("collection-infinite-scroll");
      const upcomingUrl = nextLoader?.getAttribute("next-url");
      if (upcomingUrl) {
        this.setAttribute("next-url", upcomingUrl);
        this.loading = false;
        this.classList.remove("is-loading");
        this.prefetchNext();
        // Re-observe so a loader that is still within range (short page, fast scroll) loads the next page too
        this.observer.unobserve(this);
        this.observer.observe(this);
      } else {
        this.observer.disconnect();
        this.remove();
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        this.loading = false;
        this.classList.remove("is-loading");
      }
    }
  }
};
if (!window.customElements.get("collection-infinite-scroll")) {
  window.customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
}

// js/common/product/product-loader.js
var loadedProducts = {};
var ProductLoader = class {
  static load(productHandle) {
    if (!productHandle) {
      return;
    }
    if (loadedProducts[productHandle]) {
      return loadedProducts[productHandle];
    }
    loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
      const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);
      if (response.ok) {
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      } else {
        reject(`
          Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
          switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
          to "active".
        `);
      }
    });
    return loadedProducts[productHandle];
  }
};

function walletHost(slot) {
  return slot.querySelector("shopify-accelerated-checkout, .shopify-payment-button");
}
function walletControl(slot) {
  const selectors = "shopify-google-pay-button, shopify-apple-pay-button, shopify-paypal-button, shopify-buy-it-now-button, .shopify-payment-button__button, button.accelerated-checkout-button";
  const nodes = Array.from(slot.querySelectorAll(selectors));
  const accelerated = slot.querySelector("shopify-accelerated-checkout");
  if (accelerated?.shadowRoot) nodes.push(...accelerated.shadowRoot.querySelectorAll("button, iframe"));
  for (const node of nodes) {
    const button = node.button && node.button.isConnected ? node.button : node;
    if (button.matches?.("button, iframe") && button.isConnected) return button;
    const inner = node.shadowRoot?.querySelector?.("button, iframe");
    if (inner?.isConnected) return inner;
  }
  return null;
}
function paymentHidden(slot) {
  const pay = slot.querySelector(".shopify-payment-button");
  return !!pay && getComputedStyle(pay).display === "none";
}
function slotMeasurable(slot) {
  return !slot.hidden && slot.getClientRects().length > 0 && slot.clientWidth > 20;
}
function walletPainted(slot) {
  if (paymentHidden(slot)) return true;
  const control = walletControl(slot);
  if (!control) return false;
  const rect = control.getBoundingClientRect();
  if (rect.height > 8 && rect.width > 20) return true;
  return !slotMeasurable(slot);
}
function markWalletReady(slot) {
  if (paymentHidden(slot)) {
    slot.classList.add("is-unavailable");
    slot.classList.remove("is-ready", "is-settled");
    return;
  }
  if (!walletPainted(slot) || !slotMeasurable(slot)) return;
  slot.classList.add("is-ready");
  slot.classList.remove("is-unavailable", "is-settled");
}
function activateWalletSlot(slot) {
  if (!slot?._paymentTemplate || slot.hidden || !slotMeasurable(slot)) return;
  if (walletPainted(slot)) {
    markWalletReady(slot);
    return;
  }
  const host = walletHost(slot);
  const mountedAt = slot._walletMountedAt || 0;
  if (host && Date.now() - mountedAt < 1e4) return;
  const rebuilds = slot._walletRebuilds || 0;
  if (host && rebuilds >= 1) return;
  if (host) slot._walletRebuilds = rebuilds + 1;
  slot._walletMountedAt = Date.now();
  slot.classList.remove("is-ready", "is-unavailable", "is-settled");
  slot.replaceChildren(slot._paymentTemplate.content.cloneNode(true));
  watchPaymentSlot(slot);
  if (paymentHidden(slot)) {
    markWalletReady(slot);
    return;
  }
  try {
    window.Shopify?.PaymentButton?.init?.();
  } catch (error) {
  }
}
function watchPaymentSlot(slot) {
  if (!slot || slot._walletObserved) return;
  slot._walletObserved = true;
  const poke = () => {
    if (walletPainted(slot)) {
      markWalletReady(slot);
      return;
    }
    if (slotMeasurable(slot)) activateWalletSlot(slot);
  };
  if ("ResizeObserver" in window) new ResizeObserver(() => poke()).observe(slot);
  if ("IntersectionObserver" in window) {
    new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) poke();
    }).observe(slot);
  }
  new MutationObserver(() => poke()).observe(slot, { attributes: true, attributeFilter: ["hidden"] });
}
function syncPaymentSlots() {
  document.querySelectorAll(".shopify-payment-button-slot").forEach((slot) => {
    if (!slot._paymentTemplate) return;
    watchPaymentSlot(slot);
    if (walletPainted(slot)) {
      markWalletReady(slot);
      return;
    }
    if (slotMeasurable(slot)) activateWalletSlot(slot);
  });
}
function schedulePaymentButtonInit() {
  syncPaymentSlots();
  if (window.__paymentButtonInit) return;
  window.__paymentButtonInit = true;
  const startedAt = Date.now();
  const timer = setInterval(() => {
    syncPaymentSlots();
    const waiting = Array.from(document.querySelectorAll(".shopify-payment-button-slot")).some((slot) => slot._paymentTemplate && slotMeasurable(slot) && !walletPainted(slot));
    if (waiting && Date.now() - startedAt <= 3e4) return;
    clearInterval(timer);
    window.__paymentButtonInit = false;
    document.querySelectorAll(".shopify-payment-button-slot").forEach((slot) => {
      if (!slot._paymentTemplate || !slotMeasurable(slot) || walletPainted(slot)) return;
      slot.classList.add("is-settled");
    });
  }, 400);
}
function restoreCachedPaymentButtons() {
  document.querySelectorAll(".shopify-payment-button-slot").forEach((slot) => {
    if (!slot._paymentTemplate) return;
    slot._walletRebuilds = 0;
    slot._walletMountedAt = 0;
    slot.classList.remove("is-ready", "is-unavailable", "is-settled");
    slot.replaceChildren();
  });
  window.__paymentButtonInit = false;
  schedulePaymentButtonInit();
}
function mountDeferredPaymentButtons(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const templates = scope.querySelectorAll("template[data-deferred-payment-button]");
  let mounted = false;
  templates.forEach((template) => {
    const slot = template.previousElementSibling;
    if (!slot || !slot.classList.contains("shopify-payment-button-slot")) return;
    if (!slot._paymentTemplate) slot._paymentTemplate = template.cloneNode(true);
    template.remove();
    watchPaymentSlot(slot);
    if (slotMeasurable(slot)) activateWalletSlot(slot);
    mounted = true;
  });
  if (mounted) schedulePaymentButtonInit();
  // The page-wide watchers below are only needed where payment buttons exist (product page, quick buy). Collection
  // and home pages without them skip two document-wide observers that otherwise run on every DOM change.
  const hasPaymentButtons = templates.length > 0 || scope.querySelector(".shopify-payment-button-slot, .shopify-payment-button, shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .additional-checkout-buttons");
  if (!window.__stripMorePaymentOptions && hasPaymentButtons) {
    window.__stripMorePaymentOptions = true;
    const selector = "more-payment-options-link";
    const strip = (node) => {
      if (!node || typeof node.querySelectorAll !== "function") return;
      node.querySelectorAll(selector).forEach((match) => match.remove());
    };
    const watch = (node) => {
      if (!node || node.__morePayWatch) return;
      node.__morePayWatch = true;
      strip(node);
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((added) => {
            if (added.nodeType !== 1) return;
            if (added.matches && added.matches(selector)) {
              added.remove();
              return;
            }
            strip(added);
            if (added.shadowRoot) watch(added.shadowRoot);
          });
        });
      }).observe(node, { childList: true, subtree: true });
    };
    const scanHosts = (node) => {
      if (!node || typeof node.querySelectorAll !== "function") return;
      node.querySelectorAll("shopify-payment-button, shopify-accelerated-checkout, shopify-google-pay-button, shopify-apple-pay-button, shopify-paypal-button").forEach((host) => {
        if (host.shadowRoot) watch(host.shadowRoot);
      });
    };
    watch(document.documentElement);
    scanHosts(document);
    new MutationObserver(() => scanHosts(document)).observe(document.documentElement, { childList: true, subtree: true });
  }
}
function scheduleDeferredPaymentButtons() {
  let started = false;
  const mount = () => {
    if (started) return;
    started = true;
    mountDeferredPaymentButtons(document);
  };
  // Wallet iframes (Google Pay etc.) keep window load open. Mount only after load so product
  // LCP and load stay under 2s; buttons still appear on first idle after that.
  const afterLoad = () => {
    requestAnimationFrame(() => {
      if ("requestIdleCallback" in window) {
        const idle = requestIdleCallback(mount, { timeout: 1200 });
        setTimeout(() => {
          if (started) return;
          if (typeof cancelIdleCallback === "function") cancelIdleCallback(idle);
          mount();
        }, 1500);
      } else setTimeout(mount, 200);
    });
  };
  afterPageLoad(afterLoad);
  const watchSlots = () => {
    const slots = document.querySelectorAll(".shopify-payment-button-slot");
    if (!slots.length || !("IntersectionObserver" in window)) return;
    let sawInitial = false;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (!sawInitial) {
        sawInitial = true;
        if (visible) return;
      }
      if (!visible) return;
      observer.disconnect();
      // Still wait for load so an early intersect cannot pull wallets in before LCP finishes.
      afterPageLoad(mount);
    });
    slots.forEach((slot) => observer.observe(slot));
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", watchSlots, { once: true });
  else watchSlots();
  if (window.__paymentPageShowBound) return;
  window.__paymentPageShowBound = true;
  let shown = false;
  const refreshPaymentSlots = () => {
    requestAnimationFrame(() => syncPaymentSlots());
  };
  document.addEventListener("homepage:audience-change", refreshPaymentSlots);
  new MutationObserver(refreshPaymentSlots).observe(document.documentElement, { attributes: true, attributeFilter: ["data-active-audience"] });
  window.addEventListener("pageshow", (event) => {
    const first = !shown;
    shown = true;
    if (first && !event.persisted) return;
    restoreCachedPaymentButtons();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (!document.querySelector(".shopify-payment-button-slot, .additional-checkout-buttons")) return;
    window.__paymentButtonInit = false;
    schedulePaymentButtonInit();
  });
}
scheduleDeferredPaymentButtons();

// js/common/product/product-rerender.js
var _abortController10, _ProductRerender_instances, onRerender_fn;
var ProductRerender = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRerender_instances);
    __privateAdd(this, _abortController10);
  }
  connectedCallback() {
    __privateSet(this, _abortController10, new AbortController());
    if (!this.id || !this.hasAttribute("observe-form")) {
      console.warn('The <product-rerender> requires an ID to identify the element to re-render, and an "observe-form" attribute referencing to the form to monitor.');
    }
    const signal = __privateGet(this, _abortController10).signal;
    whenFormReady(this.getAttribute("observe-form"), (form) => {
      form.addEventListener("product:rerender", __privateMethod(this, _ProductRerender_instances, onRerender_fn).bind(this), { signal });
    }, signal);
  }
  disconnectedCallback() {
    __privateGet(this, _abortController10).abort();
  }
};
_abortController10 = new WeakMap();
_ProductRerender_instances = new WeakSet();
onRerender_fn = function(event) {
  const matchingElement = deepQuerySelector(event.detail.htmlFragment, `#${this.id}`);
  if (!matchingElement) {
    return;
  }
  const focusedElement = document.activeElement;
  if (!this.hasAttribute("allow-partial-rerender") || event.detail.productChange) {
    this.replaceWith(matchingElement);
  } else {
    const nextGallery = matchingElement.querySelector(".quick-buy-modal__gallery");
    const currentGallery = this.querySelector(".quick-buy-modal__gallery");
    if (nextGallery && currentGallery) {
      glSortQuickBuyGallery(nextGallery, glQuickBuyGender(this));
      adoptQuickBuyGalleryImages(currentGallery, nextGallery);
      currentGallery.replaceWith(nextGallery);
    }
    const blockTypes = [
      "title",
      "sku",
      "badges",
      "quantity-selector",
      "volume-pricing",
      "price",
      "payment-terms",
      "variant-picker",
      "inventory",
      "buy-buttons",
      "pickup-availability",
      "liquid"
    ];
    blockTypes.forEach((blockType) => {
      this.querySelectorAll(`[data-block-type="${blockType}"]`).forEach((element) => {
        const matchingBlock = matchingElement.querySelector(`[data-block-type="${blockType}"][data-block-id="${element.getAttribute("data-block-id")}"]`);
        if (matchingBlock) {
          if (blockType === "buy-buttons") {
            element.querySelector("buy-buttons").replaceWith(matchingBlock.querySelector("buy-buttons"));
          } else if (blockType === "payment-terms") {
            element.querySelector('[name="id"]').value = matchingBlock.querySelector('[name="id"]').value;
            element.querySelector('[name="id"]').dispatchEvent(new Event("change", { bubbles: true }));
          } else if (blockType === "quantity-selector") {
            const quantitySelectorElement = element.querySelector("quantity-selector");
            if (quantitySelectorElement) {
              const existingQuantity = quantitySelectorElement.quantity;
              element.replaceWith(matchingBlock);
              matchingBlock.querySelector("quantity-selector").quantity = existingQuantity;
            }
          } else {
            element.replaceWith(matchingBlock);
          }
        }
      });
    });
  }
  if (focusedElement.id) {
    const element = document.getElementById(focusedElement.id);
    const productRerenderScope = document.getElementById(this.id);
    if (productRerenderScope.contains(element)) {
      element.focus();
    }
  }
  mountDeferredPaymentButtons(document.getElementById(this.id) || document);
};
if (!window.customElements.get("product-rerender")) {
  window.customElements.define("product-rerender", ProductRerender);
}

// js/common/product/quick-buy-modal.js
// The quick buy content lives in the product template's main section. Once its section ID is known, only that section
// is requested (60-250 KB) instead of the whole product page (370-660 KB). The ID is kept across visits.
var quickBuySectionId = "";
try {
  quickBuySectionId = localStorage.getItem("quick-buy-section-id") || "";
} catch (error) {
}
var quickBuyRequests = /* @__PURE__ */ new Map();
// GL UNISEX HOTSPOT (added 2026-11-04): hotspot quick buy shows the look's gender photos first (Men look -> men photos)
function glMediaGender(src) {
  const parts = String(src || "").split("?")[0].split("/").pop().toLowerCase().split("_");
  if (parts[0] === "women") return "women";
  if (parts[0] === "men") return "men";
  if (parts[0] === "kids" && (parts[1] || "").includes("girl")) return "girls";
  if (parts[0] === "kids" && (parts[1] || "").includes("boy")) return "boys";
  return "";
}
function glQuickBuyGender(element) {
  return element?.closest?.("quick-buy-modal[data-gl-gender]")?.getAttribute("data-gl-gender") || "";
}
// GL UNISEX HOTSPOT LINKS (added 2026-11-04): the product page picks its gender photos from the collection in the URL,
// so a unisex product's links inside a gendered quick buy go through that gender's collection. Same collections as
// product-card.liquid. A product outside the collection just redirects to its normal page. The gallery is rebuilt
// on colour changes, so links are updated as they are used, before the prefetch script reads them.
var GL_GENDER_COLLECTIONS = { women: "all-womens-clothing", men: "all-mens-clothing", girls: "all-girls-clothing", boys: "all-boys-clothing" };
function glGenderQuickBuyLink(event) {
  const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
  const modal = link?.closest("quick-buy-modal[data-gl-gender]");
  const collection = modal && GL_GENDER_COLLECTIONS[modal.getAttribute("data-gl-gender")];
  if (!collection) return;
  const url = new URL(link.href, window.location.origin);
  const root = window.Shopify.routes.root;
  if (url.origin !== window.location.origin || !url.pathname.startsWith(`${root}products/`)) return;
  const handle = url.pathname.slice(`${root}products/`.length);
  if (!handle.includes("unisex") || handle.includes("/")) return;
  url.pathname = `${root}collections/${collection}/products/${handle}`;
  link.href = url.toString();
}
["pointerover", "pointerdown", "focusin", "click"].forEach((type) => window.addEventListener(type, glGenderQuickBuyLink, { capture: true, passive: true }));
function glSortQuickBuy(items, gender, srcOf) {
  if (!gender || !items || items.length < 2) return items;
  const target = [];
  const other = [];
  items.forEach((item) => (glMediaGender(srcOf(item)) === gender ? target : other).push(item));
  return target.length && other.length ? target.concat(other) : items;
}
function glSortQuickBuyGallery(gallery, gender, previewId) {
  if (!gallery || !gender) return;
  const nodes = Array.from(gallery.querySelectorAll(":scope > .quick-buy-modal__media"));
  const srcOf = (node) => {
    const image = node.querySelector("img");
    return image ? image.getAttribute("src") || image.currentSrc || "" : "";
  };
  let sorted = glSortQuickBuy(nodes, gender, srcOf);
  const preview = previewId ? sorted.find((node) => node.getAttribute("data-media-id") === String(previewId)) : null;
  if (preview && glMediaGender(srcOf(preview)) === gender) sorted = [preview].concat(sorted.filter((node) => node !== preview));
  if (sorted.map((node) => node.getAttribute("data-media-id")).join(",") === nodes.map((node) => node.getAttribute("data-media-id")).join(",")) return;
  sorted.forEach((node) => gallery.appendChild(node));
}
function quickBuyProductUrl(handle) {
  const url = new URL(`${window.Shopify.routes.root}products/${handle}`, window.location.origin);
  if (quickBuySectionId) url.searchParams.set("section_id", quickBuySectionId);
  return url.toString();
}
function quickBuyPageUrl(modal) {
  const handle = modal?.getAttribute?.("handle");
  if (!handle) return "";
  try {
    return new URL(`${window.Shopify.routes.root}products/${handle}`, window.location.origin).toString();
  } catch (error) {
    return "";
  }
}
function rememberQuickBuySection(doc) {
  const section = doc.getElementById("quick-buy-content")?.closest(".shopify-section");
  const sectionId = section?.id.replace("shopify-section-", "") || doc.querySelector('variant-picker[context="quick_buy"]')?.getAttribute("section-id");
  if (!sectionId || sectionId === quickBuySectionId) return;
  quickBuySectionId = sectionId;
  try {
    localStorage.setItem("quick-buy-section-id", sectionId);
  } catch (error) {
  }
}
function forgetQuickBuySection() {
  quickBuySectionId = "";
  try {
    localStorage.removeItem("quick-buy-section-id");
  } catch (error) {
  }
}
// One request per URL, shared by every hotspot or card pointing at the same product, and reused once loaded
function requestQuickBuyHtml(url, priority) {
  let entry = quickBuyRequests.get(url);
  if (!entry) {
    entry = { html: null, content: null, url };
    const init = {};
    if (priority) init.priority = priority;
    entry.promise = fetch(url, init).then((response) => response.text()).then((html) => {
      entry.html = html;
      return html;
    });
    entry.promise.catch(() => quickBuyRequests.delete(url));
    quickBuyRequests.set(url, entry);
  }
  return entry;
}
function prepareQuickBuyEntry(entry, html) {
  if (!entry || !html) return null;
  if (entry.content) {
    if (entry._warm && !entry._warmed) {
      entry._warmed = true;
      warmQuickBuyFragment(entry.content);
    }
    return entry.content;
  }
  if (!html.includes("quick-buy-content")) return null;
  const tempDoc = new DOMParser().parseFromString(html, "text/html");
  rememberQuickBuySection(tempDoc);
  const quickBuyContent = tempDoc.getElementById("quick-buy-content")?.content;
  if (!quickBuyContent?.childNodes.length) return null;
  Array.from(quickBuyContent.querySelectorAll("noscript")).forEach((noScript) => noScript.remove());
  entry.content = quickBuyContent;
  if (entry._warm && !entry._warmed) {
    entry._warmed = true;
    warmQuickBuyFragment(quickBuyContent);
  }
  return quickBuyContent;
}
// A full product page no longer carries the quick buy template. Read the main section ID from it and request just that
// section, which does carry it. The ID is remembered, so later quick buys go straight to the section.
function followQuickBuySection(entry, html, priority) {
  if (!html || entry.url.includes("section_id=") || html.includes("quick-buy-content")) return null;
  const match = html.match(/id="shopify-section-([^"]+)"[^>]*shopify-section--main-product/);
  if (!match) return null;
  quickBuySectionId = match[1];
  try {
    localStorage.setItem("quick-buy-section-id", quickBuySectionId);
  } catch (error) {
  }
  const url = new URL(entry.url);
  url.searchParams.set("section_id", quickBuySectionId);
  const init = {};
  if (priority) init.priority = priority;
  return fetch(url.toString(), init).then((response) => response.text()).then((sectionHtml) => prepareQuickBuyEntry(entry, sectionHtml));
}
function warmQuickBuyFragment(content) {
  content.querySelectorAll(".quick-buy-modal__gallery [data-media-id]").forEach((node, index) => {
    const id = node.getAttribute("data-media-id");
    const source = node.querySelector("img");
    if (!id || !source || quickBuyImageCache.has(id)) return;
    const image = new Image();
    image.className = "quick-buy-modal__image";
    image.alt = source.alt || "";
    const sizes = source.getAttribute("sizes");
    if (sizes) image.sizes = sizes;
    if (source.getAttribute("srcset")) image.srcset = source.getAttribute("srcset");
    image.src = source.currentSrc || source.src;
    image.decoding = "async";
    image.loading = "eager";
    image.fetchPriority = index === 0 ? "high" : "low";
    quickBuyImageCache.set(id, image);
    if (typeof image.decode === "function") image.decode().catch(() => {
    });
  });
  warmQuickBuyOptions(content);
}
var _QuickBuyModal_instances, onAfterHide_fn;
var QuickBuyModal = class extends Modal {
  constructor() {
    super();
    __privateAdd(this, _QuickBuyModal_instances);
    if (window.themeVariables.settings.cartType === "drawer") {
      document.addEventListener("variant:add", this.hide.bind(this));
    }
    this.addEventListener("dialog:after-hide", __privateMethod(this, _QuickBuyModal_instances, onAfterHide_fn).bind(this));
  }
  preload(priority) {
    this._preloadUrl = quickBuyProductUrl(this.getAttribute("handle"));
    const entry = requestQuickBuyHtml(this._preloadUrl, priority);
    if (this.hasAttribute("data-preview-id")) entry._warm = true;
    if (!entry._prepare) {
      entry._prepare = entry.promise.then((html) => prepareQuickBuyEntry(entry, html) || followQuickBuySection(entry, html, priority)).catch(() => null);
    } else if (entry._warm && entry.content && !entry._warmed) {
      entry._warmed = true;
      warmQuickBuyFragment(entry.content);
    }
    return entry;
  }
  show() {
    if (this.open) return Promise.resolve();
    if (!this._showTask) {
      this._showTask = this.openQuickBuy().finally(() => {
        this._showTask = null;
      });
    } else if (this.hasAttribute("data-hotspot-quick-buy")) {
      // The modal was closed while a slow request was still retrying. Open it again when that attempt finishes.
      return this._showTask.then(() => {
        if (!this.open && this.isConnected) return this.show();
      });
    }
    return this._showTask;
  }
  quickBuyTitle() {
    return this.controls.find((control) => control.hasAttribute("aria-label"))?.getAttribute("aria-label") || "";
  }
  // Opens the modal right away with a placeholder while the product is still downloading, so the tap gets an
  // immediate response and the open animation plays during the wait
  renderLoading() {
    const title = document.createElement("p");
    title.className = "h5";
    title.slot = "header";
    title.textContent = this.quickBuyTitle();
    const content = document.createElement("div");
    content.className = "quick-buy-modal__content quick-buy-modal__loading";
    content.setAttribute("aria-busy", "true");
    content.innerHTML = '<div class="quick-buy-modal__gallery"><span class="quick-buy-modal__media"></span><span class="quick-buy-modal__media"></span><span class="quick-buy-modal__media"></span></div><span class="quick-buy-modal__loading-line"></span><span class="quick-buy-modal__loading-line"></span><span class="quick-buy-modal__loading-button"></span>';
    this.replaceChildren(title, content);
  }
  // Hotspot taps already know the variant photo. Show that photo immediately instead of the shimmer,
  // then swap in the real form without replacing a picture that has already decoded.
  renderPreview() {
    const image = warmHotspotPreview(this);
    const id = this.getAttribute("data-preview-id");
    if (!image || !id) return false;
    image.loading = "eager";
    image.fetchPriority = "high";
    image.decoding = "async";
    const title = document.createElement("p");
    title.className = "h5";
    title.slot = "header";
    title.textContent = this.quickBuyTitle();
    const content = document.createElement("div");
    content.className = "quick-buy-modal__content";
    const gallery = document.createElement("div");
    gallery.className = "quick-buy-modal__gallery";
    gallery.setAttribute("role", "list");
    const link = document.createElement("a");
    link.className = "quick-buy-modal__media";
    const pageUrl = quickBuyPageUrl(this);
    if (pageUrl) link.href = pageUrl;
    link.setAttribute("role", "listitem");
    link.setAttribute("data-media-id", id);
    link.appendChild(image);
    gallery.appendChild(link);
    content.appendChild(gallery);
    this.replaceChildren(title, content);
    return true;
  }
  mountQuickBuy(entry, shown) {
    const fragment = entry.content.cloneNode(true);
    const nextGallery = fragment.querySelector(".quick-buy-modal__gallery");
    const currentGallery = this.querySelector(".quick-buy-modal__gallery");
    glSortQuickBuyGallery(nextGallery, this.getAttribute("data-gl-gender") || "", this.getAttribute("data-preview-id"));
    if (nextGallery) adoptQuickBuyGalleryImages(currentGallery && currentGallery !== nextGallery ? currentGallery : null, nextGallery);
    this.replaceChildren(fragment);
    if (shown && !this.open) return shown;
    shown ??= super.show();
    queueMicrotask(() => {
      mountDeferredPaymentButtons(this);
      Shopify?.PaymentButton?.init();
    });
    const warm = () => this.open && warmQuickBuyOptions(this);
    if (this.hasAttribute("data-preview-id")) warm();
    else "requestIdleCallback" in window ? requestIdleCallback(warm, { timeout: 500 }) : setTimeout(warm, 200);
    return shown;
  }
  async openQuickBuy() {
    const hotspot = this.hasAttribute("data-hotspot-quick-buy");
    let entry = this.preload("high");
    if (!entry.content && entry.html) prepareQuickBuyEntry(entry, entry.html);
    let shown = null;
    if (!entry.content) {
      if (!this.renderPreview()) this.renderLoading();
      shown = super.show();
    }
    let attempt = 0;
    while (this.isConnected) {
      try {
        await entry._prepare;
        if (!entry.content && entry.html && !entry.html.includes("quick-buy-content") && this._preloadUrl?.includes("section_id=")) {
          forgetQuickBuySection();
          quickBuyRequests.delete(this._preloadUrl);
          entry = this.preload("high");
          if (!entry.content && entry.html) prepareQuickBuyEntry(entry, entry.html);
          if (!entry.content) await entry._prepare;
        }
        if (!entry.content?.childNodes.length) {
          throw new Error("Quick buy content not found");
        }
        if (shown && !this.open) return;
        return this.mountQuickBuy(entry, shown);
      } catch (error) {
        if (shown && !this.open) return;
        // A hotspot tap stays on this page. A slow or failed product request keeps the modal open and tries again.
        if (!hotspot) {
          this.hide();
          window.location.href = new URL(`${window.Shopify.routes.root}products/${this.getAttribute("handle")}`, window.location.origin).toString();
          return;
        }
        attempt += 1;
        const failedUrl = this._preloadUrl;
        if (failedUrl) quickBuyRequests.delete(failedUrl);
        if (attempt >= 2) forgetQuickBuySection();
        await new Promise((resolve) => setTimeout(resolve, Math.min(500 * attempt, 4000)));
        if (!this.open || !this.isConnected) return;
        entry = this.preload("high");
        if (!entry.content && entry.html) prepareQuickBuyEntry(entry, entry.html);
      }
    }
  }
};
_QuickBuyModal_instances = new WeakSet();
onAfterHide_fn = function() {
  if (this.open) return;
  this.innerHTML = "";
};
if (!window.customElements.get("quick-buy-modal")) {
  window.customElements.define("quick-buy-modal", QuickBuyModal);
}
document.addEventListener("pointerenter", (event) => {
  const control = event.target.closest?.("[aria-controls]");
  const modal = control && document.getElementById(control.getAttribute("aria-controls"));
  if (modal?.tagName === "QUICK-BUY-MODAL") modal.preload();
}, true);
document.addEventListener("touchstart", (event) => {
  const control = event.target.closest?.("[aria-controls]");
  const modal = control && document.getElementById(control.getAttribute("aria-controls"));
  if (modal?.tagName !== "QUICK-BUY-MODAL") return;
  if (modal.hasAttribute("data-preview-id")) warmHotspotPreview(modal);
  modal.preload(modal.hasAttribute("data-preview-id") ? "high" : void 0);
}, { capture: true, passive: true });
document.addEventListener("pointerdown", (event) => {
  const item = event.target.closest?.(".custom-hotspot-swiper .shop-the-look__item");
  if (!item) return;
  const onHotspot = Boolean(event.target.closest?.(".shop-the-look__hot-spot"));
  item.querySelectorAll("quick-buy-modal").forEach((modal) => {
    warmHotspotPreview(modal);
    modal.preload(onHotspot ? "high" : "low");
  });
}, { capture: true, passive: true });
var quickBuyPreloadQueue = [];
var quickBuyPreloadsActive = 0;
function drainQuickBuyPreload() {
  while (quickBuyPreloadsActive < 2 && quickBuyPreloadQueue.length) {
    const modal = quickBuyPreloadQueue.shift();
    quickBuyPreloadsActive += 1;
    if (modal.hasAttribute?.("data-preview-id")) warmHotspotPreview(modal);
    modal.preload("low").promise.catch(() => {
    }).finally(() => {
      quickBuyPreloadsActive -= 1;
      drainQuickBuyPreload();
    });
  }
}
function observeQuickBuyControls(selector, rootMargin) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (entry.target.checkVisibility?.({ opacityProperty: true, visibilityProperty: true }) === false) return;
      const modal = document.getElementById(entry.target.getAttribute("aria-controls"));
      if (!modal || modal.tagName !== "QUICK-BUY-MODAL") return;
      observer.unobserve(entry.target);
      if (modal.hasAttribute("data-preview-id")) warmHotspotPreview(modal);
      if (navigator.connection?.saveData || modal._queued) return;
      modal._queued = true;
      if (modal.hasAttribute("data-preview-id")) quickBuyPreloadQueue.unshift(modal);
      else quickBuyPreloadQueue.push(modal);
      drainQuickBuyPreload();
    });
  }, { rootMargin });
  document.querySelectorAll(selector).forEach((node) => {
    if (node.tagName === "QUICK-BUY-MODAL") node.controls.forEach((control) => observer.observe(control));
    else observer.observe(node);
  });
  return observer;
}
function warmHotspotPreview(modal) {
  const id = modal.getAttribute?.("data-preview-id");
  const src = modal.getAttribute?.("data-preview-src");
  if (!id || !src) return quickBuyImageCache.get(id) || null;
  const cached = quickBuyImageCache.get(id);
  if (cached) return cached;
  const width = Number(modal.getAttribute("data-preview-width")) || 720;
  const height = Number(modal.getAttribute("data-preview-height")) || width;
  const image = warmVariantImage({
    id,
    alt: "",
    preview_image: { src, width, height }
  }, quickBuyTargetWidth(width));
  if (!image) return null;
  image.className = "quick-buy-modal__image";
  image.decoding = "async";
  image.fetchPriority = "low";
  quickBuyImageCache.set(id, image);
  if (typeof image.decode === "function") image.decode().catch(() => {
  });
  return image;
}
// Hotspot buttons are warmed as soon as the DOM is ready. Waiting for the load event left the first tap
// behind the lookbook images, which is the pause on mobile.
function startHotspotQuickBuyWarm() {
  observeQuickBuyControls(".custom-hotspot-swiper .shop-the-look__hot-spot[aria-controls]", "160px 0px");
}
// The modals themselves are hidden (display: none) and never intersect, so the buttons opening them are observed
// instead. Buttons on carousel slides that are not showing do not intersect either, so only visible looks preload.
whenPageLoaded(() => {
  if (navigator.connection?.saveData) return;
  observeQuickBuyControls("quick-buy-modal", "240px 0px");
});

var warmedVariantImages = /* @__PURE__ */ new Map();
function variantMediaFromInput(input) {
  if (!input?.hasAttribute?.("data-variant-media")) {
    return null;
  }
  try {
    return JSON.parse(input.getAttribute("data-variant-media"));
  } catch (error) {
    return null;
  }
}
function sizedVariantMedia(media, width) {
  const preview = media?.preview_image;
  if (!preview?.src) {
    return null;
  }
  const targetWidth = Math.min(width, preview.width || width);
  const url = new URL(preview.src.startsWith("//") ? `https:${preview.src}` : preview.src, window.location.origin);
  url.searchParams.set("width", String(targetWidth));
  const height = preview.width ? Math.max(1, Math.round((preview.height || targetWidth) * targetWidth / preview.width)) : targetWidth;
  return {
    id: media.id,
    alt: media.alt || "",
    preview_image: {
      src: url.href,
      width: targetWidth,
      height
    }
  };
}
function warmVariantImage(media, width) {
  const sized = sizedVariantMedia(media, width);
  if (!sized) {
    return null;
  }
  const href = sized.preview_image.src;
  let image = warmedVariantImages.get(href);
  if (!image) {
    image = createMediaImg(sized, [180, 240, 360, 480, 720], {
      sizes: "(max-width: 699px) 42vw, 180px"
    });
    image.decoding = "async";
    warmedVariantImages.set(href, image);
  }
  return image;
}
var quickBuyImageCache = /* @__PURE__ */ new Map();
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", startHotspotQuickBuyWarm, { once: true });
else startHotspotQuickBuyWarm();
var QUICK_BUY_SIZES = "(max-width: 699px) 42vw, 180px";
function quickBuyCatalog(content) {
  const script = content?.querySelector("[data-quick-buy-media]");
  if (!script) return null;
  if (script._catalog === void 0) {
    try {
      script._catalog = JSON.parse(script.textContent);
    } catch (error) {
      script._catalog = null;
    }
  }
  return script._catalog;
}
function quickBuyOptionValue(control) {
  return (control.getAttribute("data-option-value-key") || control.getAttribute("data-option-value-name") || "").trim().toLowerCase();
}
function quickBuySelection(input) {
  const content = input.closest(".quick-buy-modal__content");
  const catalog = quickBuyCatalog(content);
  if (!catalog || input.hasAttribute("data-product-url")) return null;
  const selected = {};
  const byPosition = {};
  const position = input.getAttribute("data-option-position");
  const apply = (control) => {
    const optionPosition = control.getAttribute("data-option-position");
    const optionName = catalog.options?.[parseInt(optionPosition, 10) - 1];
    const valueName = quickBuyOptionValue(control);
    // Lowercased like Liquid's options_by_name lookup, so an image alt "#color_..." matches the option "Color"
    if (optionName && valueName) selected[optionName.trim().toLowerCase()] = valueName;
    if (optionPosition && valueName) byPosition[optionPosition] = valueName;
  };
  if (input.form) {
    Array.from(input.form.elements).forEach((element) => {
      if (!element.matches?.("input[data-option-position]:checked")) return;
      if (element.getAttribute("data-option-position") === position) return;
      apply(element);
    });
  }
  apply(input);
  return { catalog, selected, byPosition, content };
}
function normalizeGroupValue(value) {
  return (value || "").trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ");
}
function groupTail(value) {
  const normalized = normalizeGroupValue(value);
  const index = normalized.lastIndexOf(":");
  return (index === -1 ? normalized : normalized.slice(index + 1)).trim();
}
function valuesMatch(selectedValue, mediaValue) {
  const selected = normalizeGroupValue(selectedValue);
  const media = normalizeGroupValue(mediaValue);
  if (!media) return false;
  if (selected === media) return true;
  if (!selected.includes(":") && !media.includes(":")) return false;
  const selectedTail = groupTail(selected);
  const mediaTail = groupTail(media);
  return selectedTail.length > 1 && selectedTail === mediaTail;
}
function sameAltGroup(media, featuredMedia) {
  if (!featuredMedia?.v || media?.v !== featuredMedia.v) return false;
  if (media.p != null && featuredMedia.p != null) return String(media.p) === String(featuredMedia.p);
  const left = (media.o || "").trim().toLowerCase();
  const right = (featuredMedia.o || "").trim().toLowerCase();
  return !left || !right || left === right;
}
function visibleQuickBuyMedia(catalog, selected, featuredId, byPosition) {
  const featuredKey = featuredId == null ? "" : String(featuredId);
  const featuredMedia = featuredKey ? (catalog.media || []).find((item) => String(item.id) === featuredKey) : null;
  const featured = [];
  const rest = [];
  (catalog.media || []).forEach((media) => {
    if (featuredKey && String(media.id) === featuredKey) {
      featured.push(media);
      return;
    }
    if (sameAltGroup(media, featuredMedia)) {
      rest.push(media);
      return;
    }
    // Position stays valid when the Arabic option name no longer matches the alt tag.
    if (media.p && byPosition && byPosition[media.p] != null) {
      if (!valuesMatch(byPosition[media.p], media.v)) return;
    } else if (media.o && !valuesMatch(selected[media.o.trim().toLowerCase()] || "", media.v)) return;
    rest.push(media);
  });
  return featured.concat(rest);
}
function quickBuyTargetWidth(previewWidth) {
  const css = window.matchMedia("(max-width: 699px)").matches ? Math.round(window.innerWidth * 0.42) : 180;
  const needed = Math.ceil(css * Math.min(window.devicePixelRatio || 1, 3));
  const pick = [180, 240, 360, 480, 720].find((step) => step >= needed) || 720;
  return Math.min(pick, previewWidth || pick);
}
function quickBuyImg(media) {
  const id = String(media.id);
  const cached = quickBuyImageCache.get(id);
  if (cached) return cached;
  if (!media.src) return null;
  const width = quickBuyTargetWidth(media.w);
  const src = new URL(media.src.startsWith("//") ? `https:${media.src}` : media.src, window.location.origin);
  src.searchParams.set("width", String(width));
  const height = media.w ? Math.max(1, Math.round((media.h || width) * width / media.w)) : width;
  const image = createMediaImg({
    id: media.id,
    alt: "",
    preview_image: { src: src.href, width, height }
  }, [width], {
    class: "quick-buy-modal__image",
    sizes: QUICK_BUY_SIZES
  });
  image.decoding = "async";
  image.loading = "eager";
  quickBuyImageCache.set(id, image);
  return image;
}
function preloadQuickBuyOption(input) {
  const selection = quickBuySelection(input);
  if (!selection) return;
  const featured = variantMediaFromInput(input);
  glSortQuickBuy(visibleQuickBuyMedia(selection.catalog, selection.selected, featured?.id, selection.byPosition), glQuickBuyGender(selection.content), (media) => media.src).forEach((media, index) => {
    const image = quickBuyImg(media);
    if (!image) return;
    image.fetchPriority = index === 0 ? "high" : "auto";
    if (typeof image.decode === "function") image.decode().catch(() => {
    });
  });
}
// Once the quick buy is open, download the first two images of every other color at low priority, so switching
// color paints images that are already loaded instead of starting the downloads on tap
function warmQuickBuyOptions(root) {
  const warmed = /* @__PURE__ */ new Set();
  root.querySelectorAll("input[data-option-position][data-variant-media]:not(:checked)").forEach((input) => {
    const selection = quickBuySelection(input);
    if (!selection) return;
    const featured = variantMediaFromInput(input);
    glSortQuickBuy(visibleQuickBuyMedia(selection.catalog, selection.selected, featured?.id, selection.byPosition), glQuickBuyGender(selection.content), (media) => media.src).slice(0, 2).forEach((media) => {
      if (warmed.has(media.id) || quickBuyImageCache.has(String(media.id))) return;
      warmed.add(media.id);
      const image = quickBuyImg(media);
      if (!image) return;
      image.fetchPriority = "low";
      image.decoding = "async";
      if (typeof image.decode === "function") image.decode().catch(() => {
      });
    });
  });
}
function paintQuickBuyGallery(input) {
  const selection = quickBuySelection(input);
  const gallery = selection?.content?.querySelector(".quick-buy-modal__gallery");
  if (!selection || !gallery) return false;
  gallery.querySelectorAll(".quick-buy-modal__media").forEach((node) => {
    const id = node.getAttribute("data-media-id");
    const image = node.querySelector("img");
    if (id && image && !quickBuyImageCache.has(id)) quickBuyImageCache.set(id, image);
  });
  const featured = variantMediaFromInput(input);
  const mediaList = glSortQuickBuy(visibleQuickBuyMedia(selection.catalog, selection.selected, featured?.id, selection.byPosition), glQuickBuyGender(selection.content), (media) => media.src);
  if (!mediaList.length) return false;
  const currentIds = Array.from(gallery.querySelectorAll("[data-media-id]")).map((node) => node.getAttribute("data-media-id")).join(",");
  if (currentIds === mediaList.map((media) => String(media.id)).join(",")) return true;
  const modal = gallery.closest("quick-buy-modal");
  const currentHref = gallery.querySelector("a[href]")?.getAttribute("href");
  const href = currentHref && currentHref !== "#" ? currentHref : quickBuyPageUrl(modal);
  const label = gallery.querySelector("[aria-label]")?.getAttribute("aria-label") || modal?.quickBuyTitle?.() || "";
  const fragment = document.createDocumentFragment();
  mediaList.forEach((media, index) => {
    const image = quickBuyImg(media);
    if (!image) return;
    image.loading = "eager";
    image.fetchPriority = index === 0 ? "high" : "auto";
    image.decoding = "async";
    const link = document.createElement("a");
    link.className = "quick-buy-modal__media";
    if (href) link.href = href;
    link.setAttribute("role", "listitem");
    link.setAttribute("data-media-id", String(media.id));
    if (label) link.setAttribute("aria-label", label);
    link.appendChild(image);
    fragment.appendChild(link);
  });
  if (!fragment.childNodes.length) return false;
  gallery.replaceChildren(fragment);
  gallery.scrollLeft = 0;
  return true;
}
function adoptQuickBuyGalleryImages(currentGallery, nextGallery) {
  const liveById = /* @__PURE__ */ new Map();
  currentGallery?.querySelectorAll(".quick-buy-modal__media").forEach((node) => {
    const id = node.getAttribute("data-media-id");
    const image = node.querySelector("img");
    if (id && image) liveById.set(id, image);
  });
  nextGallery.querySelectorAll(".quick-buy-modal__media").forEach((node, index) => {
    const nextImage = node.querySelector("img");
    if (!nextImage) return;
    const id = node.getAttribute("data-media-id");
    const cached = id && quickBuyImageCache.get(id);
    const shown = id && liveById.get(id);
    const cachedReady = cached && cached !== nextImage && cached.complete && cached.naturalWidth > 0;
    const live = cachedReady ? cached : shown;
    if (live && live !== nextImage) {
      live.className = nextImage.className || "quick-buy-modal__image";
      const sizes = nextImage.getAttribute("sizes");
      if (sizes) live.sizes = sizes;
      live.loading = "eager";
      live.decoding = "async";
      if (index === 0) live.fetchPriority = "high";
      nextImage.removeAttribute("srcset");
      nextImage.removeAttribute("src");
      nextImage.replaceWith(live);
      if (id) quickBuyImageCache.set(id, live);
      return;
    }
    nextImage.loading = "eager";
    nextImage.decoding = "async";
    if (index === 0) nextImage.fetchPriority = "high";
    if (id && nextImage.getAttribute("src") && !quickBuyImageCache.has(id)) quickBuyImageCache.set(id, nextImage);
  });
}
function previewOptionMedia(input) {
  if (input.closest?.("quick-buy-modal") && paintQuickBuyGallery(input)) return;
  const media = variantMediaFromInput(input);
  if (!media) {
    return;
  }
  const picker = input.closest("variant-picker");
  const quickBuyGallery = picker?.closest(".quick-buy-modal__content")?.querySelector(".quick-buy-modal__gallery");
  if (quickBuyGallery) {
    if (paintQuickBuyGallery(input)) return;
    const current = quickBuyGallery.querySelector(".quick-buy-modal__image");
    const next = warmVariantImage(media, quickBuyTargetWidth(media.preview_image?.width));
    if (!current || !next) {
      return;
    }
    const token = quickBuyGallery._mediaToken = (quickBuyGallery._mediaToken || 0) + 1;
    next.className = current.className;
    next.sizes = current.getAttribute("sizes") || QUICK_BUY_SIZES;
    next.loading = "eager";
    next.fetchPriority = "high";
    const apply = () => {
      if (quickBuyGallery._mediaToken !== token || !quickBuyGallery.isConnected) {
        return;
      }
      const liveImage = quickBuyGallery.querySelector(".quick-buy-modal__image");
      if (!liveImage || liveImage === next) {
        return;
      }
      liveImage.replaceWith(next);
      if (media.id) {
        next.closest("[data-media-id]")?.setAttribute("data-media-id", media.id);
      }
    };
    if (next.complete && next.naturalWidth > 0) {
      apply();
    } else {
      next.addEventListener("load", apply, { once: true });
    }
    return;
  }
  if (!media.id) {
    return;
  }
  const formId = input.getAttribute("form") || picker?.getAttribute("form-id");
  const productGallery = formId && document.querySelector(`product-gallery[form="${formId}"]`);
  const cell = productGallery?.querySelector(`[data-media-id="${media.id}"]`);
  if (!cell) {
    return;
  }
  cell.removeAttribute("hidden");
  restoreDeferredImages(cell);
  const carousel = productGallery.carousel;
  if (!carousel) {
    cell.scrollIntoView({ block: "nearest", behavior: "auto" });
    return;
  }
  const index = carousel.cells.indexOf(cell);
  if (index >= 0) {
    carousel.select(index, { instant: true });
  }
}

// js/common/product/variant-picker.js
import { Delegate as Delegate5 } from "vendor";
var CACHE_EVICTION_TIME = 1e3 * 60 * 5;
// GL SIZE PRESELECT (added 2026-11-04): when a colour (or any option other than size) is picked and the current size
// is sold out in it, return the size input(s) of the first variant in stock with the new colour. Otherwise null.
function glInStockSizeInputs(picker, form, changed) {
  if (!picker || !form || !changed || changed.hasAttribute("data-product-url")) return null;
  let data = null;
  try {
    data = JSON.parse(picker.querySelector("script[data-gl-variants]")?.textContent || "null");
  } catch (error) {
    return null;
  }
  if (!data || !Array.isArray(data.s) || !data.s.length || !Array.isArray(data.v)) return null;
  const changedPosition = parseInt(changed.getAttribute("data-option-position"), 10);
  if (!changedPosition || data.s.includes(changedPosition)) return null;
  const inputs = Array.from(form.elements).filter((item) => item.matches?.("input[data-option-position]"));
  const checked = {};
  inputs.forEach((item) => {
    if (item.checked) checked[item.getAttribute("data-option-position")] = item;
  });
  checked[changedPosition] = changed;
  const positions = Object.keys(checked).map((position) => parseInt(position, 10));
  const nameAt = (position) => checked[position]?.getAttribute("data-option-value-name");
  if (positions.some((position) => nameAt(position) == null)) return null;
  const sameAs = (variant, list) => list.every((position) => variant[position - 1] === nameAt(position));
  const current = data.v.find((variant) => sameAs(variant, positions));
  if (current && current[3]) return null;
  const kept = positions.filter((position) => !data.s.includes(position));
  const next = data.v.find((variant) => variant[3] && sameAs(variant, kept));
  if (!next) return null;
  const result = {};
  for (const position of positions.filter((item) => data.s.includes(item))) {
    const input = inputs.find((item) => parseInt(item.getAttribute("data-option-position"), 10) === position && item.getAttribute("data-option-value-name") === next[position - 1]);
    if (!input) return null;
    if (input !== checked[position]) result[position] = input;
  }
  return Object.keys(result).length ? result : null;
}
var _preloadedHtml, _delegate4, _intersectionObserver2, _form, _selectedVariant, _VariantPicker_instances, ensureForm_fn, getActiveOptionValues_fn, getOptionValuesFromOption_fn, onOptionChanged_fn, onOptionPreload_fn, onIntersection_fn, renderForCombination_fn, createHashKeyForHtml_fn;
var _VariantPicker = class _VariantPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _VariantPicker_instances);
    __privateAdd(this, _delegate4, new Delegate5(document.body));
    __privateAdd(this, _intersectionObserver2, new IntersectionObserver(__privateMethod(this, _VariantPicker_instances, onIntersection_fn).bind(this)));
    __privateAdd(this, _form);
    __privateAdd(this, _selectedVariant);
  }
  async connectedCallback() {
    __privateSet(this, _selectedVariant, JSON.parse(this.querySelector("script[data-variant]")?.textContent || "{}"));
    __privateMethod(this, _VariantPicker_instances, ensureForm_fn).call(this);
    __privateGet(this, _delegate4).on("change", `input[data-option-position][form="${this.getAttribute("form-id")}"]`, __privateMethod(this, _VariantPicker_instances, onOptionChanged_fn).bind(this));
    __privateGet(this, _delegate4).on("pointerenter", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
    __privateGet(this, _delegate4).on("touchstart", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
    __privateGet(this, _intersectionObserver2).observe(this);
  }
  disconnectedCallback() {
    __privateGet(this, _delegate4).off();
    __privateGet(this, _intersectionObserver2).unobserve(this);
  }
  get selectedVariant() {
    return __privateGet(this, _selectedVariant);
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  /**
   * Select a variant using a list of option values. The list of option values might lead to no variant (for instance)
   * in the case of a combination that does not exist
   */
  async selectCombination({ optionValues, productChange }) {
    const form = __privateMethod(this, _VariantPicker_instances, ensureForm_fn).call(this);
    if (!form) return;
    const previousVariant = this.selectedVariant;
    const newContent = document.createRange().createContextualFragment(await __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, optionValues));
    if (!productChange) {
      const newVariantPicker = deepQuerySelector(newContent, `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`);
      const newVariant = JSON.parse(newVariantPicker?.querySelector("script[data-variant]")?.textContent || "{}");
      __privateSet(this, _selectedVariant, newVariant);
      const idInput = enableVariantInput(form);
      if (idInput && newVariant?.id) {
        idInput.value = newVariant.id;
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (this.updateUrl && newVariant?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("variant", newVariant.id);
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }
    }
    form.dispatchEvent(
      new CustomEvent("product:rerender", {
        detail: {
          htmlFragment: newContent,
          productChange
        }
      })
    );
    if (!productChange) {
      form.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            formId: form.id,
            variant: __privateGet(this, _selectedVariant),
            previousVariant
          }
        })
      );
    }
    Shopify?.PaymentButton?.init();
  }
};
_preloadedHtml = new WeakMap();
_delegate4 = new WeakMap();
_intersectionObserver2 = new WeakMap();
_form = new WeakMap();
_selectedVariant = new WeakMap();
_VariantPicker_instances = new WeakSet();
ensureForm_fn = function() {
  const current = __privateGet(this, _form);
  if (current?.isConnected) return current;
  const form = resolveForm(this.getAttribute("form-id"));
  __privateSet(this, _form, form);
  if (form) enableVariantInput(form);
  return form;
};
/**
 * Get the option values for the active combination
 */
getActiveOptionValues_fn = function() {
  const form = __privateMethod(this, _VariantPicker_instances, ensureForm_fn).call(this);
  if (!form) return [];
  return Array.from(form.elements).filter((item) => item.matches("input[data-option-position]:checked")).sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input) => input.value);
};
/**
 * Get the option values for a given input
 */
getOptionValuesFromOption_fn = function(input) {
  const form = __privateMethod(this, _VariantPicker_instances, ensureForm_fn).call(this);
  if (!form) return [input.value];
  const glSizes = glInStockSizeInputs(this, form, input);
  const optionValues = [input, ...Array.from(form.elements).filter((item) => item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`)).map((item) => glSizes?.[item.getAttribute("data-option-position")] || item)].sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input2) => input2.value);
  return optionValues;
};
onOptionChanged_fn = async function(event) {
  if (!event.target.name.includes("option")) {
    return;
  }
  // GL SIZE PRESELECT (added 2026-11-04): a colour change never lands on a sold-out size
  const glSizes = glInStockSizeInputs(this, __privateMethod(this, _VariantPicker_instances, ensureForm_fn).call(this), event.target);
  if (glSizes) Object.values(glSizes).forEach((input) => {
    input.checked = true;
  });
  previewOptionMedia(event.target);
  this.selectCombination({
    optionValues: __privateMethod(this, _VariantPicker_instances, getActiveOptionValues_fn).call(this),
    productChange: event.target.hasAttribute("data-product-url")
  });
};
/**
 * To improve the user experience, we preload a variant whenever the user hovers over a specific option
 */
onOptionPreload_fn = function(event, target) {
  const input = target.control;
  if (input?.closest("quick-buy-modal")) preloadQuickBuyOption(input);
  else warmVariantImage(variantMediaFromInput(input), 720);
  __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, __privateMethod(this, _VariantPicker_instances, getOptionValuesFromOption_fn).call(this, target.control));
};
/**
 * When the variant picker is intersecting the viewport, we warm the other variants' images to improve the user experience
 * so that switching variants is nearly instant
 */
onIntersection_fn = function(entries) {
  if (!entries[0].isIntersecting) return;
  // Other variants' images are warmed once the page has loaded, so they never delay the first view.
  afterPageLoad(() => {
    if (!this.isConnected) return;
    if (!this.closest("quick-buy-modal")) {
      const formId = this.getAttribute("form-id");
      const gallery = formId && document.querySelector(`product-gallery[form="${formId}"]`);
      this.querySelectorAll("input[data-variant-media]").forEach((input) => {
        const media = variantMediaFromInput(input);
        if (!media) return;
        const cell = gallery && media.id && gallery.querySelector(`[data-media-id="${media.id}"]`);
        if (cell) restoreDeferredImages(cell);
        else warmVariantImage(media, 720);
      });
    }
    // Option HTML is no longer requested for every combination (17 section renders on a 10-colour product). It is
    // requested when the shopper hovers or touches an option (onOptionPreload_fn), which comes just before the click.
  });
};
renderForCombination_fn = async function(optionValues) {
  const optionValuesAsString = optionValues.join(",");
  const hashKey = __privateMethod(this, _VariantPicker_instances, createHashKeyForHtml_fn).call(this, optionValuesAsString);
  let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
  let switchedProduct = false;
  const form = __privateMethod(this, _VariantPicker_instances, ensureForm_fn).call(this);
  for (const optionValue of optionValues) {
    const inputForOptionValue = form && Array.from(form.elements).find((item) => item.matches(`input[value="${optionValue}"]`));
    if (inputForOptionValue?.dataset.productUrl) {
      productUrl = inputForOptionValue.dataset.productUrl;
      switchedProduct = true;
      break;
    }
  }
  if (!__privateGet(_VariantPicker, _preloadedHtml).has(hashKey)) {
    const sectionId = this.getAttribute("section-id");
    const sectionQueryParam = !switchedProduct && sectionId ? `&section_id=${sectionId}` : "";
    const promise = new Promise(async (resolve) => {
      resolve(await (await fetch(`${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`)).text());
    });
    __privateGet(_VariantPicker, _preloadedHtml).set(hashKey, { htmlPromise: promise, timestamp: Date.now() });
    if (__privateGet(_VariantPicker, _preloadedHtml).size > 100) {
      __privateGet(_VariantPicker, _preloadedHtml).delete(Array.from(__privateGet(_VariantPicker, _preloadedHtml).keys())[0]);
    }
  }
  return __privateGet(_VariantPicker, _preloadedHtml).get(hashKey).htmlPromise;
};
createHashKeyForHtml_fn = function(optionValuesAsString) {
  return `${optionValuesAsString}-${this.getAttribute("section-id")}`;
};
__privateAdd(_VariantPicker, _preloadedHtml, /* @__PURE__ */ new Map());
var VariantPicker = _VariantPicker;
if (!window.customElements.get("variant-picker")) {
  window.customElements.define("variant-picker", VariantPicker);
}

// js/common/media/base-media.js
import { inView as inView7 } from "vendor";
var BaseMedia = class extends HTMLElement {
  static get observedAttributes() {
    return ["playing"];
  }
  connectedCallback() {
    this._abortController = new AbortController();
    if (this.hasAttribute("autoplay")) {
      afterPageLoad(() => {
        if (this.isConnected) inView7(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
      });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get playing() {
    return this.hasAttribute("playing");
  }
  get player() {
    return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this._playerHandler(target, prop);
        };
      }
    });
  }
  play() {
    this._wantsPlayback = true;
    if (!this.playing) {
      return this.player.play();
    }
  }
  pause() {
    const cancelPending = this._wantsPlayback === true || this.playing;
    this._wantsPlayback = false;
    if (!cancelPending) return;
    this._programmaticPauseAt = performance.now();
    if (this.playing) this.player.pause();
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        // A slide can be asked to play and then scrolled away before the file starts.
        if (this._wantsPlayback === false) {
          this.pause();
          return;
        }
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));
        if (this.hasAttribute("group")) {
          Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
            itemToPause.pause();
          });
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
      }
    }
  }
};

// js/common/media/model.js
var ModelMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    this.player;
  }
  _playerTarget() {
    return new Promise((resolve) => {
      this.setAttribute("loaded", "");
      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            const modelViewer = this.querySelector("model-viewer");
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => this.setAttribute("playing", ""));
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => this.removeAttribute("playing"));
            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
          }
        }
      ]);
    });
  }
  _playerHandler(target, prop) {
    target[prop]();
  }
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
    } else {
      const models = (await ProductLoader.load(this.getAttribute("handle")))["media"].filter((media) => media["media_type"] === "model");
      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
    }
  }
};
if (!window.customElements.get("model-media")) {
  window.customElements.define("model-media", ModelMedia);
}

// js/common/media/video.js
import { inView as inView8 } from "vendor";
var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
var VideoMedia = class extends BaseMedia {
  #mustRemoveControlsAfterSuspend = false;
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("autoplay")) {
      this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
    }
    if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(document.getElementById("video-media-default-template").content.cloneNode(true));
    }
    if (this.getAttribute("type") === "video") {
      inView8(
        this,
        () => {
          this.querySelector("video")?.setAttribute("preload", "metadata");
        },
        { margin: "200px" }
      );
    }
  }
  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute("autoplay") || matchesMediaQuery("md-max");
        const script = document.createElement("script");
        script.type = "text/javascript";
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("playing", "");
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("playing");
                }
              }
            }
          });
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on("play", () => {
            this.setAttribute("playing", "");
          });
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this.setAttribute("loaded", "");
      videoElement.addEventListener("play", () => {
        // A play the theme did not request came from the shopper (video controls, keyboard, OS media keys).
        // Without this, the _wantsPlayback === false left by their pause cancels the play straight away.
        if (this._themePlayPending) {
          this._themePlayPending = false;
        } else {
          this._wantsPlayback = true;
          this._userPausedWhileVisible = false;
        }
        this.setAttribute("playing", "");
        this.removeAttribute("suspended");
        if (this.#mustRemoveControlsAfterSuspend) {
          videoElement.controls = false;
        }
      });
      videoElement.addEventListener("pause", () => {
        this._programmaticPause = false;
        if (!videoElement.seeking && videoElement.paused) {
          this.removeAttribute("playing");
        }
      });
      return videoElement;
    }
  }
  _playerHandler(target, prop) {
    // The pause event arrives after this returns, so the mark stays until that event clears it.
    // An already paused video fires no event, so it is not marked.
    if (prop === "pause" && !this.hasAttribute("host") && !target.paused) this._programmaticPause = true;
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else if (prop === "play" && !this.hasAttribute("host")) {
      this._themePlayPending = true;
      const playPromise = target.play().catch((error) => {
        if (error.name === "NotAllowedError") {
          this.setAttribute("suspended", "");
          if (!this.hasAttribute("controls")) {
            this.#mustRemoveControlsAfterSuspend = true;
            target.controls = true;
          }
          return;
        }
        // load() from a slide reveal can abort the play that just started. Try once more
        // when the file is actually ready, instead of leaving the poster up.
        if (error.name === "AbortError" && this._wantsPlayback && target.paused) {
          const retry = () => {
            if (!this._wantsPlayback || !target.paused) return;
            this._themePlayPending = true;
            target.play().catch(() => {}).finally(() => {
              this._themePlayPending = false;
            });
          };
          if (target.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) retry();
          else target.addEventListener("canplay", retry, { once: true });
        }
      });
      // play() on an already playing video fires no play event, so the mark is cleared here too.
      playPromise.finally(() => {
        this._themePlayPending = false;
      });
    } else {
      target[prop]();
    }
  }
};
if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}

// js/common/navigation/accordion-disclosure.js
import { timeline as timeline5 } from "vendor";

// js/common/navigation/custom-details.js
var _onSummaryClickedListener, _CustomDetails_instances, onSummaryClicked_fn;
var CustomDetails = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _CustomDetails_instances);
    __privateAdd(this, _onSummaryClickedListener, __privateMethod(this, _CustomDetails_instances, onSummaryClicked_fn).bind(this));
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.toggle(true, !event.detail.load));
      this.addEventListener("shopify:block:deselect", (event) => this.toggle(false, !event.detail.load));
    }
  }
  static get observedAttributes() {
    return ["open", "aria-expanded"];
  }
  connectedCallback() {
    const details = this.disclosureElement;
    // theme.js can run while this tag is still being parsed, before <details> exists.
    if (!details || !details.firstElementChild) {
      if (this._detailsObserver) return;
      this._detailsObserver = new MutationObserver(() => {
        if (!this.disclosureElement?.firstElementChild) return;
        this._detailsObserver.disconnect();
        this._detailsObserver = null;
        this.connectedCallback();
      });
      this._detailsObserver.observe(this, { childList: true, subtree: true });
      return;
    }
    if (this._detailsReady) return;
    this._detailsReady = true;
    details.setAttribute("aria-expanded", details.open ? "true" : "false");
    const isLocked = this.hasAttribute("data-locked") || this.classList.contains("accordion--locked");
    if (isLocked) {
      details.open = true;
      details.setAttribute("aria-expanded", "true");
      this.summaryElement.style.pointerEvents = "none";
      this.summaryElement.style.cursor = "default";
    } else {
      this.summaryElement.addEventListener("click", __privateGet(this, _onSummaryClickedListener));
    }
  }
  disconnectedCallback() {
    this._detailsObserver?.disconnect();
    this._detailsObserver = null;
    if (!this._detailsReady) return;
    this._detailsReady = false;
    if (!this.hasAttribute("data-locked") && !this.classList.contains("accordion--locked") && this.summaryElement) {
      this.summaryElement.removeEventListener("click", __privateGet(this, _onSummaryClickedListener));
    }
  }
  get disclosureElement() {
    return this.querySelector("details");
  }
  get summaryElement() {
    return this.disclosureElement.firstElementChild;
  }
  get contentElement() {
    return this.disclosureElement.lastElementChild;
  }
  toggle(force = void 0, animate27 = true) {
    // Prevent toggle if locked
    if (this.hasAttribute("data-locked") || this.classList.contains("accordion--locked")) {
      return;
    }
    
    const newValue = typeof force === "boolean" ? force : !(this.disclosureElement.getAttribute("aria-expanded") === "true");
    if (newValue) {
      this.open({ instant: !animate27 });
    } else {
      this.close();
    }
  }
  async open({ instant = false } = {}) {
    if (this.disclosureElement.getAttribute("aria-expanded") === "true") {
      return;
    }
    this.disclosureElement.open = true;
    this.disclosureElement.setAttribute("aria-expanded", "true");
    const controls = this.createShowAnimationControls();
    if (instant) {
      controls.finish();
    }
  }
  async close() {
    // Prevent closing if locked
    if (this.hasAttribute("data-locked") || this.classList.contains("accordion--locked")) {
      return;
    }
    
    this.disclosureElement.setAttribute("aria-expanded", "false");
    if (!this.disclosureElement.open) {
      return;
    }
    this.createHideAnimationControls()?.finished.then((event) => {
      if (event !== void 0) {
        this.disclosureElement.removeAttribute("open");
      }
    });
  }
  createShowAnimationControls() {
  }
  createHideAnimationControls() {
  }
};
_onSummaryClickedListener = new WeakMap();
_CustomDetails_instances = new WeakSet();
/**
 * By default, when clicking on the summary, the browser directly toggle the "open" attribute, which prevent to
 * perform animation. We therefore block that to allow doing an animation
 */
onSummaryClicked_fn = function(event) {
  event.preventDefault();
  if (this.disclosureElement.open && this.summaryElement.hasAttribute("data-follow-link")) {
    const href = this.summaryElement.getAttribute("data-follow-link");
    window.location.href = href;
    return;
  }
  this.toggle();
};

// js/common/navigation/accordion-disclosure.js
var AccordionDisclosure = class extends CustomDetails {
  async open({ instant = false } = {}) {
    if (this.disclosureElement.getAttribute("aria-expanded") === "true") {
      return;
    }
    this.closeExclusiveSiblings();
    return super.open({ instant });
  }
  closeExclusiveSiblings() {
    const scope = this.closest(".footer__accordion-group") || this.closest(".product:not(.product--editorial)");
    if (!scope) {
      return;
    }
    scope.querySelectorAll("accordion-disclosure").forEach((accordion) => {
      if (accordion === this || accordion.contains(this) || this.contains(accordion)) {
        return;
      }
      if (accordion.disclosureElement?.getAttribute("aria-expanded") === "true") {
        accordion.close();
      }
    });
  }
  createShowAnimationControls() {
    this.disclosureElement.style.overflow = "hidden";
    const animationControls = timeline5([
      [this.disclosureElement, { height: [`${this.summaryElement.clientHeight}px`, `${this.disclosureElement.scrollHeight}px`] }, { duration: 0.25, easing: "ease" }],
      [this.contentElement, { opacity: [0, 1], transform: ["translateY(4px)", `translateY(0)`] }, { duration: 0.15, at: "-0.1" }]
    ]);
    animationControls.finished.then(() => {
      this.disclosureElement.style.height = null;
      this.disclosureElement.style.overflow = null;
    });
    return animationControls;
  }
  createHideAnimationControls() {
    const animationControls = timeline5([
      [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
      [this.disclosureElement, { height: [`${this.disclosureElement.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: "<", easing: "ease" }]
    ]);
    animationControls.finished.then(() => {
      this.disclosureElement.style.height = null;
    });
    return animationControls;
  }
};
if (!window.customElements.get("accordion-disclosure")) {
  window.customElements.define("accordion-disclosure", AccordionDisclosure);
}

// js/common/navigation/menu-disclosure.js
var _hoverTimer, _detectClickOutsideListener, _detectEscKeyboardListener, _detectFocusOutListener, _detectHoverOutsideListener, _detectHoverListener, _MenuDisclosure_instances, handleKeyboard_fn, detectClickOutside_fn, detectHover_fn, detectHoverOutside_fn, detectEscKeyboard_fn, detectFocusOut_fn;
var _MenuDisclosure = class _MenuDisclosure extends CustomDetails {
  constructor() {
    super();
    __privateAdd(this, _MenuDisclosure_instances);
    __privateAdd(this, _hoverTimer);
    __privateAdd(this, _detectClickOutsideListener, __privateMethod(this, _MenuDisclosure_instances, detectClickOutside_fn).bind(this));
    __privateAdd(this, _detectEscKeyboardListener, __privateMethod(this, _MenuDisclosure_instances, detectEscKeyboard_fn).bind(this));
    __privateAdd(this, _detectFocusOutListener, __privateMethod(this, _MenuDisclosure_instances, detectFocusOut_fn).bind(this));
    __privateAdd(this, _detectHoverOutsideListener, __privateMethod(this, _MenuDisclosure_instances, detectHoverOutside_fn).bind(this));
    __privateAdd(this, _detectHoverListener, __privateMethod(this, _MenuDisclosure_instances, detectHover_fn).bind(this));
    this.disclosureElement.addEventListener("mouseover", __privateGet(this, _detectHoverListener).bind(this));
    this.disclosureElement.addEventListener("mouseout", __privateGet(this, _detectHoverListener).bind(this));
    this.disclosureElement.addEventListener("keydown", __privateMethod(this, _MenuDisclosure_instances, handleKeyboard_fn).bind(this));
  }
  /**
   * Get the trigger mode (can be "click" or "hover"). However, for touch devices, it is always forced to click
   * to provide a better user experience
   */
  get trigger() {
    return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
  }
  /**
   * In ms, describe the delay before which we close the menu
   */
  get mouseOverDelayTolerance() {
    return 250;
  }
  /**
   * -------------------------------------------------------------------------------------------------------------------
   * PRIVATE API
   * -------------------------------------------------------------------------------------------------------------------
   */
  async open({ instant = false } = {}) {
    super.open({ instant });
    document.addEventListener("click", __privateGet(this, _detectClickOutsideListener));
    document.addEventListener("keydown", __privateGet(this, _detectEscKeyboardListener));
    document.addEventListener("focusout", __privateGet(this, _detectFocusOutListener));
    document.addEventListener("mouseover", __privateGet(this, _detectHoverOutsideListener));
  }
  async close() {
    super.close();
    document.removeEventListener("click", __privateGet(this, _detectClickOutsideListener));
    document.removeEventListener("keydown", __privateGet(this, _detectEscKeyboardListener));
    document.removeEventListener("focusout", __privateGet(this, _detectFocusOutListener));
    document.removeEventListener("mouseover", __privateGet(this, _detectHoverOutsideListener));
  }
};
_hoverTimer = new WeakMap();
_detectClickOutsideListener = new WeakMap();
_detectEscKeyboardListener = new WeakMap();
_detectFocusOutListener = new WeakMap();
_detectHoverOutsideListener = new WeakMap();
_detectHoverListener = new WeakMap();
_MenuDisclosure_instances = new WeakSet();
/**
 * Handle the keyboard events to ensure we can close the menu with Esc
 *
 * @param {KeyboardEvent} event
 */
handleKeyboard_fn = function(event) {
  if (event.key === "Escape") {
    this.close();
    this.summaryElement.focus();
  }
};
/**
 * When dropdown menu is configured to open on click, we add a listener to detect click outside and automatically
 * close the navigation.
 */
detectClickOutside_fn = function(event) {
  if (this.trigger !== "click") {
    return;
  }
  if (!this.contains(event.target) && !(event.target.closest("details") instanceof _MenuDisclosure)) {
    this.toggle(false);
  }
};
/**
 * On desktop device, if the mode is set to hover, we open/close the dropdown on hover
 */
detectHover_fn = function(event) {
  if (this.trigger !== "hover") {
    return;
  }
  if (event.type === "mouseover") {
    clearTimeout(__privateGet(this, _hoverTimer));
    this.toggle(true);
  } else if (event.type === "mouseout") {
    __privateSet(this, _hoverTimer, setTimeout(() => this.toggle(false), this.mouseOverDelayTolerance));
  }
};
/**
 * Try to detect when the user hover a different link, to immediately close the item without extra delay
 */
detectHoverOutside_fn = function(event) {
  if (this.trigger !== "hover") {
    return;
  }
  const closestDetails = event.target.closest("details");
  if (closestDetails instanceof _MenuDisclosure && closestDetails !== this && !closestDetails.contains(this) && !this.contains(closestDetails)) {
    clearTimeout(__privateGet(this, _hoverTimer));
    this.toggle(false);
  }
};
/**
 * Detect if we hit the "Escape" key to automatically close the dropdown
 */
detectEscKeyboard_fn = function(event) {
  if (event.code === "Escape") {
    const targetMenu = event.target.closest("details[open]");
    if (targetMenu && targetMenu instanceof _MenuDisclosure) {
      targetMenu.toggle(false);
      event.stopPropagation();
    }
  }
};
/**
 * Close the dropdown automatically when the dropdown is focused out
 */
detectFocusOut_fn = function(event) {
  if (event.relatedTarget && !this.contains(event.relatedTarget)) {
    this.toggle(false);
  }
};
var MenuDisclosure = _MenuDisclosure;

// js/common/navigation/tabs.js
import { Delegate as Delegate6, animate as animate10, timeline as timeline6 } from "vendor";
var _componentID, _buttons, _panels, _delegate5, _Tabs_instances, setupComponent_fn, onButtonClicked_fn, onSlotChange_fn, handleKeyboard_fn2;
var Tabs = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _Tabs_instances);
    __privateAdd(this, _componentID, crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));
    __privateAdd(this, _buttons, []);
    __privateAdd(this, _panels, []);
    __privateAdd(this, _delegate5, new Delegate6(this));
    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(this.querySelector("template").content.cloneNode(true));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.selectedIndex = __privateGet(this, _buttons).indexOf(event.target));
    }
    __privateGet(this, _delegate5).on("click", 'button[role="tab"]', __privateMethod(this, _Tabs_instances, onButtonClicked_fn).bind(this));
    this.shadowRoot.addEventListener("slotchange", __privateMethod(this, _Tabs_instances, onSlotChange_fn).bind(this));
    this.addEventListener("keydown", __privateMethod(this, _Tabs_instances, handleKeyboard_fn2));
  }
  static get observedAttributes() {
    return ["selected-index"];
  }
  connectedCallback() {
    __privateMethod(this, _Tabs_instances, setupComponent_fn).call(this);
    this.selectedIndex = this.selectedIndex;
  }
  disconnectedCallback() {
    __privateGet(this, _delegate5).destroy();
  }
  /**
   * --------------------------------------------------------------------------
   * GETTERS AND SETTERS
   * --------------------------------------------------------------------------
   */
  get animationDuration() {
    return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.3;
  }
  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }
  set selectedIndex(index) {
    this.setAttribute("selected-index", Math.min(Math.max(index, 0), Math.max(0, __privateGet(this, _buttons).length - 1)).toString());
    this.style.setProperty("--selected-index", this.selectedIndex.toString());
  }
  /**
   * --------------------------------------------------------------------------
   * METHODS
   * --------------------------------------------------------------------------
   */
  attributeChangedCallback(name, oldValue, newValue) {
    __privateGet(this, _buttons).forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));
    if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
      this.transition(__privateGet(this, _panels)[parseInt(oldValue)], __privateGet(this, _panels)[parseInt(newValue)]);
    }
  }
  /**
   * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
   */
  async transition(fromPanel, toPanel) {
    const beforeHeight = this.clientHeight;
    await animate10(fromPanel, { transform: ["translateY(0px)", "translateY(10px)"], opacity: [1, 0] }, { duration: this.animationDuration }).finished;
    fromPanel.hidden = true;
    toPanel.hidden = false;
    await timeline6([
      [this, { height: [`${beforeHeight}px`, `${this.clientHeight}px`], overflow: ["hidden", "visible"] }, { duration: 0.15, easing: [0.85, 0, 0.15, 1] }],
      [toPanel, { transform: ["translateY(10px)", "translateY(0px)"], opacity: [0, 1] }, { duration: this.animationDuration, at: "+0.1" }]
    ]).finished;
    this.style.removeProperty("height");
  }
};
_componentID = new WeakMap();
_buttons = new WeakMap();
_panels = new WeakMap();
_delegate5 = new WeakMap();
_Tabs_instances = new WeakSet();
setupComponent_fn = function() {
  __privateSet(this, _buttons, Array.from(
    this.shadowRoot.querySelector('slot[name="title"]')?.assignedNodes() ?? [],
    (item) => item.nodeType === 1 && (item.matches("button") && item || item.querySelector("button"))
  ).filter(Boolean));
  __privateSet(this, _panels, Array.from(this.shadowRoot.querySelector('slot[name="content"]')?.assignedNodes() ?? []).filter((node) => node.nodeType === 1));
  __privateGet(this, _buttons).forEach((button, index) => {
    button.setAttribute("role", "tab");
    button.setAttribute("aria-controls", `tab-panel-${__privateGet(this, _componentID)}-${index}`);
    button.id = `tab-${__privateGet(this, _componentID)}-${index}`;
  });
  __privateGet(this, _panels).forEach((panel, index) => {
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", `tab-${__privateGet(this, _componentID)}-${index}`);
    panel.id = `tab-panel-${__privateGet(this, _componentID)}-${index}`;
    panel.hidden = index !== this.selectedIndex;
  });
  this.style.setProperty("--item-count", __privateGet(this, _buttons).length.toString());
};
onButtonClicked_fn = function(event, button) {
  this.selectedIndex = __privateGet(this, _buttons).indexOf(button);
};
onSlotChange_fn = function() {
  __privateMethod(this, _Tabs_instances, setupComponent_fn).call(this);
};
/**
 * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
 * left and right arrow should switch the tab
 */
handleKeyboard_fn2 = function(event) {
  const index = __privateGet(this, _buttons).indexOf(document.activeElement);
  if (index === -1 || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
    return;
  }
  if (event.key === "ArrowLeft") {
    this.selectedIndex = (this.selectedIndex - 1 + __privateGet(this, _buttons).length) % __privateGet(this, _buttons).length;
  } else {
    this.selectedIndex = (this.selectedIndex + 1 + __privateGet(this, _buttons).length) % __privateGet(this, _buttons).length;
  }
  __privateGet(this, _buttons)[this.selectedIndex].focus();
};
if (!window.customElements.get("x-tabs")) {
  window.customElements.define("x-tabs", Tabs);
}

// js/common/search/predictive-search.js
var _listenersAbortController2, _fetchAbortController, _searchForm, _queryInput, _PredictiveSearch_instances, onInputChanged_fn2, onFormSubmitted_fn2, doPredictiveSearch_fn, onSearchCleared_fn;
var PredictiveSearch = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _PredictiveSearch_instances);
    __privateAdd(this, _listenersAbortController2);
    __privateAdd(this, _fetchAbortController);
    __privateAdd(this, _searchForm);
    __privateAdd(this, _queryInput);
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="results"></slot>`));
  }
  connectedCallback() {
    __privateSet(this, _listenersAbortController2, new AbortController());
    __privateSet(this, _searchForm, document.querySelector(`[aria-owns="${this.id}"]`));
    __privateSet(this, _queryInput, __privateGet(this, _searchForm).elements["q"]);
    __privateGet(this, _searchForm).addEventListener("submit", __privateMethod(this, _PredictiveSearch_instances, onFormSubmitted_fn2).bind(this), { signal: __privateGet(this, _listenersAbortController2).signal });
    __privateGet(this, _searchForm).addEventListener("reset", __privateMethod(this, _PredictiveSearch_instances, onSearchCleared_fn).bind(this), { signal: __privateGet(this, _listenersAbortController2).signal });
    __privateGet(this, _queryInput).addEventListener("input", debounce(__privateMethod(this, _PredictiveSearch_instances, onInputChanged_fn2).bind(this), this.autoCompleteDelay, { signal: __privateGet(this, _listenersAbortController2).signal }));
  }
  disconnectedCallback() {
    __privateGet(this, _listenersAbortController2).abort();
  }
  /**
   * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
   * refresh too often, so we recommend to keep the default one
   */
  get autoCompleteDelay() {
    return 280;
  }
  /**
   * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
   * search is simply disabled and only the standard search is used
   */
  supportsPredictiveApi() {
    return JSON.parse(document.getElementById("shopify-features").innerHTML)["predictiveSearch"];
  }
};
_listenersAbortController2 = new WeakMap();
_fetchAbortController = new WeakMap();
_searchForm = new WeakMap();
_queryInput = new WeakMap();
_PredictiveSearch_instances = new WeakSet();
/**
 * Check if the input is not empty, and if so, trigger the predictive search
 */
onInputChanged_fn2 = function() {
  if (__privateGet(this, _queryInput).value === "") {
    return __privateMethod(this, _PredictiveSearch_instances, onSearchCleared_fn).call(this);
  }
  __privateGet(this, _fetchAbortController)?.abort();
  __privateSet(this, _fetchAbortController, new AbortController());
  try {
    return __privateMethod(this, _PredictiveSearch_instances, doPredictiveSearch_fn).call(this);
  } catch (e) {
    if (e.name !== "AbortError") {
      throw e;
    }
  }
};
/**
 * Prevent the form submission if the query is empty
 */
onFormSubmitted_fn2 = function(event) {
  if (__privateGet(this, _queryInput).value === "") {
    return event.preventDefault();
  }
};
function predictiveSearchAllowsSuggestions(searchElement) {
  // Search page setting overrides header when present so both surfaces share one on/off control.
  const pageToggle = document.querySelector("[data-show-search-suggestions]");
  if (pageToggle) {
    return pageToggle.getAttribute("data-show-search-suggestions") !== "false";
  }
  if (!searchElement) return true;
  const value = searchElement.getAttribute("show-suggestions");
  if (value === null) return true;
  return value !== "false" && value !== "0";
}
var predictiveApiRejectedLocale = false;
function predictiveSearchResourceTypes(showSuggestions, supportsPredictiveApi) {
  if (showSuggestions || !supportsPredictiveApi) return "";
  return "&resources[type]=product,collection,article,page";
}
function removePredictiveSearchSuggestions(resultsRoot) {
  if (!resultsRoot) return;
  resultsRoot.querySelectorAll(".predictive-search__suggestions").forEach((node) => {
    const item = node.closest(".predictive-search__resource-item");
    (item || node).remove();
  });
  const resultsBlock = resultsRoot.querySelector(".predictive-search__results");
  if (!resultsBlock) return;
  resultsBlock.classList.remove("predictive-search__results--with-suggestions");
  const hasVisibleResult = resultsBlock.querySelector(".predictive-search__products, .predictive-search__collections, .predictive-search__blog-posts, .predictive-search__pages");
  if (!hasVisibleResult) resultsBlock.remove();
}
// Searches tried in order until one returns products, so a specific product still gets matching or close results:
// the typed words, then the same words in descriptions and SKUs, then fewer words.
function predictiveSearchAttempts(rawQuery, fields) {
  const broadFields = [...new Set([...fields.split(","), "body", "variants.sku"])].join(",");
  const words = rawQuery.trim().split(/\s+/).filter(Boolean);
  const attempts = [{ q: rawQuery, fields }, { q: rawQuery, fields: broadFields }];
  if (words.length > 1) {
    attempts.push({ q: words.slice(0, -1).join(" "), fields: broadFields });
    const longest = words.reduce((a, b) => (b.length > a.length ? b : a));
    if (longest.length >= 3) attempts.push({ q: longest, fields: broadFields });
  }
  return attempts.filter((a, i) => attempts.findIndex((b) => b.q === a.q && b.fields === a.fields) === i);
}
doPredictiveSearch_fn = async function() {
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  const signal = __privateGet(this, _fetchAbortController).signal;
  try {
    const showSuggestions = predictiveSearchAllowsSuggestions(this);
    // Both endpoints match the same product fields (theme settings > Predictive search), so English and Arabic agree.
    // Without this, /search also matches descriptions and returns T-shirts that mention "hoodie" in their text.
    const fields = this.getAttribute("search-fields") || "title,product_type,variants.title,vendor,tag";
    const fetchSection = async ({ q, fields: attemptFields }) => {
      const supportsPredictiveApi = this.supportsPredictiveApi() && !predictiveApiRejectedLocale;
      const baseParams = `q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=10&resources[limit_scope]=each`;
      const encodedFields = encodeURIComponent(attemptFields);
      const suggestOptions = `&resources[options][fields]=${encodedFields}&resources[options][prefix]=last`;
      // Languages without the predictive API (such as Arabic) use standard search; prefix matching keeps partial words working.
      const fallbackUrl = `${window.Shopify.routes.root}search?${baseParams}&options[fields]=${encodedFields}&options[prefix]=last`;
      let response;
      if (supportsPredictiveApi) {
        const url = `${window.Shopify.routes.root}search/suggest`;
        const resourceTypes = predictiveSearchResourceTypes(showSuggestions, supportsPredictiveApi);
        response = await cachedFetch(`${url}?${baseParams}${suggestOptions}${resourceTypes}`, { signal });
        if (!response.ok && resourceTypes) response = await cachedFetch(`${url}?${baseParams}${suggestOptions}`, { signal });
        // Shopify can flag the API as available yet refuse the language (417 "Unsupported buyer locale").
        if (response.status === 417) {
          predictiveApiRejectedLocale = true;
          response = await cachedFetch(fallbackUrl, { signal });
        }
      } else {
        response = await cachedFetch(fallbackUrl, { signal });
      }
      if (!response.ok) return null;
      const tempDoc = new DOMParser().parseFromString(await response.text(), "text/html");
      return tempDoc.querySelector(".shopify-section");
    };
    let section = null;
    const attempts = predictiveSearchAttempts(__privateGet(this, _queryInput).value, fields);
    for (const attempt of attempts) {
      const attemptSection = await fetchSection(attempt);
      if (signal.aborted) return;
      if (!section) section = attemptSection;
      if (attemptSection?.querySelector(".predictive-search__products > *")) {
        section = attemptSection;
        break;
      }
    }
    const results = this.querySelector('[slot="results"]');
    if (section && results) {
      // The section shows "No exact match" only when the card is not that product.
      // A later attempt can still be the exact name, so the notice stays hidden then.
      results.replaceChildren(...document.importNode(section, true).children);
      if (!showSuggestions) removePredictiveSearchSuggestions(results);
    }
    if (!signal.aborted) document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  } catch (error) {
    if (error?.name === "AbortError" || signal.aborted) return;
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    throw error;
  }
};
/**
 * If any search is pending, we abort them, and transition to the idle slot
 */
onSearchCleared_fn = function() {
  __privateGet(this, _fetchAbortController)?.abort();
  __privateGet(this, _queryInput).focus();
  this.querySelector('[slot="results"]').innerHTML = "";
};
if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch);
}

// js/sections/announcement-bar.js
import { animate as animate11 } from "vendor";
var AnnouncementBarCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate11(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate11(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] })
    };
  }
};
if (!window.customElements.get("announcement-bar-carousel")) {
  window.customElements.define("announcement-bar-carousel", AnnouncementBarCarousel);
}

// js/sections/before-after-image.js
import { animate as animate12, inView as inView9 } from "vendor";
var _onPointerMoveListener, _touchStartTimestamp, _BeforeAfter_instances, onPointerDown_fn, onPointerMove_fn, onTouchStart_fn, onPointerUp_fn, onKeyboardNavigation_fn2, calculatePosition_fn, animateInitialPosition_fn;
var BeforeAfter = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _BeforeAfter_instances);
    __privateAdd(this, _onPointerMoveListener, __privateMethod(this, _BeforeAfter_instances, onPointerMove_fn).bind(this));
    __privateAdd(this, _touchStartTimestamp, 0);
    this.addEventListener("pointerdown", __privateMethod(this, _BeforeAfter_instances, onPointerDown_fn));
    this.addEventListener("keydown", __privateMethod(this, _BeforeAfter_instances, onKeyboardNavigation_fn2));
    this.addEventListener("touchstart", __privateMethod(this, _BeforeAfter_instances, onTouchStart_fn), { passive: false });
  }
  connectedCallback() {
    inView9(this, __privateMethod(this, _BeforeAfter_instances, animateInitialPosition_fn).bind(this));
  }
};
_onPointerMoveListener = new WeakMap();
_touchStartTimestamp = new WeakMap();
_BeforeAfter_instances = new WeakSet();
onPointerDown_fn = function(event) {
  if (event.target.tagName === "A") {
    return;
  }
  document.addEventListener("pointerup", __privateMethod(this, _BeforeAfter_instances, onPointerUp_fn).bind(this), { once: true });
  if (matchesMediaQuery("supports-hover")) {
    document.addEventListener("pointermove", __privateGet(this, _onPointerMoveListener));
    __privateMethod(this, _BeforeAfter_instances, calculatePosition_fn).call(this, event);
  }
};
onPointerMove_fn = function(event) {
  __privateMethod(this, _BeforeAfter_instances, calculatePosition_fn).call(this, event);
};
onTouchStart_fn = function(event) {
  const cursor = this.querySelector(".before-after__cursor");
  if (event.target === cursor || cursor.contains(event.target)) {
    event.preventDefault();
    document.addEventListener("pointermove", __privateGet(this, _onPointerMoveListener));
  } else {
    __privateSet(this, _touchStartTimestamp, event.timeStamp);
  }
};
onPointerUp_fn = function(event) {
  document.removeEventListener("pointermove", __privateGet(this, _onPointerMoveListener));
  if (!matchesMediaQuery("supports-hover")) {
    if (event.timeStamp - __privateGet(this, _touchStartTimestamp) <= 250) {
      __privateMethod(this, _BeforeAfter_instances, calculatePosition_fn).call(this, event);
    }
  }
};
onKeyboardNavigation_fn2 = function(event) {
  if (!event.target.classList.contains("before-after__cursor") || !this.hasAttribute("vertical") && event.code !== "ArrowLeft" && event.code !== "ArrowRight" || this.hasAttribute("vertical") && event.code !== "ArrowUp" && event.code !== "ArrowDown") {
    return;
  }
  event.preventDefault();
  let currentPosition = parseInt(this.style.getPropertyValue("--before-after-cursor-position"));
  if (Number.isNaN(currentPosition)) {
    currentPosition = parseInt(getComputedStyle(this).getPropertyValue("--before-after-initial-cursor-position"));
  }
  let newPosition;
  if (this.hasAttribute("vertical")) {
    newPosition = event.code === "ArrowUp" ? currentPosition - 1 : currentPosition + 1;
  } else {
    newPosition = event.code === "ArrowLeft" ? currentPosition - 1 : currentPosition + 1;
  }
  this.style.setProperty("--before-after-cursor-position", `${Math.min(Math.max(newPosition, 0), 100)}%`);
};
calculatePosition_fn = function(event) {
  let rectangle = this.getBoundingClientRect(), percentage;
  if (this.hasAttribute("vertical")) {
    percentage = (event.clientY - rectangle.top) / this.clientHeight * 100;
  } else {
    percentage = (event.clientX - rectangle.left) / this.clientWidth * 100;
    percentage = document.dir === "rtl" ? 100 - percentage : percentage;
  }
  this.style.setProperty("--before-after-cursor-position", `${Math.min(Math.max(percentage, 0), 100)}%`);
};
animateInitialPosition_fn = function() {
  animate12(
    (progress) => {
      this.style.setProperty("--before-after-cursor-position", `calc(var(--before-after-initial-cursor-position) * ${progress})`);
    },
    { duration: 0.6, easing: [0.85, 0, 0.15, 1] }
  );
};
if (!window.customElements.get("before-after")) {
  window.customElements.define("before-after", BeforeAfter);
}

// js/sections/blog-posts.js
import { animate as animate13, stagger as stagger2, inView as inView10 } from "vendor";
var _BlogPosts_instances, reveal_fn;
var BlogPosts = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _BlogPosts_instances);
    if (this.hasAttribute("reveal-on-scroll") && matchesMediaQuery("motion-safe")) {
      inView10(this, __privateMethod(this, _BlogPosts_instances, reveal_fn).bind(this), { margin: "-50px 0px" });
    }
  }
};
_BlogPosts_instances = new WeakSet();
reveal_fn = function() {
  this.style.opacity = "1";
  animate13(this.children, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.25, delay: stagger2(0.1, { easing: "ease-out" }), easing: "ease" });
};
if (!window.customElements.get("blog-posts")) {
  window.customElements.define("blog-posts", BlogPosts);
}

// js/sections/cart-drawer.js
import { animate as animate14, timeline as timeline7 } from "vendor";
var _sectionId, _CartDrawer_instances, onBundleSection_fn, onCartChange_fn, onBeforeShow_fn, onPageShow_fn, refreshCart_fn, replaceContent_fn, scheduleImageWarmup_fn;
var CartDrawer = class extends Drawer {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDrawer_instances);
    __privateAdd(this, _sectionId);
  }
  connectedCallback() {
    super.connectedCallback();
    this.dataset.cartBound = "true";
    __privateGet(this, _sectionId) ?? __privateSet(this, _sectionId, extractSectionId(this));
    document.addEventListener("cart:prepare-bundled-sections", __privateMethod(this, _CartDrawer_instances, onBundleSection_fn).bind(this), { signal: this.abortController.signal });
    document.addEventListener("cart:change", __privateMethod(this, _CartDrawer_instances, onCartChange_fn).bind(this), { signal: this.abortController.signal });
    document.addEventListener("cart:refresh", __privateMethod(this, _CartDrawer_instances, refreshCart_fn).bind(this), { signal: this.abortController.signal });
    window.addEventListener("pageshow", __privateMethod(this, _CartDrawer_instances, onPageShow_fn).bind(this), { signal: this.abortController.signal });
    this.addEventListener("dialog:before-show", __privateMethod(this, _CartDrawer_instances, onBeforeShow_fn));
    __privateMethod(this, _CartDrawer_instances, scheduleImageWarmup_fn).call(this);
    this.dispatchEvent(new CustomEvent("cart-drawer:ready", { bubbles: true }));
  }
  /**
   * Faster slide-in than the default drawer, so the bag feels instant when it opens
   */
  createEnterAnimationControls() {
    this.getShadowPartByName("content").style.marginInlineStart = this.openFrom === "right" ? "auto" : 0;
    return timeline7([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.2, easing: "ease-out" }],
      [
        this.getShadowPartByName("content"),
        { transform: [`translateX(calc(var(--transform-logical-flip) * ${this.openFrom === "right" ? "100%" : "-100%"}))`, "translateX(0)"] },
        { duration: 0.22, at: "<", easing: [0.22, 1, 0.36, 1] }
      ]
    ]);
  }
  hideForOutsideClickTarget(target) {
    if (target?.closest?.("[data-product-card-add], [data-product-card-size-trigger], [data-product-card-size-modal], product-form [type='submit']")) return false;
    return !this.contains(target);
  }
  scrollAddedLineIntoView() {
    scrollCartDrawerToAddedLine(this);
  }
};
/**
 * Line item photos are lazy in the markup and the closed drawer is display: none, so a lazy photo would only start
 * downloading when the drawer opens. They switch to eager once the page is idle (low priority), and right away with
 * high priority when the shopper reaches for the cart or adds a product.
 */
function warmCartDrawerImages(root, priority) {
  root?.querySelectorAll?.("img.line-item__media").forEach((image) => {
    if (priority === "high" || image.fetchPriority === "auto" || !image.fetchPriority) image.fetchPriority = priority;
    if (image.loading !== "eager") image.loading = "eager";
  });
}
_sectionId = new WeakMap();
_CartDrawer_instances = new WeakSet();
/**
 * This method is called when the cart is changing, and allow custom sections to order a "re-render"
 */
onBundleSection_fn = function(event) {
  event.detail.sections.push(__privateGet(this, _sectionId));
};
onCartChange_fn = async function(event) {
  const sectionId = __privateGet(this, _sectionId);
  let html = event.detail?.cart?.sections?.[sectionId];
  if (!html) {
    try {
      html = await (await fetch(`${Shopify.routes.root}?section_id=${sectionId}`, { cache: "no-store" })).text();
    } catch (error) {
      return;
    }
  }
  await __privateMethod(this, _CartDrawer_instances, replaceContent_fn).call(this, html);
  if ((window.themeVariables.settings.cartType === "drawer" || event.detail["onSuccessDo"] === "force_open_drawer") && event.detail.baseEvent === "variant:add") {
    this.show();
  }
};
onBeforeShow_fn = function() {
  warmCartDrawerImages(this, "high");
  const drawerFooter = this.shadowRoot.querySelector('[part="footer"]');
  if (!drawerFooter) {
    return;
  }
  if (this.classList.contains("cart-drawer--adding")) {
    drawerFooter.style.opacity = "1";
    drawerFooter.style.transform = "none";
    return;
  }
  drawerFooter.style.opacity = "0";
  animate14(drawerFooter, { opacity: [0, 1], transform: ["translateY(16px)", "translateY(0)"] }, { duration: 0.2, delay: 0.08, easing: [0.25, 0.46, 0.45, 0.94] });
};
scheduleImageWarmup_fn = function() {
  const warmNow = (event) => {
    if (event.target?.closest?.('[aria-controls="cart-drawer"]')) warmCartDrawerImages(this, "high");
  };
  const options = { passive: true, signal: this.abortController.signal };
  document.addEventListener("pointerover", warmNow, options);
  document.addEventListener("touchstart", warmNow, options);
  document.addEventListener("focusin", warmNow, options);
  const warmIdle = () => {
    const run = () => warmCartDrawerImages(this, "low");
    if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 300);
  };
  if (document.readyState === "complete") warmIdle();
  else window.addEventListener("load", warmIdle, { once: true, signal: this.abortController.signal });
};
onPageShow_fn = async function(event) {
  if (!event.persisted) {
    return;
  }
  __privateMethod(this, _CartDrawer_instances, refreshCart_fn).call(this);
};
refreshCart_fn = async function() {
  __privateMethod(this, _CartDrawer_instances, replaceContent_fn).call(this, await (await fetch(`${Shopify.routes.root}?section_id=${__privateGet(this, _sectionId)}`, { cache: "no-store" })).text());
};
function bridgeDecodedLineMedia(drawer, fragment) {
  fragment.querySelectorAll("line-item").forEach((line) => {
    const variantId = line.getAttribute("data-variant-id");
    if (!variantId) return;
    const live = drawer.querySelector(`line-item[data-variant-id="${CSS.escape(variantId)}"] img.line-item__media`);
    const next = line.querySelector("img.line-item__media");
    if (!live || !next || live.naturalWidth < 1) return;
    const shown = live.currentSrc || live.src;
    if (!shown || next.currentSrc === shown || next.getAttribute("src") === shown) return;
    const finalSrc = next.getAttribute("src");
    const finalSrcset = next.getAttribute("srcset");
    next.removeAttribute("srcset");
    next.src = shown;
    if (!finalSrc && !finalSrcset) return;
    const probe = new Image();
    probe.decoding = "async";
    const upgrade = () => {
      if (!next.isConnected) return;
      if (finalSrcset) next.setAttribute("srcset", finalSrcset);
      if (finalSrc) next.src = finalSrc;
    };
    probe.addEventListener("load", upgrade, { once: true });
    if (finalSrcset) probe.srcset = finalSrcset;
    if (finalSrc) probe.src = finalSrc;
  });
}
replaceContent_fn = async function(html) {
  const domElement = new DOMParser().parseFromString(html, "text/html"), section = domElement.getElementById(`shopify-section-${__privateGet(this, _sectionId)}`) || domElement.querySelector(".shopify-section"), drawer = section?.querySelector("cart-drawer") || domElement.querySelector("cart-drawer");
  if (!drawer) {
    return;
  }
  const newCartDrawer = document.createRange().createContextualFragment(drawer.innerHTML);
  // The drawer usually opens right after a cart change, so its photos start downloading before they are inserted
  warmCartDrawerImages(newCartDrawer, "high");
  bridgeDecodedLineMedia(this, newCartDrawer);
  const countNode = drawer.querySelector(".cart-drawer__count");
  const parsedCount = countNode ? parseInt(countNode.textContent.replace(/\D/g, ""), 10) : NaN;
  const itemCount = Number.isFinite(parsedCount) ? parsedCount : drawer.classList.contains("drawer--center-body") ? 0 : (await fetchCart)["item_count"];
  if (itemCount === 0 && !this.classList.contains("cart-drawer--adding")) {
    const controls = timeline7([
      [this.getShadowPartByName("body"), { opacity: [1, 0] }, { duration: 0.15, easing: "ease-in" }],
      [this.getShadowPartByName("footer"), { opacity: [1, 0], transform: ["translateY(0)", "translateY(30px)"] }, { duration: 0.15, at: "<", easing: "ease-in" }]
    ]);
    await controls.finished;
    this.replaceChildren(...newCartDrawer.children);
    animate14(this.getShadowPartByName("body"), { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.25, easing: [0.25, 0.46, 0.45, 0.94] });
  } else {
    this.replaceChildren(...newCartDrawer.children);
  }
  this.classList.toggle("drawer--center-body", itemCount === 0);
  convertCartDrawerPrices();
  if (this.dataset.addedVariant) {
    scrollCartDrawerToAddedLine(this);
    requestAnimationFrame(() => requestAnimationFrame(() => scrollCartDrawerToAddedLine(this)));
  }
  this.dispatchEvent(new CustomEvent("cart-drawer:refreshed", { bubbles: true }));
};
var CartNoteDialog = class extends DialogElement {
  createEnterAnimationControls() {
    return animate14(this, { transform: ["translateY(100%)", "translateY(0)"] }, { duration: 0.2, easing: "ease-in" });
  }
  createLeaveAnimationControls() {
    return animate14(this, { transform: ["translateY(0)", "translateY(100%)"] }, { duration: 0.2, easing: "ease-in" });
  }
};
if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}
if (!window.customElements.get("cart-note-dialog")) {
  window.customElements.define("cart-note-dialog", CartNoteDialog);
}

// js/sections/collection.js
import { timeline as timeline8, inView as inView11, Delegate as Delegate7 } from "vendor";
var _CollectionBanner_instances, reveal_fn2;
var CollectionBanner = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CollectionBanner_instances);
  }
  connectedCallback() {
    if (this.hasAttribute("reveal-on-scroll") && matchesMediaQuery("motion-safe")) {
      inView11(this, __privateMethod(this, _CollectionBanner_instances, reveal_fn2).bind(this));
    }
  }
};
_CollectionBanner_instances = new WeakSet();
reveal_fn2 = async function() {
  const image = this.querySelector(".content-over-media > picture img, .content-over-media > image-parallax img"), hasParallax = this.querySelector(".content-over-media image-parallax") !== null, content = this.querySelector(".content-over-media > .prose");
  await imageLoaded(image);
  const transformEffect = 0.15 * 100 / 1.3;
  const imageTransform = hasParallax ? [`scale(1.5) translateY(-${transformEffect}%)`, `scale(1.3) translateY(-${transformEffect}%)`] : ["scale(1.2)", "scale(1)"];
  return timeline8([
    [this, { opacity: 1 }, { duration: 0, easing: [0.25, 0.46, 0.45, 0.94] }],
    [image, { opacity: [0, 1], transform: imageTransform }, { duration: 0.8, delay: 0.25, at: "<", easing: [0.25, 0.46, 0.45, 0.94] }],
    [content, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }]
  ]);
};
var _delegate6, _CollectionLayoutSwitch_instances, onLayoutSwitch_fn, setCartAttribute_fn;
var CollectionLayoutSwitch = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CollectionLayoutSwitch_instances);
    __privateAdd(this, _delegate6, new Delegate7(this));
  }
  connectedCallback() {
    __privateGet(this, _delegate6).on("click", 'button[type="button"]', __privateMethod(this, _CollectionLayoutSwitch_instances, onLayoutSwitch_fn).bind(this));
  }
  get controlledList() {
    return document.getElementById(this.getAttribute("aria-controls"));
  }
};
_delegate6 = new WeakMap();
_CollectionLayoutSwitch_instances = new WeakSet();
function updateProductListImageSizes(list) {
  const mobileSizes = list.getAttribute(`data-sizes-mobile-${list.getAttribute("collection-mobile-layout")}`), desktopSizes = list.getAttribute(`data-sizes-desktop-${list.getAttribute("collection-desktop-layout")}`);
  if (!mobileSizes || !desktopSizes) {
    return;
  }
  const sizes = `(max-width: 699px) ${mobileSizes}, ${desktopSizes}`;
  list.querySelectorAll(".product-card__image").forEach((image) => {
    if (image.getAttribute("sizes") !== sizes) {
      image.setAttribute("sizes", sizes);
    }
  });
}
onLayoutSwitch_fn = function(event, target) {
  if (target.classList.contains("is-active")) {
    return;
  }
  this.controlledList.setAttribute(`collection-${this.getAttribute("device")}-layout`, target.value);
  updateProductListImageSizes(this.controlledList);
  Array.from(this.querySelectorAll("button")).forEach((item) => item.classList.toggle("is-active", item === target));
  this.controlledList.reveal();
  __privateMethod(this, _CollectionLayoutSwitch_instances, setCartAttribute_fn).call(this, target.value);
};
setCartAttribute_fn = function(newLayout) {
  const attributeProperty = `products_${this.getAttribute("device")}_grid_mode`;
  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      attributes: {
        [attributeProperty]: newLayout
      }
    }),
    keepalive: true
    // Allows to make sure the request is fired even when submitting the form
  });
};
if (!window.customElements.get("collection-banner")) {
  window.customElements.define("collection-banner", CollectionBanner);
}
if (!window.customElements.get("collection-layout-switch")) {
  window.customElements.define("collection-layout-switch", CollectionLayoutSwitch);
}

// js/sections/countdown-timer.js
import { animate as animate15, inView as inView12 } from "vendor";
var _flips, _expirationDate, _interval, _isVisible, _CountdownTimer_instances, recalculateFlips_fn;
var CountdownTimer = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CountdownTimer_instances);
    __privateAdd(this, _flips);
    __privateAdd(this, _expirationDate);
    __privateAdd(this, _interval);
    __privateAdd(this, _isVisible);
  }
  connectedCallback() {
    __privateSet(this, _flips, Array.from(this.querySelectorAll("countdown-timer-flip")));
    const expiresAt = this.getAttribute("expires-at");
    if (expiresAt !== "") {
      __privateSet(this, _expirationDate, new Date(expiresAt));
      __privateSet(this, _interval, setInterval(__privateMethod(this, _CountdownTimer_instances, recalculateFlips_fn).bind(this), 1e3));
      __privateMethod(this, _CountdownTimer_instances, recalculateFlips_fn).call(this);
    }
    inView12(
      this,
      () => {
        __privateSet(this, _isVisible, true);
        return () => __privateSet(this, _isVisible, false);
      },
      { margin: "500px" }
    );
  }
  disconnectedCallback() {
    clearInterval(__privateGet(this, _interval));
  }
  get daysFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "days");
  }
  get hoursFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "hours");
  }
  get minutesFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "minutes");
  }
  get secondsFlip() {
    return __privateGet(this, _flips).find((flip) => flip.getAttribute("type") === "seconds");
  }
};
_flips = new WeakMap();
_expirationDate = new WeakMap();
_interval = new WeakMap();
_isVisible = new WeakMap();
_CountdownTimer_instances = new WeakSet();
recalculateFlips_fn = function() {
  const dateNow = /* @__PURE__ */ new Date();
  if (__privateGet(this, _expirationDate) < dateNow) {
    if (this.getAttribute("expiration-behavior") === "hide") {
      this.closest(".shopify-section").remove();
    } else {
      return clearInterval(__privateGet(this, _interval));
    }
  }
  if (!__privateGet(this, _isVisible)) {
    return;
  }
  let delta = Math.abs(__privateGet(this, _expirationDate) - dateNow) / 1e3;
  const days = Math.floor(delta / 86400);
  delta -= days * 86400;
  const hours = Math.floor(delta / 3600) % 24;
  delta -= hours * 3600;
  const minutes = Math.floor(delta / 60) % 60;
  delta -= minutes * 60;
  const seconds = Math.floor(delta % 60);
  this.daysFlip?.updateValue(days);
  this.hoursFlip?.updateValue(hours);
  this.minutesFlip?.updateValue(minutes);
  this.secondsFlip?.updateValue(seconds);
};
var CountdownTimerFlip = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    let flipHtml = [...this.textContent].map(
      () => `<countdown-timer-flip-digit part="digit" ${this.hasAttribute("animate") ? "animate" : ""} style="display: inline-block">0</countdown-timer-flip-digit>`
    );
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(flipHtml.join("")));
  }
  updateValue(value) {
    const newValue = Math.min(99, value).toString().padStart(2, "0");
    [...newValue].forEach((digit, index) => {
      this.shadowRoot.children[index].setAttribute("number", digit);
    });
  }
};
var CountdownTimerFlipDigit = class extends HTMLElement {
  static observedAttributes = ["number"];
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment("<div><slot></slot></div>"));
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.hasAttribute("animate")) {
      return this.textContent = newValue;
    }
    await animate15(this.shadowRoot.firstElementChild, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-8px)"] }, { duration: 0.3, easing: [0.64, 0, 0.78, 0] }).finished;
    this.textContent = newValue;
    animate15(this.shadowRoot.firstElementChild, { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0px)"] }, { duration: 0.3, easing: [0.22, 1, 0.36, 1] });
  }
};
if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}
if (!window.customElements.get("countdown-timer-flip")) {
  window.customElements.define("countdown-timer-flip", CountdownTimerFlip);
}
if (!window.customElements.get("countdown-timer-flip-digit")) {
  window.customElements.define("countdown-timer-flip-digit", CountdownTimerFlipDigit);
}

// js/sections/dynamic-grid.js
import { animate as animate16, scroll as scroll2 } from "vendor";
var _DynamicGrid_instances, setupEmulateScrollTimeline_fn;
var DynamicGrid = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _DynamicGrid_instances);
    if (window.Shopify.designMode) {
      this.closest(".shopify-section")?.addEventListener("shopify:section:select", () => this.classList.add("editor-is-selected"));
      this.closest(".shopify-section")?.addEventListener("shopify:section:deselect", () => this.classList.remove("editor-is-selected"));
      this.addEventListener("shopify:block:select", (event) => event.target.classList.add("editor-is-selected"));
      this.addEventListener("shopify:block:deselect", (event) => event.target.classList.remove("editor-is-selected"));
    }
  }
  connectedCallback() {
    if (!("ViewTimeline" in window)) {
      __privateMethod(this, _DynamicGrid_instances, setupEmulateScrollTimeline_fn).call(this);
    }
  }
};
_DynamicGrid_instances = new WeakSet();
/**
 * On older browsers, we fallback to a more standard "scroll listener" based approach, by using MotionJS.
 */
setupEmulateScrollTimeline_fn = function() {
  Array.from(this.querySelectorAll(".dynamic-grid__cell")).forEach((element) => {
    const parallaxSpeed = parseInt(element.style.getPropertyValue("--parallax-speed"));
    if (parallaxSpeed === 0) {
      return;
    }
    const offsetAmount = `${parallaxSpeed * 10 / 2}vmin`;
    scroll2(
      animate16(element, {
        transform: element.getAttribute("data-parallax-direction") === "horizontal" ? [`translateX(calc(-1 * ${offsetAmount}))`, `translateX(${offsetAmount})`] : [`translateY(calc(-1 * ${offsetAmount}))`, `translateY(${offsetAmount})`]
      }),
      {
        offset: ["start end", "end start"],
        target: this.closest(".shopify-section")
      }
    );
  });
};
if (!window.customElements.get("dynamic-grid")) {
  window.customElements.define("dynamic-grid", DynamicGrid);
}

// js/sections/customer.js
import { animate as animate17 } from "vendor";
var _AccountLogin_instances, loginForm_get, recoverForm_get, switchForm_fn;
var AccountLogin = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _AccountLogin_instances);
    window.addEventListener("hashchange", __privateMethod(this, _AccountLogin_instances, switchForm_fn).bind(this));
    if (window.location.hash === "#recover") {
      __privateGet(this, _AccountLogin_instances, loginForm_get).hidden = true;
      __privateGet(this, _AccountLogin_instances, recoverForm_get).hidden = false;
    }
  }
};
_AccountLogin_instances = new WeakSet();
loginForm_get = function() {
  return this.querySelector("#login");
};
recoverForm_get = function() {
  return this.querySelector("#recover");
};
switchForm_fn = async function() {
  const fromForm = window.location.hash === "#recover" ? __privateGet(this, _AccountLogin_instances, loginForm_get) : __privateGet(this, _AccountLogin_instances, recoverForm_get), toForm = window.location.hash === "#recover" ? __privateGet(this, _AccountLogin_instances, recoverForm_get) : __privateGet(this, _AccountLogin_instances, loginForm_get);
  await animate17(fromForm, { transform: ["translateY(0)", "translateY(30px)"], opacity: [1, 0] }, { duration: 0.6, easing: "ease" }).finished;
  fromForm.hidden = true;
  toForm.hidden = false;
  await animate17(toForm, { transform: ["translateY(30px)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.6, easing: "ease" });
};
if (!window.customElements.get("account-login")) {
  window.customElements.define("account-login", AccountLogin);
}

// js/sections/faq.js
var _observer, _FaqToc_instances, onObserve_fn;
var FaqToc = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _FaqToc_instances);
    __privateAdd(this, _observer, new IntersectionObserver(__privateMethod(this, _FaqToc_instances, onObserve_fn).bind(this), { rootMargin: "0px 0px -70% 0px" }));
  }
  connectedCallback() {
    this.anchoredElements.forEach((anchoredElement) => __privateGet(this, _observer).observe(anchoredElement));
  }
  disconnectedCallback() {
    __privateGet(this, _observer).disconnect();
  }
  get anchorLinks() {
    return Array.from(this.querySelectorAll('a[href^="#"]'));
  }
  get anchoredElements() {
    return this.anchorLinks.map((anchor) => document.querySelector(anchor.getAttribute("href")));
  }
};
_observer = new WeakMap();
_FaqToc_instances = new WeakSet();
onObserve_fn = function(entries) {
  for (const entry of entries) {
    const anchorLink = this.anchorLinks.find((anchor) => anchor.getAttribute("href") === `#${entry.target.id}`);
    if (!entry.isIntersecting && anchorLink.classList.contains("is-active")) {
      continue;
    }
    if (entry.isIntersecting) {
      this.anchorLinks.forEach((link) => link.classList.toggle("is-active", link === anchorLink));
    }
  }
};
if (!window.customElements.get("faq-toc")) {
  window.customElements.define("faq-toc", FaqToc);
}

// js/sections/featured-collections.js
import { animate as animate18 } from "vendor";
var FeaturedCollectionsCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate18(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(15px)"] }, { duration: 0.3, easing: "ease-in" }),
      enterControls: () => animate18(toSlide, { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.2, delay: 0.2, easing: "ease-out" })
    };
  }
};
if (!window.customElements.get("featured-collections-carousel")) {
  window.customElements.define("featured-collections-carousel", FeaturedCollectionsCarousel);
}

// js/sections/header.js
import { animate as animate19, timeline as timeline9, stagger as stagger3, Delegate as Delegate8 } from "vendor";
var _headerTrackerIntersectionObserver, _abortController11, _scrollYTrackingPosition, _isVisible2, _Header_instances, onHeaderTrackerIntersection_fn, detectMousePosition_fn, detectScrollDirection_fn, setVisibility_fn;
var Header = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _Header_instances);
    __privateAdd(this, _headerTrackerIntersectionObserver, new IntersectionObserver(__privateMethod(this, _Header_instances, onHeaderTrackerIntersection_fn).bind(this)));
    __privateAdd(this, _abortController11);
    __privateAdd(this, _scrollYTrackingPosition, 0);
    __privateAdd(this, _isVisible2, true);
  }
  connectedCallback() {
    __privateSet(this, _abortController11, new AbortController());
    __privateGet(this, _headerTrackerIntersectionObserver).observe(document.getElementById("header-scroll-tracker"));
    if (this.hasAttribute("hide-on-scroll")) {
      window.addEventListener("scroll", __privateMethod(this, _Header_instances, detectScrollDirection_fn).bind(this), { signal: __privateGet(this, _abortController11).signal });
      window.addEventListener("pointermove", __privateMethod(this, _Header_instances, detectMousePosition_fn).bind(this), { signal: __privateGet(this, _abortController11).signal });
    }
  }
  disconnectedCallback() {
    __privateGet(this, _abortController11).abort();
  }
  resetForNavigation() {
    __privateSet(this, _isVisible2, true);
    __privateSet(this, _scrollYTrackingPosition, 0);
    this.classList.remove("is-hidden");
    document.documentElement.style.setProperty("--header-is-visible", "1");
  }
};
_headerTrackerIntersectionObserver = new WeakMap();
_abortController11 = new WeakMap();
_scrollYTrackingPosition = new WeakMap();
_isVisible2 = new WeakMap();
_Header_instances = new WeakSet();
onHeaderTrackerIntersection_fn = function(entries) {
  this.classList.toggle("is-solid", !entries[0].isIntersecting);
};
detectMousePosition_fn = function(event) {
  if (event.clientY < 100 && window.matchMedia("screen and (pointer: fine)").matches) {
    __privateMethod(this, _Header_instances, setVisibility_fn).call(this, true);
    __privateSet(this, _scrollYTrackingPosition, 0);
  }
};
detectScrollDirection_fn = function() {
  let isVisible;
  const scrollY = Math.max(0, window.scrollY);
  if (scrollY > __privateGet(this, _scrollYTrackingPosition) && scrollY - __privateGet(this, _scrollYTrackingPosition) > 100) {
    isVisible = false;
    __privateSet(this, _scrollYTrackingPosition, scrollY);
  } else if (window.scrollY < __privateGet(this, _scrollYTrackingPosition)) {
    __privateSet(this, _scrollYTrackingPosition, scrollY);
    isVisible = true;
  }
  if (isVisible !== void 0) {
    __privateMethod(this, _Header_instances, setVisibility_fn).call(this, isVisible);
  }
};
setVisibility_fn = function(isVisible) {
  if (isVisible !== __privateGet(this, _isVisible2)) {
    if (!isVisible && this.querySelectorAll(":where(.header__primary-nav, .header__secondary-nav) [open]").length > 0) {
      return;
    }
    __privateSet(this, _isVisible2, isVisible);
    document.documentElement.style.setProperty("--header-is-visible", isVisible ? "1" : "0");
    this.classList.toggle("is-hidden", !isVisible);
  }
};
var DropdownMenuDisclosure = class extends MenuDisclosure {
  createShowAnimationControls() {
    let menuItemsSequence = [];
    if (window.themeVariables.settings.staggerMenuApparition) {
      menuItemsSequence = [
        this.contentElement.querySelectorAll(":scope > li"),
        { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] },
        { duration: 0.15, at: "-0.15", delay: stagger3(0.1) }
      ];
    }
    return timeline9([[this.contentElement, { opacity: [0, 1] }, { duration: 0.25 }], menuItemsSequence]);
  }
  createHideAnimationControls() {
    return timeline9([[this.contentElement, { opacity: [1, 0] }, { duration: 0.4 }]]);
  }
};
var MegaMenuDisclosure = class extends MenuDisclosure {
  createShowAnimationControls() {
    const linklists = Array.from(this.contentElement.querySelectorAll(".mega-menu__linklist > li"));
    let menuItemsSequence = [];
    if (window.themeVariables.settings.staggerMenuApparition) {
      menuItemsSequence = [
        { name: "content", at: "-0.5" },
        [linklists, { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] }, { duration: 0.3, at: "content", delay: stagger3(0.1) }],
        [this.contentElement.querySelector(".mega-menu__promo"), { opacity: [0, 1] }, { duration: 0.3, at: "-0.15" }]
      ];
    }
    return timeline9([[this.contentElement, { opacity: [0, 1] }, { duration: 0.25 }], ...menuItemsSequence]);
  }
  createHideAnimationControls() {
    return timeline9([[this.contentElement, { opacity: [1, 0] }, { duration: 0.4 }]]);
  }
};
var _HeaderSearch_instances, calculateMaxHeight_fn;
var HeaderSearch = class extends DialogElement {
  constructor() {
    super();
    __privateAdd(this, _HeaderSearch_instances);
    this.addEventListener("dialog:before-show", __privateMethod(this, _HeaderSearch_instances, calculateMaxHeight_fn).bind(this));
  }
  get shadowDomTemplate() {
    return "header-search-default-template";
  }
  get shouldLock() {
    return true;
  }
  createEnterAnimationControls() {
    return timeline9([
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.2, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { opacity: [0, 1], transform: ["translateY(calc(-1 * var(--header-height)))", "translateY(0)"] },
        { duration: 0.2, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
  createLeaveAnimationControls() {
    return timeline9([
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.2, easing: [0.645, 0.045, 0.355, 1] }],
      [
        this.getShadowPartByName("content"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(calc(-1 * var(--header-height)))"] },
        { duration: 0.2, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ]
    ]);
  }
};
_HeaderSearch_instances = new WeakSet();
calculateMaxHeight_fn = function() {
  const boundingRect = this.getBoundingClientRect(), maxHeight = window.innerHeight - boundingRect.top;
  this.style.setProperty("--header-search-max-height", `${maxHeight}px`);
};
var _collapsiblePanel, _buttonElements, _HeaderSidebar_instances, openCollapsiblePanel_fn, onSidebarBeforeShow_fn, onSidebarAfterShow_fn, onSidebarBeforeHide_fn, onSidebarAfterHide_fn;
var HeaderSidebar = class extends Drawer {
  constructor() {
    super();
    __privateAdd(this, _HeaderSidebar_instances);
    __privateAdd(this, _collapsiblePanel);
    __privateAdd(this, _buttonElements);
    this.addEventListener("dialog:before-show", __privateMethod(this, _HeaderSidebar_instances, onSidebarBeforeShow_fn));
    this.addEventListener("dialog:after-show", __privateMethod(this, _HeaderSidebar_instances, onSidebarAfterShow_fn));
    this.addEventListener("dialog:before-hide", __privateMethod(this, _HeaderSidebar_instances, onSidebarBeforeHide_fn));
    this.addEventListener("dialog:after-hide", __privateMethod(this, _HeaderSidebar_instances, onSidebarAfterHide_fn));
  }
  connectedCallback() {
    super.connectedCallback();
    __privateSet(this, _collapsiblePanel, this.querySelector('[slot="collapsible-panel"]'));
    __privateSet(this, _buttonElements, Array.from(this.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist [aria-controls]")));
    __privateGet(this, _buttonElements).forEach((button) => button.addEventListener("click", __privateMethod(this, _HeaderSidebar_instances, openCollapsiblePanel_fn).bind(this), { signal: this.abortController.signal }));
  }
  revealItems(withDelay = false) {
    const sequence = [
      [this.querySelector(".header-sidebar__main-panel"), { opacity: 1, transform: "translateX(0)" }, { duration: 0, delay: withDelay ? 0.5 : 0 }],
      [
        this.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist li"),
        { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] },
        { duration: 0.15, at: "-0.15", delay: window.themeVariables.settings.staggerMenuApparition ? stagger3(0.1) : 0 }
      ]
    ];
    const footer = this.querySelector(".header-sidebar__footer");
    if (footer) {
      sequence.push([footer, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }]);
    }
    return timeline9(sequence);
  }
};
_collapsiblePanel = new WeakMap();
_buttonElements = new WeakMap();
_HeaderSidebar_instances = new WeakSet();
openCollapsiblePanel_fn = function(event) {
  __privateGet(this, _buttonElements).forEach((button) => button.setAttribute("aria-expanded", button === event.currentTarget ? "true" : "false"));
  __privateGet(this, _collapsiblePanel)?.setAttribute("aria-activedescendant", event.currentTarget.getAttribute("aria-controls"));
  if (matchesMediaQuery("md-max")) {
    animate19(this.querySelector(".header-sidebar__main-panel"), { opacity: [1, 0], transform: ["translateX(0)", "translateX(-10px)"] }, { duration: 0.25 });
  }
};
onSidebarBeforeShow_fn = function() {
  animate19(this.querySelector(".header-sidebar__main-panel"), { opacity: 0, transform: "translateX(0)" }, { duration: 0 });
};
onSidebarAfterShow_fn = function() {
  this.revealItems();
};
onSidebarBeforeHide_fn = function() {
  if (matchesMediaQuery("md")) {
    __privateGet(this, _collapsiblePanel)?.removeAttribute("aria-activedescendant");
    __privateGet(this, _buttonElements).forEach((button) => button.setAttribute("aria-expanded", "false"));
  }
};
onSidebarAfterHide_fn = function() {
  if (matchesMediaQuery("md-max")) {
    __privateGet(this, _collapsiblePanel)?.removeAttribute("aria-activedescendant");
    __privateGet(this, _buttonElements).forEach((button) => button.setAttribute("aria-expanded", "false"));
  }
  Array.from(this.querySelectorAll("details")).forEach((detail) => detail.open = false);
};
var _sidebarDelegate, _HeaderSidebarCollapsiblePanel_instances, closePanel_fn, switchPanel_fn;
var HeaderSidebarCollapsiblePanel = class extends DialogElement {
  constructor() {
    super();
    __privateAdd(this, _HeaderSidebarCollapsiblePanel_instances);
    __privateAdd(this, _sidebarDelegate, new Delegate8(this));
    __privateGet(this, _sidebarDelegate).on("click", '[data-action="close-panel"]', __privateMethod(this, _HeaderSidebarCollapsiblePanel_instances, closePanel_fn).bind(this));
  }
  static get observedAttributes() {
    return [...super.observedAttributes, "aria-activedescendant"];
  }
  hideForOutsideClickTarget(target) {
    return false;
  }
  allowOutsideClickForTarget(target) {
    return target.closest(".header-sidebar") !== void 0;
  }
  createEnterAnimationControls() {
    if (matchesMediaQuery("md-max")) {
      return timeline9([[this, { opacity: [0, 1], transform: "translateX(0)" }, { duration: 0.3 }]]);
    } else {
      return timeline9([[this, { opacity: [0, 1], transform: ["translateX(0)", "translateX(calc(var(--transform-logical-flip) * 100%)"] }, { duration: 0.3 }]]);
    }
  }
  createLeaveAnimationControls() {
    if (matchesMediaQuery("md-max")) {
      return timeline9([[this, { opacity: [1, 0], transform: ["translateX(0)", "translateX(10px)"] }, { duration: 0.3 }]]);
    } else {
      return timeline9([[this, { opacity: [1, 0], transform: ["translateX(calc(var(--transform-logical-flip) * 100%))", "translateX(0)"] }, { duration: 0.3 }]]);
    }
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "aria-activedescendant") {
      if (oldValue === newValue) {
        return;
      }
      if (newValue !== null) {
        __privateMethod(this, _HeaderSidebarCollapsiblePanel_instances, switchPanel_fn).call(this, this.querySelector(`#${oldValue}`), this.querySelector(`#${newValue}`));
      } else {
        await this.hide();
        Array.from(this.querySelectorAll(".header-sidebar__sub-panel")).forEach((subPanel) => subPanel.hidden = true);
      }
    }
  }
};
_sidebarDelegate = new WeakMap();
_HeaderSidebarCollapsiblePanel_instances = new WeakSet();
closePanel_fn = function() {
  this.removeAttribute("aria-activedescendant");
  this.closest("header-sidebar").revealItems(true);
};
switchPanel_fn = async function(fromPanel, toPanel) {
  if (!this.open) {
    await this.show();
  }
  if (fromPanel) {
    await animate19(fromPanel, { opacity: [1, 0] }, { duration: 0.15 }).finished;
    fromPanel.hidden = true;
  }
  toPanel.hidden = false;
  const listSelector = matchesMediaQuery("md-max") ? ".header-sidebar__back-button, .header-sidebar__linklist li" : ".header-sidebar__linklist li";
  timeline9([
    [toPanel, { opacity: 1 }, { duration: 0 }],
    [
      toPanel.querySelectorAll(listSelector),
      { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] },
      { duration: 0.15, at: "-0.15", delay: window.themeVariables.settings.staggerMenuApparition ? stagger3(0.1) : 0 }
    ],
    [toPanel.querySelector(".header-sidebar__promo"), { opacity: [0, 1] }, { duration: 0.45 }]
  ]);
};
if (!window.customElements.get("x-header")) {
  window.customElements.define("x-header", Header);
}
if (!window.customElements.get("dropdown-menu-disclosure")) {
  window.customElements.define("dropdown-menu-disclosure", DropdownMenuDisclosure);
}
if (!window.customElements.get("mega-menu-disclosure")) {
  window.customElements.define("mega-menu-disclosure", MegaMenuDisclosure);
}
if (!window.customElements.get("header-search")) {
  window.customElements.define("header-search", HeaderSearch);
}
if (!window.customElements.get("header-sidebar")) {
  window.customElements.define("header-sidebar", HeaderSidebar);
}
if (!window.customElements.get("header-sidebar-collapsible-panel")) {
  window.customElements.define("header-sidebar-collapsible-panel", HeaderSidebarCollapsiblePanel);
}

class GeoModalTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.open.bind(this));
  }

  open() {
    var button = this.querySelector('[data-header-geo-open]');
    if (!button) return;

    var modalId = button.getAttribute('aria-controls');
    if (!modalId) return;

    this.dispatchEvent(
      new CustomEvent('header:geo-modal-open', {
        bubbles: true,
        composed: true,
        detail: { modalId: modalId }
      })
    );
  }
}

if (!window.customElements.get('geo-modal-trigger')) {
  window.customElements.define('geo-modal-trigger', GeoModalTrigger);
}


(function () {
  function bindGeoModalScrollLock(dialog) {
    if (!dialog || dialog.__geoScrollLockBound) return;
    dialog.__geoScrollLockBound = true;

    var lockedByGeo = false;
    var nativeShowModal = dialog.showModal;
    var nativeClose = dialog.close.bind(dialog);

    dialog.close = function () {
      var mobileSheet = window.matchMedia('(max-width: 699px)').matches
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      if (!mobileSheet) return nativeClose();

      var settled = false;
      function done(event) {
        if (event && event.target !== dialog) return;
        if (settled) return;
        settled = true;
        dialog.removeEventListener('animationend', done);
        dialog.classList.remove('is-closing');
        nativeClose();
      }

      dialog.classList.add('is-closing');
      dialog.addEventListener('animationend', done);
      window.setTimeout(done, 350);
    };

    dialog.addEventListener('cancel', function (event) {
      var mobileSheet = window.matchMedia('(max-width: 699px)').matches
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!mobileSheet || !dialog.open) return;
      event.preventDefault();
      dialog.close();
    });

    dialog.showModal = function () {
      if (!dialog.open && !lockedByGeo) {
        lockLayerCount += 1;
        syncDocumentScrollLock();
        lockedByGeo = true;
      }

      try {
        return nativeShowModal.call(dialog);
      } catch (error) {
        if (lockedByGeo && !dialog.open) {
          lockedByGeo = false;
          lockLayerCount -= 1;
          syncDocumentScrollLock();
        }
        throw error;
      }
    };

    dialog.addEventListener('close', function () {
      if (!lockedByGeo) return;
      lockedByGeo = false;
      lockLayerCount -= 1;
      syncDocumentScrollLock();
    });
  }

  function getAllGeoModals() {
    return Array.prototype.slice.call(document.querySelectorAll('dialog.header-geo-modal'));
  }

  function getOpenSidebars() {
    return Array.prototype.slice.call(document.querySelectorAll('header-sidebar')).filter(function (sidebar) {
      return sidebar.open;
    });
  }

  getAllGeoModals().forEach(bindGeoModalScrollLock);

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-header-geo-open]');
    if (!trigger) return;
    bindGeoModalScrollLock(document.getElementById(trigger.getAttribute('aria-controls')));
  }, true);

  document.addEventListener('header:geo-modal-open', function (event) {
    var target = document.getElementById(event.detail.modalId);
    if (!target) return;
    bindGeoModalScrollLock(target);

    getAllGeoModals().forEach(function (dialog) {
      if (dialog !== target && dialog.open) {
        dialog.close();
      }
    });

    getOpenSidebars().forEach(function (sidebar) {
      if (typeof sidebar.hide === 'function') {
        sidebar.hide();
      }
    });

    if (typeof target.showModal === 'function') target.showModal();
  });

  document.addEventListener('click', function (event) {
    if (
      event.target.tagName === 'DIALOG' &&
      event.target.classList.contains('header-geo-modal')
    ) {
      event.target.close();
    }

    var closeBtn = event.target.closest('[data-header-geo-close]');
    if (closeBtn) {
      var dialog = closeBtn.closest('dialog.header-geo-modal');
      if (dialog) dialog.close();
    }
  });

  function syncGeoCombo(root) {
    var countrySelect = root.querySelector('[data-header-geo-country-select]');
    var currencySelect = root.querySelector('[data-header-geo-currency-select]');

    if (countrySelect) {
      var flag = root.querySelector('[data-header-geo-country-flag], .header-geo-modal__flag');
      var countryLabel = root.querySelector('[data-header-geo-country-label]');
      if (flag && root.dataset.geoFlag === undefined) root.dataset.geoFlag = flag.getAttribute('src') || '';
      if (countryLabel && root.dataset.geoName === undefined) root.dataset.geoName = countryLabel.textContent.trim();

      if (countrySelect.selectedIndex >= 0) {
        var countryOption = countrySelect.options[countrySelect.selectedIndex];
        if (flag && countryOption.getAttribute('data-flag')) {
          flag.src = countryOption.getAttribute('data-flag');
        }
        if (countryLabel) {
          countryLabel.textContent = countryOption.getAttribute('data-name') || countryOption.textContent;
        }
      } else {
        // The Shopify country is not in the location list: show it as rendered by the server
        if (flag) flag.src = root.dataset.geoFlag;
        if (countryLabel) countryLabel.textContent = root.dataset.geoName;
      }
    }

    if (currencySelect && currencySelect.selectedIndex >= 0) {
      var currencyOption = currencySelect.options[currencySelect.selectedIndex];
      var currencyLabel = root.querySelector('[data-header-geo-currency-label]');
      if (currencyLabel) {
        currencyLabel.textContent = currencyOption.getAttribute('data-label') || currencyOption.textContent;
      }
    }
  }

  // Location and currency are saved apart: a location sets its currency, but a currency never moves the location
  function readGeoCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    if (!match) return '';
    try { return decodeURIComponent(match[1]).toUpperCase(); } catch (error) { return ''; }
  }

  function rememberLocation(geo) {
    if (!geo) return;
    document.cookie = 'gl_location=' + encodeURIComponent(geo) + ';path=/;max-age=31536000;SameSite=Lax';
  }

  function findLocationByGeo(countrySelect, geo) {
    for (var i = 0; i < countrySelect.options.length; i++) {
      if (countrySelect.options[i].getAttribute('data-geo') === geo) return countrySelect.options[i];
    }
    return null;
  }

  // Visitors from before gl_location only have a saved currency: their location is the one that uses it
  function findLocationOption(countrySelect, currency) {
    var fallback = null;
    for (var i = 0; i < countrySelect.options.length; i++) {
      var option = countrySelect.options[i];
      if (option.getAttribute('data-currency') !== currency) continue;
      if (option.getAttribute('data-market') === 'true') return option;
      fallback = fallback || option;
    }
    return fallback;
  }

  // Header trigger flags are rendered from the Shopify country, which never changes here (display-only locations).
  // Some triggers live in the sidebar's Shadow DOM (and its template), so those roots are searched too.
  function setTriggerFlags(root, option) {
    var scopes = [document];
    document.querySelectorAll('template').forEach(function (template) { scopes.push(template.content); });
    document.querySelectorAll('header-sidebar').forEach(function (sidebar) {
      if (sidebar.shadowRoot) scopes.push(sidebar.shadowRoot);
    });

    var selector = '[data-header-geo-open][aria-controls="' + root.id + '"] .header-geo-trigger__flag img';
    scopes.forEach(function (scope) {
      scope.querySelectorAll(selector).forEach(function (img) {
        if (img.dataset.geoSrc === undefined) {
          img.dataset.geoSrc = img.getAttribute('src') || '';
          img.dataset.geoSrcset = img.getAttribute('srcset') || '';
          img.dataset.geoAlt = img.getAttribute('alt') || '';
        }

        if (option && option.getAttribute('data-flag')) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = option.getAttribute('data-flag');
          img.alt = option.getAttribute('data-name') || option.textContent.trim();
        } else {
          img.src = img.dataset.geoSrc;
          if (img.dataset.geoSrcset) img.setAttribute('srcset', img.dataset.geoSrcset);
          img.alt = img.dataset.geoAlt;
        }
      });
    });
  }

  // The header follows the selected location; the server-rendered flag is kept while it is the Shopify country
  function syncTriggerFlags(root) {
    var countrySelect = root.querySelector('[data-header-geo-country-select]');
    if (!countrySelect) return;
    var option = countrySelect.options[countrySelect.selectedIndex];
    setTriggerFlags(root, option && !option.defaultSelected ? option : null);
  }

  document.addEventListener('change', function (event) {
    var root = event.target.closest('dialog.header-geo-modal');
    if (!root) return;

    var countrySelect = root.querySelector('[data-header-geo-country-select]');
    var currencySelect = root.querySelector('[data-header-geo-currency-select]');

    // Location and currency are display-only (Beast): the Shopify market is never switched, so nothing reloads
    var countryInput = root.querySelector('[data-header-geo-country]');
    if (countryInput && root.dataset.initialCountry) countryInput.value = root.dataset.initialCountry;

    if (event.target === countrySelect) {
      var option = countrySelect.options[countrySelect.selectedIndex];
      if (option) {
        rememberLocation(option.getAttribute('data-geo'));
        if (currencySelect) currencySelect.value = option.getAttribute('data-currency') || currencySelect.value;
      }
    }

    if ((event.target === currencySelect || event.target === countrySelect) && currencySelect && typeof window.applyGlCurrency === 'function') {
      window.applyGlCurrency(currencySelect.value);
    }

    syncGeoCombo(root);
    syncTriggerFlags(root);
  });

  // The header shows the Shopify country on every page: show the saved location and currency instead.
  // Runs on load, and again after first-visit geolocation picks them (layout/theme.liquid).
  function restoreSavedLocation() {
    var geo = readGeoCookie('gl_location');
    var currency = readGeoCookie('gl_currency');
    if (!geo && !currency) return;

    getAllGeoModals().forEach(function (root) {
      var countrySelect = root.querySelector('[data-header-geo-country-select]');
      var currencySelect = root.querySelector('[data-header-geo-currency-select]');
      if (!countrySelect || !currencySelect) return;

      var locationOption = geo ? findLocationByGeo(countrySelect, geo) : null;
      if (!geo && currency && currency !== root.dataset.initialCurrency) {
        locationOption = findLocationOption(countrySelect, currency);
      }
      if (locationOption) countrySelect.selectedIndex = locationOption.index;

      for (var i = 0; currency && i < currencySelect.options.length; i++) {
        if (currencySelect.options[i].value === currency) {
          currencySelect.selectedIndex = i;
          break;
        }
      }
      syncGeoCombo(root);
      syncTriggerFlags(root);
    });
  }

  window.glSyncGeoLocation = restoreSavedLocation;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreSavedLocation, { once: true });
  else restoreSavedLocation();

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.classList || !form.classList.contains('header-geo-modal__form')) return;

      var currencySelect = form.querySelector('[data-header-geo-currency-select]');
      var langInput = form.querySelector('[data-header-geo-language]');
      var countryInput = form.querySelector('[data-header-geo-country]');
      if (currencySelect && currencySelect.value && typeof window.applyGlCurrency === 'function') {
        window.applyGlCurrency(currencySelect.value);
      }

      var dialog = form.closest('dialog');
      var languageUnchanged = !langInput || !dialog || langInput.value === dialog.dataset.initialLanguage;
      var countryUnchanged = !countryInput || !dialog || countryInput.value === dialog.dataset.initialCountry;
      if (languageUnchanged && countryUnchanged) {
        event.preventDefault();
        if (dialog && typeof dialog.close === 'function') dialog.close();
      }
    },
    true
  );
})();

// js/sections/image-with-text.js
import { animate as animate20, inView as inView13 } from "vendor";
var _ImageWithText_instances, onBecameVisible_fn;
var ImageWithText = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ImageWithText_instances);
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe")) {
      inView13(this.querySelector('[reveal-on-scroll="true"]'), ({ target }) => __privateMethod(this, _ImageWithText_instances, onBecameVisible_fn).call(this, target), { margin: "-200px 0px 0px 0px" });
    }
  }
};
_ImageWithText_instances = new WeakSet();
onBecameVisible_fn = async function(target) {
  await imageLoaded(target);
  const fromValue = (window.direction === "rtl" ? -1 : 1) * (matchesMediaQuery("md-max") ? 0.6 : 1) * (this.classList.contains("image-with-text--reverse") ? 25 : -25);
  animate20(target, { opacity: 1, transform: [`translateX(${fromValue}px)`, "translateX(0)"] }, { easing: [0.215, 0.61, 0.355, 1] }, { duration: 0.8 });
};
if (!window.customElements.get("image-with-text")) {
  window.customElements.define("image-with-text", ImageWithText);
}

// js/sections/image-with-text-overlay.js
import { timeline as timeline10, inView as inView14 } from "vendor";
var _preventInitialTransition2, _ImageWithTextOverlay_instances, onBecameVisible_fn2;
var ImageWithTextOverlay = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _ImageWithTextOverlay_instances);
    __privateAdd(this, _preventInitialTransition2, false);
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => __privateSet(this, _preventInitialTransition2, event.detail.load));
    }
  }
  connectedCallback() {
    const revealOnScroll = this.getAttribute("reveal-on-scroll");
    if (matchesMediaQuery("motion-safe") && (revealOnScroll === "true" || revealOnScroll === "")) {
      inView14(this, ({ target }) => __privateMethod(this, _ImageWithTextOverlay_instances, onBecameVisible_fn2).call(this, target), { amount: 0.05 });
    }
  }
};
_preventInitialTransition2 = new WeakMap();
_ImageWithTextOverlay_instances = new WeakSet();
onBecameVisible_fn2 = async function(target) {
  const media = target.querySelector(":scope > picture img, :scope > svg, picture img, svg");
  const content = target.querySelector(":scope > .content, :scope > :not(picture, svg, a)");
  await imageLoaded(media);
  const animationControls = timeline10([
    [target, { opacity: 1 }],
    [media, { opacity: [0, 1], scale: [1.1, 1], easing: [0.215, 0.61, 0.355, 1], duration: 0.8 }],
    [content, { opacity: [0, 1], duration: 0.8 }]
  ]);
  if (__privateGet(this, _preventInitialTransition2)) {
    animationControls.finish();
  }
};
if (!window.customElements.get("image-with-text-overlay")) {
  window.customElements.define("image-with-text-overlay", ImageWithTextOverlay);
}

// js/sections/images-with-text-scroll.js
import { timeline as timeline11, animate as animate21, inView as inView15, scroll as scroll3, ScrollOffset } from "vendor";
var _itemElements, _imageElements, _textElements, _visibleImageElement, _ImagesWithTextScroll_instances, setupScrollObservers_fn, onBreakpointChanged_fn;
var ImagesWithTextScroll = class extends EffectCarousel {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ImagesWithTextScroll_instances);
    __privateAdd(this, _itemElements);
    __privateAdd(this, _imageElements);
    __privateAdd(this, _textElements);
    __privateAdd(this, _visibleImageElement);
  }
  // Reference to the currently visible image elements
  connectedCallback() {
    super.connectedCallback();
    __privateSet(this, _itemElements, Array.from(this.querySelectorAll(".images-with-text-scroll__item")));
    __privateSet(this, _imageElements, Array.from(this.querySelectorAll(".images-with-text-scroll__image")));
    __privateSet(this, _textElements, Array.from(this.querySelectorAll(".images-with-text-scroll__text")));
    __privateSet(this, _visibleImageElement, __privateGet(this, _imageElements)[0]);
    inView15(this, () => {
      __privateGet(this, _imageElements).forEach((imageElement) => imageElement.removeAttribute("loading"));
    });
    if (matchesMediaQuery("md")) {
      __privateMethod(this, _ImagesWithTextScroll_instances, setupScrollObservers_fn).call(this);
    }
    mediaQueryListener("md", __privateMethod(this, _ImagesWithTextScroll_instances, onBreakpointChanged_fn).bind(this));
  }
  /**
   * Override the "cellSelector". In this component, what makes a "slide" (on mobile) is the piece of text
   */
  get cellSelector() {
    return ".images-with-text-scroll__item";
  }
  /**
   * Swipe should only be available on mobile and tablet, otherwise it is a scroll-based experience
   */
  get allowSwipe() {
    return matchesMediaQuery("md-max");
  }
  /**
   * Perform the mobile animation
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    let imageAnimationSequence = [], toSlideImage = toSlide.querySelector(".images-with-text-scroll__image");
    if (toSlideImage && toSlideImage !== __privateGet(this, _visibleImageElement)) {
      imageAnimationSequence.push(
        [__privateGet(this, _visibleImageElement), { opacity: [1, 0] }, { duration: 0.8, delay: 0.4 }],
        [toSlideImage, { opacity: [0, 1] }, { duration: 0.8, at: "<", delay: 0.4 }]
      );
      __privateSet(this, _visibleImageElement, toSlideImage);
    }
    return timeline11([
      ...imageAnimationSequence,
      [
        fromSlide.querySelector(".images-with-text-scroll__text"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(-15px)"] },
        { duration: 0.4, at: "<", easing: [0.55, 0.055, 0.675, 0.19] }
      ],
      [
        toSlide.querySelector(".images-with-text-scroll__text"),
        { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] },
        { duration: 0.4, at: "+0.4", easing: [0.25, 0.46, 0.45, 0.94] }
      ]
    ]);
  }
};
_itemElements = new WeakMap();
_imageElements = new WeakMap();
_textElements = new WeakMap();
_visibleImageElement = new WeakMap();
_ImagesWithTextScroll_instances = new WeakSet();
/**
 * Setup the different observers for the desktop experience
 */
setupScrollObservers_fn = function() {
  __privateGet(this, _textElements).forEach((textElement) => {
    scroll3(animate21(textElement, { opacity: [0, 0.25, 1, 0.25, 0] }), { target: textElement, offset: ScrollOffset.Any });
  });
  scroll3(
    (info) => {
      const index = Math.min(Math.floor(info.y.progress / (1 / __privateGet(this, _itemElements).length)), __privateGet(this, _itemElements).length - 1), toImage = __privateGet(this, _itemElements)[index].querySelector(".images-with-text-scroll__image");
      if (toImage && toImage !== __privateGet(this, _visibleImageElement)) {
        timeline11([
          [__privateGet(this, _visibleImageElement), { opacity: [1, 0] }, { duration: 0.25 }],
          [toImage, { opacity: [0, 1] }, { duration: 0.25, at: "<" }]
        ]);
        __privateSet(this, _visibleImageElement, toImage);
      }
    },
    { target: this, offset: ["start center", "end center"] }
  );
};
/**
 * Due to how different the experience is on mobile and desktop, we use an observer to toggle between one mode and other
 */
onBreakpointChanged_fn = function(event) {
  if (event.matches) {
    __privateGet(this, _imageElements).forEach((image) => image.style = null);
    __privateGet(this, _textElements).forEach((text) => text.style = null);
    __privateMethod(this, _ImagesWithTextScroll_instances, setupScrollObservers_fn).call(this);
  } else {
    this.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  }
};
if (!window.customElements.get("images-with-text-scroll")) {
  window.customElements.define("images-with-text-scroll", ImagesWithTextScroll);
}

// js/sections/main-article.js
import { scroll as scroll4 } from "vendor";
var ArticleToolbar = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      scroll4(
        (info) => {
          this.classList.toggle("is-visible", info.y.progress > 0 && info.y.progress < 1);
        },
        {
          target: this.closest(".shopify-section"),
          offset: ["100px start", "end start"]
        }
      );
    }
  }
};
if (!window.customElements.get("article-toolbar")) {
  window.customElements.define("article-toolbar", ArticleToolbar);
}

// js/sections/media-grid.js
import { animate as animate22, inView as inView16 } from "vendor";
var _MediaGrid_instances, onReveal_fn;
var MediaGrid = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _MediaGrid_instances);
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe")) {
      inView16(this.querySelectorAll('[reveal-on-scroll="true"]'), __privateMethod(this, _MediaGrid_instances, onReveal_fn).bind(this), { margin: "-200px 0px 0px 0px" });
    }
  }
};
_MediaGrid_instances = new WeakSet();
onReveal_fn = async function(entry) {
  await imageLoaded(entry.target.querySelector(":scope > img"));
  animate22(entry.target, { opacity: [0, 1] }, { duration: 0.35, easing: "ease" });
};
if (!window.customElements.get("media-grid")) {
  window.customElements.define("media-grid", MediaGrid);
}

// js/sections/multi-column.js
var MultiColumn = class extends HTMLElement {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => {
        event.target.scrollIntoView({ inline: "center", block: "nearest", behavior: event.detail["load"] ? "auto" : "smooth" });
      });
    }
  }
};
if (!window.customElements.get("multi-column")) {
  window.customElements.define("multi-column", MultiColumn);
}

// js/sections/multiple-media-with-text.js
import { timeline as timeline12, inView as inView17 } from "vendor";
var _preventInitialTransition3, _MultipleMediaWithText_instances, onBecameVisible_fn3;
var MultipleMediaWithText = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _MultipleMediaWithText_instances);
    __privateAdd(this, _preventInitialTransition3, false);
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => __privateSet(this, _preventInitialTransition3, event.detail.load));
    }
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe") && this.hasAttribute("reveal-on-scroll")) {
      inView17(this, __privateMethod(this, _MultipleMediaWithText_instances, onBecameVisible_fn3).bind(this), { margin: "-10% 0px" });
    }
  }
};
_preventInitialTransition3 = new WeakMap();
_MultipleMediaWithText_instances = new WeakSet();
onBecameVisible_fn3 = function() {
  const timelineSequence = timeline12([
    [this, { opacity: 1 }, { duration: 0 }],
    "media",
    ...Array.from(this.querySelectorAll(".multiple-media-with-text__media-wrapper > *"), (media) => {
      return [media, { opacity: [0, 1], transform: ["rotate(0deg)", `rotate(${media.style.getPropertyValue("--media-rotate")})`] }, { duration: 0.5, at: "media" }];
    }),
    [this.querySelector(".multiple-media-with-text__content-wrapper"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5 }]
  ]);
  if (__privateGet(this, _preventInitialTransition3)) {
    timelineSequence.finish();
  }
};
if (!window.customElements.get("multiple-media-with-text")) {
  window.customElements.define("multiple-media-with-text", MultipleMediaWithText);
}

// js/sections/newsletter-popup.js
var NewsletterPopup = class extends PopIn {
  connectedCallback() {
    super.connectedCallback();
    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }
  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }
  get shouldAppearAutomatically() {
    return !(localStorage.getItem("theme:popup-filled") === "true" || // Never open if popup has been filled successfully
    this.hasAttribute("only-once") && localStorage.getItem("theme:popup-appeared") === "true");
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "open" && this.open) {
      localStorage.setItem("theme:popup-appeared", "true");
    }
  }
};
if (!window.customElements.get("newsletter-popup")) {
  window.customElements.define("newsletter-popup", NewsletterPopup);
}

// js/sections/privacy-banner.js
import { Delegate as Delegate9 } from "vendor";
var _delegate7, _PrivacyBanner_instances, onConsentLibraryLoaded_fn, acceptPolicy_fn, declinePolicy_fn;
var PrivacyBanner = class extends PopIn {
  constructor() {
    super();
    __privateAdd(this, _PrivacyBanner_instances);
    __privateAdd(this, _delegate7, new Delegate9(this));
    window.Shopify.loadFeatures([
      {
        name: "consent-tracking-api",
        version: "0.1",
        onLoad: __privateMethod(this, _PrivacyBanner_instances, onConsentLibraryLoaded_fn).bind(this)
      }
    ]);
  }
  connectedCallback() {
    super.connectedCallback();
    __privateGet(this, _delegate7).on("click", '[data-action="accept"]', __privateMethod(this, _PrivacyBanner_instances, acceptPolicy_fn).bind(this));
    __privateGet(this, _delegate7).on("click", '[data-action="decline"]', __privateMethod(this, _PrivacyBanner_instances, declinePolicy_fn).bind(this));
  }
  disconnectedCallback() {
    __privateGet(this, _delegate7).off();
  }
};
_delegate7 = new WeakMap();
_PrivacyBanner_instances = new WeakSet();
onConsentLibraryLoaded_fn = function() {
  if (window.Shopify.customerPrivacy?.shouldShowBanner()) {
    this.show();
  }
};
acceptPolicy_fn = function() {
  window.Shopify.customerPrivacy?.setTrackingConsent(true, this.hide.bind(this));
};
declinePolicy_fn = function() {
  window.Shopify.customerPrivacy?.setTrackingConsent(false, this.hide.bind(this));
};
if (!window.customElements.get("privacy-banner")) {
  window.customElements.define("privacy-banner", PrivacyBanner);
}

// js/sections/product.js
var _intersectionObserver3, _formElement, _footerElement, _latestFooterCondition, _latestFormCondition, _ProductStickyBar_instances, onFormVisibilityChange_fn;
var ProductStickyBar = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductStickyBar_instances);
    __privateAdd(this, _intersectionObserver3, new IntersectionObserver(__privateMethod(this, _ProductStickyBar_instances, onFormVisibilityChange_fn).bind(this)));
    __privateAdd(this, _formElement);
    __privateAdd(this, _footerElement);
    __privateAdd(this, _latestFooterCondition, false);
    __privateAdd(this, _latestFormCondition, false);
  }
  connectedCallback() {
    const bind = (form) => {
      if (!this.isConnected || !form) return;
      __privateSet(this, _formElement, form);
      __privateSet(this, _footerElement, document.querySelector(".shopify-section--footer"));
      __privateGet(this, _intersectionObserver3).observe(form);
      const footer = __privateGet(this, _footerElement);
      if (footer) __privateGet(this, _intersectionObserver3).observe(footer);
    };
    const form = resolveForm(this.getAttribute("form"));
    if (form) bind(form);
    else whenFormReady(this.getAttribute("form"), bind);
  }
  disconnectedCallback() {
    __privateGet(this, _intersectionObserver3).disconnect();
  }
};
_intersectionObserver3 = new WeakMap();
_formElement = new WeakMap();
_footerElement = new WeakMap();
_latestFooterCondition = new WeakMap();
_latestFormCondition = new WeakMap();
_ProductStickyBar_instances = new WeakSet();
onFormVisibilityChange_fn = function(entries) {
  const [formEntry, footerEntry] = [entries.find((entry) => entry.target === __privateGet(this, _formElement)), entries.find((entry) => entry.target === __privateGet(this, _footerElement))];
  if (formEntry) {
    __privateSet(this, _latestFormCondition, !formEntry.isIntersecting && formEntry.boundingClientRect.bottom < 0);
  }
  if (footerEntry) {
    __privateSet(this, _latestFooterCondition, !footerEntry.isIntersecting);
  }
  this.classList.toggle("is-visible", __privateGet(this, _latestFooterCondition) && __privateGet(this, _latestFormCondition));
};
if (!window.customElements.get("product-sticky-bar")) {
  window.customElements.define("product-sticky-bar", ProductStickyBar);
}

// js/sections/product-recommendations.js
var _isLoaded, _ProductRecommendations_instances, loadRecommendations_fn;
var ProductRecommendations = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRecommendations_instances);
    __privateAdd(this, _isLoaded, false);
  }
  connectedCallback() {
    const start = () => {
      if (this.isConnected) __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
    };
    const arm = () => {
      if (!("IntersectionObserver" in window)) {
        start();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        start();
      }, { rootMargin: "0px" });
      observer.observe(this);
    };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
  }
};
_isLoaded = new WeakMap();
_ProductRecommendations_instances = new WeakSet();
loadRecommendations_fn = async function() {
  if (__privateGet(this, _isLoaded)) {
    return;
  }
  __privateSet(this, _isLoaded, true);
  const section = this.closest(".shopify-section"), intent = this.getAttribute("intent") || "related", url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${extractSectionId(section)}&intent=${intent}`, response = await fetch(url, { priority: "low" });
  const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");
  if (productRecommendationsElement.childElementCount > 0) {
    this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);
    this.hidden = false;
  } else {
    this.remove();
  }
};
if (!window.customElements.get("product-recommendations")) {
  window.customElements.define("product-recommendations", ProductRecommendations);
}

// js/sections/quick-order-list.js
var _abortController12, _QuickOrderList_instances, onQuantityChange_fn, onUpdate_fn, onQuantityUpdated_fn;
var QuickOrderList = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderList_instances);
    __privateAdd(this, _abortController12);
    this.addEventListener("quick-order-list:update", __privateMethod(this, _QuickOrderList_instances, onUpdate_fn).bind(this));
    this.addEventListener("change", __privateMethod(this, _QuickOrderList_instances, onQuantityChange_fn).bind(this));
  }
};
_abortController12 = new WeakMap();
_QuickOrderList_instances = new WeakSet();
onQuantityChange_fn = async function() {
  __privateGet(this, _abortController12)?.abort();
};
onUpdate_fn = async function(event) {
  let sectionsToBundle = [extractSectionId(this)];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
  __privateSet(this, _abortController12, new AbortController());
  const inputWrapper = event.target.closest(".quick-order-list__quantity-actions")?.querySelector(".quantity-selector__input-wrapper");
  inputWrapper?.setAttribute("aria-busy", "true");
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      signal: __privateGet(this, _abortController12).signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        updates: event.detail.updates,
        sections: sectionsToBundle.join(",")
      })
    });
    inputWrapper?.removeAttribute("aria-busy");
    if (response.ok) {
      setTimeout(() => {
        __privateMethod(this, _QuickOrderList_instances, onQuantityUpdated_fn).call(this, response);
      }, 250);
    } else {
      const { message } = await response.json();
      event.target.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { message } }));
    }
  } catch (error) {
  }
};
onQuantityUpdated_fn = async function(response) {
  const cartContent = await response.json(), sectionId = extractSectionId(this);
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: "quick-order-list:add",
        cart: cartContent
      }
    })
  );
  this.closest(".shopify-section").outerHTML = cartContent["sections"][sectionId];
};
var _QuickOrderListQuantitySelector_instances, onQuantityChange_fn2, onCartError_fn2;
var QuickOrderListQuantitySelector = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderListQuantitySelector_instances);
    this.addEventListener("change", debounce(__privateMethod(this, _QuickOrderListQuantitySelector_instances, onQuantityChange_fn2).bind(this), 300));
    this.addEventListener("cart:error", __privateMethod(this, _QuickOrderListQuantitySelector_instances, onCartError_fn2).bind(this));
  }
};
_QuickOrderListQuantitySelector_instances = new WeakSet();
onQuantityChange_fn2 = function(event) {
  this.dispatchEvent(
    new CustomEvent("quick-order-list:update", {
      bubbles: true,
      detail: {
        updates: { [this.getAttribute("variant-id")]: parseInt(event.target.value) }
      }
    })
  );
};
onCartError_fn2 = function(event) {
  const errorSvg = `<svg width="13" height="13" fill="none" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.5" r="6.5" fill="#BF1515"/>
        <path fill="#fff" d="M6.75 7.97a.387.387 0 0 1-.3-.12.606.606 0 0 1-.12-.34l-.3-3.82c-.02-.247.033-.443.16-.59.127-.153.313-.23.56-.23.24 0 .42.077.54.23.127.147.18.343.16.59l-.3 3.82a.522.522 0 0 1-.12.34.344.344 0 0 1-.28.12Zm0 2.08a.744.744 0 0 1-.55-.21.751.751 0 0 1-.2-.54c0-.213.067-.387.2-.52.14-.14.323-.21.55-.21.233 0 .413.07.54.21.133.133.2.307.2.52 0 .22-.067.4-.2.54-.127.14-.307.21-.54.21Z"/>
      </svg>`;
  this.insertAdjacentHTML("afterend", `<p class="h-stack gap-2 justify-center text-xs" role="alert">${errorSvg} ${event.detail.message}</p>`);
  this.querySelector("quantity-selector")?.restoreDefaultValue();
};
var _QuickOrderListRemoveVariant_instances, onClick_fn;
var QuickOrderListRemoveVariant = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderListRemoveVariant_instances);
    this.addEventListener("click", __privateMethod(this, _QuickOrderListRemoveVariant_instances, onClick_fn));
  }
};
_QuickOrderListRemoveVariant_instances = new WeakSet();
onClick_fn = function() {
  this.dispatchEvent(
    new CustomEvent("quick-order-list:update", {
      bubbles: true,
      detail: {
        updates: { [this.getAttribute("variant-id")]: 0 }
      }
    })
  );
};
var _QuickOrderListRemoveAll_instances, onClick_fn2;
var QuickOrderListRemoveAll = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _QuickOrderListRemoveAll_instances);
    this.addEventListener("click", __privateMethod(this, _QuickOrderListRemoveAll_instances, onClick_fn2));
  }
};
_QuickOrderListRemoveAll_instances = new WeakSet();
onClick_fn2 = function() {
  const updates = JSON.parse(this.getAttribute("variant-ids")).reduce((acc, variantId) => {
    acc[variantId] = 0;
    return acc;
  }, {});
  this.dispatchEvent(
    new CustomEvent("quick-order-list:update", {
      bubbles: true,
      detail: {
        updates
      }
    })
  );
};
if (!window.customElements.get("quick-order-list")) {
  window.customElements.define("quick-order-list", QuickOrderList);
}
if (!window.customElements.get("quick-order-list-quantity-selector")) {
  window.customElements.define("quick-order-list-quantity-selector", QuickOrderListQuantitySelector);
}
if (!window.customElements.get("quick-order-list-remove-variant")) {
  window.customElements.define("quick-order-list-remove-variant", QuickOrderListRemoveVariant);
}
if (!window.customElements.get("quick-order-list-remove-all")) {
  window.customElements.define("quick-order-list-remove-all", QuickOrderListRemoveAll);
}

// js/sections/recently-viewed-products.js
var _isLoaded2, _RecentlyViewedProducts_instances, searchQueryString_get, loadProducts_fn;
var RecentlyViewedProducts = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _RecentlyViewedProducts_instances);
    __privateAdd(this, _isLoaded2, false);
  }
  connectedCallback() {
    __privateMethod(this, _RecentlyViewedProducts_instances, loadProducts_fn).call(this);
  }
};
_isLoaded2 = new WeakMap();
_RecentlyViewedProducts_instances = new WeakSet();
searchQueryString_get = function() {
  const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));
  if (this.hasAttribute("exclude-id")) {
    items.delete(parseInt(this.getAttribute("exclude-id")));
  }
  return Array.from(items.values(), (item) => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
};
loadProducts_fn = async function() {
  if (__privateGet(this, _isLoaded2)) {
    return;
  }
  __privateSet(this, _isLoaded2, true);
  const section = this.closest(".shopify-section"), url = `${Shopify.routes.root}search?type=product&q=${__privateGet(this, _RecentlyViewedProducts_instances, searchQueryString_get)}&section_id=${extractSectionId(section)}`, response = await fetch(url, { priority: "low" });
  const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
  if (recentlyViewedProductsElement.childElementCount > 0) {
    this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);
  } else {
    section.remove();
  }
};
if (!window.customElements.get("recently-viewed-products")) {
  window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
}

// js/sections/shop-the-look.js
import { animate as animate23, timeline as timeline13 } from "vendor";
var _controlledPopover, _selectedHotSpot, _ShopTheLookMobileCarousel_instances, setInitialPosition_fn, onSpotSelected_fn, onUpdateHotSpotPosition_fn, onLookChanged_fn, changeLookFocalPoint_fn, restorePosition_fn;
var ShopTheLookMobileCarousel = class extends ScrollCarousel {
  constructor() {
    super();
    __privateAdd(this, _ShopTheLookMobileCarousel_instances);
    __privateAdd(this, _controlledPopover);
    __privateAdd(this, _selectedHotSpot);
    this.addEventListener("carousel:change", __privateMethod(this, _ShopTheLookMobileCarousel_instances, onLookChanged_fn));
    Array.from(this.querySelectorAll(".shop-the-look__hot-spot-list")).forEach((list) => {
      list.carousel.addEventListener("carousel:select", __privateMethod(this, _ShopTheLookMobileCarousel_instances, onSpotSelected_fn).bind(this));
      list.carousel.addEventListener("carousel:change", () => __privateMethod(this, _ShopTheLookMobileCarousel_instances, onUpdateHotSpotPosition_fn).call(this, list));
    });
    Array.from(this.querySelectorAll(".shop-the-look__popover")).forEach((popover) => {
      popover.addEventListener("dialog:before-show", __privateMethod(this, _ShopTheLookMobileCarousel_instances, changeLookFocalPoint_fn).bind(this));
      popover.addEventListener("dialog:before-hide", __privateMethod(this, _ShopTheLookMobileCarousel_instances, restorePosition_fn).bind(this));
    });
  }
  connectedCallback() {
    super.connectedCallback();
    __privateMethod(this, _ShopTheLookMobileCarousel_instances, setInitialPosition_fn).call(this);
  }
  get isExpanded() {
    return this.classList.contains("is-expanded");
  }
};
_controlledPopover = new WeakMap();
_selectedHotSpot = new WeakMap();
_ShopTheLookMobileCarousel_instances = new WeakSet();
setInitialPosition_fn = function() {
  __privateSet(this, _selectedHotSpot, this.selectedCell.querySelector('.shop-the-look__hot-spot[aria-current="true"]'));
  __privateMethod(this, _ShopTheLookMobileCarousel_instances, onLookChanged_fn).call(this);
};
onSpotSelected_fn = function() {
  if (!this.isExpanded) {
    document.getElementById(this.selectedCell.getAttribute("data-popover-id")).show();
  }
};
onUpdateHotSpotPosition_fn = function(list) {
  __privateSet(this, _selectedHotSpot, list.querySelector('.shop-the-look__hot-spot[aria-current="true"]'));
  if (this.isExpanded) {
    __privateMethod(this, _ShopTheLookMobileCarousel_instances, changeLookFocalPoint_fn).call(this);
  }
};
onLookChanged_fn = function() {
  const popoverId = this.selectedCell.getAttribute("data-popover-id");
  __privateSet(this, _controlledPopover, document.getElementById(popoverId));
  this.nextElementSibling.setAttribute("aria-controls", popoverId);
};
changeLookFocalPoint_fn = function() {
  const scale = window.innerWidth / this.selectedCell.clientWidth, remainingSpace = window.innerHeight - __privateGet(this, _controlledPopover).shadowRoot.querySelector('[part="base"]').clientHeight, imageHeightAfterScale = Math.round(this.selectedCell.querySelector(".shop-the-look__image-wrapper").clientHeight * scale), outsideViewportImageHeight = Math.max(imageHeightAfterScale - remainingSpace, 0), insideViewportImageHeight = imageHeightAfterScale - outsideViewportImageHeight, hotSpotFocalPoint = Math.round((__privateGet(this, _selectedHotSpot).offsetTop + __privateGet(this, _selectedHotSpot).clientHeight / 2) * scale), offsetToMove = Math.round(hotSpotFocalPoint - insideViewportImageHeight / 2), minTranslateY = Math.round(-(this.parentElement.getBoundingClientRect().top - (imageHeightAfterScale - this.selectedCell.offsetHeight) / 2)), maxTranslateY = Math.round(minTranslateY - outsideViewportImageHeight), translateY = Math.min(Math.max(minTranslateY - offsetToMove, maxTranslateY), minTranslateY);
  if (!this.isExpanded) {
    animate23(this, { transform: ["translateY(0) scale(1)", `translateY(${translateY}px) scale(${scale})`] }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] });
    document.documentElement.style.setProperty("--hide-header-group", "1");
  } else {
    animate23(this, { transform: `translateY(${translateY}px) scale(${scale})` }, { duration: 0.4, easing: "ease-in-out" });
  }
  this.classList.add("is-expanded");
};
restorePosition_fn = function() {
  animate23(this, { transform: "translateY(0) scale(1)" }, { duration: 0.4, easing: [0.645, 0.045, 0.355, 1] }).finished.then(() => {
    this.style.transform = null;
  });
  this.classList.remove("is-expanded");
  document.documentElement.style.removeProperty("--hide-header-group");
};
var _ShopTheLookProductListCarousel_instances, updateButtonLink_fn;
var ShopTheLookProductListCarousel = class extends EffectCarousel {
  constructor() {
    super();
    __privateAdd(this, _ShopTheLookProductListCarousel_instances);
    this.addEventListener("carousel:change", __privateMethod(this, _ShopTheLookProductListCarousel_instances, updateButtonLink_fn).bind(this));
  }
};
_ShopTheLookProductListCarousel_instances = new WeakSet();
updateButtonLink_fn = function(event) {
  const productCard = event.detail.cell.querySelector(".product-card");
  if (productCard.hasAttribute("handle")) {
    this.nextElementSibling.href = `${Shopify.routes.root}products/${productCard.getAttribute("handle")}`;
  }
};
var ShopTheLookDesktopCarousel = class extends EffectCarousel {
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate23(toSlide.querySelectorAll(".shop-the-look__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5 });
  }
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return timeline13([
      [fromSlide.querySelectorAll(".shop-the-look__item-content"), { opacity: [1, 0] }, { duration: 0.3 }],
      [
        fromSlide.querySelectorAll(".shop-the-look__image-wrapper > *"),
        { opacity: [1, 0], transform: ["translateX(0)", "translateX(-15px)"] },
        { duration: 0.5, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ],
      [toSlide.querySelectorAll(".shop-the-look__image-wrapper > *"), { opacity: [0, 1], transform: ["translateX(-15px)", "translateX(0)"] }, { duration: 0.5, at: "<" }],
      [toSlide.querySelectorAll(".shop-the-look__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5, at: "-0.1" }]
    ]);
  }
};
var ShopTheLookPopover = class extends Popover {
  hideForOutsideClickTarget(target) {
    return false;
  }
  allowOutsideClickForTarget(target) {
    return target.classList.contains("shop-the-look__hot-spot");
  }
};
if (!window.customElements.get("shop-the-look-mobile-carousel")) {
  window.customElements.define("shop-the-look-mobile-carousel", ShopTheLookMobileCarousel);
}
if (!window.customElements.get("shop-the-look-product-list-carousel")) {
  window.customElements.define("shop-the-look-product-list-carousel", ShopTheLookProductListCarousel);
}
if (!window.customElements.get("shop-the-look-desktop-carousel")) {
  window.customElements.define("shop-the-look-desktop-carousel", ShopTheLookDesktopCarousel);
}
if (!window.customElements.get("shop-the-look-popover")) {
  window.customElements.define("shop-the-look-popover", ShopTheLookPopover);
}

// js/sections/slideshow.js
import { timeline as timeline14, Delegate as Delegate10 } from "vendor";
var _delegate8, _onVideoEndedListener, _SlideshowCarousel_instances, autoplayPauseOnVideo_get, getSlideEnteringSequence_fn, getSlideLeavingSequence_fn, muteVideo_fn, unmuteVideo_fn, onVolumeChange_fn, onSlideSettle_fn, onVideoEnded_fn, onNextButtonClicked_fn, handleAutoplayProgress_fn;
var SlideshowCarousel = class extends EffectCarousel {
  constructor() {
    super();
    __privateAdd(this, _SlideshowCarousel_instances);
    __privateAdd(this, _delegate8, new Delegate10(this));
    __privateAdd(this, _onVideoEndedListener, __privateMethod(this, _SlideshowCarousel_instances, onVideoEnded_fn).bind(this));
    __privateGet(this, _delegate8).on("click", '[data-action="navigate-next"]', __privateMethod(this, _SlideshowCarousel_instances, onNextButtonClicked_fn).bind(this));
    __privateGet(this, _delegate8).on("click", '[data-action="unmute"]', __privateMethod(this, _SlideshowCarousel_instances, unmuteVideo_fn).bind(this));
    __privateGet(this, _delegate8).on("click", '[data-action="mute"]', __privateMethod(this, _SlideshowCarousel_instances, muteVideo_fn).bind(this));
    this.addEventListener("volumechange", __privateMethod(this, _SlideshowCarousel_instances, onVolumeChange_fn), { capture: true });
    this.addEventListener("carousel:settle", __privateMethod(this, _SlideshowCarousel_instances, onSlideSettle_fn));
    if (this.hasAttribute("autoplay") && this.player) {
      this.player.addEventListener("player:start", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
      this.player.addEventListener("player:stop", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
      this.player.addEventListener("player:visibility-pause", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
      this.player.addEventListener("player:visibility-resume", __privateMethod(this, _SlideshowCarousel_instances, handleAutoplayProgress_fn).bind(this));
    }
  }
  disconnectedCallback() {
    __privateGet(this, _delegate8).destroy();
  }
  /**
   * Check when the media is fully loaded so that we can initiate the switch
   */
  async createOnBecameVisibleAnimationControls(toSlide) {
    if (toSlide.getAttribute("media-type") === "image") {
      await imageLoaded(toSlide.querySelectorAll("img"));
    } else {
      await videoLoaded(toSlide.querySelectorAll("video"));
    }
    if (toSlide.hasAttribute("reveal-on-scroll")) {
      return timeline14([...__privateMethod(this, _SlideshowCarousel_instances, getSlideEnteringSequence_fn).call(this, toSlide)]);
    }
    return { finished: Promise.resolve() };
  }
  /**
   * Create the animation when it changes from one slide to another, by making sure to properly handling the videos
   */
  createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
    const fromVideo = Array.from(fromSlide.querySelectorAll("video")), toVideo = Array.from(toSlide.querySelectorAll("video")).filter((video) => video.offsetParent);
    fromSlide.removeEventListener("ended", __privateGet(this, _onVideoEndedListener), { capture: true });
    fromVideo.forEach((video) => {
      video.muted = true;
      video.pause();
    });
    toVideo.forEach((video) => {
      video.muted = true;
      video.currentTime = 0;
      video.play();
    });
    return {
      leaveControls: () => timeline14(__privateMethod(this, _SlideshowCarousel_instances, getSlideLeavingSequence_fn).call(this, fromSlide)),
      enterControls: () => timeline14(__privateMethod(this, _SlideshowCarousel_instances, getSlideEnteringSequence_fn).call(this, toSlide))
    };
  }
};
_delegate8 = new WeakMap();
_onVideoEndedListener = new WeakMap();
_SlideshowCarousel_instances = new WeakSet();
autoplayPauseOnVideo_get = function() {
  return this.hasAttribute("autoplay-pause-on-video");
};
/**
 * Generate the part of the sequence when the given slide is entering
 */
getSlideEnteringSequence_fn = function(slide) {
  const slideContent = slide.querySelector(".slideshow__slide-content");
  if (slideContent.classList.contains("slideshow__slide-content--boxed")) {
    return [
      [slide, { opacity: [0, 1] }, { duration: 0.8, easing: [0.25, 0.46, 0.45, 0.94] }],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [0, 1], transform: ["scale(1.2)", "scale(1)"] },
        { duration: 0.8, at: "<", easing: [0.25, 0.46, 0.45, 0.94] }
      ],
      [slideContent, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }]
    ];
  } else {
    return [
      [slide, { opacity: [0, 1] }, { duration: 0.8, easing: [0.25, 0.46, 0.45, 0.94] }],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [0, 1], transform: ["scale(1.2)", "scale(1)"] },
        { duration: 0.8, at: "<", easing: [0.25, 0.46, 0.45, 0.94] }
      ],
      [
        slideContent.querySelector(".prose"),
        { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] },
        { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }
      ],
      [
        slideContent.querySelector(".button-group"),
        { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] },
        { duration: 0.6, at: "-0.4", easing: [0.215, 0.61, 0.355, 1] }
      ]
    ];
  }
};
/**
 * Generate the part of the sequence when the given slide is leaving
 */
getSlideLeavingSequence_fn = function(slide) {
  const slideContent = slide.querySelector(".slideshow__slide-content");
  if (slideContent.classList.contains("slideshow__slide-content--boxed")) {
    return [
      [slideContent, { opacity: [1, 0], transform: ["translateY(0)", "translateY(20px)"] }, { duration: 0.25, at: "leaving", easing: [0.55, 0.055, 0.675, 0.19] }],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [1, 0] },
        { duration: 0.2, at: "-0.1", easing: [0.55, 0.055, 0.675, 0.19] }
      ]
    ];
  } else {
    return [
      [
        slideContent.querySelector(".prose"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(10px)"] },
        { duration: 0.25, at: "leaving", easing: [0.55, 0.055, 0.675, 0.19] }
      ],
      [
        slideContent.querySelector(".button-group"),
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(20px)"] },
        { duration: 0.25, at: "<", easing: [0.55, 0.055, 0.675, 0.19] }
      ],
      [
        slide.querySelectorAll(".content-over-media > :is(video-media, svg), .content-over-media > picture img"),
        { opacity: [1, 0] },
        { duration: 0.2, at: "-0.1", easing: [0.55, 0.055, 0.675, 0.19] }
      ]
    ];
  }
};
/**
 * Mute the video for the active slide, by ignoring the video that may not be visible
 */
muteVideo_fn = function(event) {
  event.preventDefault();
  Array.from(this.selectedCell.querySelectorAll("video")).filter((video) => video.offsetParent).forEach((video) => video.muted = true);
};
/**
 * Unmute the video for the active slide, by ignoring the video that may not be visible
 */
unmuteVideo_fn = function(event) {
  event.preventDefault();
  Array.from(this.selectedCell.querySelectorAll("video")).filter((video) => video.offsetParent).forEach((video) => video.muted = false);
};
/**
 * Update the volume controls button (if any) based on whether the video is mute or not
 */
onVolumeChange_fn = function(event) {
  const volumeControl = event.target.closest(".slideshow__slide").querySelector(".slideshow__volume-control");
  if (volumeControl) {
    volumeControl.querySelector('[data-action="unmute"]').hidden = !event.target.muted;
    volumeControl.querySelector('[data-action="mute"]').hidden = event.target.muted;
  }
};
/**
 * Do action when the slide settles
 */
onSlideSettle_fn = function(event) {
  const videoList = Array.from(event.detail.cell.querySelectorAll("video"));
  if (__privateGet(this, _SlideshowCarousel_instances, autoplayPauseOnVideo_get) && this.cells.length > 1 && videoList.length > 0) {
    this.player?.pause();
    event.detail.cell.addEventListener("ended", __privateGet(this, _onVideoEndedListener), { capture: true, once: true });
  }
};
/**
 * If the merchant decide to autorotate but to stop autoplay when video is playing, we register a listener that
 * will move to the next slide (if any)
 */
onVideoEnded_fn = function() {
  this.next();
};
/**
 * Scroll to the next section when the button is clicked, by leveraging JS native scroll
 */
onNextButtonClicked_fn = function() {
  this.closest(".shopify-section").nextElementSibling?.scrollIntoView({ block: "start", behavior: "smooth" });
};
handleAutoplayProgress_fn = async function(event) {
  switch (event.type) {
    case "player:start":
      let autoplayDuration = this.getAttribute("autoplay");
      if (__privateGet(this, _SlideshowCarousel_instances, autoplayPauseOnVideo_get) && this.selectedCell.getAttribute("media-type") === "video") {
        const video = Array.from(this.selectedCell.querySelectorAll("video")).filter((video2) => video2.offsetParent).pop();
        if (isNaN(video.duration)) {
          await new Promise((resolve) => {
            video.onloadedmetadata = () => resolve();
          });
        }
        autoplayDuration = video.duration;
      }
      this.style.setProperty("--slideshow-progress-duration", `${autoplayDuration}s`);
      this.style.setProperty("--slideshow-progress-play-state", "running");
      break;
    case "player:stop":
      this.style.setProperty("--slideshow-progress-duration", `0s`);
      this.style.setProperty("--slideshow-progress-play-state", "paused");
      break;
    case "player:visibility-pause":
      this.style.setProperty("--slideshow-progress-play-state", "paused");
      break;
    case "player:visibility-resume":
      this.style.setProperty("--slideshow-progress-play-state", "running");
      break;
  }
};
if (!window.customElements.get("slideshow-carousel")) {
  window.customElements.define("slideshow-carousel", SlideshowCarousel);
}

// js/sections/testimonials.js
import { animate as animate24 } from "vendor";
var TestimonialCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide, { direction }) {
    return {
      leaveControls: () => animate24(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-15px)"] }, { duration: 0.4, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate24(toSlide, { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.4, delay: 0, easing: [0.25, 0.46, 0.45, 0.94] })
    };
  }
};
if (!window.customElements.get("testimonial-carousel")) {
  window.customElements.define("testimonial-carousel", TestimonialCarousel);
}

// js/sections/text-with-icons.js
import { animate as animate25 } from "vendor";
var TextWithIconsCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate25(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.3, easing: "ease-in" }),
      enterControls: () => animate25(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.3, delay: 0.2, easing: "ease-out" })
    };
  }
};
if (!window.customElements.get("text-with-icons-carousel")) {
  window.customElements.define("text-with-icons-carousel", TextWithIconsCarousel);
}

window.initDragSliders = (() => {
  const DRAG_THRESHOLD = 5;
  const MOMENTUM_DECAY = 0.92;
  const initialized = new WeakSet();

  return (selector = '.floating-controls-container--no-carousel') => {
    document.querySelectorAll(selector).forEach((slider) => {
      if (initialized.has(slider)) return;
      initialized.add(slider);

      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let didDrag = false;
      let velocity = 0;
      let rafId = null;

      const cancelMomentum = () => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
        velocity = 0;
      };

      const applyMomentum = () => {
        if (Math.abs(velocity) < 0.5) {
          cancelMomentum();
          return;
        }

        slider.scrollLeft += velocity;
        velocity *= MOMENTUM_DECAY;
        rafId = requestAnimationFrame(applyMomentum);
      };

      const endDrag = () => {
        if (pointerId === null) return;
        if (slider.hasPointerCapture(pointerId)) {
          slider.releasePointerCapture(pointerId);
        }
        pointerId = null;
        dragging = false;

        if (didDrag && velocity !== 0) {
          applyMomentum();
        } else {
          slider.classList.remove('is-dragging');
        }
      };

      slider.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (slider.scrollWidth <= slider.clientWidth) return;

        cancelMomentum();
        pointerId = e.pointerId;
        startX = e.clientX;
        startScroll = slider.scrollLeft;
        didDrag = false;
        velocity = 0;
      });

      slider.addEventListener('pointermove', (e) => {
        if (e.pointerId !== pointerId) return;

        const dx = e.clientX - startX;

        if (!dragging) {
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          dragging = true;
          didDrag = true;
          slider.setPointerCapture(pointerId);
          slider.classList.add('is-dragging');
          cancelMomentum();
        }

        velocity = -dx / 10; // store velocity for momentum
        slider.scrollLeft = startScroll - dx;
      });

      slider.addEventListener('pointerup', endDrag);
      slider.addEventListener('pointercancel', endDrag);

      slider.addEventListener(
        'click',
        (e) => {
          if (!didDrag) return;
          e.preventDefault();
          e.stopPropagation();
          didDrag = false;
        },
        true
      );

      slider.addEventListener('dragstart', (e) => e.preventDefault());
    });
  };
})();



// js/sections/timeline.js
import { animate as animate26, timeline as timeline15 } from "vendor";
var TimelineCarousel = class extends EffectCarousel {
  createOnBecameVisibleAnimationControls(toSlide) {
    return animate26(toSlide.querySelectorAll(".timeline__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5 });
  }
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return timeline15([
      [fromSlide.querySelectorAll(".timeline__item-content"), { opacity: [1, 0] }, { duration: 0.3 }],
      [
        fromSlide.querySelector(".timeline__item-image-wrapper :is(img, svg)"),
        { opacity: [1, 0], transform: ["translateX(0)", "translateX(-15px)"] },
        { duration: 0.5, at: "<", easing: [0.645, 0.045, 0.355, 1] }
      ],
      [toSlide.querySelector(".timeline__item-image-wrapper :is(img, svg)"), { opacity: [0, 1], transform: ["translateX(-15px)", "translateX(0)"] }, { duration: 0.5, at: "<" }],
      [toSlide.querySelectorAll(".timeline__item-content"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.5, at: "-0.1" }]
    ]);
  }
};
if (!window.customElements.get("timeline-carousel")) {
  window.customElements.define("timeline-carousel", TimelineCarousel);
}

// js/theme.js
import { Delegate as Delegate11 } from "vendor";
(() => {
  const delegateDocument = new Delegate11(document.documentElement);
  delegateDocument.on("click", 'a[href*="#"]', (event, target) => {
    if (event.defaultPrevented || target.matches("[allow-hash-change]") || target.pathname !== window.location.pathname || target.search !== window.location.search) {
      return;
    }
    const url = new URL(target.href);
    if (url.hash === "") {
      return;
    }
    const anchorElement = document.querySelector(url.hash);
    if (anchorElement) {
      event.preventDefault();
      anchorElement.scrollIntoView({ block: "start", behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto" });
      document.documentElement.dispatchEvent(new CustomEvent("hashchange:simulate", { bubbles: true, detail: { hash: url.hash } }));
    }
  });
  if (navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)) {
    document.head.querySelector('meta[name="viewport"]').content = "width=device-width, initial-scale=1.0, height=device-height, minimum-scale=1.0, maximum-scale=1.0";
  }
  Array.from(document.querySelectorAll(".prose table")).forEach((table) => {
    table.outerHTML = '<div class="table-scroller">' + table.outerHTML + "</div>";
  });
})();
export {
  AccordionDisclosure,
  AccountLogin,
  AnnouncementBarCarousel,
  ArticleToolbar,
  BeforeAfter,
  BlogPosts,
  BuyButtons,
  CarouselNavigation,
  CarouselNextButton,
  CarouselPrevButton,
  CartCount,
  CartDiscountBanner,
  CartDiscountField,
  CartDiscountRemoveButton,
  CartDot,
  CartDrawer,
  CartNote,
  CollectionBanner,
  CollectionLayoutSwitch,
  ConfirmButton,
  CopyButton,
  CountdownTimer,
  CountdownTimerFlip,
  CountdownTimerFlipDigit,
  CountrySelector,
  CustomDetails,
  DialogCloseButton,
  DialogElement,
  Drawer,
  DynamicGrid,
  EffectCarousel,
  FacetLink,
  FacetsDrawer,
  FacetsForm,
  FacetsSortPopover,
  FaqToc,
  FeaturedCollectionsCarousel,
  FreeShippingBar,
  GestureArea,
  GiftCardRecipient,
  Header,
  HeightObserver,
  ImageParallax,
  ImageWithText,
  ImageWithTextOverlay,
  ImagesWithTextScroll,
  LineItemQuantity,
  Listbox,
  LoadingBar,
  MarqueeText,
  MediaGrid,
  MenuDisclosure,
  Modal,
  ModelMedia,
  MultiColumn,
  MultipleMediaWithText,
  NewsletterPopup,
  OpenLightBoxButton,
  Player,
  PopIn,
  Popover,
  PredictiveSearch,
  PriceRange,
  PrivacyBanner,
  ProductCard,
  ProductForm,
  ProductGallery,
  ProductGalleryNavigation,
  ProductList,
  ProductLoader,
  ProductRecommendations,
  ProductRerender,
  ProductStickyBar,
  ProgressBar,
  QrCode,
  QuantityInput,
  QuantitySelector,
  QuickBuyModal,
  QuickOrderList,
  RecentlyViewedProducts,
  SafeSticky,
  ScrollCarousel,
  ShareButton,
  ShippingEstimator,
  ShopTheLookDesktopCarousel,
  ShopTheLookMobileCarousel,
  ShopTheLookPopover,
  ShopTheLookProductListCarousel,
  SlideshowCarousel,
  Tabs,
  TestimonialCarousel,
  TextWithIconsCarousel,
  TimelineCarousel,
  VariantPicker,
  VideoMedia,
  announceError,
  announceStatus,
  cachedFetch,
  createMediaImg,
  debounce,
  deepQuerySelector,
  extractSectionId,
  fetchCart,
  generateSrcset,
  imageLoaded,
  matchesMediaQuery,
  mediaQueryListener,
  throttle,
  videoLoaded,
  waitForEvent
};
